import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ThreeCircles } from 'react-loader-spinner';
import { Fetching } from '../UI/Modal/Modal';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ForumIcon from '@mui/icons-material/Forum';
import BadgeIcon from '@mui/icons-material/Badge';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import css from './SchoolAdminDashboard.module.css';
import { formatLargeText, renderMarkdownNote } from '../../utils/client-functions';
import teacherModalCss from './TeacherDashboard.module.css';
import { PREBUILT_EXAM_CATALOG } from '../../utils/prebuiltExamCatalog';
import { compressImageIfNeeded, validateImageFile, convertToJpeg } from '../../utils/imageUtils';
import { SPECIAL_SYMBOLS } from '../../utils/specialSymbols';
import { ensureMarkingGuideMarks, reconcileQuestionsForDisplay } from '../../utils/examMarkingGuide';
import CareerPathSummaryCard from './CareerPathSummaryCard';
import MessagingSegment from './MessagingSegment';
import SchoolPasswordInput from './SchoolPasswordInput';
import TimetableGrid from './TimetableGrid';
import SchoolPWAInstallBanner from './SchoolPWAInstallBanner';
import SchoolPoweredFooter from './SchoolPoweredFooter';
import SchoolNotificationCenter from './SchoolNotificationCenter';
import useSchoolExamSync from '../../hooks/useSchoolExamSync';
import SubjectChoicesSegment from './SubjectChoicesSegment';
import ExamModeChoiceModal from './ExamModeChoiceModal';
import ManualScriptUploadDrawer from './ManualScriptUploadDrawer';
import ManualScoreEntryDrawer from './ManualScoreEntryDrawer';
import ManualExamMissedAction from './ManualExamMissedAction';
import ExamActionsMenu from './ExamActionsMenu';
import ReportCardPreviewModal from './ReportCardPreviewModal';
import { RetakeAssignmentModal, RetakeBadge, RetakeHistoryList } from './RetakeControls';

//  API helper 
function schoolFetch(path, opts = {}) {
  const token = localStorage.getItem('school-token');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers = {
    'x-access-token': token || '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(opts.headers || {}),
  };
  return fetch(path, {
    ...opts,
    headers,
  }).then(async (r) => {
    let data = {};
    let isJson = true;
    try {
      data = await r.json();
    } catch {
      isJson = false;
    }
    if (r.status === 202 && data.queued && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('school-sub-admin-action-queued', { detail: data }));
    }
    return { ok: r.ok, status: r.status, data, isJson };
  });
}

// Raw-Response variant (mirrors parentFetch/studentFetch's contract) — used by
// <MessagingSegment/>, which expects a Response object (res.ok/res.json()).
function schoolFetchRaw(path, opts = {}) {
  const token = localStorage.getItem('school-token');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers = {
    'x-access-token': token || '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(opts.headers || {}),
  };
  return fetch(path, {
    ...opts,
    headers,
  });
}

//  Sidebar nav items 
const NAV = [
  { key: 'overview',   label: 'Overview',          Icon: DashboardIcon   },
  { key: 'students',   label: 'Students',          Icon: PeopleIcon      },
  { key: 'parents',    label: 'Parents',           Icon: FamilyRestroomIcon },
  { key: 'teachers',   label: 'Teachers',          Icon: SchoolIcon      },
  { key: 'classes',    label: 'Classes & Subjects', Icon: AssignmentIcon  },
  { key: 'curriculum', label: 'Curriculum',        Icon: MenuBookIcon    },
  { key: 'exams',      label: 'Tests & Exams',     Icon: AssignmentIcon  },
  { key: 'fees',       label: 'Fees & Billing',    Icon: PaymentsIcon    },
  { key: 'calendar',   label: 'Calendar',          Icon: CalendarMonthIcon },
  { key: 'timetable',  label: 'Timetable',         Icon: ScheduleIcon     },
  { key: 'attendance', label: 'Attendance',        Icon: EventAvailableIcon },
  { key: 'lesson-plans', label: 'Lesson Plans',    Icon: AutoStoriesIcon },
  { key: 'expenses',   label: 'Expenses',          Icon: ReceiptLongIcon },
  { key: 'payroll',    label: 'Payroll',           Icon: BadgeIcon },
  { key: 'inventory',  label: 'Store & Inventory', Icon: Inventory2Icon },
  { key: 'messages',   label: 'Messages',          Icon: ForumIcon },
  { key: 'analytics',  label: 'Analytics',         Icon: BarChartIcon    },
  { key: 'reports',    label: 'Reports',           Icon: DescriptionIcon },
  { key: 'sub-admins', label: 'Sub-Admins',         Icon: AdminPanelSettingsIcon },
  { key: 'settings',   label: 'Settings',          Icon: SettingsIcon    },
  { key: 'change-password', label: 'Change Password', Icon: LockIcon      },
];

// ─── Tiny helper components ───────────────────────────────────────────────────
function Initials({ first, last }) {
  return (
    <div className={css.avatarInitials}>
      {(first?.[0] || '').toUpperCase()}{(last?.[0] || '').toUpperCase()}
    </div>
  );
}

function StatusChip({ status }) {
  const cls = {
    active: css.chipActive, removed: css.chipRemoved, graduated: css.chipInactive, transferred: css.chipInactive,
    // Exam-specific statuses (reused by AdminExamsTab)
    draft: css.chipInactive, scheduled: css.chipActive, closed: css.chipActive, pending_approval: css.chipPending,
    manual_score_ready: css.chipRemoved, manual_score_recorded: css.chipActive,
    manual_grading_ready: css.chipRemoved,
    // Sub-admin account & activity-log statuses (reused by SubAdminsTab)
    suspended: css.chipRemoved, inactive: css.chipInactive,
    auto_approved: css.chipActive, approved: css.chipActive, pending: css.chipPending,
    rejected: css.chipRemoved, failed: css.chipRemoved,
  }[status] || css.chipInactive;
  const labels = {
    pending_approval: 'Pending Approval', auto_approved: 'Auto-Approved', pending: 'Pending Review',
    inactive: 'Invite Pending',
    manual_score_ready: 'ready for score recording',
    manual_score_recorded: 'Scores recorded',
    manual_grading_ready: 'Ready for Script Grading',
  };
  const label = labels[status] || status;
  return <span className={`${css.chip} ${cls}`}>{label}</span>;
}

function renderExamDate(dateStr) {
  if (!dateStr) return <span className={css.dateCellEmpty}>—</span>;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return <span className={css.dateCellEmpty}>—</span>;
  const dateFormatted = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeFormatted = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={css.dateCellStacked}>
      <span className={css.dateCellDate}>{dateFormatted}</span>
      <span className={css.dateCellTime}>{timeFormatted}</span>
    </div>
  );
}

const SUB_ADMIN_ACTION_LABELS = {
  'parents.invite': 'Invite a parent',
  'teachers.invite': 'Invite a teacher',
  'classes.create': 'Create a class',
  'fees.generateInvoices': 'Generate fee invoices',
  'fees.payoutAccount': 'Change the payout account',
  'calendar.create': 'Create a calendar event',
  'expenses.record': 'Record an expense',
  'payroll.process': 'Process a payroll run',
};

function SubAdminActionLabel({ action, onViewDetails }) {
  const moduleItem = NAV.find(item => item.key === action.moduleKey);
  const label = SUB_ADMIN_ACTION_LABELS[action.actionKey]
    || `${(action.method || 'Update').toLowerCase()} ${moduleItem?.label || 'school data'}`;
  return (
    <div>
      <div>{label}</div>
      {onViewDetails && (
        <button type="button" className={css.btnSecondary} style={{ marginTop: 6, padding: '6px 10px', fontSize: '0.76rem' }} onClick={() => onViewDetails(action)}>
          View details
        </button>
      )}
    </div>
  );
}

// Owner view: all pending requests for this module, with approve/reject controls.
// Sub-admin view: that sub-admin's own recent requests for this module, with
// status, details, edit, and withdraw actions for pending requests.
function PendingSubAdminApprovals({ moduleKey, title, onNavigate, isOwner }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [withdrawTarget, setWithdrawTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const path = isOwner
      ? `/school/sub-admins/actions?limit=100&moduleKey=${moduleKey}&status=pending`
      : `/school/sub-admins/my-actions?limit=20&moduleKey=${moduleKey}`;
    const result = await schoolFetch(path);
    if (result.ok) setActions(isOwner ? (result.data.actions || []).filter(action => action.status === 'pending') : (result.data.actions || []));
    else setError(result.data?.error || 'Could not load requests.');
    setLoading(false);
  }, [isOwner, moduleKey]);

  useEffect(() => { load(); }, [load]);
  // Refresh a sub-admin's own list the moment their submission is queued.
  useEffect(() => {
    if (isOwner) return;
    const handleQueued = () => load();
    window.addEventListener('school-sub-admin-action-queued', handleQueued);
    return () => window.removeEventListener('school-sub-admin-action-queued', handleQueued);
  }, [isOwner, load]);

  const approve = async password => {
    const result = await schoolFetch(`/school/sub-admins/actions/${approveTarget._id}/approve`, { method: 'POST', headers: { 'x-admin-password': password } });
    if (result.ok) { setApproveTarget(null); load(); }
    return result;
  };

  const finishReject = () => { setRejectTarget(null); load(); };
  const approveFromDetails = () => { setDetailsTarget(null); setApproveTarget(detailsTarget); };
  const rejectFromDetails = () => { setDetailsTarget(null); setRejectTarget(detailsTarget); };
  const withdraw = async () => {
    const result = await schoolFetch(`/school/sub-admins/my-actions/${withdrawTarget._id}`, { method: 'DELETE' });
    if (result.ok) { setWithdrawTarget(null); load(); }
    return result;
  };

  if (!loading && actions.length === 0 && !error) return null;
  const hasPending = actions.some(action => action.status === 'pending');
  return (
    <div className={`${css.card} ${hasPending ? css.pendingApprovalGlow : ''}`}>
      <div className={css.cardHeader}>
        <div>
          <h2 className={css.cardTitle}>{isOwner ? (title || 'Pending Sub-Admin Approvals') : 'Your Requests Awaiting Approval'}</h2>
          <p className={css.modalSub}>{isOwner ? 'Only the school owner can review or action these requests.' : 'Requests you submitted here that needed the owner\u2019s approval.'}</p>
        </div>
        <button type="button" className={css.btnSecondary} onClick={load} disabled={loading}>Refresh</button>
      </div>
      {error && <div className={css.inlineError}>{error}</div>}
      {loading ? <SkeletonTableRows rows={3} cols={isOwner ? 5 : 4} /> : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead><tr><th>Date</th>{isOwner && <th>Sub-Admin</th>}<th>Request</th><th>Status</th>{isOwner && <th>Actions</th>}{!isOwner && <th>Actions</th>}</tr></thead>
            <tbody>{actions.map(action => (
              <tr key={action._id}>
                <td>{new Date(action.createdAt).toLocaleString()}</td>
                {isOwner && <td>{action.subAdminLabel}</td>}
                <td><SubAdminActionLabel action={action} onViewDetails={isOwner ? null : setDetailsTarget} /></td>
                <td><StatusChip status={action.status} /></td>
                <td>{isOwner ? (
                  <div className={css.actionButtons}><button type="button" className={css.btnPrimary} onClick={() => setApproveTarget(action)}>Approve</button><button type="button" className={css.btnDanger} onClick={() => setRejectTarget(action)}>Reject</button><button type="button" className={css.btnSecondary} onClick={() => setDetailsTarget(action)}>View details</button></div>
                ) : action.status === 'pending' ? (
                  <div className={css.actionButtons}><button type="button" className={css.btnSecondary} onClick={() => setDetailsTarget(action)}>View details</button><button type="button" className={css.btnSecondary} onClick={() => setEditTarget(action)}>Edit request</button><button type="button" className={css.btnDanger} onClick={() => setWithdrawTarget(action)}>Withdraw request</button></div>
                ) : <span className={css.cellSub}>No actions available</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {approveTarget && <DeleteConfirmModal title="Approve Action" message={`Approve this request from ${approveTarget.subAdminLabel}?`} confirmLabel="Approve" danger={false} onCancel={() => setApproveTarget(null)} onConfirm={approve} />}
      {rejectTarget && <RejectSubAdminActionModal action={rejectTarget} onNavigate={onNavigate} onClose={() => setRejectTarget(null)} onDone={finishReject} />}
      {detailsTarget && <SubAdminActionDetailsModal action={detailsTarget} isOwner={isOwner} onClose={() => setDetailsTarget(null)} onApprove={approveFromDetails} onReject={rejectFromDetails} />}
      {editTarget && <SubAdminActionEditModal action={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      {withdrawTarget && <WithdrawSubAdminActionModal action={withdrawTarget} onClose={() => setWithdrawTarget(null)} onConfirm={withdraw} />}
    </div>
  );
}

function WithdrawSubAdminActionModal({ action, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const confirm = async () => {
    setLoading(true); setError('');
    const result = await onConfirm();
    setLoading(false);
    if (!result?.ok) setError(result?.data?.error || 'Could not withdraw request.');
  };
  return <div className={css.modalOverlay} onClick={onClose}><div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={event => event.stopPropagation()}>
    <h2 className={css.modalTitle} style={{ color: '#ef5350' }}>Withdraw request?</h2>
    <p className={css.modalSub}>This will remove your pending <strong>{SUB_ADMIN_ACTION_LABELS[action.actionKey] || 'approval request'}</strong>. The owner will no longer be able to review it.</p>
    {error && <div className={css.inlineError}>{error}</div>}
    <div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose}>Keep request</button><button type="button" className={css.btnDanger} onClick={confirm} disabled={loading}>{loading ? 'Withdrawing...' : 'Withdraw request'}</button></div>
  </div></div>;
}

function SubAdminActionDetailsModal({ action, isOwner = false, onClose, onApprove, onReject }) {
  const details = action;
  const entries = Object.entries(details.body || {});
  const displayValue = value => {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.map(item => typeof item === 'object' ? Object.values(item).filter(Boolean).join(' · ') : String(item)).join(', ') : 'None';
    if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${formatActionField(key)}: ${displayValue(item)}`).join(' · ');
    return String(value);
  };
  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 620 }} onClick={event => event.stopPropagation()}>
        <h2 className={css.modalTitle}>Request Details</h2>
        <p className={css.modalSub}><strong>{SUB_ADMIN_ACTION_LABELS[details.actionKey] || details.actionKey}</strong> submitted by {details.subAdminLabel}</p>
        <div className={css.requestMetaGrid}><div><span>Status</span><strong><StatusChip status={details.status} /></strong></div><div><span>Submitted</span><strong>{new Date(details.createdAt).toLocaleString()}</strong></div></div>
        <div className={css.requestDetailsList}>
          <h3>Request information</h3>
          {entries.length ? entries.map(([key, value]) => <div className={css.requestDetailRow} key={key}><span>{formatActionField(key)}</span><strong>{displayValue(value)}</strong></div>) : <p className={css.emptySub}>No additional request information was submitted.</p>}
        </div>
        <div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose}>Close</button>{isOwner && action.status === 'pending' && <><button type="button" className={css.btnDanger} onClick={onReject}>Reject</button><button type="button" className={css.btnPrimary} onClick={onApprove}>Approve</button></>}</div>
      </div>
    </div>
  );
}

function formatActionField(key) {
  return String(key).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

function SubAdminActionEditModal({ action, onClose, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(Object.entries(action.body || {}).map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')])));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async event => {
    event.preventDefault(); setSaving(true); setError('');
    const body = {};
    Object.entries(form).forEach(([key, value]) => {
      const original = action.body?.[key];
      if (typeof original === 'boolean') body[key] = value === 'true';
      else if (typeof original === 'number') body[key] = value === '' ? '' : Number(value);
      else if (original && typeof original === 'object') { try { body[key] = JSON.parse(value); } catch { body[key] = value; } }
      else body[key] = value;
    });
    const result = await schoolFetch(`/school/sub-admins/my-actions/${action._id}`, { method: 'PATCH', body: JSON.stringify(body) });
    setSaving(false);
    if (result.ok) onSaved(); else setError(result.data?.error || 'Could not update request.');
  };
  return <div className={css.modalOverlay} onClick={onClose}><form className={`${css.modal} ${css.requestEditModal} BubblePopIn`} onClick={event => event.stopPropagation()} onSubmit={submit}>
    <h2 className={css.modalTitle}>Edit request</h2><p className={css.modalSub}>Update the request before the owner reviews it.</p>
    {error && <div className={css.inlineError}>{error}</div>}
    <div className={css.requestEditGrid}>{Object.entries(form).map(([key, value]) => <label key={key} className={css.requestEditField}><span>{formatActionField(key)}</span><textarea value={value} onChange={event => setForm(current => ({ ...current, [key]: event.target.value }))} rows={typeof action.body?.[key] === 'object' ? 3 : 1} /></label>)}</div>
    <div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button><button type="submit" className={css.btnPrimary} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button></div>
  </form></div>;
}

// ─── Storage Bar ─────────────────────────────────────────────────────────────
const PLAN_STORAGE_LIMITS = { standard: 1 * 1024 ** 3, premium: 3 * 1024 ** 3 };
function StorageBar({ subscription }) {
  if (!subscription) return null;
  const plan = subscription.plan || 'standard';
  const effectiveLimit = subscription.storageLimitBytes > 0
    ? subscription.storageLimitBytes
    : (PLAN_STORAGE_LIMITS[plan] || PLAN_STORAGE_LIMITS.standard);
  const usedBytes = subscription.storageUsedBytes || 0;
  const pct = Math.min(100, (usedBytes / effectiveLimit) * 100);
  const isHigh = pct >= 85;
  const isMed  = pct >= 60;

  const fmt = (b) => {
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`;
    return `${(b / 1024).toFixed(0)} KB`;
  };

  const barColor = isHigh ? '#e53935' : isMed ? '#f59e0b' : '#5fbec5';

  return (
    <div style={{ background: '#fff', border: '1px solid #e0eaec', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Storage
        </span>
        <span style={{ fontSize: '0.78rem', color: isHigh ? '#e53935' : '#607d8b', fontWeight: isHigh ? 700 : 500 }}>
          {fmt(usedBytes)} <span style={{ color: '#90a4ae' }}>of</span> {fmt(effectiveLimit)} used
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: '#edf2f4', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99,
          width: `${pct}%`,
          background: barColor,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: '0.7rem', color: '#90a4ae' }}>
        <span>{pct.toFixed(1)}% used</span>
        <span style={{ color: '#b0bec5' }}>{fmt(effectiveLimit - usedBytes)} free</span>
      </div>
    </div>
  );
}

// ─── Skeleton Loaders ────────────────────────────────────────────────────────
function SkeletonTableRows({ rows = 5, cols = 6 }) {
  const widths = ['72%', '55%', '48%', '52%', '60%', '44%'];
  return (
    <div className={css.tableWrap}>
      <table className={css.table}>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {Array.from({ length: cols }, (_, j) => (
                <td key={j}>
                  <span className={css.skel} style={{ height: 13, width: widths[j % widths.length] }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SkeletonStatCards({ count = 8 }) {
  const labelW = ['62%', '48%', '58%', '52%', '66%', '44%', '54%', '50%'];
  return (
    <div className={css.statsRow} style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(160px,100%),1fr))' }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={css.statCard}>
          <span className={css.skel} style={{ height: 9, width: labelW[i % labelW.length] }} />
          <span className={css.skel} style={{ height: 30, width: '45%', marginTop: 10 }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonCards({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={css.card}>
          <span className={css.skel} style={{ height: 13, width: '38%', marginBottom: 14 }} />
          <span className={css.skel} style={{ height: 11, width: '85%', marginBottom: 9 }} />
          <span className={css.skel} style={{ height: 11, width: '65%' }} />
        </div>
      ))}
    </>
  );
}

function SkeletonClassesSubjects() {
  return (
    <>
      <div className={css.card}>
        <span className={css.skel} style={{ height: 14, width: '22%', marginBottom: 18 }} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} className={css.skel} style={{ height: 72, width: 148, borderRadius: 12 }} />
          ))}
        </div>
      </div>
      <div className={css.card}>
        <span className={css.skel} style={{ height: 14, width: '18%', marginBottom: 16 }} />
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={css.skel} style={{ height: 42, display: 'block', marginBottom: 8, borderRadius: 8 }} />
        ))}
      </div>
    </>
  );
}

// ─── Curriculum templates for bulk subject import ─────────────────────────────
const SUBJECT_TEMPLATES = {
  primary: {
    label: 'Primary School',
    emoji: '🏫',
    subjects: [
      'English Language', 'Mathematics', 'Basic Science & Technology',
      'Social Studies', 'National Values Education', 'Religious & Moral Education',
      'Civic Education', 'Cultural & Creative Arts', 'Physical & Health Education',
      'Computer Studies', 'Yoruba Language',
    ],
  },
  jss: {
    label: 'Junior Secondary (JSS)',
    emoji: '📚',
    subjects: [
      'English Language', 'Mathematics', 'Basic Science', 'Basic Technology',
      'Social Studies', 'Agricultural Science', 'Home Economics', 'Computer Studies',
      'CRS / IRS', 'Civic Education', 'French Language', 'Fine Arts',
      'Business Studies', 'Physical Education', 'Music',
    ],
  },
  sss: {
    label: 'Senior Secondary (SSS)',
    emoji: '🎓',
    subjects: [
      'English Language', 'Mathematics', 'Biology', 'Chemistry', 'Physics',
      'Further Mathematics', 'Agricultural Science', 'Economics', 'Commerce',
      'Accounting', 'Government', 'Geography', 'History', 'Literature in English',
      'CRS / IRS', 'French Language', 'Computer Studies', 'Technical Drawing',
      'Food & Nutrition', 'Civic Education',
    ],
  },
  technical: {
    label: 'Technical / Vocational',
    emoji: '🔧',
    subjects: [
      'Applied Electricity', 'Auto Mechanics', 'Building Construction',
      'Catering & Hotel Management', 'Computer Craft Studies', 'Electronics',
      'Metalwork', 'Woodwork', 'Plumbing & Pipe Fitting',
      'Garment Making & Textile', 'Animal Husbandry', 'Crop Production',
    ],
  },
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
// Prompts the admin for their password before executing a destructive action.
// Props:
//   message      — descriptive warning text shown above the input
//   title        — modal heading (default: "Confirm Action")
//   confirmLabel — submit button text (default: "Confirm Delete")
//   danger       — false renders the submit button as primary instead of danger styling
//   onCancel     — called when the admin dismisses without confirming
//   onConfirm    — async fn(password) called with the entered password; should
//                  return { ok, data } from schoolFetch
function DeleteConfirmModal({ message, title = 'Confirm Action', confirmLabel = 'Confirm Delete', danger = true, onCancel, onConfirm }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Enter your password to confirm.'); return; }
    setError('');
    setLoading(true);
    try {
      const result = await onConfirm(password);
      if (result && !result.ok) {
        setError(result.data?.error || 'Action failed. Check your password and try again.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={onCancel}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle} style={danger ? { color: '#ef5350' } : undefined}>{title}</h2>
        <p className={css.modalSub} style={{ marginBottom: 8 }}>{message}</p>
        <p style={{ fontSize: '0.82rem', color: '#90a4ae', marginBottom: 16 }}>
          Enter your admin password to proceed.
        </p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <SchoolPasswordInput
            className={css.formInput}
            placeholder="Your admin password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
          />
          <div className={css.modalFooter} style={{ marginTop: 16 }}>
            <button type="button" className={css.btnSecondary} onClick={onCancel} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className={danger ? css.btnDanger : css.btnPrimary} disabled={loading || !password}>
              {loading ? 'Processing…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Student onboarding helpers ──────────────────────────────────────────────
const STUDENT_STATUS_LABELS = { active: 'Active', graduated: 'Graduated', transferred: 'Transferred', removed: 'Removed' };

const STUDENT_SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const STUDENT_IMPORT_FIELDS = [
  { key: 'email', label: 'Student Email', multi: false },
  { key: 'firstName', label: 'First Name *', multi: false },
  { key: 'lastName', label: 'Last Name *', multi: false },
  { key: 'studentId', label: 'Student ID *', multi: false },
  { key: 'className', label: 'Class', multi: false },
  { key: 'classArm', label: 'Class Arm', multi: false },
  { key: 'dob', label: 'Date of Birth', multi: false },
  { key: 'sex', label: 'Sex', multi: false },
  { key: 'profileImg', label: 'Passport Photo URL', multi: false },
  { key: 'mobile', label: 'Mobile Number', multi: false },
  { key: 'streetCity', label: 'Street / City', multi: false },
  { key: 'stateRegion', label: 'State / Region', multi: false },
  { key: 'country', label: 'Country', multi: false },
  { key: 'demographicNotes', label: 'Other Demographic Notes', multi: false },
];

const STUDENT_IMPORT_ALIASES = {
  email: ['email', 'studentemail', 'studentemailaddress', 'emailaddress'],
  firstName: ['firstname', 'first_name', 'first name', 'givenname', 'given_name', 'given name'],
  lastName: ['lastname', 'last_name', 'last name', 'surname', 'familyname', 'family_name', 'family name'],
  studentId: ['studentid', 'student_id', 'student id', 'admno', 'admissionno', 'matricno', 'matricnumber'],
  className: ['class', 'classname', 'class name', 'classlevel', 'class level', 'grade', 'gradelevel'],
  classArm: ['classarm', 'class_arm', 'class arm', 'arm', 'section'],
  dob: ['dob', 'dateofbirth', 'date_of_birth', 'date of birth', 'birthdate', 'birth_date'],
  sex: ['sex', 'gender'],
  profileImg: ['profileimg', 'profile_img', 'avatar', 'passportphoto', 'passportphotourl', 'photo', 'photourl', 'image', 'imageurl'],
  mobile: ['mobile', 'phone', 'phonenumber', 'studentphone'],
  streetCity: ['streetcity', 'street_city', 'address', 'homeaddress', 'city', 'residence'],
  stateRegion: ['stateregion', 'state_region', 'state', 'region'],
  country: ['country', 'nationality'],
  demographicNotes: ['demographicnotes', 'notes', 'otherdetails', 'otherinfo', 'remarks'],
};

function normalizeImportHeader(header) {
  return String(header || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function guessStudentImportField(header) {
  const norm = normalizeImportHeader(header);
  if (!norm) return '';
  for (const field of Object.keys(STUDENT_IMPORT_ALIASES)) {
    if (STUDENT_IMPORT_ALIASES[field].includes(norm)) return field;
  }
  return '';
}

function parseStudentImportRows(rows) {
  if (!rows || rows.length < 2) return { headers: [], dataRows: [], error: 'File must have a header row and at least one data row.' };
  const headers = rows[0].map(h => String(h || '').trim());
  const dataRows = rows.slice(1).filter(r => Array.isArray(r) && r.some(c => String(c || '').trim() !== ''));
  if (dataRows.length === 0) return { headers, dataRows: [], error: 'No data rows found below the header row.' };
  return { headers, dataRows, error: null };
}

function buildStudentImportRows(dataRows, mapping) {
  return dataRows.map(vals => {
    const row = {
      email: '', firstName: '', lastName: '', studentId: '', className: '', classArm: '', dob: '', sex: '', profileImg: '', mobile: '', streetCity: '', stateRegion: '', country: '', demographicNotes: '',
    };
    Object.entries(mapping).forEach(([colIdx, field]) => {
      if (!field) return;
      const raw = String(vals[colIdx] ?? '').trim();
      if (!raw) return;
      if (field === 'sex') {
        const value = raw.toLowerCase().replace(/[^a-z_\s-]/g, '').replace(/[\s-]+/g, '_');
        row.sex = ['male', 'female', 'other', 'prefer_not_to_say'].includes(value) ? value : raw;
      } else {
        row[field] = raw;
      }
    });
    return row;
  }).filter(r => r.firstName && r.lastName);
}

const STUDENT_SAMPLE_ROWS = [
  ['First Name', 'Last Name', 'Student Email', 'Student ID', 'Class', 'Class Arm', 'Date of Birth', 'Sex', 'Passport Photo URL', 'Mobile Number', 'Street / City', 'State / Region', 'Country', 'Other Demographic Notes'],
  ['Chidi', 'Okonkwo', 'chidi@example.com', 'STD001', 'SS2', 'A', '2010-05-12', 'male', 'https://example.com/passport/chidi.jpg', '08012345678', '12 Okafor Street', 'Lagos', 'Nigeria', 'Allergic to peanuts'],
  ['Ngozi', 'Eze', 'ngozi@example.com', 'STD002', 'JSS1', 'B', '2011-11-03', 'female', '', '08087654321', '45 Unity Road', 'Anambra', 'Nigeria', 'Prefers morning classes'],
];

function downloadStudentSampleCSV() {
  const csv = STUDENT_SAMPLE_ROWS.map(r => r.join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bubble_students_bulk_import_sample.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function downloadStudentSampleXLSX() {
  const ws = XLSX.utils.aoa_to_sheet(STUDENT_SAMPLE_ROWS);
  ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 24 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 24 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Students');
  XLSX.writeFile(wb, 'bubble_students_bulk_import_sample.xlsx');
}

function downloadStudentFailedRowsCSV(failedRows, filename = 'failed_students.csv') {
  if (!failedRows?.length) return;
  const rows = [['Email', 'First Name', 'Last Name', 'Reason']];
  failedRows.forEach(f => rows.push([f.email, f.firstName, f.lastName, f.reason]));
  const csv = rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AddStudentModal({ classes, subjects, onClose, onSuccess }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', studentId: '', classId: '', classArm: '', password: '',
    dob: '', sex: '', mobile: '', streetCity: '', stateRegion: '', country: '', demographicNotes: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const avatarUrlRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => () => {
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
  }, []);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setFieldErrors(current => ({ ...current, [k]: '' }));
    setError('');
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setError('');
    setFieldErrors(current => ({ ...current, file: '' }));
    let finalFile = file;
    if (file.type === 'image/webp' || file.type === 'image/gif') {
      try {
        finalFile = await convertToJpeg(file);
      } catch {
        setError('Image conversion failed. Try JPEG or PNG format.');
        return;
      }
    }
    if (finalFile.size > 2 * 1024 * 1024) {
      try {
        const compressed = await compressImageIfNeeded(finalFile, 2);
        finalFile = compressed.file;
      } catch {
        setError('Image compression failed. Try a smaller passport photo.');
        return;
      }
    }
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    const objectUrl = URL.createObjectURL(finalFile);
    avatarUrlRef.current = objectUrl;
    setAvatarFile(finalFile);
    setAvatarPreview(objectUrl);
  };

  const resetForm = () => {
    setResult(null);
    setError('');
    setFieldErrors({});
    setAvatarFile(null);
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    avatarUrlRef.current = '';
    setAvatarPreview('');
    setForm({
      firstName: '', lastName: '', email: '', studentId: '', classId: '', classArm: '', password: '',
      dob: '', sex: '', mobile: '', streetCity: '', stateRegion: '', country: '', demographicNotes: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errors = {};
    const email = form.email.trim();
    const birthDate = form.dob;
    const parsedBirthDate = /^\d{4}-\d{2}-\d{2}$/.test(birthDate) ? new Date(`${birthDate}T00:00:00.000Z`) : null;
    const earliestBirthDate = new Date();
    earliestBirthDate.setUTCFullYear(earliestBirthDate.getUTCFullYear() - 100);
    if (!form.firstName.trim()) errors.firstName = 'First name is required.';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required.';
    if (!email) errors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';
    if (!form.studentId.trim()) errors.studentId = 'Student ID is required.';
    if (!form.classId) errors.classId = 'Class is required.';
    if (!form.dob) errors.dob = 'Date of birth is required.';
    else if (!parsedBirthDate || Number.isNaN(parsedBirthDate.getTime()) || parsedBirthDate.toISOString().slice(0, 10) !== birthDate || birthDate > new Date().toISOString().slice(0, 10) || parsedBirthDate < earliestBirthDate) {
      errors.dob = 'Enter a valid date of birth within the last 100 years.';
    }
    if (!form.sex) errors.sex = 'Sex is required.';
    if (form.password && form.password.length < 8) errors.password = 'Password must be at least 8 characters.';
    if (form.password && new TextEncoder().encode(form.password).length > 72) errors.password = 'Password must be no more than 72 bytes.';
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      const firstInvalid = e.currentTarget.querySelector(`[name="${Object.keys(errors)[0]}"]`);
      firstInvalid?.focus();
      setError('Please correct the highlighted fields.');
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) payload.append(key, value);
      });
      if (avatarFile) payload.append('file', avatarFile);
      if (!form.email) payload.delete('email');
      if (!form.password) payload.delete('password');
      const { ok, status, data, isJson } = await schoolFetch('/school/students/add', {
        method: 'POST',
        body: payload,
      });
      if (ok) {
        setResult(data);
        onSuccess();
      } else {
        setFieldErrors(data.fields || {});
        if (data.code === 'PHOTO_TOO_LARGE' || data.code === 'PHOTO_INVALID_TYPE' || data.code === 'PHOTO_UPLOAD_FAILED') {
          setFieldErrors(current => ({ ...current, file: data.error || 'Check the passport photo.' }));
        }
        if (!isJson) setError(`The server returned an unexpected response (status ${status}).`);
        else if (data.code === 'QUOTA_REACHED') setError(data.error);
        else if (status === 401) setError('Your session expired. Sign in again.');
        else if (status === 403) setError(data.error || 'You do not have permission to enrol students.');
        else if (status >= 500) setError(data.error || `Something went wrong while enrolling this student.${data.reference ? ` Reference: ${data.reference}` : ''}`);
        else if (data.fields) setError(data.error || 'Please correct the highlighted fields.');
        else if (data.code === 'PHOTO_TOO_LARGE' || data.code === 'PHOTO_INVALID_TYPE' || data.code === 'PHOTO_UPLOAD_FAILED') setError('The passport photo could not be uploaded.');
        else if (data.code === 'STORAGE_LIMIT_REACHED') setError(data.error);
        else if (data.code === 'EMAIL_ALREADY_ENROLLED' || data.code === 'STUDENT_ID_TAKEN') setError(data.error);
        else if (data.code === 'VALIDATION_ERROR') setError(data.error || 'Please correct the highlighted fields.');
        else if (status === 413 || status === 429) setError(data.error || 'The request could not be processed right now.');
        else if (data.code === 'INVALID_VALUE') setError(data.error || 'One or more values are invalid.');
        else if (data.code === 'DUPLICATE_STUDENT') setError(data.error || 'This student conflicts with an existing record.');
        else if (data.error) setError(data.error);
        else if (data.message) setError(data.message);
        else setError(`The server returned an unexpected response (status ${status || 'unknown'}).`);
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentAvatar = avatarPreview || result?.student?.profileImg || '/uploads/avatar.png';

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Enrol a Student</h2>
        <p className={css.modalSub}>Manually add one student to your school.</p>

        {error && <div className={css.inlineError}>{error}</div>}

        {result ? (
          <>
            <div className={css.inlineSuccess}>
              ✓ {result.student?.firstName} {result.student?.lastName} enrolled!
              {result.student?.generatedPassword
                ? <><br />Temp password: <strong>{result.student.generatedPassword}</strong> — share securely.</>
                : result.student?.isExistingUser
                  ? <><br />This is an existing Bubble Ai user. They can log in with their existing password.</>
                  : null
              }
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={resetForm}>Add another</button>
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form noValidate onSubmit={handleSubmit}>
            <p className={css.requiredLegend}><span className={css.requiredMark}>*</span> Required</p>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-first-name">First Name <span className={css.requiredMark}>*</span></label>
                <input id="student-first-name" name="firstName" className={`${css.formInput} ${fieldErrors.firstName ? css.formInputError : ''}`} value={form.firstName} onChange={set('firstName')} required aria-required="true" aria-invalid={Boolean(fieldErrors.firstName)} maxLength={60} placeholder="e.g. Chidi" />
                {fieldErrors.firstName && <span className={css.fieldError}>{fieldErrors.firstName}</span>}
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-last-name">Last Name <span className={css.requiredMark}>*</span></label>
                <input id="student-last-name" name="lastName" className={`${css.formInput} ${fieldErrors.lastName ? css.formInputError : ''}`} value={form.lastName} onChange={set('lastName')} required aria-required="true" aria-invalid={Boolean(fieldErrors.lastName)} maxLength={60} placeholder="e.g. Okonkwo" />
                {fieldErrors.lastName && <span className={css.fieldError}>{fieldErrors.lastName}</span>}
              </div>
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Passport Photo</label>
              <div className={css.avatarUploadRow}>
                <div className={css.studentAvatarPreview}>
                  {currentAvatar && currentAvatar !== '/uploads/avatar.png'
                    ? <img src={currentAvatar} alt="Student passport preview" />
                    : <span>{(form.firstName?.[0] || 'S').toUpperCase()}{(form.lastName?.[0] || '').toUpperCase()}</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <input className={css.formInput} type="file" accept="image/*" onChange={handleAvatarChange} />
                  <p className={css.reportControlHint}>Upload a passport-style image, or leave blank to use the default avatar.</p>
                  {fieldErrors.file && <span className={css.fieldError}>{fieldErrors.file}</span>}
                </div>
              </div>
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel} htmlFor="student-email">Email <span className={css.requiredMark}>*</span></label>
                <input id="student-email" name="email" className={`${css.formInput} ${fieldErrors.email ? css.formInputError : ''}`} type="email" value={form.email} onChange={set('email')} required aria-required="true" aria-invalid={Boolean(fieldErrors.email)} maxLength={254} placeholder="student@example.com" />
              {fieldErrors.email && <span className={css.fieldError}>{fieldErrors.email}</span>}
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel} htmlFor="student-class">Class <span className={css.requiredMark}>*</span></label>
              <select id="student-class" name="classId" className={`${css.formSelect} ${fieldErrors.classId ? css.formInputError : ''}`} value={form.classId} onChange={set('classId')} required aria-required="true" aria-invalid={Boolean(fieldErrors.classId)}>
                <option value="">— Select class —</option>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name || c.level}</option>)}
              </select>
              {fieldErrors.classId && <span className={css.fieldError}>{fieldErrors.classId}</span>}
            </div>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-id">Student ID <span className={css.requiredMark}>*</span></label>
                <input id="student-id" name="studentId" className={`${css.formInput} ${fieldErrors.studentId ? css.formInputError : ''}`} value={form.studentId} onChange={set('studentId')} required aria-required="true" aria-invalid={Boolean(fieldErrors.studentId)} maxLength={40} placeholder="e.g. STD/001" />
                {fieldErrors.studentId && <span className={css.fieldError}>{fieldErrors.studentId}</span>}
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Class Arm</label>
                <input className={css.formInput} value={form.classArm} onChange={set('classArm')} placeholder="e.g. A or B or C" />
              </div>
            </div>

            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-dob">Date of Birth <span className={css.requiredMark}>*</span></label>
                <input id="student-dob" name="dob" className={`${css.formInput} ${fieldErrors.dob ? css.formInputError : ''}`} type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={set('dob')} required aria-required="true" aria-invalid={Boolean(fieldErrors.dob)} />
                {fieldErrors.dob && <span className={css.fieldError}>{fieldErrors.dob}</span>}
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-sex">Sex <span className={css.requiredMark}>*</span></label>
                <select id="student-sex" name="sex" className={`${css.formSelect} ${fieldErrors.sex ? css.formInputError : ''}`} value={form.sex} onChange={set('sex')} required aria-required="true" aria-invalid={Boolean(fieldErrors.sex)}>
                  <option value="">— Select sex —</option>
                  {STUDENT_SEX_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {fieldErrors.sex && <span className={css.fieldError}>{fieldErrors.sex}</span>}
              </div>
            </div>

            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Mobile Number</label>
                <input className={css.formInput} value={form.mobile} onChange={set('mobile')} placeholder="08012345678" />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Country</label>
                <input className={css.formInput} value={form.country} onChange={set('country')} placeholder="Nigeria" />
              </div>
            </div>

            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Street / City</label>
                <input className={css.formInput} value={form.streetCity} onChange={set('streetCity')} placeholder="12 Okafor Street" />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>State / Region</label>
                <input className={css.formInput} value={form.stateRegion} onChange={set('stateRegion')} placeholder="Lagos" />
              </div>
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Other Demographic Notes</label>
              <textarea className={css.formInput} rows={3} value={form.demographicNotes} onChange={set('demographicNotes')} placeholder="Allergies, special requirements, guardian notes, etc." style={{ resize: 'vertical' }} />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel} htmlFor="student-password">Password (optional — auto-generated if blank)</label>
              <SchoolPasswordInput id="student-password" name="password" className={`${css.formInput} ${fieldErrors.password ? css.formInputError : ''}`} value={form.password} onChange={set('password')} placeholder="Leave blank to auto-generate" aria-invalid={Boolean(fieldErrors.password)} />
              {fieldErrors.password && <span className={css.fieldError}>{fieldErrors.password}</span>}
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" className={css.btnPrimary} disabled={loading}>
                {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><AddIcon style={{ fontSize: '1rem' }} /> Enrol Student</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function BulkImportModal({ classes, onClose, onSuccess }) {
  const [step, setStep] = useState('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef();

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const parsed = parseStudentImportRows(rows);
        if (parsed.error) { setError(parsed.error); return; }
        const initialMapping = {};
        parsed.headers.forEach((header, index) => { initialMapping[index] = guessStudentImportField(header); });
        setHeaders(parsed.headers);
        setDataRows(parsed.dataRows);
        setMapping(initialMapping);
        setStep('map');
      } catch {
        setError('Could not read file. Please use a valid CSV or Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const setColumnField = (colIdx, field) => setMapping(current => ({ ...current, [colIdx]: field }));

  const mappingIssues = (() => {
    const counts = {};
    Object.values(mapping).forEach(field => { if (field) counts[field] = (counts[field] || 0) + 1; });
    const issues = [];
    if (!counts.firstName) issues.push('Map one column to "First Name" — it is required.');
    if (!counts.lastName) issues.push('Map one column to "Last Name" — it is required.');
    if (!counts.studentId) issues.push('Map one column to "Student ID" — it is required and must be unique in your school.');
    STUDENT_IMPORT_FIELDS.forEach(field => {
      if (!field.multi && counts[field.key] > 1) issues.push(`Only one column can be mapped to "${field.label}".`);
    });
    return issues;
  })();

  const previewRows = mappingIssues.length === 0 ? buildStudentImportRows(dataRows, mapping) : [];

  const handleSubmit = async () => {
    if (mappingIssues.length > 0) return;
    const students = buildStudentImportRows(dataRows, mapping);
    if (students.length === 0) { setError('No valid rows found — every row needs a first and last name.'); return; }
    if (students.length > 200) { setError('Maximum 200 students per import.'); return; }

    const classMap = {};
    classes.forEach(c => {
      if (c.name) classMap[c.name.toLowerCase().trim()] = String(c._id);
      if (c.level) classMap[String(c.level).toLowerCase().trim()] = String(c._id);
    });

    const resolved = students.map(row => {
      const resolvedRow = { ...row };
      if (resolvedRow.className) {
        const classId = classMap[resolvedRow.className.toLowerCase().trim()];
        if (classId) resolvedRow.classId = classId;
      }
      if (!resolvedRow.email) delete resolvedRow.email;
      if (!resolvedRow.profileImg) delete resolvedRow.profileImg;
      if (!resolvedRow.sex) delete resolvedRow.sex;
      return resolvedRow;
    });

    setSubmitting(true);
    setError('');
    try {
      const { ok, data } = await schoolFetch('/school/students/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ students: resolved }),
      });
      if (ok || data.results) {
        setResult(data);
        onSuccess();
        setStep('result');
      } else {
        setError(data.error || 'Bulk import failed.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const succeeded = result?.results?.success || [];
  const failed = result?.results?.failed || [];

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <h2 className={css.modalTitle}>Bulk Import Students</h2>

        {error && <div className={css.inlineError}>{error}</div>}

        {step === 'upload' && (
          <>
            <p className={css.modalSub}>Upload a spreadsheet with any column layout — you'll map its columns to the right fields next.</p>
            <div className={css.csvHint}>
              <strong>For best result</strong> (any header names, any order): include the fields you have and map them next. First name, last name and student ID are required; date of birth, sex, passport photo URL and other demographics are optional.
            </div>
            <div className={css.sampleDownloads}>
              <span className={css.sampleLabel}>Download sample template:</span>
              <button className={css.sampleBtn} type="button" onClick={downloadStudentSampleCSV}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> CSV
              </button>
              <button className={css.sampleBtn} type="button" onClick={downloadStudentSampleXLSX}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> Excel (.xlsx)
              </button>
            </div>
            <div
              className={css.dropZone}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { fileInputRef.current.files = e.dataTransfer.files; handleFile({ target: { files: e.dataTransfer.files } }); } }}
            >
              <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: 'none' }} onChange={handleFile} />
              <div className={css.dropZonePlaceholder}>
                <UploadIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 8 }} />
                <div className={css.dropZoneText}>Click to browse or drag &amp; drop</div>
                <div className={css.dropZoneHint}>Accepts .csv, .xls, .xlsx — up to 200 rows</div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {step === 'map' && (
          <>
            <p className={css.modalSub}><strong>{fileName}</strong> — {dataRows.length} row(s) detected. We auto-matched columns below; change any dropdown to fix a mismatch.</p>
            <div className={css.tableWrap} style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className={css.table}>
                <thead>
                  <tr><th>Column in your file</th><th>Sample value</th><th>Maps to</th></tr>
                </thead>
                <tbody>
                  {headers.map((header, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 600 }}>{header || `Column ${index + 1}`}</td>
                      <td style={{ color: '#78909c', fontSize: '0.8rem' }}>{String(dataRows[0]?.[index] ?? '')}</td>
                      <td>
                        <select className={css.formSelect} value={mapping[index] || ''} onChange={e => setColumnField(index, e.target.value)}>
                          <option value="">— Ignore this column —</option>
                          {STUDENT_IMPORT_FIELDS.map(field => <option key={field.key} value={field.key}>{field.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mappingIssues.length > 0 ? (
              <div className={css.inlineError} style={{ marginTop: 10 }}>{mappingIssues.join(' ')}</div>
            ) : (
              <div className={css.inlineSuccess} style={{ marginTop: 10 }}>
                ✓ {previewRows.length} student row(s) ready to import.
              </div>
            )}

            <div className={css.csvHint} style={{ marginTop: 10 }}>
              <strong>Tip:</strong> if your sheet has a passport photo column, map it to <code>Passport Photo URL</code>. The image should already be hosted at a reachable URL.
            </div>

            <div className={css.tableWrap} style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto' }}>
              <table className={css.table}>
                <thead>
                  <tr><th>Name</th><th>Class</th><th>DOB</th><th>Sex</th><th>Photo</th></tr>
                </thead>
                <tbody>
                  {previewRows.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      <td>{row.firstName} {row.lastName}</td>
                      <td>{row.className || row.classId || '—'}</td>
                      <td>{row.dob || '—'}</td>
                      <td>{row.sex || '—'}</td>
                      <td>{row.profileImg ? 'Yes' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={() => setStep('upload')}>Back</button>
              <button type="button" className={css.btnPrimary} onClick={handleSubmit} disabled={submitting || mappingIssues.length > 0}>
                {submitting ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><UploadIcon style={{ fontSize: '1rem' }} /> Start Bulk Import</>}
              </button>
            </div>
          </>
        )}

        {step === 'result' && result && (
          <>
            <div className={css.inlineSuccess}>
              ✓ {succeeded.length} enrolled.{failed.length > 0 ? ` ${failed.length} failed.` : ''}
            </div>

            {succeeded.length > 0 && (
              <>
                <div className={css.credsHeader}>
                  <span className={css.credsTitle}>Student Login Credentials</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className={css.sampleBtn} type="button" onClick={() => {
                      const rows = [['First Name', 'Last Name', 'Email', 'Password']];
                      succeeded.forEach(s => rows.push([s.firstName, s.lastName, s.email, s.generatedPassword || '(existing account)']));
                      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = 'student_credentials.csv'; a.click();
                      URL.revokeObjectURL(url);
                    }}>
                      <DownloadIcon style={{ fontSize: '0.9rem' }} /> CSV
                    </button>
                    <button className={css.sampleBtn} type="button" onClick={() => {
                      const rows = [['First Name', 'Last Name', 'Email', 'Password']];
                      succeeded.forEach(s => rows.push([s.firstName, s.lastName, s.email, s.generatedPassword || '(existing account)']));
                      const ws = XLSX.utils.aoa_to_sheet(rows);
                      ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 32 }, { wch: 18 }];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Credentials');
                      XLSX.writeFile(wb, 'student_credentials.xlsx');
                    }}>
                      <DownloadIcon style={{ fontSize: '0.9rem' }} /> Excel
                    </button>
                  </div>
                </div>
                <div className={css.credsNote}>⚠ Save or download these now — passwords are shown only once.</div>
                <div className={css.credsTableWrap}>
                  <table className={css.credsTable}>
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Password</th></tr>
                    </thead>
                    <tbody>
                      {succeeded.map((student, index) => (
                        <tr key={index}>
                          <td>{student.firstName} {student.lastName}</td>
                          <td className={css.credsMono}>{student.email}</td>
                          <td className={css.credsMono}>{student.generatedPassword || <span style={{ color: '#90a4ae' }}>existing account</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {failed.length > 0 && (
              <div className={css.csvHint} style={{ marginTop: 10 }}>
                <strong>Failed rows:</strong><br />
                {failed.map((failure, index) => (
                  <div key={index}>{failure.row?.firstName} {failure.row?.lastName}: {failure.reason}</div>
                ))}
                <button className={css.sampleBtn} type="button" onClick={() => downloadStudentFailedRowsCSV(failed)} style={{ marginTop: 8 }}>
                  <DownloadIcon style={{ fontSize: '0.9rem' }} /> Download failed rows CSV
                </button>
              </div>
            )}

            <div className={css.modalFooter}>
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditStudentModal({ membership, classes, onClose, onSuccess }) {
  const user = membership.userId || {};
  const [form, setForm] = useState({
    classId: membership.classId?._id || '',
    classArm: membership.classArm || '',
    studentId: membership.studentId || '',
    status: membership.status || 'active',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    dob: user.dob || '',
    sex: user.sex || '',
    mobile: user.mobile || '',
    streetCity: user.streetCity || '',
    stateRegion: user.stateRegion || '',
    country: user.country || '',
    demographicNotes: user.demographicNotes || '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user.profileImg || '');
  const avatarUrlRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const originalStatus = membership.status || 'active';
  const statusLocked = Boolean(membership.statusLocked);
  const deactivating = originalStatus === 'active' && form.status !== 'active';

  useEffect(() => () => {
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
  }, []);

  const set = (k) => (e) => setForm(current => ({ ...current, [k]: e.target.value }));

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setError('');
    let finalFile = file;
    if (file.type === 'image/webp' || file.type === 'image/gif') {
      try {
        finalFile = await convertToJpeg(file);
      } catch {
        setError('Image conversion failed. Try JPEG or PNG format.');
        return;
      }
    }
    if (finalFile.size > 2 * 1024 * 1024) {
      try {
        const compressed = await compressImageIfNeeded(finalFile, 2);
        finalFile = compressed.file;
      } catch {
        setError('Image compression failed. Try a smaller passport photo.');
        return;
      }
    }
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    const objectUrl = URL.createObjectURL(finalFile);
    avatarUrlRef.current = objectUrl;
    setAvatarFile(finalFile);
    setAvatarPreview(objectUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowPasswordConfirm(true);
  };

  const confirmUpdate = async (password) => {
    setLoading(true);
    setError('');
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) payload.append(key, value);
      });
      if (avatarFile) payload.append('file', avatarFile);
      const { ok, data } = await schoolFetch(`/school/students/${membership._id}`, {
        method: 'PATCH',
        headers: { 'x-admin-password': password },
        body: payload,
      });
      if (ok) {
        setShowPasswordConfirm(false);
        onSuccess();
        onClose();
      } else {
        return { ok, data };
      }
      return { ok, data };
    } catch {
      return { ok: false, data: { error: 'Connection error.' } };
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Student Settings</h2>
        <p className={css.modalSub}>{user.firstName} {user.lastName}</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Passport Photo</label>
            <div className={css.avatarUploadRow}>
              <div className={css.studentAvatarPreview}>
                {avatarPreview && avatarPreview !== '/uploads/avatar.png'
                  ? <img src={avatarPreview} alt="Student passport preview" />
                  : <span>{(user.firstName?.[0] || 'S').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <input className={css.formInput} type="file" accept="image/*" onChange={handleAvatarChange} />
                <p className={css.reportControlHint}>Upload a new passport-style image to replace the current one.</p>
              </div>
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>First Name</label>
              <input className={css.formInput} value={form.firstName} onChange={set('firstName')} required />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Last Name</label>
              <input className={css.formInput} value={form.lastName} onChange={set('lastName')} required />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Date of Birth</label>
              <input className={css.formInput} type="date" value={form.dob} onChange={set('dob')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Sex</label>
              <select className={css.formSelect} value={form.sex} onChange={set('sex')}>
                <option value="">Not specified</option>
                {STUDENT_SEX_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Mobile Number</label>
              <input className={css.formInput} value={form.mobile} onChange={set('mobile')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.country} onChange={set('country')} />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Street / City</label>
              <input className={css.formInput} value={form.streetCity} onChange={set('streetCity')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>State / Region</label>
              <input className={css.formInput} value={form.stateRegion} onChange={set('stateRegion')} />
            </div>
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Other Demographic Notes</label>
            <textarea className={css.formInput} rows={3} value={form.demographicNotes} onChange={set('demographicNotes')} style={{ resize: 'vertical' }} />
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Student ID</label>
              <input className={css.formInput} value={form.studentId} onChange={set('studentId')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Class Arm</label>
              <input className={css.formInput} value={form.classArm} onChange={set('classArm')} />
            </div>
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Class</label>
            <select className={css.formSelect} value={form.classId} onChange={set('classId')}>
              <option value="">— None —</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name || c.level}</option>)}
            </select>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Status</label>
            <select className={css.formSelect} value={form.status} onChange={set('status')} disabled={statusLocked}>
              <option value="active">Active</option>
              <option value="graduated">Graduated</option>
              <option value="transferred">Transferred</option>
              <option value="removed">Removed</option>
            </select>
            {statusLocked && (
              <p className={css.reportControlHint}>
                This student&apos;s status is locked until after the current term. Contact Bubble Ai support if it needs to change.
              </p>
            )}
            {!statusLocked && deactivating && (
              <p className={css.reportControlHint} style={{ color: '#e65100' }}>
                Once saved, this student cannot be returned to Active until after the current term.
              </p>
            )}
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      {showPasswordConfirm && (
        <DeleteConfirmModal
          title={deactivating ? 'Confirm Status Change' : 'Confirm Student Changes'}
          message={deactivating
            ? `You are changing this student's status to ${STUDENT_STATUS_LABELS[form.status] || form.status}. They will no longer be counted as an active student for this term, and you will NOT be able to return them to Active status until after the current term. Continue?`
            : 'Updating this student profile requires password confirmation.'}
          confirmLabel="Save Changes"
          danger={deactivating}
          onCancel={() => setShowPasswordConfirm(false)}
          onConfirm={confirmUpdate}
        />
      )}
    </div>
  );
}

function AssignStudentsClassModal({ count, classes, onClose, onContinue }) {
  const [classId, setClassId] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = event => {
    event.preventDefault();
    if (!classId) {
      setError('Select a class to continue.');
      return;
    }
    onContinue(classId);
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h2 className={css.modalTitle}>Assign Students to a Class</h2>
        <p className={css.modalSub}>{count} student{count === 1 ? '' : 's'} selected</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel} htmlFor="bulk-student-class">Class</label>
            <select
              id="bulk-student-class"
              className={css.formSelect}
              value={classId}
              onChange={event => { setClassId(event.target.value); setError(''); }}
              autoFocus
            >
              <option value="">Select a class</option>
              {classes.map(schoolClass => (
                <option key={schoolClass._id} value={schoolClass._id}>
                  {schoolClass.name || schoolClass.level}
                </option>
              ))}
            </select>
            <p className={css.reportControlHint}>The selected students will keep their current class arms and subjects.</p>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={classes.length === 0}>
              <SchoolIcon style={{ fontSize: '1rem' }} /> Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Students Tab ─────────────────────────────────────────────────────────────
function StudentsTab({ school, classes }) {
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [modal, setModal] = useState(null); // null | 'add' | 'bulk' | { edit: membership }
  const [deleteTarget, setDeleteTarget] = useState(null); // { membershipId }
  const [careerPathTarget, setCareerPathTarget] = useState(null); // { membershipId, studentName }
  const [skillReportTarget, setSkillReportTarget] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [assignClassModalOpen, setAssignClassModalOpen] = useState(false);
  const [classAssignment, setClassAssignment] = useState(null); // { membershipIds, classId }
  const [statusCounts, setStatusCounts] = useState(null); // { all, active }
  const searchTimeout = useRef(null);

  const fetchStatusCounts = useCallback(async () => {
    const [allRes, activeRes] = await Promise.all([
      schoolFetch('/school/students?limit=1&status=all'),
      schoolFetch('/school/students?limit=1&status=active'),
    ]);
    if (allRes.ok && activeRes.ok) {
      setStatusCounts({
        all: allRes.data?.pagination?.total || 0,
        active: activeRes.data?.pagination?.total || 0,
      });
    }
  }, []);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  const fetchStudents = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, status: filterStatus });
      if (filterClass) params.set('classId', filterClass);
      if (search) params.set('search', search);
      const { ok, data } = await schoolFetch(`/school/students?${params}`);
      if (ok) {
        setStudents(data.students || []);
        setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
      }
    } finally {
      setLoading(false);
    }
  }, [filterClass, filterStatus, search]);

  const refreshStudents = useCallback((page = 1) => {
    fetchStatusCounts();
    return fetchStudents(page);
  }, [fetchStatusCounts, fetchStudents]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchStudents(1), search ? 400 : 0);
    return () => clearTimeout(searchTimeout.current);
  }, [fetchStudents, search]);

  const handleRemove = (membershipId) => {
    setSelectedStudentIds(current => current.filter(id => id !== membershipId));
    setDeleteTarget({ membershipId });
  };

  const toggleStudentSelection = membershipId => {
    setSelectedStudentIds(current => current.includes(membershipId)
      ? current.filter(id => id !== membershipId)
      : [...current, membershipId]);
  };

  const toggleAllVisibleStudents = () => {
    const visibleIds = students.map(student => String(student._id));
    setSelectedStudentIds(current => {
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(studentId => current.includes(studentId));
      return allVisibleSelected
        ? current.filter(studentId => !visibleIds.includes(studentId))
        : [...new Set([...current, ...visibleIds])];
    });
  };

  const continueClassAssignment = classId => {
    setAssignClassModalOpen(false);
    setClassAssignment({ classId, membershipIds: selectedStudentIds });
  };

  const confirmClassAssignment = async password => {
    if (!classAssignment) return { ok: false, data: { error: 'No students selected.' } };
    const result = await schoolFetch('/school/students/assign-class', {
      method: 'PATCH',
      headers: { 'x-admin-password': password },
      body: JSON.stringify(classAssignment),
    });
    if (result.ok) {
      setClassAssignment(null);
      setSelectedStudentIds([]);
      await refreshStudents(pagination.page);
    }
    return result;
  };

  const confirmRemoveStudent = async (password) => {
    const result = await schoolFetch(`/school/students/${deleteTarget.membershipId}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': password },
    });
    if (result.ok) {
      setDeleteTarget(null);
      refreshStudents(pagination.page);
    }
    return result;
  };

  const maxStudents = school?.subscription?.maxStudents;
  const quotaLabel = (!maxStudents || maxStudents === 0) ? 'Unlimited' : String(maxStudents);
  const visibleStudentIds = students.map(student => String(student._id));
  const allVisibleSelected = visibleStudentIds.length > 0 && visibleStudentIds.every(studentId => selectedStudentIds.includes(studentId));
  const someVisibleSelected = visibleStudentIds.some(studentId => selectedStudentIds.includes(studentId));

  return (
    <div>
      {statusCounts && statusCounts.all > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: 10, padding: '12px 16px', marginBottom: 18 }}>
          <div style={{ fontWeight: 700, color: '#e65100', marginBottom: 6, fontSize: '0.9rem' }}>⚠ Keep student statuses up to date for this term</div>
          <div style={{ fontSize: '0.83rem', color: '#5d4037', lineHeight: 1.5 }}>
            Your school is billed per <strong>active</strong> student each term. Students who are not active for this term should have their
            status changed to <strong>Transferred</strong>, <strong>Graduated</strong> or <strong>Removed</strong>, otherwise they will be billed for the term.
            Once a student is moved out of Active, they cannot be added back for the rest of the term unless you contact Bubble Ai support.
          </div>
        </div>
      )}
      {/* Actions bar */}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Students</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
              {statusCounts
                ? `${statusCounts.active} active · ${statusCounts.all} total enrolled`
                : `${pagination.total} enrolled`} · Quota: {quotaLabel}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css.btnSecondary} onClick={() => setModal('bulk')}>
              <UploadIcon style={{ fontSize: '1rem' }} /> Bulk Import
            </button>
            <button className={css.btnPrimary} onClick={() => setModal('add')}>
              <AddIcon style={{ fontSize: '1rem' }} /> Add Student
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={css.filtersBar}>
          <input
            className={css.searchInput}
            placeholder="Search by name, email, or student ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className={css.filterSelect} value={filterClass} onChange={e => setFilterClass(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name || c.level}</option>)}
          </select>
          <select className={css.filterSelect} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }}>
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="graduated">Graduated</option>
            <option value="transferred">Transferred</option>
            <option value="removed">Removed</option>
          </select>
        </div>

        {selectedStudentIds.length > 0 && (
          <div className={css.studentSelectionToolbar}>
            <div className={css.studentSelectionText}>
              <strong>{selectedStudentIds.length}</strong> student{selectedStudentIds.length === 1 ? '' : 's'} selected
              {someVisibleSelected && !allVisibleSelected && <span> · selection continues across pages</span>}
            </div>
            <div className={css.studentSelectionActions}>
              <button type="button" className={css.btnSecondary} onClick={() => setSelectedStudentIds([])}>Clear</button>
              <button type="button" className={css.btnPrimary} onClick={() => setAssignClassModalOpen(true)} disabled={classes.length === 0}>
                <SchoolIcon style={{ fontSize: '1rem' }} /> Assign to class
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <SkeletonTableRows rows={6} cols={7} />
        ) : students.length === 0 ? (
          <div className={css.emptyState}>
            <PeopleIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No students found</p>
            <p className={css.emptySub}>
              {filterStatus === 'active' ? 'Start by adding students manually or importing a CSV file.' : 'No students match this filter.'}
            </p>
            {filterStatus === 'active' && (
              <button className={css.btnPrimary} onClick={() => setModal('add')}>
                <AddIcon style={{ fontSize: '1rem' }} /> Add First Student
              </button>
            )}
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={`${css.table} ${css.studentsTable}`}>
              <thead>
                <tr>
                  <th className={css.selectCell}>
                    <input
                      className={`schoolCheckbox ${css.studentCheckbox}`}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisibleStudents}
                      aria-label="Select all students shown"
                      ref={element => { if (element) element.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                    />
                  </th>
                  <th>Student</th>
                  <th>Student ID</th>
                  <th>Class</th>
                  <th>Arm</th>
                  <th>Status</th>
                  <th>Enrolled</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(m => (
                  <tr key={m._id}>
                    <td className={css.selectCell}>
                      <input
                        className={`schoolCheckbox ${css.studentCheckbox}`}
                        type="checkbox"
                        checked={selectedStudentIds.includes(String(m._id))}
                        onChange={() => toggleStudentSelection(String(m._id))}
                        aria-label={`Select ${m.userId?.firstName || ''} ${m.userId?.lastName || ''}`.trim()}
                      />
                    </td>
                    <td>
                      <div className={css.avatarCell}>
                        {m.userId?.profileImg && m.userId.profileImg !== '/uploads/avatar.png'
                          ? <img src={m.userId.profileImg} alt="" className={css.avatar} />
                          : <Initials first={m.userId?.firstName} last={m.userId?.lastName} />
                        }
                        <div>
                          <div className={css.cellName}>{m.userId?.firstName} {m.userId?.lastName}</div>
                          <div className={css.cellSub}>{m.userId?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{m.studentId || <span style={{ color: '#b0bec5' }}>—</span>}</td>
                    <td>{m.classId?.name || m.classId?.level || <span style={{ color: '#b0bec5' }}>—</span>}</td>
                    <td>{m.classArm || <span style={{ color: '#b0bec5' }}>—</span>}</td>
                    <td><StatusChip status={m.status} /></td>
                    <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
                      {m.enrolledAt ? new Date(m.enrolledAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} title="AI Career Path" onClick={() => setCareerPathTarget({ membershipId: m._id, studentName: `${m.userId?.firstName || ''} ${m.userId?.lastName || ''}`.trim() })}>
                          <AutoAwesomeIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} title="Skill ratings" onClick={() => setSkillReportTarget({ membershipId: m._id, studentName: `${m.userId?.firstName || ''} ${m.userId?.lastName || ''}`.trim() })}>
                          <AssignmentIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ edit: m })}>
                          <EditIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                        {m.status !== 'removed' && !m.statusLocked && (
                          <button className={css.btnDanger} title="Remove student" onClick={() => handleRemove(m._id)}>
                            <PersonRemoveIcon style={{ fontSize: '0.9rem' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className={css.pagination}>
            <button className={css.pageBtn} disabled={pagination.page <= 1} onClick={() => fetchStudents(pagination.page - 1)}>← Prev</button>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`${css.pageBtn} ${p === pagination.page ? css.pageBtnActive : ''}`} onClick={() => fetchStudents(p)}>{p}</button>
            ))}
            <button className={css.pageBtn} disabled={pagination.page >= pagination.pages} onClick={() => fetchStudents(pagination.page + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === 'add' && (
        <AddStudentModal classes={classes} subjects={[]} onClose={() => setModal(null)} onSuccess={() => refreshStudents(1)} />
      )}
      {modal === 'bulk' && (
        <BulkImportModal classes={classes} onClose={() => setModal(null)} onSuccess={() => refreshStudents(1)} />
      )}
      {modal?.edit && (
        <EditStudentModal membership={modal.edit} classes={classes} onClose={() => setModal(null)} onSuccess={() => refreshStudents(pagination.page)} />
      )}
      {assignClassModalOpen && (
        <AssignStudentsClassModal
          count={selectedStudentIds.length}
          classes={classes}
          onClose={() => setAssignClassModalOpen(false)}
          onContinue={continueClassAssignment}
        />
      )}
      {classAssignment && (
        <DeleteConfirmModal
          title="Confirm Class Assignment"
          message={`Assign ${classAssignment.membershipIds.length} selected student${classAssignment.membershipIds.length === 1 ? '' : 's'} to the chosen class?`}
          confirmLabel="Assign Students"
          danger={false}
          onCancel={() => setClassAssignment(null)}
          onConfirm={confirmClassAssignment}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          title="Remove Student"
          message="Remove this student from your school? Their access will be revoked immediately and they will no longer be counted as an active student. You will NOT be able to return them to Active status until after the current term."
          confirmLabel="Remove Student"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmRemoveStudent}
        />
      )}
      {careerPathTarget && (
        <StudentCareerPathDrawer
          membershipId={careerPathTarget.membershipId}
          studentName={careerPathTarget.studentName}
          onClose={() => setCareerPathTarget(null)}
        />
      )}
      {skillReportTarget && (
        <StudentSkillReportModal
          target={skillReportTarget}
          defaultTerm={school?.currentTerm || 'first'}
          defaultSession={school?.academicSession || ''}
          onClose={() => setSkillReportTarget(null)}
        />
      )}
    </div>
  );
}

// Removes ratings invalidated by later admin setting changes.
function filterValidSkillRatings(ratingsMap, settings) {
  const validKeys = new Set();
  const validValues = new Set((settings?.scale || []).map(entry => entry.value));
  (settings?.groups || []).forEach(group => {
    group.items.forEach(item => validKeys.add(`${group.key}:${item.key}`));
  });
  return Object.fromEntries(
    Object.entries(ratingsMap).filter(([key, value]) => validKeys.has(key) && validValues.has(Number(value)))
  );
}

function SkillRatingGroupsEditor({ settings, ratings, onChange, disabled = false }) {
  if (!settings?.groups?.length) {
    return <div className={css.emptyState}><AssignmentIcon className={css.emptyIcon} /><p className={css.emptyText}>No skill groups configured</p><p className={css.emptySub}>Add groups in Settings before recording ratings.</p></div>;
  }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {settings.groups.map(group => (
        <div key={group.key} className={css.tableWrap}>
          <table className={css.table}>
            <thead><tr><th>{group.title}</th><th style={{ width: 170 }}>Rating</th></tr></thead>
            <tbody>
              {group.items.map(item => {
                const key = `${group.key}:${item.key}`;
                return (
                  <tr key={key}>
                    <td>{item.label}</td>
                    <td>
                      <select className={css.formSelect} value={ratings[key] ?? ''} disabled={disabled} onChange={event => onChange(key, event.target.value === '' ? null : Number(event.target.value))}>
                        <option value="">Not rated</option>
                        {settings.scale.map(entry => <option key={entry.value} value={entry.value}>{entry.value} - {entry.label}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <p className={css.reportControlHint}>Rating scale: {settings.scale.map(entry => `${entry.value} = ${entry.label}`).join(' | ')}</p>
    </div>
  );
}

function StudentSkillReportModal({ target, defaultTerm, defaultSession, onClose }) {
  const [term, setTerm] = useState(defaultTerm || 'first');
  const [session, setSession] = useState(defaultSession || '');
  const [settings, setSettings] = useState(null);
  const [ratings, setRatings] = useState({});
  const [formTeacherComment, setFormTeacherComment] = useState('');
  const [headTeacherComment, setHeadTeacherComment] = useState('');
  const [carriedOver, setCarriedOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    if (!session.trim()) { setMessage({ error: 'Enter an academic session first.' }); return; }
    setLoading(true); setMessage(null);
    const params = new URLSearchParams({ term, session: session.trim() });
    const { ok, data } = await schoolFetch(`/school/students/${target.membershipId}/report-card-extras?${params}`);
    if (ok) {
      setSettings(data.settings);
      const loadedRatings = (data.ratings || []).reduce((map, rating) => ({ ...map, [`${rating.groupKey}:${rating.itemKey}`]: rating.value }), {});
      setRatings(filterValidSkillRatings(loadedRatings, data.settings));
      setFormTeacherComment(data.formTeacherComment || '');
      setHeadTeacherComment(data.headTeacherComment || '');
      setCarriedOver(!!data.carriedOver);
    } else setMessage({ error: data.error || 'Could not load report card details.' });
    setLoading(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true); setMessage(null);
    const skillRatings = Object.entries(ratings).filter(([, value]) => value !== null && value !== '').map(([key, value]) => {
      const [groupKey, itemKey] = key.split(':');
      return { groupKey, itemKey, value: Number(value) };
    });
    const { ok, data } = await schoolFetch(`/school/students/${target.membershipId}/report-card-extras`, {
      method: 'PATCH',
      body: JSON.stringify({ term, session: session.trim(), skillRatings, formTeacherComment, headTeacherComment }),
    });
    if (ok) {
      setCarriedOver(false);
      setMessage({ success: 'Student report card details saved.' });
    } else setMessage({ error: data.error || 'Could not save report card details.' });
    setSaving(false);
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 760 }} onClick={event => event.stopPropagation()}>
        <h2 className={css.modalTitle}>Skill Ratings &amp; Comments</h2>
        <p className={css.modalSub}>{target.studentName}</p>
        {message?.error && <div className={css.inlineError}>{message.error}</div>}
        {message?.success && <div className={css.inlineSuccess}>{message.success}</div>}
        <div className={css.formRow}>
          <div className={css.formGroup}><label className={css.formLabel}>Term</label><select className={css.formSelect} value={term} onChange={event => setTerm(event.target.value)}><option value="first">First Term</option><option value="second">Second Term</option><option value="third">Third Term</option></select></div>
          <div className={css.formGroup}><label className={css.formLabel}>Academic Session</label><input className={css.formInput} value={session} onChange={event => setSession(event.target.value)} placeholder="e.g. 2025/2026" /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}><button type="button" className={css.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Load Report Card Details'}</button></div>
        {carriedOver && <div className={css.assessmentModeInfo}><CheckCircleIcon /><div><strong>Previous ratings loaded</strong><p>These values came from the latest available term. Save to record them for this term.</p></div></div>}
        {settings && <SkillRatingGroupsEditor settings={settings} ratings={ratings} onChange={(key, value) => setRatings(previous => ({ ...previous, [key]: value }))} disabled={saving} />}
        {settings && (
          <>
            <div className={css.formGroup} style={{ marginTop: 14 }}><label className={css.formLabel}>Form Teacher&apos;s Comment</label><textarea className={css.formTextarea} rows={4} maxLength={1000} value={formTeacherComment} onChange={event => setFormTeacherComment(event.target.value)} /></div>
            <div className={css.formGroup}><label className={css.formLabel}>Principal&apos;s Remark</label><textarea className={css.formTextarea} rows={4} maxLength={1000} value={headTeacherComment} onChange={event => setHeadTeacherComment(event.target.value)} /></div>
          </>
        )}
        <div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose} disabled={saving}>Close</button><button type="button" className={css.btnPrimary} onClick={save} disabled={saving || !settings}>{saving ? 'Saving...' : 'Save Details'}</button></div>
      </div>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ profile, stats, onGoToSettings, isOwner }) {
  const plan = profile?.subscription?.plan || 'premium';
  const planColor = { standard: '#5fbec5', premium: '#f9a825' }[plan] || '#f9a825';

  const [bannerClosed, setBannerClosed] = useState(false);

  const FOUR_MONTHS_MS = 4 * 30 * 24 * 60 * 60 * 1000;
  const ONE_YEAR_MS    = 365 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  const termStale = !profile?.currentTermUpdatedAt ||
    (now - new Date(profile.currentTermUpdatedAt).getTime()) > FOUR_MONTHS_MS;

  const sessionStale = !profile?.academicSession ||
    !profile?.academicSessionUpdatedAt ||
    (now - new Date(profile.academicSessionUpdatedAt).getTime()) > ONE_YEAR_MS;

  const showBanner = (termStale || sessionStale) && !bannerClosed;

  const copyCode = () => {
    if (profile?.schoolCode) navigator.clipboard.writeText(profile.schoolCode).catch(() => {});
  };

  return (
    <div>
      {showBanner && (
        <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: 10, padding: '12px 16px', marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#e65100', marginBottom: 6, fontSize: '0.9rem' }}>⚠ Academic Setup Needs Attention</div>
            {termStale && (
              <div style={{ fontSize: '0.83rem', color: '#5d4037', marginBottom: 4 }}>
                <strong>Current Term:</strong> {profile?.currentTermUpdatedAt
                  ? 'Not updated in over 4 months. Teachers tag new exams to the active term — an outdated term causes incorrect report cards.'
                  : 'Has never been explicitly set. Without it, exam term tags and report cards will not be reliable.'}
              </div>
            )}
            {sessionStale && (
              <div style={{ fontSize: '0.83rem', color: '#5d4037', marginBottom: 4 }}>
                <strong>Academic Session:</strong> {!profile?.academicSession
                  ? 'Not set. Set the session (e.g. "2025/2026") so all exams and report cards are correctly grouped.'
                  : 'Not updated in over a year. Update it when a new academic year begins.'}
              </div>
            )}
            <button
              className={css.btnPrimary}
              style={{ marginTop: 8, padding: '6px 16px', fontSize: '0.82rem' }}
              onClick={onGoToSettings}
            >
              Set Current Term &amp; Session
            </button>
          </div>
          <button
            onClick={() => setBannerClosed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#90a4ae', fontSize: '1.1rem', lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      <div className={css.statsRow}>
        <div className={css.statCard}>
          <span className={css.statLabel}>Total Students</span>
          <span className={css.statValue}>{stats.totalStudents ?? '—'}</span>
          <span className={css.statSub}>all statuses (incl. graduated, transferred, removed)</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Active Students</span>
          <span className={css.statValue}>{stats.students ?? '—'}</span>
          <span className={css.statSub}>billable this term · of {(!profile?.subscription?.maxStudents || profile.subscription.maxStudents === 0) ? 'Unlimited' : profile.subscription.maxStudents} allowed</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Teachers</span>
          <span className={css.statValue}>{stats.teachers ?? '—'}</span>
          <span className={css.statSub}>of {profile?.subscription?.maxTeachers || 10} allowed</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Classes</span>
          <span className={css.statValue}>{stats.classes ?? '—'}</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Subjects</span>
          <span className={css.statValue}>{stats.subjects ?? '—'}</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Plan</span>
          <span className={css.statValue} style={{ fontSize: '1.2rem', textTransform: 'capitalize', color: planColor }}>{plan}</span>
          <span className={css.statSub}>
            {profile?.subscription?.endDate
              ? `Expires ${new Date(profile.subscription.endDate).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}`
              : 'No expiry set'}
          </span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Male Students</span>
          <span className={css.statValue}>{stats.male ?? '—'}</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Female Students</span>
          <span className={css.statValue}>{stats.female ?? '—'}</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Unclassified</span>
          <span className={css.statValue}>{stats.unclassified ?? '—'}</span>
          <span className={css.statSub}>sex not specified</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Average Age</span>
          <span className={css.statValue}>{stats.averageAge ?? '—'}</span>
        </div>
        <div className={css.statCard}>
          <span className={css.statLabel}>Parents</span>
          <span className={css.statValue}>{stats.parents ?? '—'}</span>
        </div>
      </div>

      {/* ── Storage Usage Bar ── */}
      <StorageBar subscription={profile?.subscription} />

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>School Profile</h2>
          {isOwner && (
            <button className={css.btnSecondary} onClick={onGoToSettings}>
              <EditIcon style={{ fontSize: '1rem' }} /> Edit Profile
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: '0.875rem' }}>
          {[
            ['School Name',       profile?.name],
            ['Contact Email',     profile?.contactEmail],
            ['Phone',             profile?.phone || '—'],
            ['Address',           profile?.address || '—'],
            ['Type',              profile?.type || '—'],
            ['Country',           profile?.country || '—'],
            ['State',             profile?.state || '—'],
            ['Academic Session',  profile?.academicSession || '—'],
            ['Current Term',      profile?.currentTerm || '—'],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
              <div style={{ color: '#0C1E28', fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>
        {profile?.schoolCode && (
          <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0fafb', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Student Enrolment Code</div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#3e8f93', letterSpacing: '0.08em' }}>{profile.schoolCode}</div>
            </div>
            <button className={css.btnSecondary} style={{ padding: '5px 12px', fontSize: '0.78rem' }} onClick={copyCode}>
              Copy
            </button>
            <span style={{ fontSize: '0.75rem', color: '#90a4ae' }}>Share with students to allow self-enrolment</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Analytics Tab ────────────────────────────────────────────────────────────
const ANALYTICS_SUBS = [
  { key: 'overview',     label: 'Overview',      Icon: DashboardIcon   },
  { key: 'by-class',     label: 'By Class',      Icon: SchoolIcon      },
  { key: 'by-subject',   label: 'By Subject',    Icon: AssignmentIcon  },
  { key: 'by-teacher',   label: 'By Teacher',    Icon: PeopleIcon      },
  { key: 'top-students', label: 'Top Students',  Icon: EmojiEventsIcon },
  { key: 'career-readiness', label: 'Career Readiness', Icon: AutoAwesomeIcon },
];

function RiskBadge({ pct, passmark = 50 }) {
  if (pct === null || pct === undefined) return <span style={{ color: '#b0bec5' }}>—</span>;
  const risk = pct < 40 ? 'high' : pct < passmark ? 'medium' : 'low';
  const styles = {
    high:   { background: '#fce4ec', color: '#c62828' },
    medium: { background: '#fff3e0', color: '#e65100' },
    low:    { background: '#e8f5e9', color: '#2e7d32' },
  };
  return (
    <span className={css.chip} style={styles[risk]}>
      {pct}%
    </span>
  );
}

function AnalyticsTab({ school }) {
  const [sub, setSub]       = useState('overview');
  const [data, setData]     = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const fetchSub = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const path = key === 'overview' ? '/school/analytics/overview' : `/school/analytics/${key}`;
      const { ok, data: d } = await schoolFetch(path);
      if (!ok) { setError(d.error || 'Failed to load analytics'); return; }
      setData(prev => ({ ...prev, [key]: d }));
    } catch {
      setError('Connection error.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!data[sub]) fetchSub(sub);
  }, [sub, data, fetchSub]);

  const d = data[sub] || {};

  return (
    <div>
      {/* Sub-tab bar */}
      <div className={css.subTabBar}>
        {ANALYTICS_SUBS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`${css.subTabBtn} ${sub === key ? css.subTabBtnActive : ''}`}
            onClick={() => setSub(key)}
          >
            <Icon style={{ fontSize: '1rem' }} /> {label}
          </button>
        ))}
      </div>

      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <SkeletonStatCards count={8} />
      ) : sub === 'overview' ? (
        <div>
          <div className={css.statsRow} style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(160px,100%),1fr))' }}>
            {[
              { label: 'Active Students',  value: d.students         ?? '—' },
              { label: 'Teachers',         value: d.teachers         ?? '—' },
              { label: 'Exams Created',    value: d.exams            ?? '—' },
              { label: 'Total Attempts',   value: d.totalAttempts    ?? '—' },
              { label: 'Submitted',        value: d.submitted        ?? '—' },
              { label: 'Avg Score',        value: d.avgPercentage    != null ? `${d.avgPercentage}%` : '—' },
              { label: 'Pass Rate',        value: d.passRate         != null ? `${d.passRate}%`       : '—' },
              { label: 'Completion Rate',  value: d.completionRate   != null ? `${d.completionRate}%` : '—' },
            ].map(({ label, value }) => (
              <div key={label} className={css.statCard}>
                <span className={css.statLabel}>{label}</span>
                <span className={css.statValue} style={{ fontSize: '1.6rem' }}>{value}</span>
              </div>
            ))}
          </div>
          {!school?.subscription?.advancedAnalyticsEnabled && (
            <div className={css.upgradeNotice}>
              Upgrade to <strong>Premium</strong> to unlock detailed analytics broken down by class, subject, and teacher.
            </div>
          )}
        </div>
      ) : sub === 'by-class' ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Performance by Class</h2>
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Students</th>
                  <th>Exams</th>
                  <th>Attempts</th>
                  <th>Submitted</th>
                  <th>Avg Score</th>
                  <th>Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {(d.classes || []).length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#90a4ae', padding: '24px' }}>No data yet</td></tr>
                ) : (d.classes || []).map(row => (
                  <tr key={row.classId}>
                    <td><strong>{row.className}</strong></td>
                    <td>{row.studentCount}</td>
                    <td>{row.examCount}</td>
                    <td>{row.totalAttempts}</td>
                    <td>{row.submitted}</td>
                    <td><RiskBadge pct={row.avgPercentage} passmark={50} /></td>
                    <td>{row.passRate != null ? `${row.passRate}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : sub === 'by-subject' ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Performance by Subject</h2>
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Exams</th>
                  <th>Attempts</th>
                  <th>Avg Score</th>
                  <th>Pass Rate</th>
                  <th>Top Weak Topics</th>
                </tr>
              </thead>
              <tbody>
                {(d.subjects || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#90a4ae', padding: '24px' }}>No data yet</td></tr>
                ) : (d.subjects || []).map(row => (
                  <tr key={row.subjectId}>
                    <td><strong>{row.subjectName}</strong></td>
                    <td>{row.examCount}</td>
                    <td>{row.totalAttempts}</td>
                    <td><RiskBadge pct={row.avgPercentage} passmark={50} /></td>
                    <td>{row.passRate != null ? `${row.passRate}%` : '—'}</td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}>
                      {(row.weakTopics || []).slice(0, 3).map(t => t.topic).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : sub === 'by-teacher' ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Performance by Teacher</h2>
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Exams</th>
                  <th>Students Reached</th>
                  <th>Submitted</th>
                  <th>Avg Score</th>
                  <th>Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {(d.teachers || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#90a4ae', padding: '24px' }}>No data yet</td></tr>
                ) : (d.teachers || []).map(row => (
                  <tr key={row.teacherId}>
                    <td>
                      <div className={css.cellName}>{row.firstName} {row.lastName}</div>
                      <div className={css.cellSub}>{row.email}</div>
                    </td>
                    <td>{row.examCount}</td>
                    <td>{row.studentsReached}</td>
                    <td>{row.submitted}</td>
                    <td><RiskBadge pct={row.avgPercentage} passmark={50} /></td>
                    <td>{row.passRate != null ? `${row.passRate}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : sub === 'top-students' ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Top Students</h2>
            <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>Ranked by average score across all submitted exams</span>
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Student ID</th>
                  <th>Exams</th>
                  <th>Avg Score</th>
                  <th>Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {(d.students || []).length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#90a4ae', padding: '24px' }}>No graded exams yet</td></tr>
                ) : (d.students || []).map((row, i) => (
                  <tr key={String(row.studentId)}>
                    <td style={{ fontWeight: 700, color: i < 3 ? '#3e8f93' : '#90a4ae' }}>#{i + 1}</td>
                    <td>
                      <div className={css.cellName}>{row.firstName} {row.lastName}</div>
                      <div className={css.cellSub}>{row.email}</div>
                    </td>
                    <td>{row.className} {row.classArm}</td>
                    <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>{row.studentCode || '—'}</td>
                    <td>{row.examCount}</td>
                    <td>
                      <span className={css.chip} style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                        {row.avgPercentage}%
                      </span>
                    </td>
                    <td>{row.passRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : sub === 'career-readiness' ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Career Readiness</h2>
            <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>School-wide rollup of AI career-path predictions (nightly, read-only)</span>
          </div>
          <div className={css.statsRow} style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(160px,100%),1fr))', marginBottom: 20 }}>
            {[
              { label: 'Students Tracked', value: d.totalTracked ?? '—' },
              { label: 'Ready',            value: d.readyCount ?? '—' },
              { label: 'Pending/Generating', value: (d.statusCounts?.pending || 0) + (d.statusCounts?.generating || 0) },
              { label: 'Not Yet Eligible', value: d.statusCounts?.not_eligible ?? '—' },
              { label: 'Failed',           value: d.statusCounts?.failed ?? '—' },
            ].map(({ label, value }) => (
              <div key={label} className={css.statCard}>
                <span className={css.statLabel}>{label}</span>
                <span className={css.statValue} style={{ fontSize: '1.6rem' }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#455a64', marginBottom: 10 }}>Most Common Top Career Paths</div>
          {(d.topCareerPaths || []).length === 0 ? (
            <p style={{ color: '#90a4ae', fontSize: '0.85rem' }}>No predictions generated yet.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {d.topCareerPaths.map(c => (
                <span key={c.title} className={css.chip} style={{ background: '#e0f4f5', color: '#0c7c84' }}>
                  {c.title} <strong style={{ marginLeft: 4 }}>×{c.count}</strong>
                </span>
              ))}
            </div>
          )}
          {school?.subscription?.plan !== 'premium' && !school?.subscription?.careerPathEnabled && (
            <div className={css.upgradeNotice} style={{ marginTop: 16 }}>
              Upgrade to <strong>Premium</strong> to unlock AI career-path predictions for your students.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ─── Reports Tab ──────────────────────────────────────────────────────────────
function ReportsTab({ school, classes, onConfigureReportCards, onConfigureMidTermReports }) {
  const [subjects, setSubjects]   = useState([]);
  const [teachers, setTeachers]   = useState([]);
  const [students, setStudents]   = useState([]);
  const [exams, setExams]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dlLoading, setDlLoading] = useState('');
  const [dlError, setDlError]     = useState('');
  const [previewLoading, setPreviewLoading] = useState('');
  const [reportPreview, setReportPreview] = useState(null);
  const [reportSettings, setReportSettings] = useState(null);
  const [midTermSettings, setMidTermSettings] = useState(null);
  const [broadsheetTerm, setBroadsheetTerm]       = useState(school?.currentTerm || 'first');
  const [broadsheetSession, setBroadsheetSession] = useState(school?.academicSession || '');

  const exportEnabled = school?.subscription?.exportEnabled;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sRes, tRes, stRes, eRes, rcRes, mtRes] = await Promise.all([
          schoolFetch('/school/subjects'),
          schoolFetch('/school/teachers'),
          schoolFetch('/school/students?limit=200&status=active'),
          schoolFetch('/teacher/exams'),
          schoolFetch('/school/report-card-settings'),
          schoolFetch('/school/mid-term-report-settings'),
        ]);
        if (!cancelled) {
          setSubjects(sRes.data.subjects || []);
          setTeachers(tRes.data.teachers || []);
          setStudents(stRes.data.students || []);
          setExams(eRes.data.exams || []);
          if (rcRes.ok) setReportSettings(rcRes.data.settings);
          if (mtRes.ok) setMidTermSettings(mtRes.data.settings);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const download = async (path, filename) => {
    if (!exportEnabled) return;
    setDlLoading(path);
    setDlError('');
    try {
      const token = localStorage.getItem('school-token');
      const res   = await fetch(path, { headers: { 'x-access-token': token || '' } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setDlError(d.error || 'Failed to generate report.');
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDlError('Connection error while generating report.');
    } finally {
      setDlLoading('');
    }
  };

  const downloadReportCard = async (path, filename) => {
    setDlLoading(path);
    setDlError('');
    try {
      const token = localStorage.getItem('school-token');
      const res   = await fetch(path, { headers: { 'x-access-token': token || '' } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setDlError(d.error || 'Failed to generate report card.');
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDlError('Connection error while generating report card.');
    } finally {
      setDlLoading('');
    }
  };

  const previewReport = async (path, title, filename) => {
    setPreviewLoading(path);
    setDlError('');
    try {
      const token = localStorage.getItem('school-token');
      const res = await fetch(path, { headers: { 'x-access-token': token || '' } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setDlError(d.error || 'Failed to prepare report preview.');
        return;
      }
      const blob = await res.blob();
      setReportPreview({ url: URL.createObjectURL(blob), title, filename });
    } catch {
      setDlError('Connection error while preparing report preview.');
    } finally {
      setPreviewLoading('');
    }
  };

  useEffect(() => () => {
    if (reportPreview?.url) URL.revokeObjectURL(reportPreview.url);
  }, [reportPreview]);

  const ReportCard = ({ icon: Icon, title, description, pdfPath, csvPath, pdfFilename, csvFilename, excelPath, excelFilename }) => {
    const isPreviewing = previewLoading === pdfPath;
    return (
      <div className={css.reportCard}>
        <div className={css.reportIcon}><Icon /></div>
        <div className={css.reportBody}>
          <div className={css.reportTitle}>{title}</div>
          <div className={css.reportDesc}>{description}</div>
        </div>
        <div className={css.downloadBtnGroup}>
          <button
            className={css.btnSecondary}
            disabled={!exportEnabled || !!dlLoading || !!previewLoading}
            onClick={() => previewReport(pdfPath, `${title} Preview`, pdfFilename)}
            title="Preview PDF"
            aria-label={`Preview ${title} PDF`}
          >
            {isPreviewing ? <ThreeCircles height="14" width="14" color="#3e8f93" visible /> : <VisibilityIcon style={{ fontSize: '1rem' }} />}
            Preview
          </button>
          <button
            className={css.btnSecondary}
            disabled={!exportEnabled || !!dlLoading || !!previewLoading}
            onClick={() => download(pdfPath, pdfFilename)}
            title="Download PDF"
          >
            {dlLoading === pdfPath ? <ThreeCircles height="14" width="14" color="#3e8f93" visible /> : <DownloadIcon style={{ fontSize: '1rem' }} />}
            PDF
          </button>
          {excelPath ? (
            <button
              className={css.btnSecondary}
              disabled={!exportEnabled || !!dlLoading || !!previewLoading}
              onClick={() => download(excelPath, excelFilename)}
              title="Download Excel"
            >
              {dlLoading === excelPath ? <ThreeCircles height="14" width="14" color="#3e8f93" visible /> : <DownloadIcon style={{ fontSize: '1rem' }} />}
              Excel
            </button>
          ) : (
            <button
              className={css.btnSecondary}
              disabled={!exportEnabled || !!dlLoading || !!previewLoading}
              onClick={() => download(csvPath, csvFilename)}
              title="Download CSV"
            >
              {dlLoading === csvPath ? <ThreeCircles height="14" width="14" color="#3e8f93" visible /> : <DownloadIcon style={{ fontSize: '1rem' }} />}
              CSV
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {!exportEnabled && (
        <div className={css.upgradeNotice}>
          <strong>Exports are not enabled</strong> on your current plan. Upgrade to <strong>Premium</strong> to unlock PDF & CSV reports.
        </div>
      )}

      {dlError && <div className={css.inlineError} style={{ marginBottom: 16 }}>{dlError}</div>}

      {loading ? (
        <SkeletonCards count={4} />
      ) : (
        <div>
          {/* ── School-wide ─────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>School-Wide Report</h2>
            <ReportCard
              icon={TrendingUpIcon}
              title="Full School Report"
              description="Overview of students, teachers, exams, pass rates, and top performers across the whole school."
              pdfPath="/school/reports/school?format=pdf"
              csvPath="/school/reports/school?format=csv"
              pdfFilename={`school-report-${new Date().toISOString().split('T')[0]}.pdf`}
              csvFilename={`school-report-${new Date().toISOString().split('T')[0]}.csv`}
            />
          </div>

          {/* ── By Class ────────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>Class Reports (Broadsheet)</h2>
            <p style={{ color: '#90a4ae', fontSize: '0.8125rem', marginTop: -8, marginBottom: 12 }}>
              Full class broadsheet — every student, every subject score, total, average, position and grade.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.75rem', color: '#546e7a', gap: 4 }}>
                Term
                <select className={css.formSelect} value={broadsheetTerm} onChange={e => setBroadsheetTerm(e.target.value)}>
                  <option value="first">First Term</option>
                  <option value="second">Second Term</option>
                  <option value="third">Third Term</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.75rem', color: '#546e7a', gap: 4 }}>
                Session
                <input
                  className={css.formInput}
                  value={broadsheetSession}
                  onChange={e => setBroadsheetSession(e.target.value)}
                  placeholder="e.g. 2025/2026"
                  style={{ minWidth: 140 }}
                />
              </label>
            </div>
            {classes.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No classes found.</p>
            ) : classes.map(cls => {
              const className = cls.name || cls.level;
              const termQS    = `term=${encodeURIComponent(broadsheetTerm)}&session=${encodeURIComponent(broadsheetSession)}`;
              return (
                <ReportCard
                  key={cls._id}
                  icon={SchoolIcon}
                  title={className}
                  description={`Full broadsheet for ${className} — ${broadsheetTerm} term, ${broadsheetSession || 'session not set'}.`}
                  pdfPath={`/school/reports/class/${cls._id}?format=pdf&${termQS}`}
                  excelPath={`/school/reports/class/${cls._id}?format=excel&${termQS}`}
                  pdfFilename={`class-${className.replace(/\s+/g, '-')}-broadsheet.pdf`}
                  excelFilename={`class-${className.replace(/\s+/g, '-')}-broadsheet.xlsx`}
                />
              );
            })}
          </div>

          {/* ── By Subject ──────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>Subject Reports</h2>
            {subjects.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No subjects found.</p>
            ) : subjects.map(subj => (
              <ReportCard
                key={subj._id}
                icon={AssignmentIcon}
                title={subj.name}
                description={`Exam breakdown and pass rates for ${subj.name}.`}
                pdfPath={`/school/reports/subject/${subj._id}?format=pdf`}
                csvPath={`/school/reports/subject/${subj._id}?format=csv`}
                pdfFilename={`subject-${subj.name.replace(/\s+/g, '-')}-report.pdf`}
                csvFilename={`subject-${subj.name.replace(/\s+/g, '-')}-report.csv`}
              />
            ))}
          </div>

          {/* ── By Teacher ──────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>Teacher Reports</h2>
            {teachers.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No teachers found.</p>
            ) : teachers.map(t => (
              <ReportCard
                key={t._id}
                icon={PeopleIcon}
                title={`${t.firstName} ${t.lastName}`}
                description={`${t.email} — exams created, students reached, and average scores.`}
                pdfPath={`/school/reports/teacher/${t._id}?format=pdf`}
                csvPath={`/school/reports/teacher/${t._id}?format=csv`}
                pdfFilename={`teacher-${t.firstName}-${t.lastName}-report.pdf`}
                csvFilename={`teacher-${t.firstName}-${t.lastName}-report.csv`}
              />
            ))}
          </div>

          {/* ── Term Report Cards ────────────────────────────── */}
          <div className={css.card}>
            <div className={css.cardHeader}>
              <div>
                <h2 className={css.cardTitle} style={{ marginBottom: 4 }}>Term Report Cards</h2>
                {reportSettings && (
                  <span className={css.reportDesignStatus}>
                    {(REPORT_CARD_TEMPLATE_OPTIONS.find(template => template.key === reportSettings.template)?.name || 'Classic Academic')}
                    {' · '}{reportSettings.assessmentDisplay === 'detailed' ? 'All assessments' : reportSettings.assessmentDisplay === 'custom' ? 'Custom columns' : 'Summary columns'}
                  </span>
                )}
              </div>
              <button className={css.btnSecondary} onClick={onConfigureReportCards}>
                <SettingsIcon style={{ fontSize: '1rem' }} /> Configure Design
              </button>
            </div>
            <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 16 }}>
              Generate per-student PDF report cards for a specific term. Only CA and end-of-term exams are included.
            </p>
            <TermReportCards
              students={students.map(m => ({
                userId: m.userId?._id ? String(m.userId._id) : String(m.userId || ''),
                name: `${m.userId?.firstName || ''} ${m.userId?.lastName || ''}`.trim() || '—',
                className: m.classId?.name || m.classId?.level || '',
                classArm: m.classArm || '',
                studentId: m.studentId || '',
              }))}
              download={downloadReportCard}
              dlLoading={dlLoading}
              preview={previewReport}
              previewLoading={previewLoading}
              school={school}
            />
          </div>

          {midTermSettings?.enabled && (
            <div className={css.card}>
              <div className={css.cardHeader}>
                <div>
                  <h2 className={css.cardTitle} style={{ marginBottom: 4 }}>Mid-Term Results</h2>
                  <span className={css.reportDesignStatus}>
                    {midTermSettings.title || 'Mid-Term Result'} · {midTermSettings.columns?.length || 0} CA columns
                  </span>
                </div>
                <button className={css.btnSecondary} onClick={onConfigureMidTermReports}>
                  <SettingsIcon style={{ fontSize: '1rem' }} /> Configure
                </button>
              </div>
              <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 16 }}>
                Generate an interim result using only continuous assessments taken so far. The same CA scores remain part of the full term report.
              </p>
              <MidTermReportCards
                students={students.map(m => ({
                  userId: m.userId?._id ? String(m.userId._id) : String(m.userId || ''),
                  name: `${m.userId?.firstName || ''} ${m.userId?.lastName || ''}`.trim() || '—',
                  className: m.classId?.name || m.classId?.level || '',
                  classArm: m.classArm || '',
                  studentId: m.studentId || '',
                }))}
                download={downloadReportCard}
                dlLoading={dlLoading}
                preview={previewReport}
                previewLoading={previewLoading}
                school={school}
              />
            </div>
          )}

          {/* ── By Student ──────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>Individual Student Reports</h2>
            {students.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No active students found.</p>
            ) : (
              <StudentReportList
                students={students}
                download={download}
                dlLoading={dlLoading}
                preview={previewReport}
                previewLoading={previewLoading}
                exportEnabled={exportEnabled}
              />
            )}
          </div>

          {/* ── By Exam ─────────────────────────────────────── */}
          <div className={css.card}>
            <h2 className={css.cardTitle} style={{ marginBottom: 16 }}>Exam Reports</h2>
            {exams.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No exams found.</p>
            ) : exams.map(e => (
              <ReportCard
                key={e._id}
                icon={DescriptionIcon}
                title={e.title}
                description={`${e.subjectName || 'General'} — full student results for this exam.`}
                pdfPath={`/school/reports/exam/${e._id}?format=pdf`}
                csvPath={`/school/reports/exam/${e._id}?format=csv`}
                pdfFilename={`exam-${e.title.replace(/\s+/g, '-')}-report.pdf`}
                csvFilename={`exam-${e.title.replace(/\s+/g, '-')}-report.csv`}
              />
            ))}
          </div>

        </div>
      )}
      <ReportCardPreviewModal
        url={reportPreview?.url}
        title={reportPreview?.title}
        downloadName={reportPreview?.filename}
        onClose={() => setReportPreview(null)}
      />
    </div>
  );
}

// Term report card generator component
function TermReportCards({ students, download, dlLoading, preview, previewLoading, school, reportKind = 'term' }) {
  const isMidTerm = reportKind === 'midterm';
  const reportPath = isMidTerm ? 'mid-term-report' : 'report-card';
  const releasePath = isMidTerm ? 'mid-term-report' : 'report-card';
  const reportLabel = isMidTerm ? 'mid-term result' : 'report card';
  const [term, setTerm]               = useState(school?.currentTerm || 'first');
  const [session, setSession]         = useState(school?.academicSession || '');
  const [classFilter, setClassFilter] = useState('');
  const [armFilter, setArmFilter]     = useState('');
  const [nameFilter, setNameFilter]   = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total }

  // ── Report card release gate (current term/session only) ──────────────────
  const [release, setRelease]               = useState(null); // { term, session, isCurrentTerm, released }
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError]     = useState('');

  useEffect(() => {
    if (!session.trim()) { setRelease(null); return; }
    let cancelled = false;
    const statusPath = reportKind === 'midterm' ? 'mid-term-report' : 'report-card';
    schoolFetch(`/school/reports/${statusPath}-release-status?term=${term}&session=${encodeURIComponent(session.trim())}`)
      .then(({ ok, data }) => { if (!cancelled) setRelease(ok ? data : null); })
      .catch(() => { if (!cancelled) setRelease(null); });
    return () => { cancelled = true; };
  }, [term, session, reportKind]);

  const toggleRelease = async (nextReleased) => {
    if (!sessionOk()) return;
    setReleaseLoading(true);
    setReleaseError('');
    try {
      const { ok, data } = await schoolFetch(`/school/reports/${releasePath}-release`, {
        method: 'POST',
        body: JSON.stringify({ term, session: session.trim(), released: nextReleased }),
      });
      if (ok) {
        setRelease(prev => ({ ...(prev || {}), term, session: session.trim(), released: data.released, isCurrentTerm: true }));
      } else {
        setReleaseError(data.error || 'Failed to update release status.');
      }
    } catch {
      setReleaseError('Connection error.');
    } finally {
      setReleaseLoading(false);
    }
  };

  // Derive unique class & arm options from student data
  const uniqueClasses = [...new Set(students.map(s => s.className).filter(Boolean))].sort();
  const uniqueArms    = [...new Set(
    students
      .filter(s => !classFilter || s.className === classFilter)
      .map(s => s.classArm)
      .filter(Boolean)
  )].sort();

  const handleClassChange = e => { setClassFilter(e.target.value); setArmFilter(''); };

  const filteredStudents = students.filter(s => {
    const q = nameFilter.toLowerCase();
    return (
      (!q || s.name?.toLowerCase().includes(q) || (s.studentId || '').toLowerCase().includes(q)) &&
      (!classFilter || s.className === classFilter) &&
      (!armFilter    || s.classArm  === armFilter)
    );
  });

  const sessionOk = () => {
    if (!session.trim()) { alert('Please enter an academic session (e.g. 2024/2025)'); return false; }
    return true;
  };

  const downloadCard = (userId, name) => {
    if (!sessionOk()) return;
    const p = `/school/reports/${reportPath}/${userId}?term=${term}&session=${encodeURIComponent(session.trim())}`;
    const filename = `${isMidTerm ? 'mid-term-report' : 'report-card'}-${(name || 'student').replace(/\s+/g, '-')}-${term}-${session.replace('/', '-')}.pdf`;
    download(p, filename);
  };

  const previewCard = (userId, name) => {
    if (!sessionOk()) return;
    const p = `/school/reports/${reportPath}/${userId}?term=${term}&session=${encodeURIComponent(session.trim())}`;
    const filename = `${isMidTerm ? 'mid-term-report' : 'report-card'}-${(name || 'student').replace(/\s+/g, '-')}-${term}-${session.replace('/', '-')}.pdf`;
    preview(p, `${name || 'Student'} · ${term.charAt(0).toUpperCase() + term.slice(1)} Term`, filename);
  };

  const generateAll = async () => {
    if (!sessionOk()) return;
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: filteredStudents.length });
    for (let i = 0; i < filteredStudents.length; i++) {
      const s = filteredStudents[i];
      const p = `/school/reports/${reportPath}/${s.userId}?term=${term}&session=${encodeURIComponent(session.trim())}`;
      const filename = `${isMidTerm ? 'mid-term-report' : 'report-card'}-${(s.name || 'student').replace(/\s+/g, '-')}-${term}-${session.replace('/', '-')}.pdf`;
      await download(p, filename).catch(() => {});
      setBulkProgress({ done: i + 1, total: filteredStudents.length });
    }
    setBulkLoading(false);
    setBulkProgress(null);
  };

  const inputStyle  = { padding: '7px 12px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.85rem' };
  const labelStyle  = { fontSize: '0.78rem', fontWeight: 700, color: '#546e7a', display: 'block', marginBottom: 4 };

  return (
    <div>
      {/* ── Report card release banner ── */}
      {release && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 10, marginBottom: 14,
          background: release.released ? '#e8f7f4' : '#fff8e6',
          border: `1px solid ${release.released ? '#a8e0d3' : '#f0d999'}`,
        }}>
          <span style={{ fontSize: '0.83rem', color: '#37474f', flex: 1, minWidth: 220 }}>
            {release.isCurrentTerm ? (
              <>
                <strong>{release.released ? 'Released' : 'Not released'}</strong> to students & parents for{' '}
                {term.charAt(0).toUpperCase() + term.slice(1)} Term — {session} (current term).{' '}
                {!release.released && `Students and parents cannot download this ${reportLabel} until you release it.`}
              </>
            ) : (
              <>This is a past term — report cards are automatically available to students & parents.</>
            )}
          </span>
          {release.isCurrentTerm && (
            <button
              onClick={() => toggleRelease(!release.released)}
              disabled={releaseLoading}
              style={{
                padding: '6px 16px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: '0.8rem',
                cursor: releaseLoading ? 'not-allowed' : 'pointer', opacity: releaseLoading ? 0.7 : 1,
                background: release.released ? '#c62828' : '#3e8f93', color: '#fff',
              }}
            >
              {releaseLoading ? '…' : release.released ? `Un-release ${isMidTerm ? 'Results' : 'Report Cards'}` : `Release ${isMidTerm ? 'Mid-Term Results' : 'Report Cards'}`}
            </button>
          )}
        </div>
      )}
      {releaseError && <div className={css.inlineError} style={{ marginBottom: 12 }}>{releaseError}</div>}

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Term</label>
          <select style={inputStyle} value={term} onChange={e => setTerm(e.target.value)}>
            <option value="first">First Term</option>
            <option value="second">Second Term</option>
            <option value="third">Third Term</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Session</label>
          <input
            style={{ ...inputStyle, width: 140 }}
            value={session}
            onChange={e => setSession(e.target.value)}
            placeholder="e.g. 2024/2025"
          />
        </div>
        <div>
          <label style={labelStyle}>Class</label>
          <select style={{ ...inputStyle, minWidth: 130 }} value={classFilter} onChange={handleClassChange}>
            <option value="">All Classes</option>
            {uniqueClasses.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Arm</label>
          <select
            style={{ ...inputStyle, minWidth: 100 }}
            value={armFilter}
            onChange={e => setArmFilter(e.target.value)}
            disabled={uniqueArms.length === 0}
          >
            <option value="">All Arms</option>
            {uniqueArms.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Search Student</label>
          <input
            style={{ ...inputStyle, width: '100%', minWidth: 160 }}
            value={nameFilter}
            onChange={e => setNameFilter(e.target.value)}
            placeholder="Name or Student ID…"
          />
        </div>
      </div>

      {/* ── Result count + Generate All ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', color: '#546e7a' }}>
          {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} shown
        </span>
        {filteredStudents.length > 1 && (
          <button
            onClick={generateAll}
            disabled={bulkLoading || !!dlLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#3e8f93', color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 16px', fontWeight: 700, fontSize: '0.82rem',
              cursor: (bulkLoading || !!dlLoading) ? 'not-allowed' : 'pointer',
              opacity: (bulkLoading || !!dlLoading) ? 0.7 : 1,
            }}
          >
            <DownloadIcon style={{ fontSize: '0.9rem' }} />
            {bulkLoading
              ? `Generating… ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? filteredStudents.length}`
              : `Generate All PDFs (${filteredStudents.length})`}
          </button>
        )}
      </div>

      {students.length === 0 ? (
        <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No active students found.</p>
      ) : filteredStudents.length === 0 ? (
        <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No students match the selected filters.</p>
      ) : (
        <div style={{ maxHeight: 480, overflowX: 'auto', overflowY: 'auto' }}>
          <table className={css.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ background: '#f0f5f7' }}>
                <th style={{ padding: '7px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 700 }}>Name</th>
                <th style={{ padding: '7px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 700 }}>Class</th>
                <th style={{ padding: '7px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 700 }}>Student ID</th>
                <th style={{ padding: '7px 10px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(s => {
                const cardPath = `/school/reports/${reportPath}/${s.userId}?term=${term}&session=${encodeURIComponent(session.trim())}`;
                const isLoading = dlLoading === cardPath;
                const isPreviewing = previewLoading === cardPath;
                return (
                  <tr key={s.userId} style={{ borderBottom: '1px solid #eef2f5' }}>
                    <td style={{ padding: '7px 10px', color: '#263238' }}>{s.name}</td>
                    <td style={{ padding: '7px 10px', color: '#263238' }}>{s.className}{s.classArm ? ` (${s.classArm})` : ''}</td>
                    <td style={{ padding: '7px 10px', color: '#90a4ae' }}>{s.studentId || '—'}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          className={css.btnSecondary}
                          style={{ padding: '5px 8px', fontSize: '0.78rem' }}
                          onClick={() => previewCard(s.userId, s.name)}
                          disabled={!!dlLoading || !!previewLoading || bulkLoading}
                          title="Preview report card"
                          aria-label={`Preview ${s.name}'s report card`}
                        >
                          {isPreviewing ? <ThreeCircles height="12" width="12" color="#3e8f93" visible /> : <VisibilityIcon style={{ fontSize: '0.9rem' }} />}
                        </button>
                      <button
                        className={css.btnSecondary}
                        style={{ background: '#3e8f93', color: '#fff', borderColor: '#3e8f93', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        onClick={() => downloadCard(s.userId, s.name)}
                        disabled={!!dlLoading || !!previewLoading || bulkLoading}
                      >
                        {isLoading
                          ? <ThreeCircles height="12" width="12" color="#fff" visible />
                          : <DownloadIcon style={{ fontSize: '0.9rem' }} />}
                        PDF
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MidTermReportCards({ students, download, dlLoading, preview, previewLoading, school }) {
  return <TermReportCards reportKind="midterm" students={students} download={download} dlLoading={dlLoading} preview={preview} previewLoading={previewLoading} school={school} />;
}

// Searchable student list for report exports
function StudentReportList({ students, download, dlLoading, preview, previewLoading, exportEnabled }) {
  const [search, setSearch] = useState('');
  const filtered = students.filter(m => {
    const q = search.toLowerCase();
    const u = m.userId || {};
    return !q
      || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (m.studentId || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <input
        className={css.searchInput}
        placeholder="Search students by name, email, or ID…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12, maxWidth: 380 }}
      />
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {filtered.slice(0, 50).map(m => {
          const u    = m.userId || {};
          const uid  = u._id || m.userId;
          const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Student';
          const pdfPath = `/school/reports/student/${uid}?format=pdf`;
          const csvPath = `/school/reports/student/${uid}?format=csv`;
          const pdfFilename = `student-${name.replace(/\s+/g, '-')}-report.pdf`;
          const isPreviewing = previewLoading === pdfPath;
          return (
            <div key={String(uid)} className={css.reportCard}>
              <div className={css.reportIcon} style={{ background: 'linear-gradient(135deg,#5fbec5,#3e8f93)' }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}>
                  {(u.firstName?.[0] || '').toUpperCase()}{(u.lastName?.[0] || '').toUpperCase()}
                </span>
              </div>
              <div className={css.reportBody}>
                <div className={css.reportTitle}>{name}</div>
                <div className={css.reportDesc}>{u.email || ''} {m.studentId ? `· ${m.studentId}` : ''}</div>
              </div>
              <div className={css.downloadBtnGroup}>
                <button
                  className={css.btnSecondary}
                  disabled={!exportEnabled || !!dlLoading || !!previewLoading}
                  onClick={() => preview(pdfPath, `${name} Preview`, pdfFilename)}
                  title="Preview PDF"
                  aria-label={`Preview ${name} PDF`}
                >
                  {isPreviewing ? <ThreeCircles height="12" width="12" color="#3e8f93" visible /> : <VisibilityIcon style={{ fontSize: '0.9rem' }} />}
                  Preview
                </button>
                <button
                  className={css.btnSecondary}
                  disabled={!exportEnabled || !!dlLoading || !!previewLoading}
                  onClick={() => download(pdfPath, pdfFilename)}
                >
                  {dlLoading === pdfPath ? <ThreeCircles height="12" width="12" color="#3e8f93" visible /> : <DownloadIcon style={{ fontSize: '0.9rem' }} />}
                  PDF
                </button>
                <button
                  className={css.btnSecondary}
                  disabled={!exportEnabled || !!dlLoading || !!previewLoading}
                  onClick={() => download(csvPath, `student-${name.replace(/\s+/g, '-')}-report.csv`)}
                >
                  {dlLoading === csvPath ? <ThreeCircles height="12" width="12" color="#3e8f93" visible /> : <DownloadIcon style={{ fontSize: '0.9rem' }} />}
                  CSV
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No students match.</p>}
      </div>
    </div>
  );
}

// ─── Teachers Tab ────────────────────────────────────────────────────────────
function InviteTeacherModal({ subjects, classes, onClose, onSuccess }) {
  const [form, setForm]     = useState({ email: '', firstName: '', lastName: '', staffId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/teachers/invite', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (ok) { setDone(data.teacher); onSuccess(); }
      else setError(data.error || 'Invite failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Invite a Teacher</h2>
        <p className={css.modalSub}>An invite link will be sent to their email address.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        {done ? (
          <>
            <div className={css.inlineSuccess}>✓ Invite sent to <strong>{done.email}</strong>. They will appear as inactive until they accept.</div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => { setDone(null); setForm({ email: '', firstName: '', lastName: '', staffId: '' }); }}>Invite another</button>
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email *</label>
              <input className={css.formInput} type="email" required value={form.email} onChange={set('email')} placeholder="teacher@school.edu" />
            </div>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>First Name *</label>
                <input className={css.formInput} required value={form.firstName} onChange={set('firstName')} placeholder="e.g. Amaka" />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Last Name *</label>
                <input className={css.formInput} required value={form.lastName} onChange={set('lastName')} placeholder="e.g. Obi" />
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Staff ID *</label>
              <input className={css.formInput} required value={form.staffId} onChange={set('staffId')} placeholder="e.g. TCH/001" />
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" className={css.btnPrimary} disabled={loading}>
                {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><AddIcon style={{ fontSize: '1rem' }} /> Send Invite</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function EditTeacherModal({ teacher, subjects, classes, onClose, onSuccess }) {
  const isPending = teacher.status === 'inactive';
  const [form, setForm] = useState({
    email:     teacher.email     || '',
    firstName: teacher.firstName || '',
    lastName:  teacher.lastName  || '',
    staffId:   teacher.staffId   || '',
    phone:     teacher.phone     || '',
    dob:       teacher.dob       || '',
    address:   teacher.address   || '',
    city:      teacher.city      || '',
    stateRegion: teacher.stateRegion || '',
    postalCode: teacher.postalCode || '',
    country:   teacher.country   || '',
    countryOfOrigin: teacher.countryOfOrigin || '',
    occupation: teacher.occupation || '',
    emergencyContactName: teacher.emergencyContactName || '',
    emergencyContactRelationship: teacher.emergencyContactRelationship || '',
    emergencyContactPhone: teacher.emergencyContactPhone || '',
    emergencyContactEmail: teacher.emergencyContactEmail || '',
    emergencyContactAddress: teacher.emergencyContactAddress || '',
    status:    teacher.status    || 'active',
    assignedSubjects: (teacher.assignedSubjects || []).map(s => String(s._id || s)),
    assignedClasses:  (teacher.assignedClasses  || []).map(c => String(c._id || c)),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [confirming, setConfirming] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(teacher.profileImg || '');
  const avatarInputRef = useRef(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    setAvatarPreview(teacher.profileImg || '');
    setAvatarFile(null);
  }, [teacher._id, teacher.profileImg]);

  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith('blob:')) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const toggleMulti = (key, id) => setForm(f => {
    const arr = f[key];
    return { ...f, [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
  });

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (avatarInputRef.current) avatarInputRef.current.value = '';
    if (!file) return;

    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        const result = await compressImageIfNeeded(file, 2);
        finalFile = result.file;
      } catch {
        setError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    if (avatarPreview && avatarPreview.startsWith('blob:')) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(finalFile);
    setAvatarPreview(URL.createObjectURL(finalFile));
    setError('');
  };

  const buildFormData = (extra = {}) => {
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'assignedSubjects' || key === 'assignedClasses') {
        body.append(key, JSON.stringify(value));
      } else {
        body.append(key, value);
      }
    });
    if (avatarFile) body.append('file', avatarFile);
    Object.entries(extra).forEach(([key, value]) => body.append(key, value));
    return body;
  };

  const submitResend = async () => {
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch(`/school/teachers/${teacher._id}`, {
        method: 'PATCH',
        body: buildFormData({ resendInvite: 'true' }),
      });
      if (ok) { onSuccess(); onClose(); }
      else setError(data.error || 'Update failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  const confirmSave = async (password) => {
    const { ok, data } = await schoolFetch(`/school/teachers/${teacher._id}`, {
      method: 'PATCH',
      headers: { 'x-admin-password': password },
      body: buildFormData(),
    });
    if (ok) { onSuccess(); onClose(); }
    return { ok, data };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (isPending) { submitResend(); return; }
    setConfirming(true);
  };

  if (confirming) {
    return (
      <DeleteConfirmModal
        title="Confirm Teacher Changes"
        confirmLabel="Save Changes"
        danger={false}
        message={`Save these changes to ${teacher.firstName} ${teacher.lastName}'s profile?`}
        onCancel={() => setConfirming(false)}
        onConfirm={confirmSave}
      />
    );
  }

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isPending ? 'Edit Teacher Invite' : 'Edit Teacher'}</h2>
        <p className={css.modalSub}>{isPending ? 'Update the invite details, then send a fresh invite link.' : teacher.email}</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.avatarUploadRow} style={{ marginBottom: 16 }}>
            <div className={css.studentAvatarPreview}>
              {avatarPreview && avatarPreview !== '/uploads/avatar.png' ? (
                <img src={avatarPreview} alt="Teacher passport preview" />
              ) : (
                <span>{(teacher.firstName?.[0] || '').toUpperCase()}{(teacher.lastName?.[0] || '').toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className={css.avatarUploadHint}>Upload a passport photo for this teacher. It will appear in teacher-facing views and admin lists.</div>
              <input ref={avatarInputRef} type="file" accept="image/*" className={css.avatarUploadInput} onChange={handleAvatarChange} />
              <div className={css.avatarUploadActions}>
                <button type="button" className={css.btnSecondary} onClick={() => avatarInputRef.current?.click()}>Choose Image</button>
                <button type="button" className={css.btnSecondary} onClick={() => { setAvatarFile(null); setAvatarPreview(teacher.profileImg || ''); if (avatarInputRef.current) avatarInputRef.current.value = ''; }}>Reset</button>
              </div>
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Email *</label>
            <input className={css.formInput} type="email" required value={form.email} onChange={set('email')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>First Name</label>
              <input className={css.formInput} required value={form.firstName} onChange={set('firstName')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Last Name</label>
              <input className={css.formInput} required value={form.lastName} onChange={set('lastName')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Staff ID *</label>
              <input className={css.formInput} required value={form.staffId} onChange={set('staffId')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Date of Birth</label>
              <input className={css.formInput} type="date" value={form.dob} onChange={set('dob')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Occupation</label>
              <input className={css.formInput} value={form.occupation} onChange={set('occupation')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Full Address</label>
            <textarea className={css.formInput} rows={2} value={form.address} onChange={set('address')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>City</label>
              <input className={css.formInput} value={form.city} onChange={set('city')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>State / Region</label>
              <input className={css.formInput} value={form.stateRegion} onChange={set('stateRegion')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Postal Code</label>
              <input className={css.formInput} value={form.postalCode} onChange={set('postalCode')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.country} onChange={set('country')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Country of Origin</label>
            <input className={css.formInput} value={form.countryOfOrigin} onChange={set('countryOfOrigin')} />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Emergency Contact Name</label>
            <input className={css.formInput} value={form.emergencyContactName} onChange={set('emergencyContactName')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Emergency Contact Relationship</label>
              <input className={css.formInput} value={form.emergencyContactRelationship} onChange={set('emergencyContactRelationship')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Emergency Contact Phone</label>
              <input className={css.formInput} value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Emergency Contact Email</label>
              <input className={css.formInput} type="email" value={form.emergencyContactEmail} onChange={set('emergencyContactEmail')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Emergency Contact Address</label>
              <input className={css.formInput} value={form.emergencyContactAddress} onChange={set('emergencyContactAddress')} />
            </div>
          </div>
          {!isPending && (
            <div className={css.formGroup}>
              <label className={css.formLabel}>Status</label>
              <select className={css.formSelect} value={form.status} onChange={set('status')}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
          {subjects.length > 0 && (
            <div className={css.formGroup}>
              <label className={css.formLabel}>Assigned Subjects</label>
              <div className={css.checkGrid}>
                {subjects.map(s => (
                  <label key={s._id} className={css.checkLabel}>
                    <input className="schoolCheckbox" type="checkbox" checked={form.assignedSubjects.includes(String(s._id))} onChange={() => toggleMulti('assignedSubjects', String(s._id))} />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {classes.length > 0 && (
            <div className={css.formGroup}>
              <label className={css.formLabel}>Assigned Classes</label>
              <div className={css.checkGrid}>
                {classes.map(c => (
                  <label key={c._id} className={css.checkLabel}>
                    <input className="schoolCheckbox" type="checkbox" checked={form.assignedClasses.includes(String(c._id))} onChange={() => toggleMulti('assignedClasses', String(c._id))} />
                    {c.name || c.level}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>{isPending ? 'Close' : 'Cancel'}</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : isPending ? <><MailOutlineIcon style={{ fontSize: '1rem' }} /> Resend Invite</> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TeachersTab({ school, subjects, classes, onRefreshStats, isOwner, onNavigate }) {
  const [teachers, setTeachers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null); // null | 'invite' | 'bulk-invite' | { edit: teacher }
  const [error, setError]         = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { teacherId, name }
  const [bulkHistoryKey, setBulkHistoryKey] = useState(0);
  const searchTimeout = useRef(null);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();
      const { ok, data } = await schoolFetch(`/school/teachers${query ? `?${query}` : ''}`);
      if (ok) setTeachers(data.teachers || []);
      else setError(data.error || 'Failed to load teachers.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchTeachers(), search ? 400 : 0);
    return () => clearTimeout(searchTimeout.current);
  }, [fetchTeachers, search]);

  const handleRemove = (teacherId, name) => {
    setDeleteTarget({ teacherId, name });
  };

  const confirmRemoveTeacher = async (password) => {
    const result = await schoolFetch(`/school/teachers/${deleteTarget.teacherId}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': password },
    });
    if (result.ok) {
      setDeleteTarget(null);
      fetchTeachers();
      if (onRefreshStats) onRefreshStats();
    } else {
      setError(result.data?.error || 'Remove failed.');
    }
    return result;
  };

  const quota    = school?.subscription?.maxTeachers || 10;
  const active   = teachers.filter(t => t.status === 'active').length;
  const statusStyles = {
    active:    { background: '#e8f5e9', color: '#2e7d32' },
    inactive:  { background: '#fff3e0', color: '#e65100' },
    suspended: { background: '#fce4ec', color: '#c62828' },
  };

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="teachers" title="Pending Teacher Invites" isOwner={isOwner} onNavigate={onNavigate} />
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Teachers</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>{active} active · Quota: {quota}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css.btnSecondary} onClick={() => setModal('bulk-invite')}>
              <UploadIcon style={{ fontSize: '1rem' }} /> Bulk Invite
            </button>
            <button className={css.btnPrimary} onClick={() => setModal('invite')}>
              <AddIcon style={{ fontSize: '1rem' }} /> Invite Teacher
            </button>
          </div>
        </div>

        <div className={css.filtersBar}>
          <input
            className={css.searchInput}
            placeholder="Search by name, email, staff ID, subject, or class…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <SkeletonTableRows rows={4} cols={7} />
        ) : teachers.length === 0 ? (
          <div className={css.emptyState}>
            <SchoolIcon className={css.emptyIcon} />
            <p className={css.emptyText}>{search ? 'No teachers match your search' : 'No teachers yet'}</p>
            <p className={css.emptySub}>{search ? 'Try a different name, email, staff ID, subject, or class.' : 'Invite your first teacher to get started.'}</p>
            {!search && (
              <button className={css.btnPrimary} onClick={() => setModal('invite')}>
                <AddIcon style={{ fontSize: '1rem' }} /> Invite Teacher
              </button>
            )}
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Staff ID</th>
                  <th>Subjects</th>
                  <th>Classes</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map(t => (
                  <tr key={t._id}>
                    <td>
                      <div className={css.avatarCell}>
                        {t.profileImg && t.profileImg !== '/uploads/avatar.png'
                          ? <img src={t.profileImg} alt="" className={css.avatar} />
                          : <div className={css.avatarInitials}>{(t.firstName?.[0] || '').toUpperCase()}{(t.lastName?.[0] || '').toUpperCase()}</div>
                        }
                        <div>
                          <div className={css.cellName}>{t.firstName} {t.lastName}</div>
                          <div className={css.cellSub}>{t.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}>{t.staffId || <span style={{ color: '#b0bec5' }}>—</span>}</td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}>
                      {(t.assignedSubjects || []).length > 0
                        ? (t.assignedSubjects || []).map(s => s.name || '').filter(Boolean).join(', ')
                        : <span style={{ color: '#b0bec5' }}>None</span>}
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}>
                      {(t.assignedClasses || []).length > 0
                        ? (t.assignedClasses || []).map(c => c.name || c.level || '').filter(Boolean).join(', ')
                        : <span style={{ color: '#b0bec5' }}>None</span>}
                    </td>
                    <td>
                      <span className={css.chip} style={statusStyles[t.status] || statusStyles.inactive}>
                        {t.status === 'inactive' ? 'Pending' : t.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
                      {t.lastLoginAt ? new Date(t.lastLoginAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ edit: t })} title={t.status === 'inactive' ? 'Edit and resend teacher invite' : 'Edit teacher'} aria-label={t.status === 'inactive' ? 'Edit and resend teacher invite' : 'Edit teacher'}>
                          {t.status === 'inactive' ? <><MailOutlineIcon style={{ fontSize: '0.9rem' }} /> Resend</> : <EditIcon style={{ fontSize: '0.9rem' }} />}
                        </button>
                        <button className={css.btnDanger} onClick={() => handleRemove(t._id, `${t.firstName} ${t.lastName}`)}>
                          <PersonRemoveIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'invite' && (
        <InviteTeacherModal
          subjects={subjects}
          classes={classes}
          onClose={() => setModal(null)}
          onSuccess={() => { fetchTeachers(); if (onRefreshStats) onRefreshStats(); }}
        />
      )}
      {modal?.edit && (
        <EditTeacherModal
          teacher={modal.edit}
          subjects={subjects}
          classes={classes}
          onClose={() => setModal(null)}
          onSuccess={fetchTeachers}
        />
      )}
      {modal === 'bulk-invite' && (
        <BulkInviteTeachersModal
          onClose={() => { setModal(null); fetchTeachers(); setBulkHistoryKey(k => k + 1); if (onRefreshStats) onRefreshStats(); }}
          onSuccess={() => setBulkHistoryKey(k => k + 1)}
        />
      )}

      <BulkInviteTeacherHistoryPanel refreshKey={bulkHistoryKey} />

      {deleteTarget && (
        <DeleteConfirmModal
          message={`Remove ${deleteTarget.name} from this school? Their account will be suspended immediately.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmRemoveTeacher}
        />
      )}
    </div>
  );
}

// ─── Parents Tab ─────────────────────────────────────────────────────────────
// NOTE: parent invite/link is available on every subscription plan (not gated).
function InviteParentModal({ students, onClose, onSuccess }) {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [membershipIds, setMembershipIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const toggleStudent = (id) => setMembershipIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const filtered = students.filter(m => {
    const q = search.toLowerCase();
    const u = m.userId || {};
    return !q
      || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (m.studentId || '').toLowerCase().includes(q);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/parents/invite', {
        method: 'POST',
        body: JSON.stringify({ ...form, membershipIds }),
      });
      if (ok) {
        setDone(data.queued ? { email: form.email, queued: true } : data.parent);
        onSuccess();
      }
      else setError(data.error || 'Invite failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Invite a Parent</h2>
        <p className={css.modalSub}>An invite link will be sent to their email address.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        {done ? (
          <>
            <div className={css.inlineSuccess}>✓ {done.queued ? <>Invite request for <strong>{done.email}</strong> was submitted for owner approval. The email will be sent after approval.</> : <>Invite sent to <strong>{done.email}</strong>. They will appear as inactive until they accept.</>}</div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => { setDone(null); setForm({ email: '', firstName: '', lastName: '', phone: '' }); setMembershipIds([]); }}>Invite another</button>
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email *</label>
              <input className={css.formInput} type="email" required value={form.email} onChange={set('email')} placeholder="parent@example.com" />
            </div>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>First Name</label>
                <input className={css.formInput} value={form.firstName} onChange={set('firstName')} placeholder="e.g. Ngozi" />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Last Name</label>
                <input className={css.formInput} value={form.lastName} onChange={set('lastName')} placeholder="e.g. Eze" />
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone (optional)</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} placeholder="e.g. 080XXXXXXXX" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Link to Child(ren)</label>
              <input
                className={css.searchInput}
                placeholder="Search students by name, email, or ID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ marginBottom: 10 }}
              />
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e8edf2', borderRadius: 10, padding: 8 }}>
                {filtered.slice(0, 50).map(m => {
                  const u = m.userId || {};
                  return (
                    <label key={m._id} className={css.checkLabel} style={{ display: 'flex', width: '100%' }}>
                      <input className="schoolCheckbox" type="checkbox" checked={membershipIds.includes(m._id)} onChange={() => toggleStudent(m._id)} />
                      {u.firstName} {u.lastName} {m.studentId ? `(${m.studentId})` : ''}
                    </label>
                  );
                })}
                {filtered.length === 0 && <p style={{ color: '#90a4ae', fontSize: '0.8rem', margin: 4 }}>No students match.</p>}
              </div>
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" className={css.btnPrimary} disabled={loading}>
                {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><AddIcon style={{ fontSize: '1rem' }} /> Send Invite</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Edit Parent Modal ─────────────────────────────────────────────────────────
// Editable fields: firstName, lastName, phone, status, relationship (for newly
// linked children), plus linking/unlinking any number of students. Saving is a
// two-step flow: the form is collected first, then — just like removing a
// parent — the admin must confirm with their password before anything is
// actually persisted (each PATCH call the confirm step fires is protected by
// requireAdminPassword server-side).
// A still-pending (status: 'inactive') parent skips the password step entirely:
// the form (incl. email, which is only editable while pending) submits
// directly with resendInvite: true, mirroring the teacher invite-resend flow.
function EditParentModal({ parent, students, onClose, onSuccess }) {
  const isPending = parent.status === 'inactive';
  const [form, setForm] = useState({
    email: parent.email || '',
    firstName: parent.firstName || '',
    lastName: parent.lastName || '',
    phone: parent.phone || '',
    dob: parent.dob || '',
    address: parent.address || '',
    city: parent.city || '',
    stateRegion: parent.stateRegion || '',
    postalCode: parent.postalCode || '',
    country: parent.country || '',
    countryOfOrigin: parent.countryOfOrigin || '',
    occupation: parent.occupation || '',
    otherGuardianName: parent.otherGuardianName || '',
    otherGuardianRelationship: parent.otherGuardianRelationship || '',
    otherGuardianPhone: parent.otherGuardianPhone || '',
    otherGuardianEmail: parent.otherGuardianEmail || '',
    otherGuardianAddress: parent.otherGuardianAddress || '',
    status: parent.status || 'inactive',
  });
  const originalMembershipIds = (parent.linkedStudents || [])
    .map(l => String(l.membershipId?._id || l.membershipId))
    .filter(Boolean);
  const [membershipIds, setMembershipIds] = useState(originalMembershipIds);
  const [relationship, setRelationship] = useState('guardian');
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleStudent = (id) => setMembershipIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const filtered = students.filter(m => {
    const q = search.toLowerCase();
    const u = m.userId || {};
    return !q
      || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (m.studentId || '').toLowerCase().includes(q);
  });

  const resendInvite = async () => {
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch(`/school/parents/${parent._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...form, membershipIds, relationship, resendInvite: true }),
      });
      if (ok) { onSuccess(); onClose(); }
      else setError(data.error || 'Update failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (isPending) { resendInvite(); return; }
    setConfirming(true);
  };

  const confirmSave = async (password) => {
    const headers = { 'x-admin-password': password };

    const baseResult = await schoolFetch(`/school/parents/${parent._id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(form),
    });
    if (!baseResult.ok) return baseResult;

    const toLink = membershipIds.filter(id => !originalMembershipIds.includes(id));
    const toUnlink = originalMembershipIds.filter(id => !membershipIds.includes(id));

    for (const id of toLink) {
      const r = await schoolFetch(`/school/parents/${parent._id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ linkMembershipId: id, relationship }),
      });
      if (!r.ok) return r;
    }
    for (const id of toUnlink) {
      const r = await schoolFetch(`/school/parents/${parent._id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ unlinkMembershipId: id }),
      });
      if (!r.ok) return r;
    }
    return baseResult;
  };

  if (confirming) {
    return (
      <DeleteConfirmModal
        title="Confirm Parent Changes"
        confirmLabel="Save Changes"
        danger={false}
        message={`Save these changes to ${parent.firstName} ${parent.lastName}'s profile?`}
        onCancel={() => setConfirming(false)}
        onConfirm={async (password) => {
          const result = await confirmSave(password);
          if (result.ok) {
            onSuccess();
            onClose();
          }
          return result;
        }}
      />
    );
  }

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isPending ? 'Edit Parent Invite' : 'Edit Parent'}</h2>
        <p className={css.modalSub}>{isPending ? 'Update the invite details, then send a fresh invite link.' : 'Changes to an active parent account require admin password confirmation.'}</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.avatarUploadRow} style={{ marginBottom: 16 }}>
            <div className={css.studentAvatarPreview}>
              {parent.profileImg && parent.profileImg !== '/uploads/avatar.png' ? (
                <img src={parent.profileImg} alt="Parent passport" />
              ) : (
                <span>{(parent.firstName?.[0] || '').toUpperCase()}{(parent.lastName?.[0] || '').toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className={css.avatarUploadHint}>Current passport photo</div>
              <div className={css.modalSub} style={{ marginBottom: 0 }}>{parent.email}</div>
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Email *</label>
            <input className={css.formInput} type="email" required value={form.email} onChange={set('email')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>First Name</label>
              <input className={css.formInput} value={form.firstName} onChange={set('firstName')} placeholder="e.g. Ngozi" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Last Name</label>
              <input className={css.formInput} value={form.lastName} onChange={set('lastName')} placeholder="e.g. Eze" />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} placeholder="e.g. 080XXXXXXXX" />
            </div>
            {!isPending && (
              <div className={css.formGroup}>
                <label className={css.formLabel}>Status</label>
                <select className={css.formSelect} value={form.status} onChange={set('status')}>
                  <option value="active">Active</option>
                  <option value="inactive">Pending / Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Date of Birth</label>
              <input className={css.formInput} type="date" value={form.dob} onChange={set('dob')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Occupation</label>
              <input className={css.formInput} value={form.occupation} onChange={set('occupation')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Full Address</label>
            <textarea className={css.formInput} rows={2} value={form.address} onChange={set('address')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>City</label>
              <input className={css.formInput} value={form.city} onChange={set('city')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>State / Region</label>
              <input className={css.formInput} value={form.stateRegion} onChange={set('stateRegion')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Postal Code</label>
              <input className={css.formInput} value={form.postalCode} onChange={set('postalCode')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.country} onChange={set('country')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Country of Origin</label>
            <input className={css.formInput} value={form.countryOfOrigin} onChange={set('countryOfOrigin')} />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Other Guardian Name</label>
            <input className={css.formInput} value={form.otherGuardianName} onChange={set('otherGuardianName')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Other Guardian Relationship</label>
              <input className={css.formInput} value={form.otherGuardianRelationship} onChange={set('otherGuardianRelationship')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Other Guardian Phone</label>
              <input className={css.formInput} value={form.otherGuardianPhone} onChange={set('otherGuardianPhone')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Other Guardian Email</label>
              <input className={css.formInput} type="email" value={form.otherGuardianEmail} onChange={set('otherGuardianEmail')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Other Guardian Address</label>
              <input className={css.formInput} value={form.otherGuardianAddress} onChange={set('otherGuardianAddress')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Linked Children</label>
            <input
              className={css.searchInput}
              placeholder="Search students by name, email, or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e8edf2', borderRadius: 10, padding: 8 }}>
              {filtered.slice(0, 50).map(m => {
                const u = m.userId || {};
                return (
                  <label key={m._id} className={css.checkLabel} style={{ display: 'flex', width: '100%' }}>
                    <input className="schoolCheckbox" type="checkbox" checked={membershipIds.includes(String(m._id))} onChange={() => toggleStudent(String(m._id))} />
                    {u.firstName} {u.lastName} {m.studentId ? `(${m.studentId})` : ''}
                  </label>
                );
              })}
              {filtered.length === 0 && <p style={{ color: '#90a4ae', fontSize: '0.8rem', margin: 4 }}>No students match.</p>}
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Relationship (applied to newly linked children)</label>
            <select className={css.formSelect} value={relationship} onChange={e => setRelationship(e.target.value)}>
              <option value="mother">Mother</option>
              <option value="father">Father</option>
              <option value="guardian">Guardian</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>{isPending ? 'Close' : 'Cancel'}</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : isPending ? <><MailOutlineIcon style={{ fontSize: '1rem' }} /> Resend Invite</> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Bulk Invite Parents (spreadsheet, any column layout, batched via cron) ───
// Admins can upload a file with ANY column names/order. We auto-suggest a
// mapping using common header aliases, but every column's target field is a
// plain dropdown the admin can freely override — no engineer needed to
// support a differently-shaped spreadsheet. Rows are canonicalized entirely
// client-side, then handed to the backend as plain { email, firstName, ... }
// objects; the server just enqueues a durable job that a cron poller works
// through in small batches, so imports of thousands of parents never block
// this request or the event loop.
const BULK_INVITE_FIELDS = [
  { key: 'email', label: 'Parent Email *', multi: false },
  { key: 'firstName', label: 'Parent First Name', multi: false },
  { key: 'lastName', label: 'Parent Last Name', multi: false },
  { key: 'phone', label: 'Parent Phone', multi: false },
  { key: 'relationship', label: 'Relationship (mother/father/guardian/other)', multi: false },
  { key: 'studentIdentifiers', label: 'Linked Student ID(s) / Email(s)', multi: true },
];

const BULK_INVITE_ALIASES = {
  email: ['parentemail', 'email', 'guardianemail', 'emailaddress', 'mothersemail', 'fathersemail', 'guardiansemail'],
  firstName: ['parentfirstname', 'firstname', 'guardianfirstname'],
  lastName: ['parentlastname', 'lastname', 'guardianlastname'],
  phone: ['phone', 'phonenumber', 'mobile', 'contact', 'parentphone', 'contactnumber'],
  relationship: ['relationship', 'relation'],
  studentIdentifiers: ['studentid', 'studentids', 'linkedstudents', 'linkedstudent', 'student', 'students', 'childid', 'childrenid', 'studentemail', 'childstudentid', 'wardid'],
};

function guessBulkInviteField(header) {
  const norm = String(header || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (!norm) return '';
  for (const field of Object.keys(BULK_INVITE_ALIASES)) {
    if (BULK_INVITE_ALIASES[field].includes(norm)) return field;
  }
  return '';
}

function parseRawSheetRows(rows) {
  if (!rows || rows.length < 2) return { headers: [], dataRows: [], error: 'File must have a header row and at least one data row.' };
  const headers = rows[0].map(h => String(h || '').trim());
  const dataRows = rows.slice(1).filter(r => Array.isArray(r) && r.some(c => String(c || '').trim() !== ''));
  if (dataRows.length === 0) return { headers, dataRows: [], error: 'No data rows found below the header row.' };
  return { headers, dataRows, error: null };
}

function buildBulkInviteRows(dataRows, mapping) {
  return dataRows.map(vals => {
    const row = { email: '', firstName: '', lastName: '', phone: '', relationship: 'guardian', studentIdentifiers: [] };
    Object.entries(mapping).forEach(([colIdx, field]) => {
      if (!field) return;
      const raw = String(vals[colIdx] ?? '').trim();
      if (!raw) return;
      if (field === 'studentIdentifiers') {
        raw.split(/[;,]/).map(s => s.trim()).filter(Boolean).forEach(id => row.studentIdentifiers.push(id));
      } else if (field === 'relationship') {
        const norm = raw.toLowerCase();
        row.relationship = ['mother', 'father', 'guardian', 'other'].includes(norm) ? norm : 'guardian';
      } else {
        row[field] = raw;
      }
    });
    return row;
  }).filter(r => r.email);
}

const BULK_INVITE_SAMPLE_ROWS = [
  ['Parent First Name', 'Parent Last Name', 'Parent Email', 'Parent Phone', 'Relationship', 'Linked Student ID(s)/Email(s)'],
  ['Ngozi', 'Eze', 'ngozi.eze@example.com', '080xxxxxxxx', 'mother', 'STT001'],
  ['Tunde', 'Bakare', 'tunde.bakare@example.com', '080xxxxxxxx', 'father', 'STT002;STT003'],
];

function downloadBulkInviteSampleCSV() {
  const csv = BULK_INVITE_SAMPLE_ROWS.map(r => r.join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'bubble_parents_bulk_invite_sample.csv'; a.click();
  URL.revokeObjectURL(url);
}

function downloadBulkInviteSampleXLSX() {
  const ws = XLSX.utils.aoa_to_sheet(BULK_INVITE_SAMPLE_ROWS);
  ws['!cols'] = [{ wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 16 }, { wch: 12 }, { wch: 20 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Parents');
  XLSX.writeFile(wb, 'bubble_parents_bulk_invite_sample.xlsx');
}

function downloadFailedRowsCSV(failedRows, filename = 'failed_parent_invites.csv') {
  if (!failedRows?.length) return;
  const rows = [['Email', 'First Name', 'Last Name', 'Reason']];
  failedRows.forEach(f => rows.push([f.email, f.firstName, f.lastName, f.reason]));
  const csv = rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Polls a bulk-invite job's status/progress every 2.5s until it settles.
// Reused both inline (right after starting a new job) and from "Recent Bulk
// Invites" history (reopening an older, possibly still-running job).
// `endpoint` lets this be shared across actor types (parents, teachers, …) —
// each just points at its own `/school/<actor>/bulk-invite` base path.
function BulkInviteProgressView({ jobId, onClose, endpoint = '/school/parents/bulk-invite', failedFileName = 'failed_parent_invites.csv' }) {
  const [job, setJob] = useState(null);
  const pollRef = useRef();

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { ok, data } = await schoolFetch(`${endpoint}/${jobId}`);
        if (!cancelled && ok) {
          setJob(data);
          if (data.status !== 'done' && data.status !== 'failed') {
            pollRef.current = setTimeout(poll, 2500);
          }
        }
      } catch { /* keep trying on the next interval */ }
    };
    poll();
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [jobId, endpoint]);

  const percent = job?.totals?.total ? Math.round((job.totals.processed / job.totals.total) * 100) : 0;

  return (
    <>
      {!job ? (
        <p className={css.modalSub}>Loading…</p>
      ) : (
        <>
          <div className={css.spTeacherProgress}>
            <div className={css.spTeacherProgressBar}><div className={css.spTeacherProgressFill} style={{ width: `${percent}%` }} /></div>
            <span className={css.spTeacherProgressLabel}>{percent}%</span>
          </div>
          <p className={css.modalSub}>
            {job.totals.processed} / {job.totals.total} processed — {job.totals.succeeded} invited, {job.totals.failed} failed.
            {job.status !== 'done' && job.status !== 'failed'
              ? ' Large imports continue automatically in the background — you can close this and check back later.'
              : ''}
          </p>
          {job.failedRows?.length > 0 && (
            <>
              <div className={css.csvHint}>
                <strong>Failed rows:</strong><br />
                {job.failedRows.slice(0, 20).map((f, i) => <div key={i}>{f.email}: {f.reason}</div>)}
                {job.failedRows.length > 20 && <div>…and {job.failedRows.length - 20} more (download for full list)</div>}
              </div>
              <button className={css.sampleBtn} type="button" onClick={() => downloadFailedRowsCSV(job.failedRows, failedFileName)} style={{ marginTop: 8 }}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> Download failed rows CSV
              </button>
            </>
          )}
        </>
      )}
      <div className={css.modalFooter}>
        <button className={css.btnPrimary} onClick={onClose}>Close</button>
      </div>
    </>
  );
}

function BulkInviteParentsModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'map' | 'progress'
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({}); // colIdx -> field key
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const fileInputRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const parsed = parseRawSheetRows(rows);
        if (parsed.error) { setError(parsed.error); return; }
        const initialMapping = {};
        parsed.headers.forEach((h, i) => { initialMapping[i] = guessBulkInviteField(h); });
        setHeaders(parsed.headers);
        setDataRows(parsed.dataRows);
        setMapping(initialMapping);
        setStep('map');
      } catch {
        setError('Could not read file. Please use a valid CSV or Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const setColumnField = (colIdx, field) => setMapping(m => ({ ...m, [colIdx]: field }));

  // Exactly one column must map to Parent Email; other singular fields may
  // only be mapped once each (studentIdentifiers may span several columns).
  const mappingIssues = (() => {
    const counts = {};
    Object.values(mapping).forEach(f => { if (f) counts[f] = (counts[f] || 0) + 1; });
    const issues = [];
    if (!counts.email) issues.push('Map one column to "Parent Email" — it is required.');
    BULK_INVITE_FIELDS.forEach(f => {
      if (!f.multi && counts[f.key] > 1) issues.push(`Only one column can be mapped to "${f.label}".`);
    });
    return issues;
  })();

  const previewRows = mappingIssues.length === 0 ? buildBulkInviteRows(dataRows, mapping) : [];

  const handleSubmit = async () => {
    if (mappingIssues.length > 0) return;
    const rows = buildBulkInviteRows(dataRows, mapping);
    if (rows.length === 0) { setError('No valid rows found — every row needs a parent email.'); return; }
    if (rows.length > 10000) { setError('Maximum 10,000 rows per bulk invite. Please split your file.'); return; }

    const columnMapping = {};
    headers.forEach((h, i) => { if (mapping[i]) columnMapping[h || `Column ${i + 1}`] = mapping[i]; });

    setSubmitting(true);
    setError('');
    try {
      const { ok, data } = await schoolFetch('/school/parents/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ rows, columnMapping }),
      });
      if (ok) {
        setJobId(data.jobId);
        setStep('progress');
        onSuccess();
      } else {
        setError(data.error || 'Bulk invite failed to start.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={step === 'progress' ? undefined : onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h2 className={css.modalTitle}>Bulk Invite Parents</h2>
        {error && <div className={css.inlineError}>{error}</div>}

        {step === 'upload' && (
          <>
            <p className={css.modalSub}>Upload a spreadsheet with any column layout — you'll map its columns to the right fields next.</p>
            <div className={css.csvHint}>
              <strong>For best result</strong> (any header names, any order): Your excel data should have <code>Parent Email</code>, <code>Parent First Name</code>, <code>Parent Last Name</code>, <code>Parent Phone</code>, <code>Relationship</code>, and <code>Linked Student ID(s)/Email(s)</code> to auto-link children.
            </div>
            <div className={css.sampleDownloads}>
              <span className={css.sampleLabel}>Download sample template:</span>
              <button className={css.sampleBtn} type="button" onClick={downloadBulkInviteSampleCSV}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> CSV
              </button>
              <button className={css.sampleBtn} type="button" onClick={downloadBulkInviteSampleXLSX}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> Excel (.xlsx)
              </button>
            </div>
            <div
              className={css.dropZone}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { fileInputRef.current.files = e.dataTransfer.files; handleFile({ target: { files: e.dataTransfer.files } }); } }}
            >
              <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: 'none' }} onChange={handleFile} />
              <div className={css.dropZonePlaceholder}>
                <UploadIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 8 }} />
                <div className={css.dropZoneText}>Click to browse or drag &amp; drop</div>
                <div className={css.dropZoneHint}>Accepts .csv, .xls, .xlsx — up to 10,000 rows, processed in batches</div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {step === 'map' && (
          <>
            <p className={css.modalSub}>
              <strong>{fileName}</strong> — {dataRows.length} row(s) detected. We auto-matched columns below; change any dropdown to fix a mismatch.
            </p>
            <div className={css.tableWrap} style={{ maxHeight: 260, overflowY: 'auto' }}>
              <table className={css.table}>
                <thead>
                  <tr><th>Column in your file</th><th>Sample value</th><th>Maps to</th></tr>
                </thead>
                <tbody>
                  {headers.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{h || `Column ${i + 1}`}</td>
                      <td style={{ color: '#78909c', fontSize: '0.8rem' }}>{String(dataRows[0]?.[i] ?? '')}</td>
                      <td>
                        <select className={css.formSelect} value={mapping[i] || ''} onChange={e => setColumnField(i, e.target.value)}>
                          <option value="">— Ignore this column —</option>
                          {BULK_INVITE_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mappingIssues.length > 0 ? (
              <div className={css.inlineError} style={{ marginTop: 10 }}>{mappingIssues.join(' ')}</div>
            ) : (
              <div className={css.inlineSuccess} style={{ marginTop: 10 }}>
                ✓ {previewRows.length} parent row(s) ready to invite{previewRows.length < dataRows.length ? ` (${dataRows.length - previewRows.length} skipped — missing email)` : ''}.
              </div>
            )}

            <div className={css.csvHint} style={{ marginTop: 10 }}>
              <strong>Tip:</strong> for "Linked Student ID(s) / Email(s)", separate multiple children in one cell with <code>;</code> or <code>,</code> — e.g. <code>STD001;STD002</code>.
            </div>

            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={() => setStep('upload')}>Back</button>
              <button type="button" className={css.btnPrimary} onClick={handleSubmit} disabled={submitting || mappingIssues.length > 0}>
                {submitting ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><UploadIcon style={{ fontSize: '1rem' }} /> Start Bulk Invite</>}
              </button>
            </div>
          </>
        )}

        {step === 'progress' && jobId && (
          <BulkInviteProgressView jobId={jobId} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function BulkInviteHistoryPanel({ refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [openJobId, setOpenJobId] = useState(null);

  useEffect(() => {
    schoolFetch('/school/parents/bulk-invite').then(({ ok, data }) => { if (ok) setJobs(data.jobs || []); });
  }, [refreshKey]);

  if (jobs.length === 0) return null;

  const statusStyles = {
    done:    { background: '#e8f5e9', color: '#2e7d32' },
    pending: { background: '#fff3e0', color: '#e65100' },
    running: { background: '#fff3e0', color: '#e65100' },
    failed:  { background: '#fce4ec', color: '#c62828' },
  };

  return (
    <div className={css.card} style={{ marginTop: 16 }}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle} style={{ fontSize: '1rem' }}>Recent Bulk Invites</h2>
      </div>
      <div className={css.tableWrap}>
        <table className={css.table}>
          <thead><tr><th>Date</th><th>Rows</th><th>Status</th><th>Progress</th><th></th></tr></thead>
          <tbody>
            {jobs.map(j => {
              const pct = j.totals?.total ? Math.round((j.totals.processed / j.totals.total) * 100) : 0;
              return (
                <tr key={j._id}>
                  <td style={{ fontSize: '0.78rem' }}>{new Date(j.createdAt).toLocaleString()}</td>
                  <td style={{ fontSize: '0.78rem' }}>{j.totals?.total}</td>
                  <td><span className={css.chip} style={statusStyles[j.status] || statusStyles.pending}>{j.status}</span></td>
                  <td style={{ fontSize: '0.78rem' }}>{j.totals?.succeeded} ok / {j.totals?.failed} failed ({pct}%)</td>
                  <td><button className={css.btnSecondary} onClick={() => setOpenJobId(j._id)}>View</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {openJobId && (
        <div className={css.modalOverlay} onClick={() => setOpenJobId(null)}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h2 className={css.modalTitle}>Bulk Invite Progress</h2>
            <BulkInviteProgressView jobId={openJobId} onClose={() => setOpenJobId(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk Invite Teachers (spreadsheet, any column layout, batched via cron) ──
// Mirrors the Parent bulk-invite pattern above. Minimum required per row is
// teacher email, first name, last name, and staff ID — subjects/classes are
// optional; when provided, the teacher is fully assigned to them during the
// invite, otherwise they're invited unassigned (assign later via the edit
// icon in Actions).
const BULK_INVITE_TEACHER_FIELDS = [
  { key: 'email', label: 'Teacher Email *', multi: false },
  { key: 'firstName', label: 'Teacher First Name *', multi: false },
  { key: 'lastName', label: 'Teacher Last Name *', multi: false },
  { key: 'staffId', label: 'Staff ID *', multi: false },
  { key: 'phone', label: 'Teacher Phone', multi: false },
  { key: 'subjectIdentifiers', label: 'Subject(s) to Assign', multi: true },
  { key: 'classIdentifiers', label: 'Class(es) to Assign', multi: true },
];

const BULK_INVITE_TEACHER_ALIASES = {
  email: ['teacheremail', 'email', 'emailaddress', 'staffemail'],
  firstName: ['teacherfirstname', 'firstname'],
  lastName: ['teacherlastname', 'lastname', 'surname'],
  staffId: ['staffid', 'teacherid', 'employeeid', 'staffno', 'staffnumber'],
  phone: ['phone', 'phonenumber', 'mobile', 'contact', 'teacherphone', 'contactnumber'],
  subjectIdentifiers: ['subject', 'subjects', 'assignedsubjects', 'subjectname', 'subjectnames'],
  classIdentifiers: ['class', 'classes', 'assignedclasses', 'classname', 'classnames'],
};

function guessBulkInviteTeacherField(header) {
  const norm = String(header || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (!norm) return '';
  for (const field of Object.keys(BULK_INVITE_TEACHER_ALIASES)) {
    if (BULK_INVITE_TEACHER_ALIASES[field].includes(norm)) return field;
  }
  return '';
}

function buildBulkInviteTeacherRows(dataRows, mapping) {
  return dataRows.map(vals => {
    const row = { email: '', firstName: '', lastName: '', staffId: '', phone: '', subjectIdentifiers: [], classIdentifiers: [] };
    Object.entries(mapping).forEach(([colIdx, field]) => {
      if (!field) return;
      const raw = String(vals[colIdx] ?? '').trim();
      if (!raw) return;
      if (field === 'subjectIdentifiers' || field === 'classIdentifiers') {
        raw.split(/[;,]/).map(s => s.trim()).filter(Boolean).forEach(id => row[field].push(id));
      } else {
        row[field] = raw;
      }
    });
    return row;
  }).filter(r => r.email && r.firstName && r.lastName && r.staffId);
}

const BULK_INVITE_TEACHER_SAMPLE_ROWS = [
  ['Teacher First Name', 'Teacher Last Name', 'Teacher Email', 'Staff ID', 'Teacher Phone', 'Subject(s) to Assign', 'Class(es) to Assign'],
  ['Adaeze', 'Okonkwo', 'adaeze.okonkwo@example.com', 'STF001', '080xxxxxxxx', 'Mathematics', 'JSS1;JSS2'],
  ['Emeka', 'Chukwu', 'emeka.chukwu@example.com', 'STF002', '', '', ''],
];

function downloadBulkInviteTeacherSampleCSV() {
  const csv = BULK_INVITE_TEACHER_SAMPLE_ROWS.map(r => r.join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'bubble_teachers_bulk_invite_sample.csv'; a.click();
  URL.revokeObjectURL(url);
}

function downloadBulkInviteTeacherSampleXLSX() {
  const ws = XLSX.utils.aoa_to_sheet(BULK_INVITE_TEACHER_SAMPLE_ROWS);
  ws['!cols'] = [{ wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 22 }, { wch: 22 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Teachers');
  XLSX.writeFile(wb, 'bubble_teachers_bulk_invite_sample.xlsx');
}

function BulkInviteTeachersModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'map' | 'progress'
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({}); // colIdx -> field key
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const fileInputRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const parsed = parseRawSheetRows(rows);
        if (parsed.error) { setError(parsed.error); return; }
        const initialMapping = {};
        parsed.headers.forEach((h, i) => { initialMapping[i] = guessBulkInviteTeacherField(h); });
        setHeaders(parsed.headers);
        setDataRows(parsed.dataRows);
        setMapping(initialMapping);
        setStep('map');
      } catch {
        setError('Could not read file. Please use a valid CSV or Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const setColumnField = (colIdx, field) => setMapping(m => ({ ...m, [colIdx]: field }));

  // Email, First Name, Last Name and Staff ID are required (minimum viable
  // invite); other singular fields may only be mapped once each
  // (subjects/classes may span several columns).
  const mappingIssues = (() => {
    const counts = {};
    Object.values(mapping).forEach(f => { if (f) counts[f] = (counts[f] || 0) + 1; });
    const issues = [];
    if (!counts.email) issues.push('Map one column to "Teacher Email" — it is required.');
    if (!counts.firstName) issues.push('Map one column to "Teacher First Name" — it is required.');
    if (!counts.lastName) issues.push('Map one column to "Teacher Last Name" — it is required.');
    if (!counts.staffId) issues.push('Map one column to "Staff ID" — it is required.');
    BULK_INVITE_TEACHER_FIELDS.forEach(f => {
      if (!f.multi && counts[f.key] > 1) issues.push(`Only one column can be mapped to "${f.label}".`);
    });
    return issues;
  })();

  const previewRows = mappingIssues.length === 0 ? buildBulkInviteTeacherRows(dataRows, mapping) : [];

  const handleSubmit = async () => {
    if (mappingIssues.length > 0) return;
    const rows = buildBulkInviteTeacherRows(dataRows, mapping);
    if (rows.length === 0) { setError('No valid rows found — every row needs a teacher email, first name, last name, and staff ID.'); return; }
    if (rows.length > 10000) { setError('Maximum 10,000 rows per bulk invite. Please split your file.'); return; }

    const columnMapping = {};
    headers.forEach((h, i) => { if (mapping[i]) columnMapping[h || `Column ${i + 1}`] = mapping[i]; });

    setSubmitting(true);
    setError('');
    try {
      const { ok, data } = await schoolFetch('/school/teachers/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ rows, columnMapping }),
      });
      if (ok) {
        setJobId(data.jobId);
        setStep('progress');
        onSuccess();
      } else {
        setError(data.error || 'Bulk invite failed to start.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={step === 'progress' ? undefined : onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h2 className={css.modalTitle}>Bulk Invite Teachers</h2>
        {error && <div className={css.inlineError}>{error}</div>}

        {step === 'upload' && (
          <>
            <p className={css.modalSub}>Upload a spreadsheet with any column layout — you'll map its columns to the right fields next.</p>
            <div className={css.csvHint}>
              <strong>Minimum required:</strong> <code>Teacher Email</code>, <code>Teacher First Name</code>, <code>Teacher Last Name</code>, <code>Staff ID</code>. If you also provide <code>Subject(s)</code> and/or <code>Class(es)</code>, the teacher is fully assigned during the invite — otherwise they're invited unassigned and can be assigned later via the edit icon.
            </div>
            <div className={css.sampleDownloads}>
              <span className={css.sampleLabel}>Download sample template:</span>
              <button className={css.sampleBtn} type="button" onClick={downloadBulkInviteTeacherSampleCSV}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> CSV
              </button>
              <button className={css.sampleBtn} type="button" onClick={downloadBulkInviteTeacherSampleXLSX}>
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> Excel (.xlsx)
              </button>
            </div>
            <div
              className={css.dropZone}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { fileInputRef.current.files = e.dataTransfer.files; handleFile({ target: { files: e.dataTransfer.files } }); } }}
            >
              <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: 'none' }} onChange={handleFile} />
              <div className={css.dropZonePlaceholder}>
                <UploadIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 8 }} />
                <div className={css.dropZoneText}>Click to browse or drag &amp; drop</div>
                <div className={css.dropZoneHint}>Accepts .csv, .xls, .xlsx — up to 10,000 rows, processed in batches</div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {step === 'map' && (
          <>
            <p className={css.modalSub}>
              <strong>{fileName}</strong> — {dataRows.length} row(s) detected. We auto-matched columns below; change any dropdown to fix a mismatch.
            </p>
            <div className={css.tableWrap} style={{ maxHeight: 260, overflowY: 'auto' }}>
              <table className={css.table}>
                <thead>
                  <tr><th>Column in your file</th><th>Sample value</th><th>Maps to</th></tr>
                </thead>
                <tbody>
                  {headers.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{h || `Column ${i + 1}`}</td>
                      <td style={{ color: '#78909c', fontSize: '0.8rem' }}>{String(dataRows[0]?.[i] ?? '')}</td>
                      <td>
                        <select className={css.formSelect} value={mapping[i] || ''} onChange={e => setColumnField(i, e.target.value)}>
                          <option value="">— Ignore this column —</option>
                          {BULK_INVITE_TEACHER_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mappingIssues.length > 0 ? (
              <div className={css.inlineError} style={{ marginTop: 10 }}>{mappingIssues.join(' ')}</div>
            ) : (
              <div className={css.inlineSuccess} style={{ marginTop: 10 }}>
                ✓ {previewRows.length} teacher row(s) ready to invite{previewRows.length < dataRows.length ? ` (${dataRows.length - previewRows.length} skipped — missing email/first/last name)` : ''}.
              </div>
            )}

            <div className={css.csvHint} style={{ marginTop: 10 }}>
              <strong>Tip:</strong> for "Subject(s)" or "Class(es)", separate multiple values in one cell with <code>;</code> or <code>,</code> — e.g. <code>Mathematics;Physics</code>.
            </div>

            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={() => setStep('upload')}>Back</button>
              <button type="button" className={css.btnPrimary} onClick={handleSubmit} disabled={submitting || mappingIssues.length > 0}>
                {submitting ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <><UploadIcon style={{ fontSize: '1rem' }} /> Start Bulk Invite</>}
              </button>
            </div>
          </>
        )}

        {step === 'progress' && jobId && (
          <BulkInviteProgressView jobId={jobId} onClose={onClose} endpoint="/school/teachers/bulk-invite" failedFileName="failed_teacher_invites.csv" />
        )}
      </div>
    </div>
  );
}

function BulkInviteTeacherHistoryPanel({ refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [openJobId, setOpenJobId] = useState(null);

  useEffect(() => {
    schoolFetch('/school/teachers/bulk-invite').then(({ ok, data }) => { if (ok) setJobs(data.jobs || []); });
  }, [refreshKey]);

  if (jobs.length === 0) return null;

  const statusStyles = {
    done:    { background: '#e8f5e9', color: '#2e7d32' },
    pending: { background: '#fff3e0', color: '#e65100' },
    running: { background: '#fff3e0', color: '#e65100' },
    failed:  { background: '#fce4ec', color: '#c62828' },
  };

  return (
    <div className={css.card} style={{ marginTop: 16 }}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle} style={{ fontSize: '1rem' }}>Recent Bulk Invites</h2>
      </div>
      <div className={css.tableWrap}>
        <table className={css.table}>
          <thead><tr><th>Date</th><th>Rows</th><th>Status</th><th>Progress</th><th></th></tr></thead>
          <tbody>
            {jobs.map(j => {
              const pct = j.totals?.total ? Math.round((j.totals.processed / j.totals.total) * 100) : 0;
              return (
                <tr key={j._id}>
                  <td style={{ fontSize: '0.78rem' }}>{new Date(j.createdAt).toLocaleString()}</td>
                  <td style={{ fontSize: '0.78rem' }}>{j.totals?.total}</td>
                  <td><span className={css.chip} style={statusStyles[j.status] || statusStyles.pending}>{j.status}</span></td>
                  <td style={{ fontSize: '0.78rem' }}>{j.totals?.succeeded} ok / {j.totals?.failed} failed ({pct}%)</td>
                  <td><button className={css.btnSecondary} onClick={() => setOpenJobId(j._id)}>View</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {openJobId && (
        <div className={css.modalOverlay} onClick={() => setOpenJobId(null)}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h2 className={css.modalTitle}>Bulk Invite Progress</h2>
            <BulkInviteProgressView jobId={openJobId} onClose={() => setOpenJobId(null)} endpoint="/school/teachers/bulk-invite" failedFileName="failed_teacher_invites.csv" />
          </div>
        </div>
      )}
    </div>
  );
}

function ParentsTab({ school, isOwner, onNavigate }) {
  const [parents, setParents]   = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null); // null | 'invite' | 'bulk-invite' | { edit: parent }
  const [error, setError]       = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { parentId, name }
  const [bulkHistoryKey, setBulkHistoryKey] = useState(0);
  const searchTimeout = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();
      const [parentsRes, studentsRes] = await Promise.all([
        schoolFetch(`/school/parents${query ? `?${query}` : ''}`),
        schoolFetch('/school/students?limit=200&status=active'),
      ]);
      if (parentsRes.ok) setParents(parentsRes.data.parents || []);
      else setError(parentsRes.data.error || 'Failed to load parents.');
      setStudents(studentsRes.data.students || []);
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchData(), search ? 400 : 0);
    return () => clearTimeout(searchTimeout.current);
  }, [fetchData, search]);

  const handleRemove = (parentId, name) => setDeleteTarget({ parentId, name });

  const confirmRemoveParent = async (password) => {
    const result = await schoolFetch(`/school/parents/${deleteTarget.parentId}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': password },
    });
    if (result.ok) {
      setDeleteTarget(null);
      fetchData();
    } else {
      setError(result.data?.error || 'Remove failed.');
    }
    return result;
  };

  const statusStyles = {
    active:    { background: '#e8f5e9', color: '#2e7d32' },
    inactive:  { background: '#fff3e0', color: '#e65100' },
    suspended: { background: '#fce4ec', color: '#c62828' },
  };

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="parents" title="Pending Parent Invites" isOwner={isOwner} onNavigate={onNavigate} />
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Parents</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>Available on every plan — invite parents and link them to their children</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css.btnSecondary} onClick={() => setModal('bulk-invite')}>
              <UploadIcon style={{ fontSize: '1rem' }} /> Bulk Invite
            </button>
            <button className={css.btnPrimary} onClick={() => setModal('invite')}>
              <AddIcon style={{ fontSize: '1rem' }} /> Invite Parent
            </button>
          </div>
        </div>

        <div className={css.filtersBar}>
          <input
            className={css.searchInput}
            placeholder="Search by parent name, email, or linked student…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <SkeletonTableRows rows={4} cols={4} />
        ) : parents.length === 0 ? (
          <div className={css.emptyState}>
            <FamilyRestroomIcon className={css.emptyIcon} />
            <p className={css.emptyText}>{search ? 'No parents match your search' : 'No parents yet'}</p>
            <p className={css.emptySub}>{search ? 'Try a different parent name, email, or linked student.' : "Invite a parent and link them to their child's account."}</p>
            {!search && (
              <button className={css.btnPrimary} onClick={() => setModal('invite')}>
                <AddIcon style={{ fontSize: '1rem' }} /> Invite Parent
              </button>
            )}
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Parent</th>
                  <th>Linked Children</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {parents.map(p => (
                  <tr key={p._id}>
                    <td>
                      <div className={css.avatarCell}>
                        {p.profileImg && p.profileImg !== '/uploads/avatar.png'
                          ? <img src={p.profileImg} alt="" className={css.avatar} />
                          : <div className={css.avatarInitials}>{(p.firstName?.[0] || '').toUpperCase()}{(p.lastName?.[0] || '').toUpperCase()}</div>}
                        <div>
                          <div className={css.cellName}>{p.firstName} {p.lastName}</div>
                          <div className={css.cellSub}>{p.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}>
                      {(p.linkedStudents || []).length > 0
                        ? p.linkedStudents.map(l => `${l.studentId?.firstName || ''} ${l.studentId?.lastName || ''}`.trim()).filter(Boolean).join(', ')
                        : <span style={{ color: '#b0bec5' }}>None</span>}
                    </td>
                    <td>
                      <span className={css.chip} style={statusStyles[p.status] || statusStyles.inactive}>
                        {p.status === 'inactive' ? 'Pending' : p.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ edit: p })} title={p.status === 'inactive' ? 'Edit and resend parent invite' : 'Edit parent'} aria-label={p.status === 'inactive' ? 'Edit and resend parent invite' : 'Edit parent'}>
                          {p.status === 'inactive' ? <><MailOutlineIcon style={{ fontSize: '0.9rem' }} /> Resend</> : <EditIcon style={{ fontSize: '0.9rem' }} />}
                        </button>
                        <button className={css.btnDanger} onClick={() => handleRemove(p._id, `${p.firstName} ${p.lastName}`)}>
                          <PersonRemoveIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'invite' && (
        <InviteParentModal
          students={students}
          onClose={() => setModal(null)}
          onSuccess={fetchData}
        />
      )}
      {modal?.edit && (
        <EditParentModal
          parent={modal.edit}
          students={students}
          onClose={() => setModal(null)}
          onSuccess={fetchData}
        />
      )}
      {modal === 'bulk-invite' && (
        <BulkInviteParentsModal
          onClose={() => { setModal(null); fetchData(); setBulkHistoryKey(k => k + 1); }}
          onSuccess={() => setBulkHistoryKey(k => k + 1)}
        />
      )}

      <BulkInviteHistoryPanel refreshKey={bulkHistoryKey} />

      {deleteTarget && (
        <DeleteConfirmModal
          message={`Remove ${deleteTarget.name} from this school? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmRemoveParent}
        />
      )}
    </div>
  );
}

// ─── Fees & Billing Tab ────────────────────────────────────────────────────────
const FEE_CATEGORIES = ['tuition', 'transport', 'feeding', 'uniform', 'exam', 'other'];
const TERMS = ['first', 'second', 'third'];

const FEES_SUBS = [
  { key: 'structures', label: 'Fee Structures', Icon: ReceiptLongIcon },
  { key: 'invoices',   label: 'Invoices',       Icon: DescriptionIcon },
  { key: 'payouts',    label: 'Payouts',        Icon: AccountBalanceIcon },
  { key: 'billing',    label: 'Billing & Plan',  Icon: PaymentsIcon },
];

function FeesBillingTab({ school, isOwner, onNavigate }) {
  const [sub, setSub] = useState('structures');
  return (
    <div>
      <PendingSubAdminApprovals moduleKey="fees" title="Pending Invoice Requests" isOwner={isOwner} onNavigate={onNavigate} />
      <div className={css.subTabBar}>
        {FEES_SUBS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`${css.subTabBtn} ${sub === key ? css.subTabBtnActive : ''}`}
            onClick={() => setSub(key)}
          >
            <Icon style={{ fontSize: '1rem' }} /> {label}
          </button>
        ))}
      </div>
      {sub === 'structures' && <FeeStructuresPanel school={school} />}
      {sub === 'invoices'   && <InvoicesPanel school={school} />}
      {sub === 'payouts'    && <PayoutsPanel />}
      {sub === 'billing'    && <BillingPlanPanel school={school} />}
    </div>
  );
}

function FeeStructuresPanel({ school }) {
  const [fees, setFees]       = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modal, setModal]     = useState(null); // null | 'add' | { edit } | 'generate'
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [genResult, setGenResult] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [optionFilter, setOptionFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [feesRes, classesRes] = await Promise.all([
        schoolFetch('/school/fees'),
        schoolFetch('/school/classes'),
      ]);
      if (feesRes.ok) setFees(feesRes.data.fees || []);
      else setError(feesRes.data.error || 'Failed to load fee structures.');
      setClasses(classesRes.data.classes || []);
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredFees = classFilter
    ? fees.filter(fee => {
      const feeClassId = fee.classId?._id || fee.classId || null;
      const classMatches = feeClassId === null || String(feeClassId) === String(classFilter);
      const optionMatches = !optionFilter || (fee.pricingOptions || []).some(option => option.key === optionFilter);
      return classMatches && optionMatches;
    })
    : fees.filter(fee => !optionFilter || (fee.pricingOptions || []).some(option => option.key === optionFilter));
  const feeClasses = fees
    .map(fee => fee.classId)
    .filter(classItem => classItem && typeof classItem === 'object' && classItem._id)
    .reduce((unique, classItem) => {
      if (!unique.some(item => String(item._id) === String(classItem._id))) unique.push(classItem);
      return unique;
    }, []);
  const classOptions = [...classes, ...feeClasses.filter(feeClass => !classes.some(item => String(item._id) === String(feeClass._id)))];
  const flatSubtotal = filteredFees
    .filter(fee => (fee.pricingMode || 'flat') === 'flat')
    .reduce((sum, fee) => sum + Number(fee.amount || 0), 0);
  const optionFees = filteredFees.filter(fee => fee.pricingMode === 'options' || (fee.pricingOptions || []).length > 0);
  const optionMinimum = optionFees.reduce((sum, fee) => {
    const amounts = (fee.pricingOptions || []).map(option => Number(option.amount || 0));
    return sum + (amounts.length ? Math.min(...amounts) : 0);
  }, 0);
  const optionMaximum = optionFees.reduce((sum, fee) => {
    const amounts = (fee.pricingOptions || []).map(option => Number(option.amount || 0));
    return sum + (amounts.length ? Math.max(...amounts) : 0);
  }, 0);
  const optionLabels = Array.from(new Map(
    fees.flatMap(fee => (fee.pricingOptions || []).map(option => [option.key, option.label]))
  ).entries()).sort((a, b) => a[1].localeCompare(b[1]));

  const confirmArchive = async (password) => {
    const result = await schoolFetch(`/school/fees/${archiveTarget._id}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': password },
    });
    if (result.ok) { setArchiveTarget(null); fetchData(); }
    return result;
  };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {genResult && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{genResult}</div>}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Fee Structures</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>Available on every plan — define fees per term/class, then generate invoices</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css.btnSecondary} onClick={() => setModal('generate')}>
              <AutoFixHighIcon style={{ fontSize: '1rem' }} /> Generate Invoices
            </button>
            <button className={css.btnPrimary} onClick={() => setModal('add')}>
              <AddIcon style={{ fontSize: '1rem' }} /> Add Fee
            </button>
          </div>
        </div>

        {!loading && fees.length > 0 && <>
          <div className={css.feeSummaryGrid}>
            <div className={css.statCard}><span className={css.statLabel}>Flat fees</span><span className={css.statValue}>{fmtNaira(flatSubtotal)}</span><span className={css.statSub}>Fixed-price structures</span></div>
            <div className={css.statCard}><span className={css.statLabel}>Option range</span><span className={css.statValue} style={{ fontSize: '1.15rem' }}>{optionFees.length ? `${fmtNaira(optionMinimum)} – ${fmtNaira(optionMaximum)}` : '—'}</span><span className={css.statSub}>{optionFees.length} option-priced structure{optionFees.length === 1 ? '' : 's'}</span></div>
            <div className={css.statCard}><span className={css.statLabel}>Structures</span><span className={css.statValue}>{filteredFees.length}</span><span className={css.statSub}>Select an option to narrow this view</span></div>
          </div>
          <div className={css.filtersBar}>
            <label className={css.filterLabel} htmlFor="fee-class-filter">Filter by class</label>
            <select id="fee-class-filter" className={css.filterSelect} value={classFilter} onChange={event => setClassFilter(event.target.value)}>
              <option value="">All classes</option>
              {classOptions.map(item => <option key={item._id} value={item._id}>{item.name || item.level || 'Unnamed class'}</option>)}
            </select>
            <label className={css.filterLabel} htmlFor="fee-option-filter">Filter by pricing option</label>
            <select id="fee-option-filter" className={css.filterSelect} value={optionFilter} onChange={event => setOptionFilter(event.target.value)}>
              <option value="">All pricing options</option>
              {optionLabels.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
        </>}

        {loading ? (
          <SkeletonTableRows rows={4} cols={7} />
        ) : fees.length === 0 ? (
          <div className={css.emptyState}>
            <DescriptionIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No fee structures yet</p>
            <p className={css.emptySub}>Create a fee structure (e.g. Tuition, Transport) for a term, then generate invoices for all students.</p>
            <button className={css.btnPrimary} onClick={() => setModal('add')}>
              <AddIcon style={{ fontSize: '1rem' }} /> Add Fee
            </button>
          </div>
        ) : filteredFees.length === 0 ? (
          <div className={css.emptyState}>
            <DescriptionIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No fee structures for this class</p>
            <p className={css.emptySub}>Choose another class or select All classes to see every fee structure.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Name</th><th>Class</th><th>Term</th><th>Session</th>
                  <th>Price / Options</th><th>Fee type</th><th>Mandatory</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFees.map(f => (
                  <tr key={f._id}>
                    <td><strong>{f.name}</strong></td>
                    <td>{f.classId?.name || <span style={{ color: '#b0bec5' }}>All classes</span>}</td>
                    <td style={{ textTransform: 'capitalize' }}>{f.term}</td>
                    <td>{f.session}</td>
                    <td>
                      {(f.pricingMode || 'flat') === 'options' ? (
                        <div style={{ display: 'grid', gap: 4, minWidth: 170 }}>
                          {(f.pricingOptions || []).map(option => (
                            <span key={option.key} className={css.chip} style={{ background: option.key === optionFilter ? '#d9f2f3' : '#f0f4f8', color: '#355b63', width: 'fit-content' }}>
                              {option.label}: {fmtNaira(option.amount)}
                            </span>
                          ))}
                        </div>
                      ) : fmtNaira(f.amount)}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{f.category}</td>
                    <td>{f.isMandatory ? 'Yes' : 'No'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ edit: f })}>
                          <EditIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                        <button className={css.btnDanger} onClick={() => setArchiveTarget(f)}>
                          <DeleteIcon style={{ fontSize: '0.9rem' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'add' || modal?.edit) && (
        <FeeFormModal
          fee={modal?.edit || null}
          classes={classes}
          defaultSession={school?.academicSession}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); fetchData(); }}
        />
      )}
      {modal === 'generate' && (
        <GenerateInvoicesModal
          classes={classes}
          defaultTerm={school?.currentTerm}
          defaultSession={school?.academicSession}
          onClose={() => setModal(null)}
          onSuccess={(msg) => { setModal(null); setGenResult(msg); }}
        />
      )}
      {archiveTarget && (
        <DeleteConfirmModal
          message={`Archive "${archiveTarget.name}"? It will no longer be applied to new invoices, but existing invoices keep this line item.`}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={confirmArchive}
        />
      )}
    </div>
  );
}

function FeeFormModal({ fee, classes, defaultSession, onClose, onSuccess }) {
  const isEdit = !!fee;
  const initialClassIds = fee?.classId ? [String(fee.classId?._id || fee.classId)] : [];
  const [form, setForm] = useState({
    name: fee?.name || '',
    classId: fee?.classId?._id || fee?.classId || '',
    classIds: initialClassIds,
    term: fee?.term || 'first',
    session: fee?.session || defaultSession || '',
    amount: fee?.amount ?? '',
    pricingMode: fee?.pricingMode || (fee?.pricingOptions?.length ? 'options' : 'flat'),
    pricingOptions: fee?.pricingOptions?.length
      ? fee.pricingOptions.map(option => ({ key: option.key || '', label: option.label || '', amount: option.amount ?? '' }))
      : [{ key: '', label: '', amount: '' }, { key: '', label: '', amount: '' }],
    category: fee?.category || 'tuition',
    dueDate: fee?.dueDate ? fee.dueDate.slice(0, 10) : '',
    isMandatory: fee?.isMandatory !== undefined ? fee.isMandatory : true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggleClass = (id) => setForm(f => {
    const arr = f.classIds || [];
    return { ...f, classIds: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
  });
  const selectAllClasses = () => setForm(f => ({
    ...f,
    classIds: (f.classIds || []).length === classes.length ? [] : classes.map(c => String(c._id)),
  }));
  const setOption = (index, key) => (e) => setForm(current => ({
    ...current,
    pricingOptions: current.pricingOptions.map((option, optionIndex) => optionIndex === index ? { ...option, [key]: e.target.value } : option),
  }));
  const addOption = () => setForm(current => ({ ...current, pricingOptions: [...current.pricingOptions, { key: '', label: '', amount: '' }] }));
  const removeOption = index => setForm(current => ({
    ...current,
    pricingOptions: current.pricingOptions.filter((_, optionIndex) => optionIndex !== index),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.session) { setError('Name and session are required.'); return; }
    if (form.pricingMode === 'flat' && form.amount === '') { setError('Enter the flat fee amount.'); return; }
    if (form.pricingMode === 'options') {
      if (form.pricingOptions.length < 2) { setError('Add at least two pricing options.'); return; }
      if (form.pricingOptions.some(option => !String(option.label).trim() || option.amount === '')) {
        setError('Every pricing option needs a label and amount.'); return;
      }
      const labels = form.pricingOptions.map(option => option.label.trim().toLowerCase());
      if (new Set(labels).size !== labels.length) { setError('Pricing option labels must be unique.'); return; }
    }
    setError(''); setLoading(true);
    try {
      const selectedClassIds = (form.classIds || []).filter(Boolean);
      const body = {
        name: form.name,
        category: form.category,
        dueDate: form.dueDate || null,
        isMandatory: form.isMandatory,
        pricingMode: form.pricingMode,
        ...(form.pricingMode === 'options'
          ? { pricingOptions: form.pricingOptions.map(option => ({ ...(option.key ? { key: option.key } : {}), label: option.label.trim(), amount: Number(option.amount) })) }
          : { amount: Number(form.amount) }),
        ...(isEdit ? {
          classId: form.classId || null,
        } : {
          term: form.term,
          session: form.session,
          ...(selectedClassIds.length === 1
            ? { classId: selectedClassIds[0] }
            : selectedClassIds.length > 1
              ? { classIds: selectedClassIds }
              : { classId: null }),
        }),
      };
      const { ok, data } = await schoolFetch(isEdit ? `/school/fees/${fee._id}` : '/school/fees', {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
      if (ok) onSuccess();
      else setError(data.error || 'Failed to save fee.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit Fee Structure' : 'Add Fee Structure'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Name *</label>
            <input className={css.formInput} value={form.name} onChange={set('name')} placeholder="e.g. Tuition Fee" required />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Term *</label>
              <select className={css.formSelect} value={form.term} onChange={set('term')} disabled={isEdit}>
                {TERMS.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Session *</label>
              <input className={css.formInput} value={form.session} onChange={set('session')} placeholder="e.g. 2025/2026" required disabled={isEdit} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Pricing method *</label>
              <select className={css.formSelect} value={form.pricingMode} onChange={set('pricingMode')}>
                <option value="flat">One flat price</option>
                <option value="options">Different prices by student choice</option>
              </select>
              <span className={css.formHint}>Use options when the same fee has different prices, such as Day and Boarding.</span>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Category</label>
              <select className={css.formSelect} value={form.category} onChange={set('category')}>
                {FEE_CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
              </select>
            </div>
          </div>
          {form.pricingMode === 'flat' ? (
            <div className={css.formGroup}>
              <label className={css.formLabel}>Amount (₦) *</label>
              <input className={css.formInput} type="number" min="0" value={form.amount} onChange={set('amount')} required />
            </div>
          ) : (
            <div className={css.formGroup}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div>
                  <label className={css.formLabel} style={{ marginBottom: 2 }}>Pricing options *</label>
                  <span className={css.formHint}>Parents choose one option for this fee before payment.</span>
                </div>
                <button type="button" className={css.btnSecondary} onClick={addOption} style={{ padding: '6px 10px', fontSize: '0.78rem' }}><AddIcon style={{ fontSize: '0.9rem' }} /> Add option</button>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {form.pricingOptions.map((option, index) => (
                  <div key={index} className={css.formRow} style={{ alignItems: 'end' }}>
                    <div className={css.formGroup}>
                      <label className={css.formLabel}>Option {index + 1} label</label>
                      <input className={css.formInput} value={option.label} onChange={setOption(index, 'label')} placeholder={index === 0 ? 'e.g. Day student' : 'e.g. Boarding student'} />
                    </div>
                    <div className={css.formGroup} style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
                      <div style={{ flex: 1 }}>
                        <label className={css.formLabel}>Price (₦)</label>
                        <input className={css.formInput} type="number" min="0" value={option.amount} onChange={setOption(index, 'amount')} placeholder="0" />
                      </div>
                      {form.pricingOptions.length > 2 && <button type="button" className={css.btnDanger} title="Remove pricing option" onClick={() => removeOption(index)} style={{ padding: '8px 10px' }}><DeleteIcon style={{ fontSize: '0.95rem' }} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isEdit ? (
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Class (optional — applies to all if blank)</label>
                <select className={css.formSelect} value={form.classId} onChange={set('classId')}>
                  <option value="">All classes</option>
                  {classes.map(c => <option key={c._id} value={c._id}>{c.name || c.level}</option>)}
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Due Date (optional)</label>
                <input className={css.formInput} type="date" value={form.dueDate} onChange={set('dueDate')} />
              </div>
            </div>
          ) : (
            <>
              {classes.length > 0 && (
                <div className={css.formGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label className={css.formLabel} style={{ marginBottom: 0 }}>
                      Apply to Classes <span style={{ color: '#90a4ae', fontWeight: 400 }}>({(form.classIds || []).length === 0 ? 'Applies to all classes' : `${(form.classIds || []).length} of ${classes.length} selected`})</span>
                    </label>
                    <button
                      type="button"
                      style={{ fontSize: '0.75rem', color: '#3e8f93', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                      onClick={selectAllClasses}
                    >
                      {(form.classIds || []).length === classes.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className={css.checkGrid}>
                    {classes.map(c => (
                      <label key={c._id} className={css.checkLabel}>
                        <input
                          className="schoolCheckbox"
                          type="checkbox"
                          checked={(form.classIds || []).includes(String(c._id))}
                          onChange={() => toggleClass(String(c._id))}
                        />
                        {c.name || c.level}
                      </label>
                    ))}
                  </div>
                  <span className={css.formHint} style={{ marginTop: 4 }}>
                    {(form.classIds || []).length === 0 ? 'Leaving all unchecked will create a fee structure applicable to all classes.' : (form.classIds || []).length === 1 ? 'A fee structure will be created for the selected class.' : `Fee structures will be created for each of the ${(form.classIds || []).length} selected classes.`}
                  </span>
                </div>
              )}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Due Date (optional)</label>
                <input className={css.formInput} type="date" value={form.dueDate} onChange={set('dueDate')} />
              </div>
            </>
          )}
          <div className={css.formGroup} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input className="schoolCheckbox" type="checkbox" checked={form.isMandatory} onChange={set('isMandatory')} id="fee-mandatory" />
              <label htmlFor="fee-mandatory" style={{ fontSize: '0.85rem', color: '#263238', fontWeight: 600, cursor: 'pointer' }}>Mandatory fee</label>
            </div>
            <span className={css.formHint} style={{ marginTop: 4 }}>
              When checked, students must pay this fee to be eligible for exams and Continuous Assessment (CA). If unchecked (optional fee), students remain eligible for exams/CAs as long as all mandatory fees for the term are paid.
            </span>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : (isEdit ? 'Save Changes' : 'Add Fee')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GenerateInvoicesModal({ classes, defaultTerm, defaultSession, onClose, onSuccess }) {
  const [form, setForm] = useState({ term: defaultTerm || 'first', session: defaultSession || '', classId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.session) { setError('Session is required.'); return; }
    setError(''); setLoading(true);
    try {
      const body = { term: form.term, session: form.session };
      if (form.classId) body.classId = form.classId;
      const { ok, data } = await schoolFetch('/school/fees/generate-invoices', { method: 'POST', body: JSON.stringify(body) });
      if (ok) onSuccess(data.message || 'Invoices generated.');
      else setError(data.error || 'Failed to generate invoices.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Generate Invoices</h2>
        <p className={css.modalSub}>Creates (or updates) one invoice per active student for the selected term, combining all applicable fee structures.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Term *</label>
              <select className={css.formSelect} value={form.term} onChange={set('term')}>
                {TERMS.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Session *</label>
              <input className={css.formInput} value={form.session} onChange={set('session')} placeholder="e.g. 2025/2026" required />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Class (optional — all active students if blank)</label>
            <select className={css.formSelect} value={form.classId} onChange={set('classId')}>
              <option value="">All classes</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name || c.level}</option>)}
            </select>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const INVOICE_STATUS_STYLES = {
  unpaid:  { background: '#fce4ec', color: '#c62828' },
  partial: { background: '#fff3e0', color: '#e65100' },
  paid:    { background: '#e8f5e9', color: '#2e7d32' },
  overdue: { background: '#fce4ec', color: '#c62828' },
  waived:  { background: '#eceff1', color: '#546e7a' },
  pending_selection: { background: '#fff8e1', color: '#8d6e00' },
};

// Phase 9 cross-cutting AI — surfaces families likely to default on fee
// payment, based on outstanding balance + historical on-time-payment rate.
function FeeDefaultRiskCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  useEffect(() => {
    schoolFetch('/school/fees/default-risk').then(({ ok, data }) => {
      if (ok) { setData(data); setPage(1); }
      setLoading(false);
    });
  }, []);

  const RISK_STYLES = {
    high: { background: '#fce4ec', color: '#c62828' },
    medium: { background: '#fff8e1', color: '#f57f17' },
    low: { background: '#eceff1', color: '#546e7a' },
  };

  if (loading || !data || (data.atRisk || []).length === 0) return null;

  const pageCount = Math.ceil(data.atRisk.length / PAGE_SIZE);
  const shown = data.atRisk.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle}>Fee Default Risk</h2>
        {data.highRiskCount > 0 && (
          <span className={css.chip} style={RISK_STYLES.high}>{data.highRiskCount} high risk</span>
        )}
      </div>
      {data.narrative && (
        <div style={{ marginBottom: 12, padding: 10, background: '#f4fafb', borderRadius: 8, fontSize: '0.85rem', color: '#37474f' }}>
          ✨ {data.narrative}
        </div>
      )}
      <div className={css.tableWrap}>
        <table className={css.table}>
          <thead><tr><th>Student</th><th>Balance</th><th>Past Overdue</th><th>Risk</th></tr></thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.invoiceId}>
                <td>{r.studentName}</td>
                <td>₦{Number(r.balance).toLocaleString('en-NG')}</td>
                <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>{r.pastOverdueCount}/{r.pastInvoiceCount}</td>
                <td><span className={css.chip} style={RISK_STYLES[r.riskLevel] || RISK_STYLES.low}>{r.riskLevel}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className={css.pagination}>
          <button className={css.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: '0.78rem', color: '#607d8b' }}>Page {page} / {pageCount}</span>
          <button className={css.pageBtn} disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ invoiceCount: 0, fullInvoiceTotal: 0, collected: 0, outstanding: 0, matchedLineSubtotal: 0, pendingSelectionCount: 0, pendingMinimumTotal: 0, pendingMaximumTotal: 0 });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filters, setFilters]   = useState({ term: '', session: '', status: '', classId: '', classArm: '', name: '', feeCategory: '', optionKey: '' });
  const [page, setPage] = useState(1);
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewInvoiceTarget, setViewInvoiceTarget] = useState(null);
  const PAGE_SIZE = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.term) params.set('term', filters.term);
      if (filters.session) params.set('session', filters.session);
      if (filters.status) params.set('status', filters.status);
      if (filters.feeCategory) params.set('feeCategory', filters.feeCategory);
      if (filters.optionKey) params.set('optionKey', filters.optionKey);
      const { ok, data } = await schoolFetch(`/school/invoices?${params}`);
      if (ok) { setInvoices(data.invoices || []); setSummary(data.summary || {}); setPage(1); }
      else setError(data.error || 'Failed to load invoices.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setFilter = (k) => (e) => {
    setPage(1);
    setFilters(f => ({ ...f, [k]: e.target.value }));
  };

  const classOptions = Array.from(new Map(
    invoices
      .filter(inv => inv.membershipId?.classId?._id)
      .map(inv => [String(inv.membershipId.classId._id), inv.membershipId.classId.name || 'Unnamed class'])
  ).entries()).sort((a, b) => a[1].localeCompare(b[1]));
  const armOptions = Array.from(new Set(
    invoices.map(inv => inv.membershipId?.classArm).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
  const categoryOptions = Array.from(new Set(
    invoices.flatMap(inv => (inv.lineItems || []).map(lineItem => lineItem.feeCategory || lineItem.category).filter(Boolean))
  )).sort((a, b) => a.localeCompare(b));
  const pricingOptions = Array.from(new Map(
    invoices.flatMap(inv => (inv.lineItems || []).flatMap(lineItem => (lineItem.pricingOptionsSnapshot || []).map(option => [option.key, option.label])))
  ).entries()).sort((a, b) => a[1].localeCompare(b[1]));
  const searchName = filters.name.trim().toLowerCase();
  const visibleInvoices = invoices.filter(inv => {
    const studentName = `${inv.studentId?.firstName || ''} ${inv.studentId?.lastName || ''}`.trim().toLowerCase();
    return (!searchName || studentName.includes(searchName))
      && (!filters.classId || String(inv.membershipId?.classId?._id) === filters.classId)
      && (!filters.classArm || inv.membershipId?.classArm === filters.classArm);
  });
  const pageCount = Math.ceil(visibleInvoices.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(pageCount, 1));
  const pagedInvoices = visibleInvoices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const invoiceMoney = value => `₦${Number(value || 0).toLocaleString('en-NG')}`;
  const invoiceStatusLabel = status => status === 'pending_selection' ? 'Selection needed' : String(status || '').replace(/_/g, ' ');

  const confirmWaive = async (reason, password) => {
    const result = await schoolFetch(`/school/invoices/${waiveTarget._id}/waive`, {
      method: 'PATCH',
      headers: { 'x-admin-password': password },
      body: JSON.stringify({ reason }),
    });
    if (result.ok) { setWaiveTarget(null); fetchData(); }
    return result;
  };

  const confirmDeleteInvoice = async (password) => {
    const result = await schoolFetch(`/school/invoices/${deleteTarget._id}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': password },
    });
    if (result.ok) { setDeleteTarget(null); fetchData(); }
    return result;
  };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <FeeDefaultRiskCard />
      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Invoices</h2>
        </div>
        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={filters.term} onChange={setFilter('term')}>
            <option value="">All Terms</option>
            {TERMS.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
          </select>
          <input className={css.searchInput} placeholder="Session (e.g. 2025/2026)" value={filters.session} onChange={setFilter('session')} />
          <input className={css.searchInput} placeholder="Search student name" value={filters.name} onChange={setFilter('name')} />
          <select className={css.filterSelect} value={filters.classId} onChange={setFilter('classId')}>
            <option value="">All Classes</option>
            {classOptions.map(([classId, className]) => <option key={classId} value={classId}>{className}</option>)}
          </select>
          <select className={css.filterSelect} value={filters.classArm} onChange={setFilter('classArm')}>
            <option value="">All Arms</option>
            {armOptions.map(arm => <option key={arm} value={arm}>{arm}</option>)}
          </select>
          <select className={css.filterSelect} value={filters.status} onChange={setFilter('status')}>
            <option value="">All Statuses</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="waived">Waived</option>
            <option value="pending_selection">Selection needed</option>
          </select>
          <select className={css.filterSelect} value={filters.feeCategory} onChange={setFilter('feeCategory')}>
            <option value="">All Fee Types</option>
            {categoryOptions.map(category => <option key={category} value={category}>{category}</option>)}
          </select>
          <select className={css.filterSelect} value={filters.optionKey} onChange={setFilter('optionKey')}>
            <option value="">All Pricing Options</option>
            {pricingOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>

        {!loading && invoices.length > 0 && (
          <div className={css.feeSummaryGrid}>
            <div className={css.statCard}><span className={css.statLabel}>Matched fee total</span><span className={css.statValue}>{invoiceMoney(summary.matchedLineSubtotal)}</span><span className={css.statSub}>Selected fee type / option lines</span></div>
            <div className={css.statCard}><span className={css.statLabel}>Full invoice total</span><span className={css.statValue}>{invoiceMoney(summary.fullInvoiceTotal)}</span><span className={css.statSub}>Includes other fees on matched invoices</span></div>
            <div className={css.statCard}><span className={css.statLabel}>Collected</span><span className={css.statValue}>{invoiceMoney(summary.collected)}</span><span className={css.statSub}>{summary.invoiceCount || 0} matching invoices</span></div>
            <div className={css.statCard}><span className={css.statLabel}>Outstanding</span><span className={css.statValue}>{invoiceMoney(summary.outstanding)}</span><span className={css.statSub}>{summary.pendingSelectionCount || 0} awaiting choices</span></div>
          </div>
        )}
        {!loading && summary.pendingSelectionCount > 0 && (
          <div className={css.inlineSuccess} style={{ marginBottom: 14, background: '#fff8e1', color: '#6d5500' }}>
            Pending selections represent a range of <strong>{invoiceMoney(summary.pendingMinimumTotal)} – {invoiceMoney(summary.pendingMaximumTotal)}</strong>; they are excluded from collected and outstanding revenue until parents confirm their choices.
          </div>
        )}

        {loading ? (
          <SkeletonTableRows rows={5} cols={9} />
        ) : invoices.length === 0 ? (
          <div className={css.emptyState}>
            <DescriptionIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No invoices found</p>
            <p className={css.emptySub}>Generate invoices from the Fee Structures tab first.</p>
          </div>
        ) : visibleInvoices.length === 0 ? (
          <div className={css.emptyState}>
            <DescriptionIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No matching invoices</p>
            <p className={css.emptySub}>Try a different student name, class, arm, term, session, or status.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Student</th><th>Class</th><th>Term/Session</th>
                  <th>Selected options</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedInvoices.map(inv => (
                  <tr key={inv._id}>
                    <td>{inv.studentId?.firstName} {inv.studentId?.lastName}</td>
                    <td>{inv.membershipId?.classId?.name || '—'}</td>
                    <td style={{ fontSize: '0.78rem', color: '#546e7a' }}><span style={{ textTransform: 'capitalize' }}>{inv.term}</span> · {inv.session}</td>
                    <td>
                      <div style={{ display: 'grid', gap: 3, minWidth: 150 }}>
                        {(inv.lineItems || []).filter(lineItem => lineItem.pricingMode === 'options').map(lineItem => (
                          <span key={String(lineItem.feeId)} style={{ color: lineItem.pricingOptionLabel ? '#0c7c84' : '#8d6e00', fontSize: '.75rem' }}>
                            {lineItem.label}: {lineItem.pricingOptionLabel || 'choice needed'}
                          </span>
                        ))}
                        {!((inv.lineItems || []).some(lineItem => lineItem.pricingMode === 'options')) && <span style={{ color: '#90a4ae', fontSize: '.75rem' }}>Flat pricing</span>}
                      </div>
                    </td>
                    <td>{inv.status === 'pending_selection' ? `${invoiceMoney(inv.pendingSelection?.minimumTotalAmount)} – ${invoiceMoney(inv.pendingSelection?.maximumTotalAmount)}` : invoiceMoney(inv.totalAmount)}</td>
                    <td>{invoiceMoney(inv.amountPaid)}</td>
                    <td>{inv.status === 'pending_selection' ? '—' : invoiceMoney(inv.balance)}</td>
                    <td>
                      <span className={css.chip} style={INVOICE_STATUS_STYLES[inv.isOverdue ? 'overdue' : inv.status] || INVOICE_STATUS_STYLES.unpaid}>
                        {inv.isOverdue ? 'Overdue' : invoiceStatusLabel(inv.status)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button
                          className={css.btnSecondary}
                          style={{ padding: '5px 8px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => setViewInvoiceTarget(inv)}
                          title="View Invoice Details"
                        >
                          <VisibilityIcon style={{ fontSize: '0.85rem' }} /> Details
                        </button>
                        {inv.status !== 'waived' && inv.status !== 'paid' && inv.status !== 'pending_selection' && (
                          <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setWaiveTarget(inv)}>
                            Waive
                          </button>
                        )}
                        {['unpaid', 'pending_selection'].includes(inv.status) && (inv.amountPaid || 0) === 0 && (
                          <button className={css.btnDanger} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setDeleteTarget(inv)}>
                            <DeleteIcon style={{ fontSize: '0.9rem' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div className={css.pagination}>
                <button className={css.pageBtn} disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize: '0.78rem', color: '#607d8b' }}>
                  Page {currentPage} / {pageCount} · {visibleInvoices.length} invoices
                </span>
                <button className={css.pageBtn} disabled={currentPage >= pageCount} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>

      {waiveTarget && (
        <WaiveInvoiceModal invoice={waiveTarget} onCancel={() => setWaiveTarget(null)} onConfirm={confirmWaive} />
      )}
      {viewInvoiceTarget && (
        <InvoiceDetailsModal invoice={viewInvoiceTarget} onClose={() => setViewInvoiceTarget(null)} />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          message={`Delete this ${deleteTarget.status === 'pending_selection' ? 'pending-selection' : 'unpaid'} invoice for ${deleteTarget.studentId?.firstName} ${deleteTarget.studentId?.lastName}${deleteTarget.status === 'pending_selection' ? '' : ` (₦${Number(deleteTarget.totalAmount).toLocaleString('en-NG')})`}? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDeleteInvoice}
        />
      )}
    </div>
  );
}

function WaiveInvoiceModal({ invoice, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const studentName = `${invoice.studentId?.firstName || ''} ${invoice.studentId?.lastName || ''}`.trim() || 'Student';
  const className = invoice.membershipId?.classId?.name
    ? `${invoice.membershipId.classId.name}${invoice.membershipId.classArm ? ` (${invoice.membershipId.classArm})` : ''}`
    : (invoice.membershipId?.classArm || 'Not assigned');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!reason.trim()) { setError('Reason for waiver is compulsory.'); return; }
    if (!password) { setError('Admin password is required.'); return; }
    setLoading(true);
    try {
      const result = await onConfirm(reason.trim(), password);
      if (result && !result.ok) setError(result.data?.error || 'Failed to waive invoice.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onCancel}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Waive Fee Invoice</h2>
        <p className={css.modalSub}>
          Waiving this invoice will clear the outstanding balance and grant the student full exam and CA eligibility.
        </p>
        
        {/* Important notice about Bubble AI Origin Admin reporting */}
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderLeft: '4px solid #d97706',
          borderRadius: 6,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: '0.82rem',
          color: '#92400e',
          lineHeight: 1.45
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#78350f' }}>
            <span>⚠️</span> Bubble Ai Admin Notification Notice
          </div>
          <p style={{ margin: '0 0 6px 0' }}>
            Please note: All fee waivers are systematically logged and reported to the <strong>Bubble Ai Origin Administration</strong>. Bubble AI administration will follow up on this record for revenue reconciliation and school fee tracking.
          </p>
          <div style={{ background: 'rgba(255,255,255,0.7)', padding: '6px 10px', borderRadius: 4, fontSize: '0.78rem', marginTop: 6 }}>
            <div><strong>Student:</strong> {studentName}</div>
            <div><strong>Class:</strong> {className}</div>
            <div><strong>Term / Session:</strong> {invoice.term ? `${invoice.term.charAt(0).toUpperCase() + invoice.term.slice(1)} Term` : '–'} {invoice.session ? `(${invoice.session})` : ''}</div>
            <div><strong>Total Invoice Fee:</strong> ₦{Number(invoice.totalAmount || 0).toLocaleString('en-NG')}</div>
            {Number(invoice.amountPaid || 0) > 0 && (
              <div><strong>Already Paid:</strong> ₦{Number(invoice.amountPaid || 0).toLocaleString('en-NG')}</div>
            )}
            <div><strong>Remaining Amount to be Waived:</strong> ₦{Number(invoice.balance || 0).toLocaleString('en-NG')}</div>
          </div>
        </div>

        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <div className={css.formGroup}>
            <label className={css.formLabel}>
              Waiver Reason <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              className={css.formInput}
              rows={3}
              style={{ resize: 'vertical', minHeight: 70, paddingTop: 8 }}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Provide a detailed reason for this waiver (e.g., Full academic scholarship approved by board, hardship relief)..."
              required
            />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>
              Admin Password Confirmation <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <SchoolPasswordInput
              className={css.formInput}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your admin password"
              autoComplete="current-password"
              required
            />
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onCancel} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading || !reason.trim() || !password}>
              {loading ? 'Processing…' : 'Confirm & Waive Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InvoiceDetailsModal({ invoice, onClose }) {
  if (!invoice) return null;
  const studentName = `${invoice.studentId?.firstName || ''} ${invoice.studentId?.lastName || ''}`.trim() || 'Student';
  const className = invoice.membershipId?.classId?.name
    ? `${invoice.membershipId.classId.name}${invoice.membershipId.classArm ? ` (${invoice.membershipId.classArm})` : ''}`
    : (invoice.membershipId?.classArm || 'Not assigned');
  const isPendingSelection = invoice.status === 'pending_selection';
  const totalAmount = Number(invoice.totalAmount || 0);
  const amountPaid = Number(invoice.amountPaid || 0);
  const isWaived = invoice.status === 'waived';
  const waivedPortion = isWaived
    ? (Number(invoice.amountWaived || 0) > 0 ? Number(invoice.amountWaived) : Math.max(0, totalAmount - amountPaid))
    : 0;

  const waivedByName = invoice.waivedBy
    ? (typeof invoice.waivedBy === 'object' ? `${invoice.waivedBy.firstName || ''} ${invoice.waivedBy.lastName || ''}`.trim() : 'School Admin')
    : 'School Admin';

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 className={css.modalTitle} style={{ margin: 0 }}>Invoice Details</h2>
            <p className={css.modalSub} style={{ margin: '4px 0 0' }}>
              {studentName} · {className} · <span style={{ textTransform: 'capitalize' }}>{invoice.term}</span> Term ({invoice.session})
            </p>
          </div>
          <span className={css.chip} style={INVOICE_STATUS_STYLES[invoice.isOverdue ? 'overdue' : invoice.status] || INVOICE_STATUS_STYLES.unpaid}>
            {invoice.isOverdue ? 'Overdue' : String(invoice.status || '').replace(/_/g, ' ')}
          </span>
        </div>

        {isWaived && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderLeft: '4px solid #f59e0b',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: '0.82rem',
            color: '#92400e'
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#78350f' }}>
              <span>ℹ️</span> Fee Invoice Waived by School
            </div>
            {amountPaid > 0 ? (
              <div style={{ marginBottom: 4 }}>
                This student previously paid <strong>₦{amountPaid.toLocaleString('en-NG')}</strong>. The remaining balance of <strong>₦{waivedPortion.toLocaleString('en-NG')}</strong> was waived.
              </div>
            ) : (
              <div style={{ marginBottom: 4 }}>
                The full invoice balance of <strong>₦{waivedPortion.toLocaleString('en-NG')}</strong> was waived.
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: '0.78rem', background: 'rgba(255,255,255,0.7)', padding: '6px 10px', borderRadius: 4 }}>
              <div><strong>Waiver Reason:</strong> {invoice.waivedReason || 'No reason specified'}</div>
              <div><strong>Waived By:</strong> {waivedByName} {invoice.waivedAt ? `on ${new Date(invoice.waivedAt).toLocaleDateString()}` : ''}</div>
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '8px 0', margin: '10px 0' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', marginBottom: 6 }}>
            Fee Breakdown
          </div>
          <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#78909c', textAlign: 'left', borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '4px 0' }}>Item</th>
                <th style={{ padding: '4px 8px' }}>Type</th>
                <th style={{ padding: '4px 0', textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lineItems || []).map((li, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '6px 0', color: '#263238' }}>
                    <strong>{li.label}</strong>
                    {li.pricingOptionLabel && <span style={{ color: '#0c7c84', display: 'block', fontSize: '0.74rem' }}>Option: {li.pricingOptionLabel}</span>}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#607d8b', fontSize: '0.74rem' }}>
                    {li.isMandatory !== false ? 'Mandatory' : 'Optional'}
                  </td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: '#263238' }}>
                    ₦{Number(li.amount || 0).toLocaleString('en-NG')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'grid', gap: 6, fontSize: '0.85rem', padding: '4px 0 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#607d8b' }}>Total Invoice Amount:</span>
            <strong style={{ color: '#263238' }}>{isPendingSelection ? `${invoice.pendingSelection?.minimumTotalAmount} – ${invoice.pendingSelection?.maximumTotalAmount}` : `₦${totalAmount.toLocaleString('en-NG')}`}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#607d8b' }}>Amount Paid by Parent:</span>
            <span style={{ color: amountPaid > 0 ? '#2e7d32' : '#263238', fontWeight: 600 }}>
              ₦{amountPaid.toLocaleString('en-NG')}
            </span>
          </div>
          {isWaived && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d97706' }}>
              <span>Amount Waived by Admin:</span>
              <strong>₦{waivedPortion.toLocaleString('en-NG')}</strong>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: 6, fontSize: '0.92rem' }}>
            <span style={{ fontWeight: 700, color: '#263238' }}>Remaining Balance:</span>
            <strong style={{ color: invoice.balance > 0 ? '#c62828' : '#2e7d32' }}>
              ₦{Number(invoice.balance || 0).toLocaleString('en-NG')}
            </strong>
          </div>
        </div>

        <div className={css.modalFooter}>
          <button type="button" className={css.btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function PayoutsPanel() {
  const [account, setAccount]   = useState(null);
  const [payouts, setPayouts]   = useState([]);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [modal, setModal]       = useState(null); // null | 'account'
  const [requesting, setRequesting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, payoutsRes] = await Promise.all([
        schoolFetch('/school/payout-account'),
        schoolFetch('/school/payouts'),
      ]);
      setAccount(accRes.data.account || null);
      setPayouts(payoutsRes.data.payouts || []);
      setPendingAmount(payoutsRes.data.pendingPayoutAmount || 0);
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRequestPayout = async () => {
    setError(''); setSuccess(''); setRequesting(true);
    try {
      const { ok, data } = await schoolFetch('/school/payouts/request', { method: 'POST' });
      if (ok) { setSuccess('Payout requested successfully.'); fetchData(); }
      else setError(data.error || 'Failed to request payout.');
    } catch { setError('Connection error.'); }
    finally { setRequesting(false); }
  };

  const payoutStatusStyles = {
    pending: { background: '#fff3e0', color: '#e65100' },
    processing: { background: '#e3f2fd', color: '#1565c0' },
    paid: { background: '#e8f5e9', color: '#2e7d32' },
    failed: { background: '#fce4ec', color: '#c62828' },
  };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Payout Account</h2>
          <button className={css.btnSecondary} onClick={() => setModal('account')}>
            <EditIcon style={{ fontSize: '1rem' }} /> {account ? 'Update' : 'Set Up'}
          </button>
        </div>
        {loading ? (
          <SkeletonCards count={1} />
        ) : account ? (
          <div className={css.payoutAccountDetails}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase' }}>Bank</div>
              <div className={css.payoutAccountValue}>{account.bankName || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase' }}>Account Number</div>
              <div className={css.payoutAccountValue}>{account.accountNumberMasked}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase' }}>Account Name</div>
              <div className={css.payoutAccountValue}>{account.accountName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase' }}>Verified</div>
              <div style={{ fontWeight: 600, color: account.verified ? '#2e7d32' : '#c62828' }}>{account.verified ? 'Yes' : 'No'}</div>
            </div>
          </div>
        ) : (
          <div className={css.emptyState}>
            <AccountBalanceIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No payout account set up</p>
            <p className={css.emptySub}>Add your school's bank account to receive fee payouts.</p>
            <button className={css.btnPrimary} onClick={() => setModal('account')}>Set Up Payout Account</button>
          </div>
        )}
      </div>

      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Payouts</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
              Pending payout balance: <strong>₦{Number(pendingAmount).toLocaleString('en-NG')}</strong>
            </span>
          </div>
          <button className={css.btnPrimary} onClick={handleRequestPayout} disabled={requesting || pendingAmount <= 0 || !account?.verified}>
            {requesting ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Request Payout'}
          </button>
        </div>
        {loading ? (
          <SkeletonTableRows rows={4} cols={4} />
        ) : payouts.length === 0 ? (
          <div className={css.emptyState}>
            <AccountBalanceIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No payouts yet</p>
            <p className={css.emptySub}>Payouts are automatically batched weekly, or you can request one manually once you have a pending balance.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Period</th><th>Amount</th><th>Status</th><th>Requested</th></tr></thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p._id}>
                    <td style={{ fontSize: '0.78rem' }}>
                      {new Date(p.periodStart).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })} – {new Date(p.periodEnd).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td>₦{Number(p.totalAmount).toLocaleString('en-NG')}</td>
                    <td><span className={css.chip} style={payoutStatusStyles[p.status] || payoutStatusStyles.pending}>{p.status}</span></td>
                    <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>{new Date(p.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'account' && (
        <PayoutAccountModal onClose={() => setModal(null)} onSuccess={() => { setModal(null); fetchData(); }} />
      )}
    </div>
  );
}

function PayoutAccountModal({ onClose, onSuccess }) {
  const [banks, setBanks] = useState([]);
  const [form, setForm]   = useState({ bankCode: '', bankName: '', accountNumber: '' });
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setBanksLoading(true);
      try {
        const { ok, data } = await schoolFetch('/school/payout-account/banks');
        if (ok) setBanks(data.banks || []);
      } finally { setBanksLoading(false); }
    })();
  }, []);

  const handleBankChange = (e) => {
    const code = e.target.value;
    const bank = banks.find(b => b.code === code);
    setForm(f => ({ ...f, bankCode: code, bankName: bank?.name || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankCode || !form.accountNumber) { setError('Bank and account number are required.'); return; }
    setError(''); setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/payout-account', { method: 'POST', body: JSON.stringify(form) });
      if (ok) onSuccess();
      else setError(data.error || 'Could not verify account.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Payout Account</h2>
        <p className={css.modalSub}>We'll verify this account with your bank before saving.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Bank *</label>
            <select className={css.formSelect} value={form.bankCode} onChange={handleBankChange} disabled={banksLoading} required>
              <option value="">{banksLoading ? 'Loading banks…' : '— Select bank —'}</option>
              {banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
            </select>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Account Number *</label>
            <input className={css.formInput} value={form.accountNumber} onChange={e => setForm(f => ({ ...f, accountNumber: e.target.value }))} placeholder="10-digit account number" required />
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Verify & Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BillingPlanPanel({ school }) {
  const [billing, setBilling] = useState(null);
  const [ledger, setLedger]   = useState([]);
  const [studentsDeducted, setStudentsDeducted] = useState(0);
  const [plans, setPlans]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [changeTarget, setChangeTarget] = useState(null); // plan key pending confirm

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [billingRes, plansRes] = await Promise.all([
        schoolFetch('/school/billing'),
        schoolFetch('/school/subscription/plans'),
      ]);
      if (billingRes.ok) {
        setBilling(billingRes.data.billing);
        setLedger(billingRes.data.ledger || []);
        setStudentsDeducted(billingRes.data.studentsDeductedThisPeriod || 0);
      }
      setPlans(plansRes.data.plans || null);
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const currentPlan = school?.subscription?.plan || 'standard';

  const confirmChangePlan = async (password) => {
    const result = await schoolFetch('/school/subscription/plan', {
      method: 'PATCH',
      headers: { 'x-admin-password': password },
      body: JSON.stringify({ plan: changeTarget }),
    });
    if (result.ok) {
      setChangeTarget(null);
      setSuccess(result.data.message || 'Plan changed.');
      fetchData();
    }
    return result;
  };

  const billingStatusStyles = { current: { background: '#e8f5e9', color: '#2e7d32' }, overdue: { background: '#fce4ec', color: '#c62828' } };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      {loading ? <SkeletonStatCards count={4} /> : billing ? (
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h2 className={css.cardTitle}>Current Billing Period</h2>
            <span className={css.chip} style={billingStatusStyles[billing.status] || billingStatusStyles.current}>{billing.status}</span>
          </div>
          <div className={css.statsRow}>
            <div className={css.statCard}>
              <span className={css.statLabel}>Amount Due</span>
              <span className={css.statValue}>₦{Number(billing.amountDue).toLocaleString('en-NG')}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Collected</span>
              <span className={css.statValue}>₦{Number(billing.amountCollected).toLocaleString('en-NG')}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Balance Due</span>
              <span className={css.statValue}>₦{Number(billing.balanceDue).toLocaleString('en-NG')}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Term / Session</span>
              <span className={css.statValue} style={{ fontSize: '1.1rem', textTransform: 'capitalize' }}>{billing.term} · {billing.academicSession}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Per Student / Term</span>
              <span className={css.statValue}>₦{Number(billing.perStudentPrice || 0).toLocaleString('en-NG')}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Students Funded</span>
              <span className={css.statValue}>{studentsDeducted} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#90a4ae' }}>/ {billing.studentCount || 0}</span></span>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: '#90a4ae', marginTop: 8 }}>
            This subscription fee is funded automatically from parent-paid school fees as they come in — no separate charge to your school.
            Each student funds this once per term; extra fee payments after that go entirely to your school.
          </p>
        </div>
      ) : null}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Deduction History</h2>
        </div>
        {ledger.length === 0 ? (
          <div className={css.emptyState}>
            <PaymentsIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No deductions yet</p>
            <p className={css.emptySub}>As parents pay fees, a portion is deducted here to fund Bubble Ai's subscription.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Date</th><th>Student</th><th>Amount Deducted</th><th>Balance After</th></tr></thead>
              <tbody>
                {ledger.map(l => (
                  <tr key={l._id}>
                    <td style={{ fontSize: '0.78rem' }}>{new Date(l.deductedAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{l.studentName}</td>
                    <td>₦{Number(l.amountDeducted).toLocaleString('en-NG')}</td>
                    <td>₦{Number(l.balanceAfterDeduction).toLocaleString('en-NG')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Subscription Plan</h2>
        </div>
        {plans && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 16 }}>
            {Object.keys(plans).map(planKey => {
              const p = plans[planKey];
              const isCurrent = planKey === currentPlan;
              return (
                <div key={planKey} style={{ border: isCurrent ? '2px solid #3e8f93' : '1px solid #eceff1', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '1rem', marginBottom: 4 }}>{planKey}</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3e8f93' }}>₦{Number(p.perTermPrice).toLocaleString('en-NG')}<span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#90a4ae' }}> /term</span></div>
                  <div style={{ fontSize: '0.78rem', color: '#90a4ae', marginBottom: 12 }}>Up to {p.maxTeachers} teachers · Unlimited students</div>
                  {isCurrent ? (
                    <span className={css.chip} style={{ background: '#e8f5e9', color: '#2e7d32' }}>Current Plan</span>
                  ) : (
                    <button className={css.btnPrimary} onClick={() => setChangeTarget(planKey)}>Switch to {planKey}</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {changeTarget && (
        <DeleteConfirmModal
          message={`Switch your school to the ${changeTarget} plan? This takes effect immediately for feature access. Your next billing period will be charged at the new plan's rate.`}
          onCancel={() => setChangeTarget(null)}
          onConfirm={confirmChangePlan}
        />
      )}
    </div>
  );
}

// ─── Calendar Tab (Premium + schoolManagementEnabled gated) ───────────────────
const EVENT_TYPES = ['exam', 'holiday', 'meeting', 'deadline', 'activity', 'other'];
const EVENT_AUDIENCES = [
  { value: 'all',      label: 'Everyone' },
  { value: 'admins',   label: 'Admins only' },
  { value: 'teachers', label: 'Teachers' },
  { value: 'students', label: 'Students' },
  { value: 'parents',  label: 'Parents' },
  { value: 'class',    label: 'Specific class(es)' },
];

function eventTypeBadgeClass(type) {
  return {
    exam: css.calBadgeExam, holiday: css.calBadgeHoliday, meeting: css.calBadgeMeeting,
    deadline: css.calBadgeDeadline, activity: css.calBadgeActivity,
  }[type] || css.calBadgeOther;
}

function fmtEventDateHeading(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtEventTime(d) {
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function CalendarTab({ school, classes, isOwner, onNavigate }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modal, setModal]     = useState(null); // null | 'add' | { edit }

  const fetchEvents = useCallback(async () => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const from = monthDate.toISOString().slice(0, 10);
      const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().slice(0, 10);
      const { ok, data } = await schoolFetch(`/school/calendar?from=${from}&to=${to}`);
      if (ok) setEvents(data.events || []);
      else setError(data.error || 'Failed to load calendar events.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  }, [monthDate, managementEnabled]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.title}"? This cannot be undone.`)) return;
    const { ok, data } = await schoolFetch(`/school/calendar/${event._id}`, { method: 'DELETE' });
    if (ok) fetchEvents();
    else setError(data.error || 'Failed to delete event.');
  };

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Calendar &amp; Events is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  const grouped = {};
  events.forEach(ev => {
    const key = new Date(ev.startDate).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });
  const sortedKeys = Object.keys(grouped).sort();

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="calendar" title="Pending Calendar Requests" isOwner={isOwner} onNavigate={onNavigate} />
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className={css.btnSecondary} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>
              <ChevronLeftIcon style={{ fontSize: '1.1rem' }} />
            </button>
            <h2 className={css.cardTitle} style={{ minWidth: 170, textAlign: 'center' }}>
              {monthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </h2>
            <button className={css.btnSecondary} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>
              <ChevronRightIcon style={{ fontSize: '1.1rem' }} />
            </button>
          </div>
          <button className={css.btnPrimary} onClick={() => setModal('add')}>
            <AddIcon style={{ fontSize: '1rem' }} /> Add Event
          </button>
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : sortedKeys.length === 0 ? (
          <div className={css.emptyState}>
            <CalendarMonthIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No events this month</p>
            <p className={css.emptySub}>Create holidays, meetings, deadlines and more — teachers, students and parents will see relevant events automatically.</p>
            <button className={css.btnPrimary} onClick={() => setModal('add')}><AddIcon style={{ fontSize: '1rem' }} /> Add Event</button>
          </div>
        ) : (
          <div className={css.calAgenda}>
            {sortedKeys.map(dateKey => (
              <div key={dateKey} className={css.calDayGroup}>
                <div className={css.calDayHeading}>{fmtEventDateHeading(dateKey)}</div>
                {grouped[dateKey].map(ev => (
                  <div key={ev._id} className={css.calEventCard}>
                    <div className={css.calEventMain}>
                      <span className={`${css.calBadge} ${eventTypeBadgeClass(ev.type)}`}>{ev.type}</span>
                      <div>
                        <div className={css.calEventTitle}>{ev.title}</div>
                        <div className={css.calEventMeta}>
                          {!ev.allDay && `${fmtEventTime(ev.startDate)}${ev.endDate ? ` – ${fmtEventTime(ev.endDate)}` : ''} · `}
                          {ev.audience === 'class'
                            ? ((ev.classIds || []).map(c => c.name).join(', ') || 'Class')
                            : ev.audience.charAt(0).toUpperCase() + ev.audience.slice(1)}
                          {ev.location ? ` · ${ev.location}` : ''}
                        </div>
                        {ev.description && <div className={css.calEventDesc}>{ev.description}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ edit: ev })}>
                        <EditIcon style={{ fontSize: '0.9rem' }} />
                      </button>
                      <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem', color: '#ef5350' }} onClick={() => handleDelete(ev)}>
                        <DeleteIcon style={{ fontSize: '0.9rem' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <EventFormModal
          event={modal.edit || null}
          classes={classes}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}

function EventFormModal({ event, classes, onClose, onSuccess }) {
  const isEdit = !!event;
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    type: event?.type || 'other',
    audience: event?.audience || 'all',
    classIds: (event?.classIds || []).map(c => c._id || c),
    startDate: event?.startDate ? toDatetimeLocal(event.startDate) : '',
    endDate: event?.endDate ? toDatetimeLocal(event.endDate) : '',
    allDay: event?.allDay !== undefined ? event.allDay : true,
    location: event?.location || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const toggleClass = (classId) => {
    setForm(f => {
      const has = f.classIds.includes(classId);
      return { ...f, classIds: has ? f.classIds.filter(id => id !== classId) : [...f.classIds, classId] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.startDate) { setError('Title and start date are required.'); return; }
    if (form.audience === 'class' && form.classIds.length === 0) { setError('Select at least one class.'); return; }
    setError(''); setLoading(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        type: form.type,
        audience: form.audience,
        classIds: form.audience === 'class' ? form.classIds : [],
        startDate: localDatetimeToISO(form.startDate),
        endDate: form.endDate ? localDatetimeToISO(form.endDate) : null,
        allDay: form.allDay,
        location: form.location,
      };
      const { ok, data } = await schoolFetch(isEdit ? `/school/calendar/${event._id}` : '/school/calendar', {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
      if (ok) onSuccess();
      else setError(data.error || 'Failed to save event.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit Event' : 'Add Event'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Title *</label>
            <input className={css.formInput} value={form.title} onChange={set('title')} placeholder="e.g. Mid-Term Break" required />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Description</label>
            <textarea className={css.formTextarea} value={form.description} onChange={set('description')} rows={2} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Type</label>
              <select className={css.formSelect} value={form.type} onChange={set('type')}>
                {EVENT_TYPES.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Audience</label>
              <select className={css.formSelect} value={form.audience} onChange={set('audience')}>
                {EVENT_AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
          </div>
          {form.audience === 'class' && (
            <div className={css.formGroup}>
              <label className={css.formLabel}>Classes *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(classes || []).map(c => (
                  <label key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', border: '1px solid #eceff1', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                    <input className="schoolCheckbox" type="checkbox" checked={form.classIds.includes(c._id)} onChange={() => toggleClass(c._id)} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Start {!form.allDay ? 'Date & Time' : 'Date'} *</label>
              <DateTimePicker value={form.startDate} onChange={set('startDate')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>End {!form.allDay ? 'Date & Time' : 'Date'} (optional)</label>
              <DateTimePicker value={form.endDate} onChange={set('endDate')} />
            </div>
          </div>
          <div className={css.formGroup} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input className="schoolCheckbox" type="checkbox" checked={form.allDay} onChange={set('allDay')} id="event-allday" />
            <label htmlFor="event-allday" style={{ fontSize: '0.85rem', color: '#546e7a' }}>All-day event</label>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Location (optional)</label>
            <input className={css.formInput} value={form.location} onChange={set('location')} placeholder="e.g. School Hall" />
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : (isEdit ? 'Save Changes' : 'Add Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Attendance Tab (read-only — teachers mark attendance) ───────────────────
function AttendanceTab({ school, classes, subjects }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [mode, setMode]           = useState('records'); // 'records' | 'summary'
  const [classId, setClassId]     = useState('');
  const [classArm, setClassArm]   = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [records, setRecords]     = useState([]);
  const [summary, setSummary]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const selectedClass = classes.find(c => String(c._id) === classId);
  const arms = selectedClass?.arms || [];

  const loadRecords = useCallback(async () => {
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (classId) params.set('classId', classId);
    if (classArm) params.set('classArm', classArm);
    if (subjectId) params.set('subjectId', subjectId);
    const { ok, data } = await schoolFetch(`/school/attendance?${params}`);
    if (ok) setRecords(data.records || []);
    else setError(data.error || 'Failed to load attendance records.');
    setLoading(false);
  }, [classId, classArm, subjectId]);

  const loadSummary = useCallback(async () => {
    if (!classId) { setSummary([]); return; }
    setLoading(true); setError('');
    const params = new URLSearchParams({ classId });
    if (classArm) params.set('classArm', classArm);
    if (school?.currentTerm) params.set('term', school.currentTerm);
    if (school?.academicSession) params.set('session', school.academicSession);
    const { ok, data } = await schoolFetch(`/school/attendance/summary?${params}`);
    if (ok) setSummary(data.summary || []);
    else setError(data.error || 'Failed to load attendance summary.');
    setLoading(false);
  }, [classId, classArm, school]);

  useEffect(() => {
    if (!managementEnabled) return;
    if (mode === 'records') loadRecords();
    else loadSummary();
  }, [mode, loadRecords, loadSummary, managementEnabled]);

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Attendance is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Attendance</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={mode === 'records' ? css.btnPrimary : css.btnSecondary} onClick={() => setMode('records')}>Records</button>
            <button className={mode === 'summary' ? css.btnPrimary : css.btnSecondary} onClick={() => setMode('summary')}>Per-Student Summary</button>
          </div>
        </div>

        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={classId} onChange={e => { setClassId(e.target.value); setClassArm(''); }}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className={css.filterSelect} value={classArm} onChange={e => setClassArm(e.target.value)} disabled={!classId}>
            <option value="">All Arms</option>
            {arms.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {mode === 'records' && (
            <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
              <option value="">All Subjects</option>
              {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : mode === 'records' ? (
          records.length === 0 ? (
            <div className={css.emptyState}>
              <p className={css.emptyText}>No attendance records yet</p>
              <p className={css.emptySub}>Teachers mark attendance from their dashboard — records will appear here once submitted.</p>
            </div>
          ) : (
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr><th>Date</th><th>Class</th><th>Subject</th><th>Period</th><th>Marked By</th><th>Present</th><th>Absent</th><th>Late</th><th>Excused</th></tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r._id}>
                      <td>{r.date}</td>
                      <td>{r.className} {r.classArm ? `(${r.classArm})` : ''}</td>
                      <td>{r.subjectName}</td>
                      <td>{r.periodLabel || '—'}</td>
                      <td>{r.markedBy}</td>
                      <td>{r.counts.present}</td>
                      <td>{r.counts.absent}</td>
                      <td>{r.counts.late}</td>
                      <td>{r.counts.excused}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          !classId ? (
            <div className={css.emptyState}>
              <p className={css.emptyText}>Select a class to view its attendance summary</p>
            </div>
          ) : summary.length === 0 ? (
            <div className={css.emptyState}>
              <p className={css.emptyText}>No attendance data for this class yet</p>
            </div>
          ) : (
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr><th>Student</th><th>Arm</th><th>ID</th><th>Present</th><th>Total Periods</th><th>Rate</th></tr>
                </thead>
                <tbody>
                  {summary.map(s => (
                    <tr key={s.membershipId}>
                      <td>{s.firstName} {s.lastName}</td>
                      <td>{s.classArm || '—'}</td>
                      <td>{s.studentId || '—'}</td>
                      <td>{s.present}</td>
                      <td>{s.total}</td>
                      <td style={{ fontWeight: 700, color: s.rate == null ? '#78909c' : s.rate < 75 ? '#c62828' : '#2e7d32' }}>
                        {s.rate == null ? '—' : `${s.rate}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ─── Lesson Plans Tab (review queue) ──────────────────────────────────────────
const LESSON_PLAN_STATUSES = ['submitted', 'draft', 'approved', 'needs_revision'];
const lessonPlanStatusLabel = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', needs_revision: 'Needs Revision' };
const lessonPlanStatusChipClass = {
  draft: css.chipInactive,
  submitted: css.chipInactive,
  approved: css.chipActive,
  needs_revision: css.chipRemoved,
};

function exportAdminLessonPlan(plan) {
  const workbook = XLSX.utils.book_new();
  const displayTopics = (plan.topics || []).join(', ') || plan.topic || '';
  const summary = [['Topic(s)', displayTopics], ['Sub-topic', plan.subTopic], ['Class', plan.classId?.name], ['Class arm', plan.classArm], ['Subject', plan.subjectId?.name], ['Teacher', plan.teacherName || `${plan.teacherId?.firstName || ''} ${plan.teacherId?.lastName || ''}`], ['Number in class', plan.noInClass], ['Average age', plan.averageAge], ['Sex', plan.sex], ['Date', plan.date ? String(plan.date).slice(0, 10) : ''], ['Term', plan.term], ['Session', plan.session], ['Week', plan.week], ['Lesson number(s)', plan.lessonNumbers], ['Duration', plan.durationMinutes], ['Rationale', plan.rationale], ['Previous knowledge', plan.previousKnowledge], ['Objectives', (plan.objectives || []).join('\n')], ['Teaching methods', (plan.teachingMethods || []).join('\n')], ['Teaching materials', (plan.teachingMaterials || []).join('\n')], ['References', (plan.references || []).join('\n')], ['Resources', (plan.resources || []).join('\n')], ['Principal remark', plan.principalRemark || '']];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([['Stage', 'Minutes', 'Teacher activities', 'Student activities'], ...(plan.developmentStages || []).map(stage => [stage.stage, stage.durationMinutes, stage.teacherActivities, stage.studentActivities])]), 'Development');
  XLSX.writeFile(workbook, `${displayTopics.replace(/[^a-z0-9]+/gi, '_') || 'lesson-plan'}_review.xlsx`);
}

function exportAdminLessonPlanPdf(plan) {
  const displayTopics = (plan.topics || []).join(', ') || plan.topic || 'Lesson Plan';
  const stages = (plan.developmentStages || []).map(stage => `<tr><td>${stage.stage || ''}</td><td>${stage.durationMinutes || 0}</td><td>${stage.teacherActivities || ''}</td><td>${stage.studentActivities || ''}</td></tr>`).join('');
  const popup = window.open('', '_blank');
  if (!popup) return;
  popup.document.write(`<html><head><title>${displayTopics}</title><style>body{font:14px Arial;color:#263238;padding:28px}h1{font-size:22px}h2{font-size:16px;margin-top:22px}p{line-height:1.55;white-space:pre-wrap}table{border-collapse:collapse;width:100%}th,td{border:1px solid #b0bec5;padding:8px;text-align:left;vertical-align:top}th{background:#eceff1}@media print{button{display:none}}</style></head><body><h1>${displayTopics}</h1><p><strong>Subject:</strong> ${plan.subjectId?.name || ''} | <strong>Class:</strong> ${plan.classId?.name || ''} ${plan.classArm || ''}<br/><strong>Teacher:</strong> ${plan.teacherName || ''} | <strong>Term:</strong> ${plan.term || ''} | <strong>Session:</strong> ${plan.session || ''}<br/><strong>Week:</strong> ${plan.week || ''} | <strong>Lesson(s):</strong> ${plan.lessonNumbers || ''} | <strong>Duration:</strong> ${plan.durationMinutes || 0} minutes</p><h2>Rationale and Previous Knowledge</h2><p><strong>Rationale:</strong> ${plan.rationale || '—'}\n<strong>Previous knowledge:</strong> ${plan.previousKnowledge || '—'}</p><h2>Learning Objectives</h2><p>${(plan.objectives || []).join('\n')}</p><h2>Development of the Lesson</h2><table><tr><th>Stage</th><th>Minutes</th><th>Teacher activities</th><th>Student activities</th></tr>${stages}</table><button onclick="window.print()">Print / Save as PDF</button></body></html>`);
  popup.document.close();
}

function LessonPlanReviewModal({ plan, onClose, onReviewed }) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [principalRemark, setPrincipalRemark] = useState(plan.principalRemark || '');
  const [saving, setSaving] = useState('');
  const [generatingRemark, setGeneratingRemark] = useState(false);
  const [error, setError] = useState('');

  const handleGenerateRemark = async () => {
    setGeneratingRemark(true); setError('');
    const { ok, data } = await schoolFetch(`/school/lesson-plans/${plan._id}/ai-principal-remark`, { method: 'POST' });
    if (ok && data.remark) setPrincipalRemark(data.remark);
    else setError(data.error || 'Failed to generate principal remark.');
    setGeneratingRemark(false);
  };

  const handleReview = async (status) => {
    if (status === 'needs_revision' && !reviewNotes.trim()) {
      setError('Add review notes explaining what needs revision.');
      return;
    }
    setSaving(status); setError('');
    const { ok, data } = await schoolFetch(`/school/lesson-plans/${plan._id}/review`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reviewNotes: reviewNotes.trim(), principalRemark: principalRemark.trim() }),
    });
    if (ok) onReviewed(data.plan);
    else setError(data.error || 'Failed to submit review.');
    setSaving('');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 'min(1050px, calc(100vw - 32px))' }} onClick={e => e.stopPropagation()}>
        <div className={css.lessonPlanDetailHeader}>
          <div>
            <h2 className={css.lessonPlanDetailTitle}>{(plan.topics || []).join(', ') || plan.topic}</h2>
            <p className={css.lessonPlanDetailSubtitle}>{plan.subjectId?.name || '—'} · {plan.classId?.name || '—'}{plan.classArm ? ` · ${plan.classArm}` : ''} · {plan.term || '—'} term · Week {plan.week}{plan.lessonNumbers ? ` · Lesson(s) ${plan.lessonNumbers}` : ''} · {plan.session || '—'}</p>
          </div>
          <span className={`${css.chip} ${lessonPlanStatusChipClass[plan.status] || css.chipInactive}`}>{lessonPlanStatusLabel[plan.status] || plan.status}</span>
        </div>

        <div className={css.modalBody} style={{ maxHeight: '58vh', overflowY: 'auto' }}>
          <div className={css.lessonPlanDetailGrid}>
            {[['Subject', plan.subjectId?.name || '—'], ['Class', plan.classId?.name || '—'], ['Class arm', plan.classArm || 'All arms'], ['Term', plan.term || '—'], ['Session', plan.session || '—'], ['Week', plan.week ?? '—'], ['Lesson(s)', plan.lessonNumbers || '—'], ['Teacher', plan.teacherName || `${plan.teacherId?.firstName || ''} ${plan.teacherId?.lastName || ''}` || '—'], ['Class size', plan.noInClass ?? '—'], ['Average age', plan.averageAge ?? '—'], ['Sex', plan.sex || '—'], ['Date', plan.date ? String(plan.date).slice(0, 10) : '—'], ['Duration', `${plan.durationMinutes || 0} minutes`], ['Topic(s)', (plan.topics || []).join(', ') || plan.topic || '—'], ['Sub-topic', plan.subTopic || '—']].map(([label, value]) => <div className={css.lessonPlanDetailItem} key={label}><span className={css.lessonPlanDetailLabel}>{label}</span><span className={css.lessonPlanDetailValue}>{value}</span></div>)}
          </div>
          <div className={css.lessonPlanDetailSection}><h4>Rationale and Previous Knowledge</h4><div>{formatLargeText(`**Rationale:** ${plan.rationale || '—'}\n**Previous knowledge:** ${plan.previousKnowledge || '—'}`)}</div></div>
          <div className={css.lessonPlanDetailSection}><h4>Learning Objectives</h4>{plan.objectives?.length ? <ul className={css.lessonPlanDetailList}>{plan.objectives.map((item, i) => <li key={i}>{item}</li>)}</ul> : <p>—</p>}</div>
          <div className={css.lessonPlanDetailGrid}>
            {[['Teaching methods', plan.teachingMethods], ['Teaching materials', plan.teachingMaterials], ['References', plan.references], ['Resources', plan.resources]].map(([label, items]) => <div className={css.lessonPlanDetailSection} key={label}><h4>{label}</h4>{items?.length ? <ul className={css.lessonPlanDetailList}>{items.map((item, i) => <li key={i}>{item}</li>)}</ul> : <p>—</p>}</div>)}
          </div>
          <div className={css.lessonPlanDetailSection}><h4>Development of the Lesson</h4><div className={css.lessonPlanDetailTable}><table className={css.table}><thead><tr><th>Stage</th><th>Min</th><th>Teacher activities</th><th>Student activities</th></tr></thead><tbody>{(plan.developmentStages || []).map((stage, i) => <tr key={i}><td>{stage.stage}</td><td>{stage.durationMinutes}</td><td>{stage.teacherActivities}</td><td>{stage.studentActivities}</td></tr>)}</tbody></table></div></div>
          {plan.principalRemark && <div className={css.lessonPlanDetailSection}><h4>Principal Remark</h4><p>{plan.principalRemark}</p></div>}
          {plan.reviewNotes && <div className={`${css.lessonPlanDetailSection} ${css.lessonPlanReviewNote}`}><h4>Review Notes</h4><p>{plan.reviewNotes}</p></div>}
          <div className={teacherModalCss.drawerExportRow}>
            <button className={teacherModalCss.drawerExportBtn} onClick={() => exportAdminLessonPlanPdf(plan)}>🖨 Export PDF</button>
            <button className={teacherModalCss.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={() => exportAdminLessonPlan(plan)}>📊 Export Excel</button>
          </div>
        </div>

        {error && <div className={css.inlineError} style={{ marginBottom: 10 }}>{error}</div>}

        {plan.status === 'submitted' ? (
          <>
            <textarea
              className={css.formTextarea}
              placeholder="Review notes (required if requesting revision)"
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <textarea className={css.formTextarea} placeholder="Principal remark (visible to the teacher after approval)" value={principalRemark} onChange={e => setPrincipalRemark(e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
              <button type="button" className={css.principalRemarkAiBtn} onClick={handleGenerateRemark} disabled={generatingRemark} title={generatingRemark ? 'Generating principal remark…' : 'Generate principal remark with AI'} aria-label={generatingRemark ? 'Generating principal remark' : 'Generate principal remark with AI'}>
                {generatingRemark ? <span className={css.principalRemarkAiSpinner} aria-hidden="true" /> : <AutoFixHighIcon fontSize="small" />}
              </button>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={onClose} disabled={!!saving}>Cancel</button>
              <button className={css.btnDanger} onClick={() => handleReview('needs_revision')} disabled={!!saving}>
                {saving === 'needs_revision' ? 'Saving…' : 'Request Revision'}
              </button>
              <button className={css.btnPrimary} onClick={() => handleReview('approved')} disabled={!!saving}>
                {saving === 'approved' ? 'Saving…' : 'Approve'}
              </button>
            </div>
          </>
        ) : (
          <div className={css.modalFooter}>
            {plan.reviewNotes && (
              <p style={{ fontSize: '0.8rem', color: '#c62828', marginRight: 'auto' }}>Notes: {plan.reviewNotes}</p>
            )}
            <button className={css.btnSecondary} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function LessonPlansAdminTab({ school, classes, subjects }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [classId, setClassId] = useState('');
  const [classArm, setClassArm] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [status, setStatus] = useState('');
  const [term, setTerm] = useState('');
  const [session, setSession] = useState('');
  const [reviewPlan, setReviewPlan] = useState(null);

  const load = useCallback(async () => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (classId) params.set('classId', classId);
    if (classArm) params.set('classArm', classArm);
    if (subjectId) params.set('subjectId', subjectId);
    if (status) params.set('status', status);
    if (term) params.set('term', term);
    if (session) params.set('session', session);
    const { ok, data } = await schoolFetch(`/school/lesson-plans?${params}`);
    if (ok) setPlans(data.plans || []);
    else setError(data.error || 'Failed to load lesson plans.');
    setLoading(false);
  }, [managementEnabled, classId, classArm, subjectId, status, term, session]);

  useEffect(() => { load(); }, [load]);

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Lesson Plans is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Lesson Plans</h2>
        </div>

        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={classId} onChange={e => { setClassId(e.target.value); setClassArm(''); }}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className={css.filterSelect} value={classArm} onChange={e => setClassArm(e.target.value)} disabled={!classId}><option value="">All Arms</option>{(classes.find(c => String(c._id) === String(classId))?.arms || []).map(arm => <option key={arm} value={arm}>{arm}</option>)}</select>
          <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
            <option value="">All Subjects</option>
            {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <select className={css.filterSelect} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {LESSON_PLAN_STATUSES.map(s => <option key={s} value={s}>{lessonPlanStatusLabel[s]}</option>)}
          </select>
          <select className={css.filterSelect} value={term} onChange={e => setTerm(e.target.value)}><option value="">All Terms</option><option value="first">First Term</option><option value="second">Second Term</option><option value="third">Third Term</option></select>
          <input className={css.filterSelect} style={{ width: 130 }} placeholder="Session" value={session} onChange={e => setSession(e.target.value)} />
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : plans.length === 0 ? (
          <div className={css.emptyState}>
            <p className={css.emptyText}>No lesson plans found</p>
            <p className={css.emptySub}>Lesson plans submitted by teachers will appear here for review.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Topic</th><th>Teacher</th><th>Class</th><th>Subject</th><th>Term / Wk</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p._id}>
                    <td>{(p.topics || []).join(', ') || p.topic}</td>
                    <td>{p.teacherId?.firstName} {p.teacherId?.lastName}</td>
                    <td>{p.classId?.name || '—'} {p.classArm ? `(${p.classArm})` : ''}</td>
                    <td>{p.subjectId?.name || '—'}</td>
                    <td>{p.term ? `${p.term.charAt(0).toUpperCase()}${p.term.slice(1)}` : '—'} · Wk {p.week}{p.lessonNumbers ? ` · Lesson(s) ${p.lessonNumbers}` : ''}</td>
                    <td><span className={`${css.chip} ${lessonPlanStatusChipClass[p.status] || css.chipInactive}`}>{lessonPlanStatusLabel[p.status] || p.status}</span></td>
                    <td>
                      <button className={css.btnSecondary} onClick={() => setReviewPlan(p)}>
                        {p.status === 'submitted' ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reviewPlan && (
        <LessonPlanReviewModal
          plan={reviewPlan}
          onClose={() => setReviewPlan(null)}
          onReviewed={() => { setReviewPlan(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Expenses Tab ──────────────────────────────────────────────────────────────
const EXPENSE_CATEGORIES = ['utilities', 'maintenance', 'salaries', 'supplies', 'transport', 'other'];
const EXPENSE_STATUSES = ['pending', 'approved', 'rejected', 'paid'];
const PAYMENT_METHODS = ['cash', 'bank_transfer', 'card', 'cheque', 'other'];
const expenseStatusLabel = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', paid: 'Paid' };
const expenseStatusChipClass = {
  pending: css.chipInactive,
  approved: css.chipActive,
  paid: css.chipActive,
  rejected: css.chipRemoved,
};
const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString('en-NG')}`;

function ExpenseRejectModal({ expense, onClose, onDone }) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reviewNotes.trim()) { setError('Explain why this expense is being rejected.'); return; }
    setError(''); setLoading(true);
    const { ok, data } = await schoolFetch(`/school/expenses/${expense._id}/review`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'rejected', reviewNotes: reviewNotes.trim() }),
    });
    setLoading(false);
    if (ok) onDone();
    else setError(data.error || 'Failed to reject expense.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle} style={{ color: '#ef5350' }}>Reject Expense</h2>
        <p className={css.modalSub}>{expense.description} — {fmtNaira(expense.amount)}</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Reason *</label>
            <textarea className={css.formTextarea} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Why is this expense being rejected?" />
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnDanger} disabled={loading}>{loading ? 'Saving…' : 'Reject'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExpenseFormModal({ expense, onClose, onSuccess }) {
  const isEdit = !!expense;
  const [form, setForm] = useState({
    category: expense?.category || 'other',
    description: expense?.description || '',
    amount: expense?.amount ?? '',
    vendor: expense?.vendor || '',
    date: expense?.date ? expense.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    paymentMethod: expense?.paymentMethod || '',
    receiptUrl: expense?.receiptUrl || '',
  });
  const [aiSuggested, setAiSuggested] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSuggestCategory = async () => {
    if (!form.description.trim()) { setError('Enter a description first.'); return; }
    setError(''); setAiLoading(true);
    const { ok, data } = await schoolFetch('/school/expenses/ai-category-suggest', {
      method: 'POST',
      body: JSON.stringify({ description: form.description, vendor: form.vendor }),
    });
    setAiLoading(false);
    if (ok) { setForm(f => ({ ...f, category: data.category })); setAiSuggested(true); }
    else setError(data.error || 'AI suggestion failed.');
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const token = localStorage.getItem('school-token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/school/expenses/upload-receipt', {
        method: 'POST',
        headers: { 'x-access-token': token || '' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.url) { setError(data.error || 'Receipt upload failed.'); return; }
      setForm(f => ({ ...f, receiptUrl: data.url }));
    } catch { setError('Connection error uploading receipt.'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || form.amount === '' || !form.date) {
      setError('Description, amount and date are required.'); return;
    }
    setError(''); setLoading(true);
    const body = {
      category: form.category,
      description: form.description.trim(),
      amount: Number(form.amount),
      vendor: form.vendor.trim(),
      date: form.date,
      paymentMethod: form.paymentMethod,
      receiptUrl: form.receiptUrl,
      ...(isEdit ? {} : { aiCategorySuggested: aiSuggested }),
    };
    const { ok, data } = await schoolFetch(isEdit ? `/school/expenses/${expense._id}` : '/school/expenses', {
      method: isEdit ? 'PATCH' : 'POST',
      body: JSON.stringify(body),
    });
    setLoading(false);
    if (ok) onSuccess();
    else setError(data.error || 'Failed to save expense.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit Expense' : 'Record Expense'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Description *</label>
            <input className={css.formInput} value={form.description} onChange={set('description')} placeholder="e.g. Generator diesel refill" required />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Category</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className={css.formSelect} value={form.category} onChange={e => { set('category')(e); setAiSuggested(false); }}>
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
                </select>
                <button type="button" className={css.btnSecondary} onClick={handleSuggestCategory} disabled={aiLoading} title="Suggest category with AI">
                  {aiLoading ? '…' : '✨ AI'}
                </button>
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Amount (₦) *</label>
              <input className={css.formInput} type="number" min="0" value={form.amount} onChange={set('amount')} required />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Vendor</label>
              <input className={css.formInput} value={form.vendor} onChange={set('vendor')} placeholder="e.g. ABC Suppliers Ltd" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Date *</label>
              <input className={css.formInput} type="date" value={form.date} onChange={set('date')} required />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Payment Method</label>
              <select className={css.formSelect} value={form.paymentMethod} onChange={set('paymentMethod')}>
                <option value="">Unspecified</option>
                {PAYMENT_METHODS.map(m => <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Receipt</label>
              <input type="file" accept="image/*,application/pdf" onChange={handleReceiptUpload} disabled={uploading} />
              {uploading && <span style={{ fontSize: '0.75rem', color: '#90a4ae' }}>Uploading…</span>}
              {form.receiptUrl && !uploading && <span style={{ fontSize: '0.75rem', color: '#2e7d32' }}>Receipt attached ✓</span>}
            </div>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading || uploading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : (isEdit ? 'Save Changes' : 'Record Expense')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExpensesTab({ school, isOwner, onNavigate }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [modal, setModal] = useState(null); // null | 'record' | { editExpense }
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    const { ok, data } = await schoolFetch(`/school/expenses?${params}`);
    if (ok) { setExpenses(data.expenses || []); setSummary(data.summary || null); }
    else setError(data.error || 'Failed to load expenses.');
    setLoading(false);
  }, [managementEnabled, category, status]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleApprove = async (exp) => {
    const { ok, data } = await schoolFetch(`/school/expenses/${exp._id}/review`, {
      method: 'PATCH', body: JSON.stringify({ status: 'approved' }),
    });
    if (ok) { flash('Expense approved.'); load(); } else setError(data.error || 'Failed to approve expense.');
  };

  const handleMarkPaid = async (exp) => {
    const { ok, data } = await schoolFetch(`/school/expenses/${exp._id}/mark-paid`, { method: 'PATCH', body: JSON.stringify({}) });
    if (ok) { flash('Expense marked as paid.'); load(); } else setError(data.error || 'Failed to mark expense as paid.');
  };

  const confirmDelete = async (password) => {
    const result = await schoolFetch(`/school/expenses/${deleteTarget._id}`, {
      method: 'DELETE', headers: { 'x-admin-password': password },
    });
    if (result.ok) { setDeleteTarget(null); flash('Expense deleted.'); load(); }
    return result;
  };

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Expenses is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="expenses" title="Pending Expense Requests" isOwner={isOwner} onNavigate={onNavigate} />
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      {loading ? <SkeletonStatCards count={4} /> : summary ? (
        <div className={css.statsRow}>
          <div className={css.statCard}>
            <span className={css.statLabel}>Total Recorded</span>
            <span className={css.statValue}>{fmtNaira(summary.totalAmount)}</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statLabel}>Pending ({summary.pendingCount})</span>
            <span className={css.statValue}>{fmtNaira(summary.pendingAmount)}</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statLabel}>Approved</span>
            <span className={css.statValue}>{fmtNaira(summary.approvedAmount)}</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statLabel}>Paid</span>
            <span className={css.statValue}>{fmtNaira(summary.paidAmount)}</span>
          </div>
        </div>
      ) : null}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Expenses</h2>
          <button className={css.btnPrimary} onClick={() => setModal('record')}>
            <AddIcon style={{ fontSize: '1rem' }} /> Record Expense
          </button>
        </div>

        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
          </select>
          <select className={css.filterSelect} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {EXPENSE_STATUSES.map(s => <option key={s} value={s}>{expenseStatusLabel[s]}</option>)}
          </select>
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : expenses.length === 0 ? (
          <div className={css.emptyState}>
            <ReceiptLongIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No expenses recorded</p>
            <p className={css.emptySub}>Record your school's operating expenses to track spending and approvals.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Date</th><th>Description</th><th>Category</th><th>Vendor</th><th>Amount</th><th>Status</th><th>Recorded By</th><th></th></tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp._id}>
                    <td>{new Date(exp.date).toLocaleDateString('en-NG')}</td>
                    <td>{exp.description}</td>
                    <td style={{ textTransform: 'capitalize' }}>{exp.category}{exp.aiCategorySuggested ? ' ✨' : ''}</td>
                    <td>{exp.vendor || '—'}</td>
                    <td>{fmtNaira(exp.amount)}</td>
                    <td><span className={`${css.chip} ${expenseStatusChipClass[exp.status] || css.chipInactive}`}>{expenseStatusLabel[exp.status]}</span></td>
                    <td>{exp.recordedBy ? `${exp.recordedBy.firstName} ${exp.recordedBy.lastName}` : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {exp.status === 'pending' && (
                          <>
                            <button className={css.btnPrimary} onClick={() => handleApprove(exp)}>Approve</button>
                            <button className={css.btnDanger} onClick={() => setRejectTarget(exp)}>Reject</button>
                            <button className={css.btnSecondary} onClick={() => setModal({ editExpense: exp })}>Edit</button>
                            <button className={css.btnDanger} onClick={() => setDeleteTarget(exp)}><DeleteIcon style={{ fontSize: '0.9rem' }} /></button>
                          </>
                        )}
                        {exp.status === 'approved' && (
                          <button className={css.btnPrimary} onClick={() => handleMarkPaid(exp)}>Mark Paid</button>
                        )}
                        {exp.receiptUrl && (
                          <a href={exp.receiptUrl} target="_blank" rel="noreferrer" className={css.btnSecondary} style={{ textDecoration: 'none' }}>Receipt</a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'record' || modal?.editExpense) && (
        <ExpenseFormModal
          expense={modal?.editExpense || null}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); flash(modal?.editExpense ? 'Expense updated.' : 'Expense recorded.'); load(); }}
        />
      )}
      {rejectTarget && (
        <ExpenseRejectModal
          expense={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={() => { setRejectTarget(null); flash('Expense rejected.'); load(); }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          message={`Delete this expense ("${deleteTarget.description}")? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

// ─── Payroll Tab ───────────────────────────────────────────────────────────────
const PAYROLL_SUBS = [
  { key: 'employees', label: 'Employees',   Icon: BadgeIcon },
  { key: 'runs',      label: 'Payroll Runs', Icon: PaymentsIcon },
];

const empTypeLabel = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract' };
const payoutStatusChipClass = {
  pending: css.chipInactive, processing: css.chipInactive, paid: css.chipActive,
  failed: css.chipRemoved, no_bank_details: css.chipInactive,
};
const payoutStatusLabel = {
  pending: 'Pending', processing: 'Processing', paid: 'Paid',
  failed: 'Failed', no_bank_details: 'No Bank Details',
};

function PayrollTab({ school, isOwner, onNavigate }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [sub, setSub] = useState('employees');

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Payroll is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="payroll" title="Pending Payroll Requests" isOwner={isOwner} onNavigate={onNavigate} />
      <div className={css.subTabBar}>
        {PAYROLL_SUBS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`${css.subTabBtn} ${sub === key ? css.subTabBtnActive : ''}`}
            onClick={() => setSub(key)}
          >
            <Icon style={{ fontSize: '1rem' }} /> {label}
          </button>
        ))}
      </div>
      {sub === 'employees' && <PayrollEmployeesPanel />}
      {sub === 'runs'      && <PayrollRunsPanel />}
    </div>
  );
}

function PayrollEmployeesPanel() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(null); // null | 'addStaff' | { editEmployee }
  const [bankModalTarget, setBankModalTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const { ok, data } = await schoolFetch('/school/payroll/employees');
    if (ok) setEmployees(data.employees || []);
    else setError(data.error || 'Failed to load employees.');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const confirmDelete = async (password) => {
    const result = await schoolFetch(`/school/staff/${deleteTarget._id}`, {
      method: 'DELETE', headers: { 'x-admin-password': password },
    });
    if (result.ok) { setDeleteTarget(null); flash('Staff member removed.'); load(); }
    return result;
  };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Employees <span style={{ color: '#90a4ae', fontWeight: 400, fontSize: '0.875rem' }}>({employees.length})</span></h2>
          <button className={css.btnPrimary} onClick={() => setModal('addStaff')}>
            <AddIcon style={{ fontSize: '1rem' }} /> Add Staff
          </button>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#90a4ae', marginTop: -8, marginBottom: 16 }}>
          Teachers already on your Teachers tab appear here automatically — set their salary and bank
          details below. Non-teaching staff (accountants, security, cleaners, etc.) are added directly here.
        </p>

        {loading ? (
          <SkeletonCards count={3} />
        ) : employees.length === 0 ? (
          <div className={css.emptyState}>
            <BadgeIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No employees yet</p>
            <p className={css.emptySub}>Add non-teaching staff, or set a salary for an existing teacher, to get started.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Name</th><th>Type</th><th>Role/Dept</th><th>Employment</th><th>Basic Salary</th><th>Net Pay</th><th>Bank Details</th><th></th></tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={`${emp.employeeType}-${emp._id}`}>
                    <td>{emp.firstName} {emp.lastName}</td>
                    <td style={{ textTransform: 'capitalize' }}>{emp.employeeType}</td>
                    <td>{emp.employeeType === 'staff' ? [emp.role, emp.department].filter(Boolean).join(' · ') || '—' : '—'}</td>
                    <td>{empTypeLabel[emp.employmentType] || emp.employmentType}</td>
                    <td>{fmtNaira(emp.salary?.basic)}</td>
                    <td>{fmtNaira(computeNetPayLocal(emp.salary))}</td>
                    <td>
                      {emp.hasBankDetails
                        ? <span className={`${css.chip} ${css.chipActive}`}>{emp.accountName || 'Verified'}</span>
                        : <span className={`${css.chip} ${css.chipInactive}`}>Not set</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className={css.btnSecondary} onClick={() => setModal({ editEmployee: emp })}>Edit</button>
                        <button className={css.btnSecondary} onClick={() => setBankModalTarget(emp)}>Bank</button>
                        {emp.employeeType === 'staff' && (
                          <button className={css.btnDanger} onClick={() => setDeleteTarget(emp)}><DeleteIcon style={{ fontSize: '0.9rem' }} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'addStaff' || modal?.editEmployee) && (
        <EmployeeFormModal
          employee={modal?.editEmployee || null}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); flash(modal?.editEmployee ? 'Employee updated.' : 'Staff member added.'); load(); }}
        />
      )}
      {bankModalTarget && (
        <EmployeeBankModal
          employee={bankModalTarget}
          onClose={() => setBankModalTarget(null)}
          onSuccess={() => { setBankModalTarget(null); flash('Bank details verified and saved.'); load(); }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          message={`Remove ${deleteTarget.firstName} ${deleteTarget.lastName} from staff? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function computeNetPayLocal(salary) {
  const basic = salary?.basic || 0;
  const allowances = (salary?.allowances || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const deductions = (salary?.deductions || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  return basic + allowances - deductions;
}

function CompItemsEditor({ label, items, setItems }) {
  const update = (i, field, value) => setItems(items.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div style={{ marginBottom: 12 }}>
      <label className={css.formLabel}>{label}</label>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input className={css.formInput} placeholder="Label" value={item.label || ''} onChange={e => update(i, 'label', e.target.value)} />
          <input className={css.formInput} type="number" placeholder="Amount" value={item.amount || ''} onChange={e => update(i, 'amount', Number(e.target.value))} style={{ maxWidth: 140 }} />
          <button type="button" className={css.btnDanger} onClick={() => remove(i)}><DeleteIcon style={{ fontSize: '0.85rem' }} /></button>
        </div>
      ))}
      <button type="button" className={css.btnSecondary} onClick={() => setItems([...items, { label: '', amount: 0 }])}>
        <AddIcon style={{ fontSize: '0.9rem' }} /> Add {label.slice(0, -1)}
      </button>
    </div>
  );
}

function EmployeeFormModal({ employee, onClose, onSuccess }) {
  const isEdit = !!employee;
  const isStaff = employee ? employee.employeeType === 'staff' : true; // new employees are always staff
  const [form, setForm] = useState({
    firstName: employee?.firstName || '', lastName: employee?.lastName || '',
    email: employee?.email || '', phone: employee?.phone || '',
    role: employee?.role || '', department: employee?.department || '',
    employmentType: employee?.employmentType || 'full_time',
    basicSalary: employee?.salary?.basic || 0,
    hireDate: employee?.hireDate ? employee.hireDate.slice(0, 10) : '',
  });
  const [allowances, setAllowances] = useState(employee?.salary?.allowances || []);
  const [deductions, setDeductions] = useState(employee?.salary?.deductions || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    let result;
    if (!isStaff) {
      // Editing a teacher's payroll fields
      result = await schoolFetch(`/school/teachers/${employee._id}/payroll`, {
        method: 'PATCH',
        body: JSON.stringify({
          basicSalary: Number(form.basicSalary) || 0, allowances, deductions,
          employmentType: form.employmentType, hireDate: form.hireDate || null,
        }),
      });
    } else if (isEdit) {
      result = await schoolFetch(`/school/staff/${employee._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...form, basicSalary: Number(form.basicSalary) || 0, allowances, deductions }),
      });
    } else {
      result = await schoolFetch('/school/staff', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (result.ok && (allowances.length || deductions.length)) {
        await schoolFetch(`/school/staff/${result.data.staff._id}`, {
          method: 'PATCH', body: JSON.stringify({ allowances, deductions }),
        });
      }
    }
    setLoading(false);
    if (result.ok) onSuccess();
    else setError(result.data.error || 'Failed to save employee.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit' : 'Add'} {isStaff ? 'Staff Member' : 'Teacher Payroll'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {isStaff && (
            <>
              <label className={css.formLabel}>First Name</label>
              <input className={css.formInput} value={form.firstName} onChange={set('firstName')} required disabled={isEdit} />
              <label className={css.formLabel}>Last Name</label>
              <input className={css.formInput} value={form.lastName} onChange={set('lastName')} required disabled={isEdit} />
              <label className={css.formLabel}>Email</label>
              <input className={css.formInput} type="email" value={form.email} onChange={set('email')} />
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} />
              <label className={css.formLabel}>Role</label>
              <input className={css.formInput} placeholder="e.g. Accountant" value={form.role} onChange={set('role')} />
              <label className={css.formLabel}>Department</label>
              <input className={css.formInput} value={form.department} onChange={set('department')} />
            </>
          )}
          <label className={css.formLabel}>Employment Type</label>
          <select className={css.formInput} value={form.employmentType} onChange={set('employmentType')}>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="contract">Contract</option>
          </select>
          <label className={css.formLabel}>Hire Date</label>
          <input className={css.formInput} type="date" value={form.hireDate} onChange={set('hireDate')} />
          <label className={css.formLabel}>Basic Salary (₦)</label>
          <input className={css.formInput} type="number" min="0" value={form.basicSalary} onChange={set('basicSalary')} />

          <CompItemsEditor label="Allowances" items={allowances} setItems={setAllowances} />
          <CompItemsEditor label="Deductions" items={deductions} setItems={setDeductions} />

          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmployeeBankModal({ employee, onClose, onSuccess }) {
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState(employee.bankCode || '');
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    schoolFetch('/school/payout-account/banks?country=NG').then(({ ok, data }) => {
      if (ok) setBanks(data.banks || []);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bankCode || !accountNumber) { setError('Select a bank and enter an account number.'); return; }
    setLoading(true); setError('');
    const bankName = banks.find(b => b.code === bankCode)?.name || '';
    const path = employee.employeeType === 'teacher'
      ? `/school/teachers/${employee._id}/payroll/bank`
      : `/school/staff/${employee._id}/bank`;
    const result = await schoolFetch(path, {
      method: 'POST', body: JSON.stringify({ bankCode, bankName, accountNumber }),
    });
    setLoading(false);
    if (result.ok) onSuccess();
    else setError(result.data.error || 'Failed to verify account.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Bank Details — {employee.firstName} {employee.lastName}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className={css.formLabel}>Bank</label>
          <select className={css.formInput} value={bankCode} onChange={e => setBankCode(e.target.value)} required>
            <option value="">Select bank…</option>
            {banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
          <label className={css.formLabel}>Account Number</label>
          <input className={css.formInput} value={accountNumber} onChange={e => setAccountNumber(e.target.value)} required />
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>{loading ? 'Verifying…' : 'Verify & Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PayrollRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(null); // null | 'generate'
  const [viewRun, setViewRun] = useState(null);
  const [processTarget, setProcessTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const { ok, data } = await schoolFetch('/school/payroll/runs');
    if (ok) setRuns(data.runs || []);
    else setError(data.error || 'Failed to load payroll runs.');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const openRun = async (run) => {
    const { ok, data } = await schoolFetch(`/school/payroll/runs/${run._id}`);
    if (ok) setViewRun(data.run);
  };

  const confirmProcess = async (password) => {
    const result = await schoolFetch(`/school/payroll/runs/${processTarget._id}/process`, {
      method: 'POST', headers: { 'x-admin-password': password },
    });
    if (result.ok) {
      setProcessTarget(null); setViewRun(null);
      flash('Payroll run processed — transfers initiated and payslips emailed.');
      load();
    }
    return result;
  };

  const runStatusChipClass = { draft: css.chipInactive, processed: css.chipActive, paid: css.chipActive };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Payroll Runs</h2>
          <button className={css.btnPrimary} onClick={() => setModal('generate')}>
            <AddIcon style={{ fontSize: '1rem' }} /> Generate Run
          </button>
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : runs.length === 0 ? (
          <div className={css.emptyState}>
            <PaymentsIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No payroll runs yet</p>
            <p className={css.emptySub}>Generate a payroll run to pay teachers and staff for a period.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Period</th><th>Status</th><th>Employees</th><th>Total Amount</th><th></th></tr></thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run._id}>
                    <td>{run.period}</td>
                    <td><span className={`${css.chip} ${runStatusChipClass[run.status] || css.chipInactive}`}>{run.status}</span></td>
                    <td>{run.lineItems?.length || 0}</td>
                    <td>{fmtNaira(run.totalAmount)}</td>
                    <td>
                      <button className={css.btnSecondary} onClick={() => openRun(run)}>
                        <VisibilityIcon style={{ fontSize: '0.9rem' }} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'generate' && (
        <GenerateRunModal onClose={() => setModal(null)} onSuccess={(run) => { setModal(null); flash('Payroll run generated.'); load(); setViewRun(run); }} />
      )}
      {viewRun && (
        <PayrollRunModal
          run={viewRun}
          onClose={() => setViewRun(null)}
          onRefresh={openRun}
          onProcess={() => setProcessTarget(viewRun)}
        />
      )}
      {processTarget && (
        <DeleteConfirmModal
          message={`Process the "${processTarget.period}" payroll run for ${fmtNaira(processTarget.totalAmount)}? This will initiate bank transfers to every employee with verified bank details and email payslips. This cannot be undone.`}
          onCancel={() => setProcessTarget(null)}
          onConfirm={confirmProcess}
        />
      )}
    </div>
  );
}

function GenerateRunModal({ onClose, onSuccess }) {
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!period.trim()) { setError('Period is required, e.g. "July 2026".'); return; }
    setLoading(true); setError('');
    const result = await schoolFetch('/school/payroll/runs', {
      method: 'POST', body: JSON.stringify({ period: period.trim() }),
    });
    setLoading(false);
    if (result.ok) onSuccess(result.data.run);
    else setError(result.data.error || 'Failed to generate payroll run.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Generate Payroll Run</h2>
        <p className={css.modalSub}>
          Includes every active teacher with a salary set, plus every active staff member.
        </p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className={css.formLabel}>Period</label>
          <input className={css.formInput} placeholder='e.g. "July 2026"' value={period} onChange={e => setPeriod(e.target.value)} autoFocus />
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>{loading ? 'Generating…' : 'Generate'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PayrollLineItemRow({ run, li, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [basic, setBasic] = useState(li.basic);
  const [allowances, setAllowances] = useState(li.allowances || []);
  const [deductions, setDeductions] = useState(li.deductions || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { ok } = await schoolFetch(`/school/payroll/runs/${run._id}/line-items/${li._id}`, {
      method: 'PATCH', body: JSON.stringify({ basic: Number(basic) || 0, allowances, deductions }),
    });
    setSaving(false);
    if (ok) { setEditing(false); onSaved(); }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <div style={{ padding: 10, background: '#f7fafb', borderRadius: 8 }}>
            <label className={css.formLabel}>{li.name} — Basic Salary</label>
            <input className={css.formInput} type="number" value={basic} onChange={e => setBasic(Number(e.target.value))} style={{ maxWidth: 200 }} />
            <CompItemsEditor label="Allowances" items={allowances} setItems={setAllowances} />
            <CompItemsEditor label="Deductions" items={deductions} setItems={setDeductions} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className={css.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className={css.btnSecondary} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        {li.name}
        {li.anomaly?.flagged && (
          <span title={li.anomaly.note} style={{ marginLeft: 6, fontSize: '0.7rem', color: '#c62828', cursor: 'help' }}>⚠️</span>
        )}
      </td>
      <td style={{ textTransform: 'capitalize' }}>{li.employeeType}</td>
      <td>{fmtNaira(li.netPay)}</td>
      <td><span className={`${css.chip} ${payoutStatusChipClass[li.payoutStatus] || css.chipInactive}`}>{payoutStatusLabel[li.payoutStatus] || li.payoutStatus}</span></td>
      <td>
        {run.status === 'draft' && <button className={css.btnSecondary} onClick={() => setEditing(true)}>Edit</button>}
      </td>
    </tr>
  );
}

function PayrollRunModal({ run, onClose, onRefresh, onProcess }) {
  const anomalyCount = (run.lineItems || []).filter(li => li.anomaly?.flagged).length;
  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{run.period}</h2>
        <p className={css.modalSub}>
          Status: <strong style={{ textTransform: 'capitalize' }}>{run.status}</strong> · Total: <strong>{fmtNaira(run.totalAmount)}</strong>
        </p>
        {anomalyCount > 0 && (
          <div style={{ marginBottom: 12, padding: 10, background: '#fce4ec', borderRadius: 8, fontSize: '0.82rem', color: '#c62828' }}>
            ⚠️ {anomalyCount} employee{anomalyCount > 1 ? 's have' : ' has'} a net pay change of 20%+ vs the previous run — hover the ⚠️ next to their name for details.
          </div>
        )}
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead><tr><th>Employee</th><th>Type</th><th>Net Pay</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {(run.lineItems || []).map(li => (
                <PayrollLineItemRow key={li._id} run={run} li={li} onSaved={() => onRefresh(run)} />
              ))}
            </tbody>
          </table>
        </div>
        <div className={css.modalFooter}>
          <button className={css.btnSecondary} onClick={onClose}>Close</button>
          {run.status === 'draft' && (
            <button className={css.btnPrimary} onClick={onProcess}>Process Run</button>
          )}
          {run.status !== 'draft' && (
            <button className={css.btnSecondary} onClick={() => window.print()}>Print / Save as PDF</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Store & Inventory Tab ─────────────────────────────────────────────────────
const INVENTORY_SUBS = [
  { key: 'items',    label: 'Items',    Icon: Inventory2Icon },
  { key: 'requests', label: 'Requests', Icon: AssignmentIcon },
];
const TRANSACTION_TYPES = ['stock_in', 'stock_out', 'adjustment', 'damaged', 'transfer'];
const transactionTypeLabel = {
  stock_in: 'Stock In', stock_out: 'Stock Out', adjustment: 'Adjustment', damaged: 'Damaged', transfer: 'Transfer',
};
const requisitionStatusChipClass = {
  pending: css.chipInactive, approved: css.chipActive, rejected: css.chipRemoved, completed: css.chipActive,
};

function InventoryTab({ school }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [sub, setSub] = useState('items');

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Store &amp; Inventory is a <strong>Premium</strong> feature. Upgrade your school&apos;s plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.subTabBar}>
        {INVENTORY_SUBS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`${css.subTabBtn} ${sub === key ? css.subTabBtnActive : ''}`}
            onClick={() => setSub(key)}
          >
            <Icon style={{ fontSize: '1rem' }} /> {label}
          </button>
        ))}
      </div>
      {sub === 'items'    && <InventoryItemsPanel />}
      {sub === 'requests' && <InventoryRequestsPanel />}
    </div>
  );
}

function InventoryItemsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [category, setCategory] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [modal, setModal] = useState(null); // null | 'add' | { editItem } | { transactItem } | { reorderItem }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (lowStockOnly) params.set('lowStockOnly', 'true');
    const { ok, data } = await schoolFetch(`/school/inventory?${params}`);
    if (ok) setItems(data.items || []);
    else setError(data.error || 'Failed to load inventory items.');
    setLoading(false);
  }, [category, lowStockOnly]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const confirmDelete = async (password) => {
    const result = await schoolFetch(`/school/inventory/${deleteTarget._id}`, {
      method: 'DELETE', headers: { 'x-admin-password': password },
    });
    if (result.ok) { setDeleteTarget(null); flash('Item discontinued.'); load(); }
    return result;
  };

  const categories = Array.from(new Set(items.map(i => i.category).filter(Boolean)));
  const lowStockCount = items.filter(i => i.isLowStock).length;

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      {!loading && (
        <div className={css.statsRow}>
          <div className={css.statCard}>
            <span className={css.statLabel}>Total Items</span>
            <span className={css.statValue}>{items.length}</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statLabel}>Low Stock</span>
            <span className={css.statValue} style={{ color: lowStockCount > 0 ? '#c0392b' : undefined }}>{lowStockCount}</span>
          </div>
        </div>
      )}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Inventory Items</h2>
          <button className={css.btnPrimary} onClick={() => setModal('add')}>
            <AddIcon style={{ fontSize: '1rem' }} /> Add Item
          </button>
        </div>

        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#546e7a' }}>
            <input className="schoolCheckbox" type="checkbox" checked={lowStockOnly} onChange={e => setLowStockOnly(e.target.checked)} /> Low stock only
          </label>
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : items.length === 0 ? (
          <div className={css.emptyState}>
            <Inventory2Icon className={css.emptyIcon} />
            <p className={css.emptyText}>No inventory items</p>
            <p className={css.emptySub}>Add your school's store items to track stock levels and reorder points.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Name</th><th>Category</th><th>In Stock</th><th>Reorder Level</th><th>Unit Cost</th><th>Location</th><th></th></tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._id}>
                    <td>{item.name}{item.isLowStock ? ' ⚠️' : ''}</td>
                    <td style={{ textTransform: 'capitalize' }}>{item.category}</td>
                    <td style={item.isLowStock ? { color: '#c0392b', fontWeight: 600 } : undefined}>{item.quantityInStock} {item.unit}</td>
                    <td>{item.reorderLevel} {item.unit}</td>
                    <td>{fmtNaira(item.unitCost)}</td>
                    <td>{item.location || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className={css.btnPrimary} onClick={() => setModal({ transactItem: item })}>Record Stock</button>
                        <button className={css.btnSecondary} onClick={() => setModal({ editItem: item })}>Edit</button>
                        <button className={css.btnSecondary} onClick={() => setModal({ reorderItem: item })}>Reorder AI</button>
                        <button className={css.btnDanger} onClick={() => setDeleteTarget(item)}><DeleteIcon style={{ fontSize: '0.9rem' }} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'add' || modal?.editItem) && (
        <InventoryItemFormModal
          item={modal?.editItem || null}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); flash(modal?.editItem ? 'Item updated.' : 'Item added.'); load(); }}
        />
      )}
      {modal?.transactItem && (
        <InventoryTransactionModal
          item={modal.transactItem}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); flash('Stock transaction recorded.'); load(); }}
        />
      )}
      {modal?.reorderItem && (
        <InventoryReorderModal item={modal.reorderItem} onClose={() => setModal(null)} />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          message={`Discontinue "${deleteTarget.name}"? It will be hidden from active inventory. This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function InventoryItemFormModal({ item, onClose, onSuccess }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || '', category: item?.category || 'general', sku: item?.sku || '',
    unit: item?.unit || 'pcs', reorderLevel: item?.reorderLevel ?? 0, unitCost: item?.unitCost ?? 0,
    location: item?.location || '', supplier: item?.supplier || '',
    quantityInStock: item ? undefined : 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setLoading(true); setError('');
    const payload = {
      name: form.name, category: form.category, sku: form.sku, unit: form.unit,
      reorderLevel: Number(form.reorderLevel) || 0, unitCost: Number(form.unitCost) || 0,
      location: form.location, supplier: form.supplier,
    };
    if (!isEdit) payload.quantityInStock = Number(form.quantityInStock) || 0;
    const result = isEdit
      ? await schoolFetch(`/school/inventory/${item._id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await schoolFetch('/school/inventory', { method: 'POST', body: JSON.stringify(payload) });
    setLoading(false);
    if (result.ok) onSuccess();
    else setError(result.data.error || 'Failed to save item.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit' : 'Add'} Inventory Item</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className={css.formLabel}>Name</label>
          <input className={css.formInput} value={form.name} onChange={set('name')} required autoFocus />
          <label className={css.formLabel}>Category</label>
          <input className={css.formInput} placeholder="e.g. stationery, furniture" value={form.category} onChange={set('category')} />
          <label className={css.formLabel}>SKU</label>
          <input className={css.formInput} value={form.sku} onChange={set('sku')} />
          <label className={css.formLabel}>Unit</label>
          <input className={css.formInput} placeholder="e.g. pcs, box, kg" value={form.unit} onChange={set('unit')} />
          {!isEdit && (
            <>
              <label className={css.formLabel}>Initial Quantity in Stock</label>
              <input className={css.formInput} type="number" min="0" value={form.quantityInStock} onChange={set('quantityInStock')} />
            </>
          )}
          <label className={css.formLabel}>Reorder Level</label>
          <input className={css.formInput} type="number" min="0" value={form.reorderLevel} onChange={set('reorderLevel')} />
          <label className={css.formLabel}>Unit Cost (₦)</label>
          <input className={css.formInput} type="number" min="0" value={form.unitCost} onChange={set('unitCost')} />
          <label className={css.formLabel}>Location</label>
          <input className={css.formInput} value={form.location} onChange={set('location')} />
          <label className={css.formLabel}>Supplier</label>
          <input className={css.formInput} value={form.supplier} onChange={set('supplier')} />

          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InventoryTransactionModal({ item, onClose, onSuccess }) {
  const [type, setType] = useState('stock_in');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [issueTo, setIssueTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Number(quantity)) { setError('Quantity is required.'); return; }
    setLoading(true); setError('');
    const result = await schoolFetch(`/school/inventory/${item._id}/transactions`, {
      method: 'POST', body: JSON.stringify({ type, quantity: Number(quantity), reason, issueTo }),
    });
    setLoading(false);
    if (result.ok) onSuccess();
    else setError(result.data.error || 'Failed to record transaction.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Record Stock — {item.name}</h2>
        <p className={css.modalSub}>Currently {item.quantityInStock} {item.unit} in stock.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className={css.formLabel}>Type</label>
          <select className={css.formInput} value={type} onChange={e => setType(e.target.value)}>
            {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{transactionTypeLabel[t]}</option>)}
          </select>
          <label className={css.formLabel}>Quantity {type === 'adjustment' ? '(use a negative number to reduce stock)' : ''}</label>
          <input className={css.formInput} type="number" value={quantity} onChange={e => setQuantity(e.target.value)} required autoFocus />
          <label className={css.formLabel}>Reason</label>
          <input className={css.formInput} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Restocked from supplier" />
          <label className={css.formLabel}>Issued To (optional)</label>
          <input className={css.formInput} value={issueTo} onChange={e => setIssueTo(e.target.value)} placeholder="e.g. JSS 2A or Accounts Department" />
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InventoryReorderModal({ item, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    schoolFetch(`/school/inventory/${item._id}/reorder-suggestion`).then(({ ok, data }) => {
      if (ok) setSuggestion(data);
      else setError(data.error || 'Failed to compute reorder suggestion.');
      setLoading(false);
    });
  }, [item._id]);

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Reorder Suggestion — {item.name}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        {loading ? (
          <p style={{ color: '#90a4ae' }}>Analyzing consumption history…</p>
        ) : suggestion ? (
          <div>
            <p className={css.reorderMetric}>Avg. daily consumption: <strong>{suggestion.avgDailyConsumption}</strong> {item.unit}</p>
            <p className={css.reorderMetric}>
              Estimated days until stockout: <strong>{suggestion.daysUntilStockout !== null ? suggestion.daysUntilStockout : 'N/A (not enough history)'}</strong>
            </p>
            <p className={css.reorderMetric}>Suggested reorder quantity: <strong>{suggestion.suggestedReorderQty} {item.unit}</strong></p>
            {suggestion.narrative && (
              <div style={{ marginTop: 12, padding: 10, background: '#f4fafb', borderRadius: 8, fontSize: '0.85rem', color: '#37474f' }}>
                ✨ {suggestion.narrative}
              </div>
            )}
          </div>
        ) : null}
        <div className={css.modalFooter}>
          <button type="button" className={css.btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function InventoryRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [status, setStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const { ok, data } = await schoolFetch(`/school/inventory/transactions?${params}`);
    if (ok) setRequests((data.transactions || []).filter(t => t.requestedBy?.actorType === 'teacher'));
    else setError(data.error || 'Failed to load requests.');
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleReview = async (tx, reviewStatus) => {
    const { ok, data } = await schoolFetch(`/school/inventory/transactions/${tx._id}/review`, {
      method: 'PATCH', body: JSON.stringify({ status: reviewStatus }),
    });
    if (ok) { flash(reviewStatus === 'approved' ? 'Request approved.' : 'Request rejected.'); load(); }
    else setError(data.error || 'Failed to review request.');
  };

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Teacher Stock Requests</h2>
        </div>
        <div className={css.filtersBar}>
          <select className={css.filterSelect} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>

        {loading ? (
          <SkeletonCards count={3} />
        ) : requests.length === 0 ? (
          <div className={css.emptyState}>
            <AssignmentIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No requests</p>
            <p className={css.emptySub}>Teacher stock requests will appear here for your approval.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Item</th><th>Qty</th><th>Requested By</th><th>Issued To</th><th>Reason</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {requests.map(tx => (
                  <tr key={tx._id}>
                    <td>{tx.itemId?.name || '—'}</td>
                    <td>{tx.quantity} {tx.itemId?.unit}</td>
                    <td>{tx.requestedBy?.name}</td>
                    <td>{tx.issueTo || '—'}</td>
                    <td>{tx.reason || '—'}</td>
                    <td><span className={`${css.chip} ${requisitionStatusChipClass[tx.status] || css.chipInactive}`}>{tx.status}</span></td>
                    <td>
                      {tx.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className={css.btnPrimary} onClick={() => handleReview(tx, 'approved')}>Approve</button>
                          <button className={css.btnDanger} onClick={() => handleReview(tx, 'rejected')}>Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Classes & Subjects Tab ───────────────────────────────────────────────────
function ClassesSubjectsTab({ isOwner, onNavigate }) {
  const [classes,   setClasses]   = useState([]);
  const [subjects,  setSubjects]  = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [modal,     setModal]     = useState(null);
  // modal: null | 'addClass' | 'addSubject' | { editClass } | { editSubject } | { assignSubject }
  const [deleteTarget, setDeleteTarget] = useState(null); // { subjectId, name }
  const [error, setError] = useState('');
  const searchTimeout = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (subjectSearch) params.set('search', subjectSearch);
      const query = params.toString();
      const [cRes, sRes] = await Promise.all([
        schoolFetch('/school/classes'),
        schoolFetch(`/school/subjects${query ? `?${query}` : ''}`),
      ]);
      setClasses(cRes.data.classes  || []);
      setSubjects(sRes.data.subjects || []);
      const tRes = await schoolFetch('/school/teachers');
      if (tRes.ok) setTeachers(tRes.data.teachers || []);
    } catch { setError('Failed to load data.'); }
    finally { setLoading(false); }
  }, [subjectSearch]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchAll(), subjectSearch ? 400 : 0);
    return () => clearTimeout(searchTimeout.current);
  }, [fetchAll, subjectSearch]);

  return (
    <div>
      <PendingSubAdminApprovals moduleKey="classes" title="Pending Class Requests" isOwner={isOwner} onNavigate={onNavigate} />
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <SkeletonClassesSubjects />
      ) : (
        <>
          {/* ── Classes ─────────────────────────────────────── */}
          <div className={css.card}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>Classes <span style={{ color: '#90a4ae', fontWeight: 400, fontSize: '0.875rem' }}>({classes.length})</span></h2>
              <button className={css.btnPrimary} onClick={() => setModal('addClass')}>
                <AddIcon style={{ fontSize: '1rem' }} /> Add Class
              </button>
            </div>
            {classes.length === 0 ? (
              <div className={css.emptyState}>
                <AssignmentIcon className={css.emptyIcon} />
                <p className={css.emptyText}>No classes yet</p>
                <p className={css.emptySub}>Create your first class to start assigning students.</p>
                <button className={css.btnPrimary} onClick={() => setModal('addClass')}>
                  <AddIcon style={{ fontSize: '1rem' }} /> Add Class
                </button>
              </div>
            ) : (
              <div className={css.classPillGrid}>
                {classes.map(c => (
                  <div key={c._id} className={css.classCard}>
                    <div className={css.classCardTop}>
                      <span className={css.classCardName}>{c.name}</span>
                      <button className={css.iconActionBtn} onClick={() => setModal({ editClass: c })}>
                        <EditIcon style={{ fontSize: '0.95rem' }} />
                      </button>
                    </div>
                    {(c.arms || []).length > 0 && (
                      <div className={css.armPillRow}>
                        {c.arms.map(arm => <span key={arm} className={css.armPill}>{arm}</span>)}
                      </div>
                    )}
                    {(c.arms || []).length === 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#b0bec5' }}>No arms defined</span>
                    )}
                    <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#607d8b' }}>
                      Form Teacher: <strong>{c.formTeacherId ? `${c.formTeacherId.firstName || ''} ${c.formTeacherId.lastName || ''}`.trim() : 'Not assigned'}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Subjects ────────────────────────────────────── */}
          <div className={css.card}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>Subjects <span style={{ color: '#90a4ae', fontWeight: 400, fontSize: '0.875rem' }}>({subjects.length})</span></h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={css.btnSecondary} onClick={() => setModal('bulkSubject')}>
                  Bulk Add
                </button>
                <button className={css.btnPrimary} onClick={() => setModal('addSubject')}>
                  <AddIcon style={{ fontSize: '1rem' }} /> Add Subject
                </button>
              </div>
            </div>
            <div className={css.filtersBar}>
              <input
                className={css.searchInput}
                placeholder="Search by subject or assigned class…"
                value={subjectSearch}
                onChange={e => setSubjectSearch(e.target.value)}
              />
            </div>
            {subjects.length === 0 ? (
              <div className={css.emptyState}>
                <AssignmentIcon className={css.emptyIcon} />
                <p className={css.emptyText}>{subjectSearch ? 'No subjects match your search' : 'No subjects yet'}</p>
                <p className={css.emptySub}>{subjectSearch ? 'Try a different subject or class name.' : 'Create subjects and assign them to classes.'}</p>
                {!subjectSearch && (
                  <button className={css.btnPrimary} onClick={() => setModal('addSubject')}>
                    <AddIcon style={{ fontSize: '1rem' }} /> Add Subject
                  </button>
                )}
              </div>
            ) : (
              <div className={css.tableWrap}>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th>Subject Name</th>
                      <th>Assigned Classes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map(s => (
                      <tr key={s._id}>
                        <td><strong>{s.name}</strong></td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {(s.classIds || []).length > 0
                            ? (s.classIds || []).map(c => c.name || c.level || '').filter(Boolean).join(', ')
                            : <span style={{ color: '#b0bec5' }}>Not assigned</span>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setModal({ editSubject: s })}>
                              <EditIcon style={{ fontSize: '0.9rem' }} />
                            </button>
                            <button className={css.btnDanger} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setDeleteTarget({ subjectId: s._id, name: s.name })}>
                              <PersonRemoveIcon style={{ fontSize: '0.9rem' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Subject choices (electives) — kept mounted while the subject search reloads ── */}
      {(!loading || classes.length > 0) && (
        <SubjectChoicesSegment classes={classes} schoolFetch={schoolFetch} />
      )}

      {/* â”€â”€ Modals â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {modal === 'addClass' && <ClassFormModal teachers={teachers} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {modal === 'addSubject' && <SubjectFormModal classes={classes} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {modal === 'bulkSubject' && <BulkSubjectModal classes={classes} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {modal?.editClass && <ClassFormModal existing={modal.editClass} teachers={teachers} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {modal?.editSubject && <SubjectFormModal existing={modal.editSubject} classes={classes} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {modal?.assignSubject && <AssignSubjectModal subject={modal.assignSubject} classes={classes} onClose={() => setModal(null)} onSuccess={fetchAll} />}
      {deleteTarget && (
        <DeleteConfirmModal
          message={`Delete subject "${deleteTarget.name}"? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async (password) => {
            const result = await schoolFetch(`/school/subjects/${deleteTarget.subjectId}`, {
              method: 'DELETE',
              headers: { 'x-admin-password': password },
            });
            if (result.ok) { setDeleteTarget(null); fetchAll(); }
            return result;
          }}
        />
      )}
    </div>
  );
}

// ─── Curriculum Overview Tab ──────────────────────────────────────────────────
// Admin-wide view of EVERY curriculum uploaded by EVERY teacher, across every
// (subject, class) pair — a subject like Maths can be taken by several class
// levels, each with its own file/topics uploaded by a different teacher, so
// each row here is clearly labeled "Subject · Class · Teacher".
function CurriculumOverviewTab({ classes, subjects, school }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, uploaded: 0, missing: 0 });
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const searchTimeout = useRef(null);

  const curriculumSetByAdmin = !!school?.controlSettings?.curriculumSetByAdmin;

  // ── Admin curriculum upload (only active when curriculumSetByAdmin is on) ──
  const fileInputRef = useRef(null);
  const [pendingRow, setPendingRow] = useState(null); // { subjectId, classId }
  const [uploadingKey, setUploadingKey] = useState('');
  const [visibilitySavingKey, setVisibilitySavingKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // row being deleted (password confirm)

  const triggerUpload = (subjectId, classId) => {
    setPendingRow({ subjectId, classId });
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !pendingRow) return;
    const { subjectId, classId } = pendingRow;
    const key = `${subjectId}:${classId}`;
    const allowed = ['application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword', 'text/plain'];
    if (!allowed.includes(file.type)) { setError('Unsupported file type. Use PDF, DOCX, or TXT.'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('File too large. Maximum 20 MB.'); return; }
    setUploadingKey(key); setError(''); setSuccess('');
    try {
      const { ok: urlOk, data: urlData } = await schoolFetch('/school/curriculum/upload-url', {
        method: 'POST',
        body: JSON.stringify({ subjectId, classId, fileName: file.name, contentType: file.type, fileSize: file.size }),
      });
      if (!urlOk) { setError(urlData.error || 'Failed to get upload URL'); return; }
      const uploadRes = await fetch(urlData.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!uploadRes.ok) { setError('File upload to storage failed. Try again.'); return; }
      setSuccess('Extracting topics and summary…');
      const { ok: confirmOk, data: confirmData } = await schoolFetch('/school/curriculum/confirm', {
        method: 'POST',
        body: JSON.stringify({ subjectId, classId, key: urlData.key, originalName: file.name }),
      });
      if (!confirmOk) { setError(confirmData.error || 'Failed to process curriculum'); setSuccess(''); return; }
      setSuccess('Curriculum uploaded and processed successfully!');
      setTimeout(() => setSuccess(''), 5000);
      fetchRows();
    } catch { setError('Connection error. Try again.'); setSuccess(''); }
    finally { setUploadingKey(''); setPendingRow(null); }
  };

  const handleDelete = (row) => setDeleteTarget(row);

  const handleVisibilityChange = async (subjectId, classId, visibility) => {
    const key = `${subjectId}:${classId}`;
    setVisibilitySavingKey(key);
    try {
      const { ok, data } = await schoolFetch(`/school/curriculum/${subjectId}/${classId}/visibility`, {
        method: 'PATCH',
        body: JSON.stringify({ visibility }),
      });
      if (ok) fetchRows();
      else setError(data.error || 'Failed to update visibility');
    } catch { setError('Connection error.'); }
    finally { setVisibilitySavingKey(''); }
  };

  useEffect(() => {
    schoolFetch('/school/teachers').then(({ ok, data }) => {
      if (ok) setTeachers(data.teachers || []);
    });
  }, []);

  const openViewRow = (row) => {
    setViewRow(row);
    setCoverage(null);
    if (row.curriculum?.topics?.length > 0) {
      setCoverageLoading(true);
      schoolFetch(`/school/curriculum/coverage/${row.subjectId}/${row.classId}`).then(({ ok, data }) => {
        if (ok) setCoverage(data.coverage);
        setCoverageLoading(false);
      });
    }
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterSubject) params.set('subjectId', filterSubject);
      if (filterClass) params.set('classId', filterClass);
      if (filterTeacher) params.set('teacherId', filterTeacher);
      if (filterStatus) params.set('status', filterStatus);
      const { ok, data } = await schoolFetch(`/school/curriculum?${params}`);
      if (ok) {
        setRows(data.rows || []);
        setSummary(data.summary || { total: 0, uploaded: 0, missing: 0 });
      } else {
        setError(data.error || 'Failed to load curriculum');
      }
    } catch {
      setError('Connection error.');
    } finally {
      setLoading(false);
    }
  }, [search, filterSubject, filterClass, filterTeacher, filterStatus]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(fetchRows, search ? 400 : 0);
    return () => clearTimeout(searchTimeout.current);
  }, [fetchRows, search]);

  const resetFilters = () => {
    setSearch(''); setFilterSubject(''); setFilterClass(''); setFilterTeacher(''); setFilterStatus('');
  };

  const hasActiveFilters = search || filterSubject || filterClass || filterTeacher || filterStatus;

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div className={css.card}>
        <div className={css.cardHeader}>
          <div>
            <h2 className={css.cardTitle}>Curriculum</h2>
            <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
              {summary.total} subject-class pair{summary.total !== 1 ? 's' : ''} · {summary.uploaded} uploaded · {summary.missing} missing
              {curriculumSetByAdmin && ' · Curriculum is managed by Admin'}
            </span>
          </div>
        </div>

        {error && <div className={css.inlineError}>{error}</div>}
        {success && <div className={css.inlineSuccess}>✓ {success}</div>}

        {/* Filters */}
        <div className={css.filtersBar}>
          <input
            className={css.searchInput}
            placeholder="Search by subject, class, teacher, or file name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className={css.filterSelect} value={filterSubject} onChange={e => setFilterSubject(e.target.value)}>
            <option value="">All Subjects</option>
            {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <select className={css.filterSelect} value={filterClass} onChange={e => setFilterClass(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className={css.filterSelect} value={filterTeacher} onChange={e => setFilterTeacher(e.target.value)}>
            <option value="">All Teachers</option>
            {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
          </select>
          <select className={css.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Any Status</option>
            <option value="uploaded">Uploaded</option>
            <option value="missing">Not Uploaded</option>
          </select>
          {hasActiveFilters && (
            <button className={css.btnSecondary} onClick={resetFilters}>Clear Filters</button>
          )}
        </div>

        {loading ? (
          <SkeletonTableRows rows={6} cols={6} />
        ) : rows.length === 0 ? (
          <div className={css.emptyState}>
            <AssignmentIcon className={css.emptyIcon} />
            <p className={css.emptyText}>No curriculum records found</p>
            <p className={css.emptySub}>
              {hasActiveFilters ? 'No results match these filters.' : 'Subjects need to be assigned to classes before teachers can upload curriculum.'}
            </p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Class</th>
                  <th>Teacher</th>
                  <th>File</th>
                  <th>Topics</th>
                  <th>Uploaded</th>
                  {curriculumSetByAdmin && <th>Visibility</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const hasCurriculum = !!row.curriculum?.key;
                  return (
                    <tr key={`${row.subjectId}:${row.classId}`}>
                      <td className={css.bold}>{row.subjectName}</td>
                      <td>{row.className}</td>
                      <td>
                        {row.uploadedByTeacher
                          ? `${row.uploadedByTeacher.firstName} ${row.uploadedByTeacher.lastName}`
                          : <span style={{ color: '#b0bec5' }}>—</span>}
                      </td>
                      <td>{hasCurriculum ? row.curriculum.originalName : <span style={{ color: '#b0bec5' }}>—</span>}</td>
                      <td>{hasCurriculum ? (row.curriculum.topics?.length || 0) : <span style={{ color: '#b0bec5' }}>—</span>}</td>
                      <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
                        {hasCurriculum && row.curriculum.uploadedAt
                          ? new Date(row.curriculum.uploadedAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
                          : <span className={`${css.chip} ${css.chipInactive}`}>Not uploaded</span>}
                        {hasCurriculum && row.curriculum.uploadedByAdmin && (
                          <span className={`${css.chip} ${css.chipActive}`} style={{ marginLeft: 6 }}>By Admin</span>
                        )}
                      </td>
                      {curriculumSetByAdmin && (
                        <td>
                          {hasCurriculum ? (
                            <select
                              className={css.filterSelect}
                              style={{ padding: '4px 8px', fontSize: '0.76rem' }}
                              value={row.curriculum.visibility || 'all'}
                              disabled={visibilitySavingKey === `${row.subjectId}:${row.classId}`}
                              onChange={e => handleVisibilityChange(row.subjectId, row.classId, e.target.value)}
                            >
                              <option value="all">All (Everyone)</option>
                              <option value="teachers">Teachers Only</option>
                              <option value="admin_only">Admin Only</option>
                            </select>
                          ) : (
                            <span style={{ color: '#b0bec5', fontSize: '0.78rem' }}>—</span>
                          )}
                        </td>
                      )}
                      <td>
                        {hasCurriculum ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className={css.btnSecondary} style={{ padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => openViewRow(row)} title="View">
                              <VisibilityIcon style={{ fontSize: '0.9rem' }} />
                            </button>
                            <a
                              className={css.btnSecondary}
                              style={{ padding: '5px 10px', fontSize: '0.78rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                              href={row.curriculum.url}
                              download={row.curriculum.originalName}
                              title="Download"
                            >
                              <DownloadIcon style={{ fontSize: '0.9rem' }} />
                            </a>
                            {curriculumSetByAdmin && (
                              <>
                                <button
                                  className={css.btnSecondary}
                                  style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                                  onClick={() => triggerUpload(row.subjectId, row.classId)}
                                  disabled={uploadingKey === `${row.subjectId}:${row.classId}`}
                                  title="Replace"
                                >
                                  <UploadIcon style={{ fontSize: '0.9rem' }} />
                                </button>
                                <button
                                  className={css.btnSecondary}
                                  style={{ padding: '5px 10px', fontSize: '0.78rem', color: '#e53935' }}
                                  onClick={() => handleDelete(row)}
                                  disabled={uploadingKey === `${row.subjectId}:${row.classId}`}
                                  title="Delete"
                                >
                                  <DeleteIcon style={{ fontSize: '0.9rem' }} />
                                </button>
                              </>
                            )}
                          </div>
                        ) : curriculumSetByAdmin ? (
                          <button
                            className={css.btnPrimary}
                            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
                            onClick={() => triggerUpload(row.subjectId, row.classId)}
                            disabled={uploadingKey === `${row.subjectId}:${row.classId}`}
                          >
                            {uploadingKey === `${row.subjectId}:${row.classId}` ? 'Uploading…' : <><UploadIcon style={{ fontSize: '0.9rem', marginRight: 4 }} />Upload</>}
                          </button>
                        ) : (
                          <span style={{ color: '#b0bec5', fontSize: '0.78rem' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View / detail modal */}
      {viewRow && (
        <div className={css.modalOverlay} onClick={() => setViewRow(null)}>
          <div className={`${css.modalLarge} BubblePopIn`} style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>{viewRow.subjectName} — {viewRow.className}</h2>
              <button className={css.closeBtn} onClick={() => setViewRow(null)}>&times;</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ fontSize: '0.82rem', color: '#546e7a', marginBottom: 8 }}>
                <strong>File:</strong> {viewRow.curriculum?.originalName} &nbsp;·&nbsp;
                <strong>Uploaded by:</strong> {viewRow.uploadedByTeacher ? `${viewRow.uploadedByTeacher.firstName} ${viewRow.uploadedByTeacher.lastName}` : '—'} &nbsp;·&nbsp;
                {viewRow.curriculum?.uploadedAt ? new Date(viewRow.curriculum.uploadedAt).toLocaleDateString() : '—'}
              </p>
              {viewRow.curriculum?.summary && (
                <>
                  <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Summary</h4>
                  <p style={{ fontSize: '0.83rem', color: '#37474f', lineHeight: 1.6, marginBottom: 16 }}>{viewRow.curriculum.summary}</p>
                </>
              )}
              {viewRow.curriculum?.topics?.length > 0 && (
                <>
                  <h4 style={{ marginBottom: 8, color: '#263238', fontSize: '0.85rem' }}>Topics ({viewRow.curriculum.topics.length})</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {viewRow.curriculum.topics.map((t, i) => (
                      <span key={i} style={{ background: '#f0f4f8', color: '#37474f', borderRadius: 6, padding: '3px 10px', fontSize: '0.74rem', fontWeight: 500 }}>{t}</span>
                    ))}
                  </div>

                  {/* Topic coverage — how many of these topics already have exam questions */}
                  <h4 style={{ margin: '18px 0 8px', color: '#263238', fontSize: '0.85rem' }}>Topic Coverage</h4>
                  {coverageLoading ? (
                    <p style={{ fontSize: '0.8rem', color: '#90a4ae' }}>Checking coverage…</p>
                  ) : coverage ? (
                    <>
                      <div style={{ height: 7, borderRadius: 20, background: '#e8edf2', overflow: 'hidden', marginBottom: 5 }}>
                        <div style={{
                          height: '100%', borderRadius: 20,
                          width: coverage.total ? `${Math.round((coverage.coveredCount / coverage.total) * 100)}%` : '0%',
                          background: 'linear-gradient(90deg, #5fbec5, #1a8a92)',
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                      <p style={{ fontSize: '0.75rem', color: '#546e7a', margin: '0 0 10px' }}>
                        {coverage.coveredCount} of {coverage.total} topics have exam taken
                        {coverage.total > 0 && ` (${Math.round((coverage.coveredCount / coverage.total) * 100)}%)`}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {coverage.topics.map((t, i) => (
                          <span
                            key={i}
                            title={t.covered ? `${t.questionCount} question${t.questionCount !== 1 ? 's' : ''}` : 'No questions yet'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 10px', borderRadius: 20, fontSize: '0.73rem', fontWeight: 500,
                              background: t.covered ? '#e0f4f5' : '#f5f7fa',
                              color: t.covered ? '#00838f' : '#90a4ae',
                              border: `1px solid ${t.covered ? '#b2e0e3' : '#e0e7ef'}`,
                            }}
                          >
                            {t.covered ? '✓' : '○'} {t.name}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: '0.8rem', color: '#90a4ae' }}>No coverage data available.</p>
                  )}
                </>
              )}
            </div>
            <div className={css.modalFooter}>
              <a className={css.btnPrimary} href={viewRow.curriculum?.url} download={viewRow.curriculum?.originalName} style={{ textDecoration: 'none' }}>
                <DownloadIcon style={{ fontSize: '1rem' }} /> Download File
              </a>
              <button className={css.btnSecondary} onClick={() => setViewRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          message={`Delete curriculum for "${deleteTarget.subjectName}" — ${deleteTarget.className}? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async (password) => {
            const result = await schoolFetch(`/school/curriculum/${deleteTarget.subjectId}/${deleteTarget.classId}`, {
              method: 'DELETE',
              headers: { 'x-admin-password': password },
            });
            if (result.ok) { setDeleteTarget(null); fetchRows(); }
            return result;
          }}
        />
      )}
    </div>
  );
}

function ClassFormModal({ existing, teachers = [], onClose, onSuccess }) {
  const isEdit = !!existing;
  const [name, setName]   = useState(existing?.name || '');
  const [arms, setArms]   = useState((existing?.arms || []).join(', '));
  const [formTeacherId, setFormTeacherId] = useState(existing?.formTeacherId?._id || existing?.formTeacherId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const armsArr = arms.split(',').map(a => a.trim()).filter(Boolean);
    try {
      const path   = isEdit ? `/school/classes/${existing._id}` : '/school/classes';
      const method = isEdit ? 'PATCH' : 'POST';
      const { ok, data } = await schoolFetch(path, {
        method,
        body: JSON.stringify({ name, arms: armsArr, formTeacherId: formTeacherId || null }),
      });
      if (ok) { onSuccess(); onClose(); }
      else setError(data.error || 'Failed to save class.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit Class' : 'Add Class'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Class Name *</label>
            <input className={css.formInput} required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. SS2, JSS1, Year 10" />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Arms (comma-separated)</label>
            <input className={css.formInput} value={arms} onChange={e => setArms(e.target.value)} placeholder="e.g. A, B, C" />
            <span style={{ fontSize: '0.75rem', color: '#90a4ae', marginTop: 4, display: 'block' }}>Arms represent parallel streams within a class level (SS2A, SS2B…)</span>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Form Teacher</label>
            <select className={css.formSelect} value={formTeacherId} onChange={e => setFormTeacherId(e.target.value)}>
              <option value="">No form teacher assigned</option>
              {teachers.filter(teacher => teacher.status === 'active').map(teacher => (
                <option key={teacher._id} value={teacher._id}>{teacher.firstName} {teacher.lastName}{teacher.staffId ? ` (${teacher.staffId})` : ''}</option>
              ))}
            </select>
            <span style={{ fontSize: '0.75rem', color: '#90a4ae', marginTop: 4, display: 'block' }}>The assigned teacher records this class&apos;s skill ratings and form-teacher comment.</span>
          </div>
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : isEdit ? 'Save Changes' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SubjectFormModal({ existing, classes, onClose, onSuccess }) {
  const isEdit = !!existing;
  const [name, setName]         = useState(existing?.name || '');
  const [classIds, setClassIds] = useState(
    isEdit ? (existing.classIds || []).map(c => String(c._id || c)) : []
  );
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const toggleClass = (id) => setClassIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const path   = isEdit ? `/school/subjects/${existing._id}` : '/school/subjects';
      const method = isEdit ? 'PATCH' : 'POST';
      const { ok, data } = await schoolFetch(path, {
        method,
        body: JSON.stringify({ name, classIds }),
      });
      if (ok) { onSuccess(); onClose(); }
      else setError(data.error || (isEdit ? 'Failed to update subject.' : 'Failed to create subject.'));
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{isEdit ? 'Edit Subject' : 'Add Subject'}</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Subject Name *</label>
            <input className={css.formInput} required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Mathematics, Physics" />
          </div>
          {classes.length > 0 && (
            <div className={css.formGroup}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className={css.formLabel} style={{ marginBottom: 0 }}>Assign to Classes</label>
                <button
                  type="button"
                  style={{ fontSize: '0.75rem', color: '#3e8f93', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  onClick={() =>
                    setClassIds(classIds.length === classes.length ? [] : classes.map(c => String(c._id)))
                  }
                >
                  {classIds.length === classes.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className={css.checkGrid}>
                {classes.map(c => (
                  <label key={c._id} className={css.checkLabel}>
                    <input className="schoolCheckbox" type="checkbox" checked={classIds.includes(String(c._id))} onChange={() => toggleClass(String(c._id))} />
                    {c.name || c.level}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className={css.modalFooter}>
            <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : isEdit ? 'Save Changes' : 'Create Subject'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

//  Bulk Subject Modal 
function BulkSubjectModal({ classes, onClose, onSuccess }) {
  const [step, setStep]           = useState(1); // 1=pick template, 2=review & create
  const [selected, setSelected]   = useState(new Set());
  const [customText, setCustomText] = useState('');
  const [classIds, setClassIds]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [result, setResult]       = useState(null);

  const loadTemplate = (key) => {
    setSelected(new Set(SUBJECT_TEMPLATES[key].subjects));
    setStep(2);
  };

  const toggleSubject = (name) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const toggleClass = (id) => setClassIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const customList = customText.split('\n').map(s => s.trim()).filter(Boolean);
  const allSubjects = [...new Set([...selected, ...customList])];

  const handleSubmit = async () => {
    if (allSubjects.length === 0) { setError('Select or type at least one subject.'); return; }
    setError(''); setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/subjects/bulk', {
        method: 'POST',
        body: JSON.stringify({ subjects: allSubjects.map(name => ({ name, classIds })) }),
      });
      if (ok) setResult(data);
      else setError(data.error || 'Import failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  if (result) return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Import Complete</h2>
        <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: '1rem', margin: '16px 0 6px' }}>
          ✓ {result.created} subject{result.created !== 1 ? 's' : ''} created
        </p>
        {result.merged > 0 && (
          <p style={{ color: '#1565c0', fontSize: '0.85rem', marginBottom: 6 }}>
            ↗ {result.merged} already existed and had new classes merged in.
          </p>
        )}
        {result.skipped > 0 && (
          <p style={{ color: '#90a4ae', fontSize: '0.85rem', marginBottom: 16 }}>
            {result.skipped} already existed with no class changes — skipped.
          </p>
        )}
        <div className={css.modalFooter}>
          <button className={css.btnPrimary} onClick={() => { onSuccess(); onClose(); }}>Done</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Bulk Add Subjects</h2>
        {error && <div className={css.inlineError}>{error}</div>}

        {step === 1 ? (
          <>
            <p style={{ color: '#546e7a', fontSize: '0.875rem', marginBottom: 18 }}>
              Pick a curriculum template to load a ready-made list of subjects — then customise as needed.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {Object.entries(SUBJECT_TEMPLATES).map(([key, tmpl]) => (
                <button key={key} className={css.templateCard} onClick={() => loadTemplate(key)}>
                  <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: 6 }}>{tmpl.emoji}</span>
                  <span style={{ fontWeight: 700, color: '#0C1E28', fontSize: '0.875rem', display: 'block', marginBottom: 3 }}>{tmpl.label}</span>
                  <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>{tmpl.subjects.length} subjects</span>
                </button>
              ))}
            </div>
            <button className={css.btnSecondary} style={{ width: '100%' }} onClick={() => setStep(2)}>
              Skip — enter subjects manually
            </button>
          </>
        ) : (
          <>
            {selected.size > 0 && (
              <div className={css.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className={css.formLabel} style={{ marginBottom: 0 }}>
                    Template Subjects <span style={{ color: '#90a4ae', fontWeight: 400 }}>({selected.size} selected)</span>
                  </label>
                  <button type="button"
                    style={{ fontSize: '0.75rem', color: '#3e8f93', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setSelected(new Set())}>
                    Deselect All
                  </button>
                </div>
                <div className={css.checkGrid}>
                  {[...selected].map(name => (
                    <label key={name} className={css.checkLabel}>
                      <input className="schoolCheckbox" type="checkbox" checked onChange={() => toggleSubject(name)} />
                      {name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className={css.formGroup}>
              <label className={css.formLabel}>
                Extra Subjects <span style={{ color: '#90a4ae', fontWeight: 400 }}>(one per line)</span>
              </label>
              <textarea
                className={css.formInput}
                rows={4}
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                placeholder={'Igbo Language\nHausa Language\nPhilosophy'}
              />
            </div>

            {classes.length > 0 && (
              <div className={css.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className={css.formLabel} style={{ marginBottom: 0 }}>Assign to Classes (optional)</label>
                  <button type="button"
                    style={{ fontSize: '0.75rem', color: '#3e8f93', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() =>
                      setClassIds(classIds.length === classes.length ? [] : classes.map(c => String(c._id)))
                    }>
                    {classIds.length === classes.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className={css.checkGrid}>
                  {classes.map(c => (
                    <label key={c._id} className={css.checkLabel}>
                      <input className="schoolCheckbox" type="checkbox" checked={classIds.includes(String(c._id))} onChange={() => toggleClass(String(c._id))} />
                      {c.name || c.level}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {allSubjects.length > 0 && (
              <p style={{ color: '#3e8f93', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>
                {allSubjects.length} subject{allSubjects.length !== 1 ? 's' : ''} will be created
              </p>
            )}

            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={() => setStep(1)}>← Back</button>
              <button type="button" className={css.btnPrimary} onClick={handleSubmit} disabled={loading || allSubjects.length === 0}>
                {loading
                  ? <ThreeCircles height="18" width="18" color="#fff" visible />
                  : `Create ${allSubjects.length || ''} Subject${allSubjects.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AssignSubjectModal({ subject, classes, onClose, onSuccess }) {
  const currentIds = (subject.classIds || []).map(c => String(c._id || c));
  const [selected, setSelected] = useState(currentIds);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const toggle = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch(`/school/subjects/${subject._id}/assign-classes`, {
        method: 'PATCH',
        body: JSON.stringify({ classIds: selected }),
      });
      if (ok) { onSuccess(); onClose(); }
      else setError(data.error || 'Failed to assign classes.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Assign Classes</h2>
        <p className={css.modalSub}>{subject.name}</p>
        {error && <div className={css.inlineError}>{error}</div>}
        {classes.length === 0 ? (
          <p style={{ color: '#90a4ae', fontSize: '0.875rem' }}>No classes defined yet. Create classes first.</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Select Classes</label>
              <div className={css.checkGrid}>
                {classes.map(c => (
                  <label key={c._id} className={css.checkLabel}>
                    <input className="schoolCheckbox" type="checkbox" checked={selected.includes(String(c._id))} onChange={() => toggle(String(c._id))} />
                    {c.name || c.level}
                  </label>
                ))}
              </div>
            </div>
            <div className={css.modalFooter}>
              <button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" className={css.btnPrimary} disabled={loading}>
                {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Save'}
              </button>
            </div>
          </form>
        )}
        {classes.length === 0 && (
          <div className={css.modalFooter}>
            <button className={css.btnSecondary} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

const REPORT_CARD_TEMPLATE_OPTIONS = [
  { key: 'classic', name: 'Classic Academic', title: 'STUDENT PROGRESS REPORT', note: 'Traditional letterhead and formal table.' },
  { key: 'modern', name: 'Modern Professional', title: 'ACADEMIC PERFORMANCE', note: 'Clean, structured, and digitally polished.' },
  { key: 'institutional', name: 'Formal Institutional', title: 'OFFICIAL TERM REPORT', note: 'Centered crest and official presentation.' },
  { key: 'compact', name: 'Compact Transcript', title: 'TERM RESULT TRANSCRIPT', note: 'Landscape format for many assessments.' },
  { key: 'contemporary', name: 'Contemporary Results', title: 'STUDENT RESULTS', note: 'Summary-led design with clear grade emphasis.' },
  { key: 'heritage', name: 'Heritage Ledger', title: 'TERM RESULT', note: 'Rose-tinted ledger table with a grade key and dual skill grids.' },
];

const SKILLS_TEMPLATE_PREVIEW = {
  classic: { sectionTitle: 'Skills & Behavioural Assessment', scaleTitle: 'Rating Scale', formLabel: "Form Teacher's Comment", principalLabel: "Principal's Remark", showPrincipal: false, primary: '#0C1E28', accent: '#3e8f93', soft: '#f0f5f7' },
  modern: { sectionTitle: 'Personal Development', scaleTitle: 'Performance Scale', formLabel: 'Form Teacher Review', principalLabel: 'Principal Review', showPrincipal: true, primary: '#16313a', accent: '#00838f', soft: '#e8f6f7' },
  institutional: { sectionTitle: 'Character & Practical Development', scaleTitle: 'Development Rating Scale', formLabel: "Form Teacher's Comment", principalLabel: "Principal's Remark", showPrincipal: true, primary: '#23395d', accent: '#9b7b2f', soft: '#f6f2e8' },
  compact: { sectionTitle: 'Behavioural Assessment', scaleTitle: 'Scale', formLabel: 'Form Teacher', principalLabel: 'Principal', showPrincipal: false, primary: '#263238', accent: '#546e7a', soft: '#eceff1' },
  contemporary: { sectionTitle: 'Beyond Academics', scaleTitle: 'Rating Guide', formLabel: 'Form Teacher Insight', principalLabel: 'Principal Note', showPrincipal: true, primary: '#173f35', accent: '#2e8b71', soft: '#eaf5f1' },
  heritage: { sectionTitle: '', scaleTitle: 'Grading on a Five Point Scale', formLabel: "Form Teacher's Comment", principalLabel: "Head Teacher's Comment", showPrincipal: true, primary: '#c81e3a', accent: '#c81e3a', soft: '#fbdde6' },
};

const ASSESSMENT_MODE_INFO = {
  summary: {
    title: 'A concise official result',
    description: 'Shows only CA Total, Exam Total, Final Total, grade, and your selected identity columns. Individual tests and exams stay combined.',
    action: 'Best for compact report cards. You can rename, reorder, or hide optional summary columns below.',
  },
  detailed: {
    title: 'Every assessment, generated automatically',
    description: 'Adds one column for every CA and exam found in the selected term, followed by the protected category totals.',
    action: 'No slots need to be created. Columns adapt automatically to each term and switch the PDF to landscape when needed.',
  },
  custom: {
    title: 'You control the assessment slots',
    description: 'Shows only the CA and exam slots you add, in your chosen order and with your own labels, plus the protected totals.',
    action: 'Use + CA and + Exam below, then rename, reorder, show, hide, or remove those slots.',
  },
};

const REQUIRED_REPORT_COLUMN_TYPES = new Set(['subject', 'ca_total', 'exam_total', 'final_total', 'grade']);

function ReportCardDesignEditor({ onProfileUpdated }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [principalCandidates, setPrincipalCandidates] = useState([]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await schoolFetch('/school/report-card-settings');
    if (ok) {
      setSettings(data.settings);
      setPrincipalCandidates(data.principalCandidates || []);
    }
    else setMessage({ error: data.error || 'Failed to load report card settings.' });
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateColumn = (index, updates) => {
    setMessage(null);
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map((column, columnIndex) => columnIndex === index ? { ...column, ...updates } : column),
    }));
  };

  const moveColumn = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= settings.columns.length) return;
    setSettings(prev => {
      const columns = [...prev.columns];
      [columns[index], columns[destination]] = [columns[destination], columns[index]];
      return { ...prev, columns };
    });
  };

  const addAssessmentColumn = type => {
    const category = type === 'ca_assessment' ? 'ca' : 'exam';
    const slots = settings.columns.filter(column => column.type === type).map(column => column.slot || 0);
    const slot = Math.max(0, ...slots) + 1;
    if (slot > 10 || settings.columns.length >= 20) {
      setMessage({ error: 'A maximum of 10 columns per assessment category and 20 columns overall is supported.' });
      return;
    }
    const totalIndex = settings.columns.findIndex(column => column.type === `${category}_total`);
    const column = {
      key: `${type}_${slot}`,
      type,
      slot,
      label: `${category === 'ca' ? 'CA' : 'Exam'} ${slot}`,
      visible: true,
    };
    setSettings(prev => {
      const columns = [...prev.columns];
      columns.splice(totalIndex < 0 ? columns.length : totalIndex, 0, column);
      return { ...prev, assessmentDisplay: 'custom', columns };
    });
  };

  const removeColumn = index => {
    setSettings(prev => ({ ...prev, columns: prev.columns.filter((_, columnIndex) => columnIndex !== index) }));
  };

  const save = async () => {
    setSaving(true); setMessage(null);
    const { ok, data } = await schoolFetch('/school/report-card-settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
    if (ok) {
      setSettings(data.settings);
      setMessage({ success: 'Report card design saved. All future downloads will use this format.' });
      onProfileUpdated?.({ reportCardSettings: data.settings });
    } else setMessage({ error: data.error || 'Failed to save report card settings.' });
    setSaving(false);
  };

  if (loading) return <div className={css.card}><SkeletonCards count={1} /></div>;
  if (!settings) return <div className={css.card}><div className={css.inlineError}>Report card settings could not be loaded.</div></div>;

  const activeTemplate = REPORT_CARD_TEMPLATE_OPTIONS.find(template => template.key === settings.template) || REPORT_CARD_TEMPLATE_OPTIONS[0];
  const activeModeInfo = ASSESSMENT_MODE_INFO[settings.assessmentDisplay] || ASSESSMENT_MODE_INFO.summary;
  const visibleColumns = settings.columns.filter(column => column.visible && (settings.assessmentDisplay !== 'summary' || !column.type.endsWith('_assessment')));

  return (
    <div className={css.card} id="report-card-design">
      <div className={css.cardHeader}>
        <div>
          <h2 className={css.cardTitle}>Report Card Design</h2>
          <p className={css.reportDesignIntro}>Choose the official school-wide template and control how assessment scores appear.</p>
        </div>
        <span className={css.reportDesignStatus}>{activeTemplate.name} · {visibleColumns.length} columns</span>
      </div>

      {message?.error && <div className={css.inlineError}>{message.error}</div>}
      {message?.success && <div className={css.inlineSuccess}>{message.success}</div>}

      <div className={css.reportTemplateGrid}>
        {REPORT_CARD_TEMPLATE_OPTIONS.map(template => (
          <button
            type="button"
            key={template.key}
            className={`${css.reportTemplateOption} ${settings.template === template.key ? css.reportTemplateOptionActive : ''}`}
            onClick={() => { setMessage(null); setSettings(prev => ({ ...prev, template: template.key })); }}
            aria-pressed={settings.template === template.key}
          >
            <span className={`${css.reportTemplateMiniature} ${css[`reportTemplate_${template.key}`]}`}>
              <span className={css.reportMiniHeader} />
              <span className={css.reportMiniMeta} />
              <span className={css.reportMiniTable} />
            </span>
            <span className={css.reportTemplateName}>{template.name}</span>
            <span className={css.reportTemplateNote}>{template.note}</span>
            {settings.template === template.key && <CheckCircleIcon className={css.reportTemplateCheck} />}
          </button>
        ))}
      </div>

      <div className={css.reportDesignerLayout}>
        <div className={css.reportDesignerControls}>
          <div className={css.reportControlSection}>
            <label className={css.formLabel}>Assessment detail</label>
            <div className={css.segmentedControl}>
              {[
                ['summary', 'Summary'],
                ['detailed', 'All assessments'],
                ['custom', 'Custom'],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={settings.assessmentDisplay === value ? css.segmentedControlActive : ''}
                  onClick={() => setSettings(prev => ({ ...prev, assessmentDisplay: value }))}
                >{label}</button>
              ))}
            </div>
            <div className={css.assessmentModeInfo} aria-live="polite">
              <CheckCircleIcon />
              <div>
                <strong>{activeModeInfo.title}</strong>
                <p>{activeModeInfo.description}</p>
                <span>{activeModeInfo.action}</span>
              </div>
            </div>
            {settings.assessmentDisplay !== 'summary' && (
              <p className={css.reportControlHint}>Individual scores are weighted contributions, so CA columns add exactly to CA Total and exam columns add exactly to Exam Total.</p>
            )}
          </div>

          <div className={css.reportControlSection}>
            <label className={css.formLabel} htmlFor="report-card-title">Report heading</label>
            <input
              id="report-card-title"
              className={css.formInput}
              value={settings.title || ''}
              maxLength={80}
              placeholder={activeTemplate.title}
              onChange={event => setSettings(prev => ({ ...prev, title: event.target.value }))}
            />
            <p className={css.reportControlHint}>Printed above the student details. Leave blank to use “{activeTemplate.title}”.</p>
          </div>

          <div className={css.reportControlSection}>
            <label className={css.formLabel} htmlFor="report-card-principal">Principal identity</label>
            <select
              id="report-card-principal"
              className={css.formSelect}
              value={`${settings.principal?.actorType || 'school_admin'}:${settings.principal?.actorId || ''}`}
              onChange={event => {
                const [actorType, actorId = ''] = event.target.value.split(':');
                setSettings(prev => ({ ...prev, principal: { ...prev.principal, actorType, actorId: actorId || null } }));
              }}
            >
              <option value="school_admin:">School owner (default)</option>
              {principalCandidates.map(candidate => (
                <option key={`${candidate.actorType}:${candidate.actorId}`} value={`${candidate.actorType}:${candidate.actorId}`}>
                  {candidate.name} · {candidate.role}
                </option>
              ))}
            </select>
            <input
              className={css.formInput}
              style={{ marginTop: 8 }}
              value={settings.principal?.title || 'Principal'}
              maxLength={60}
              onChange={event => setSettings(prev => ({ ...prev, principal: { ...prev.principal, title: event.target.value } }))}
              placeholder="Printed title, e.g. Principal"
              aria-label="Principal printed title"
            />
            <p className={css.reportControlHint}>Used by templates that display a principal remark. The remark itself is recorded per student in the admin student editor.</p>
          </div>

          <div className={css.reportControlSection}>
            <div className={css.reportColumnHeading}>
              <label className={css.formLabel}>Columns</label>
              {settings.assessmentDisplay === 'custom' && (
                <div className={css.reportColumnActions}>
                  <button type="button" className={css.btnSecondary} onClick={() => addAssessmentColumn('ca_assessment')}>+ CA</button>
                  <button type="button" className={css.btnSecondary} onClick={() => addAssessmentColumn('exam_assessment')}>+ Exam</button>
                </div>
              )}
            </div>
            <div className={css.reportColumnList}>
              {settings.columns.map((column, index) => {
                const required = REQUIRED_REPORT_COLUMN_TYPES.has(column.type);
                const assessment = column.type.endsWith('_assessment');
                return (
                  <div key={column.key} className={css.reportColumnRow}>
                    <input
                      className="schoolCheckbox"
                      type="checkbox"
                      checked={column.visible}
                      disabled={required}
                      onChange={event => updateColumn(index, { visible: event.target.checked })}
                      aria-label={`Show ${column.label}`}
                    />
                    <input
                      className={css.reportColumnLabelInput}
                      value={column.label}
                      maxLength={40}
                      onChange={event => updateColumn(index, { label: event.target.value })}
                      aria-label={`${column.label} label`}
                    />
                    {required && <span className={css.reportRequiredBadge}>Required</span>}
                    <div className={css.reportColumnMoveActions}>
                      <button type="button" onClick={() => moveColumn(index, -1)} disabled={index === 0} title="Move up"><ArrowUpwardIcon /></button>
                      <button type="button" onClick={() => moveColumn(index, 1)} disabled={index === settings.columns.length - 1} title="Move down"><ArrowDownwardIcon /></button>
                      {assessment && <button type="button" onClick={() => removeColumn(index)} className={css.reportRemoveColumn} title="Remove column"><DeleteIcon /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={css.reportPrecisionRow}>
            <label className={css.formLabel}>Score precision</label>
            <select className={css.formSelect} value={settings.scorePrecision} onChange={event => setSettings(prev => ({ ...prev, scorePrecision: Number(event.target.value) }))}>
              <option value={0}>Whole numbers</option>
              <option value={1}>One decimal place</option>
            </select>
          </div>
        </div>

        <div className={`${css.reportLivePreview} ${css[`reportPreview_${settings.template}`]}`}>
          <div className={css.reportPreviewLabel}>Live preview</div>
          <div className={css.reportPreviewPaper}>
            <div className={css.reportPreviewSchool}>YOUR SCHOOL NAME</div>
            <div className={css.reportPreviewTitle}>{settings.title || activeTemplate.title}</div>
            <div className={css.reportPreviewStudent}>Student: Ada Okafor <span>First Term · 2026/2027</span></div>
            <div className={css.reportPreviewTableWrap}>
              <table>
                <thead><tr>{visibleColumns.slice(0, 9).map(column => <th key={column.key}>{column.label}</th>)}</tr></thead>
                <tbody>
                  {['Mathematics', 'English Language', 'Basic Science'].map((subject, rowIndex) => (
                    <tr key={subject}>{visibleColumns.slice(0, 9).map((column, columnIndex) => <td key={column.key}>{columnIndex === 0 ? subject : column.type === 'grade' ? ['A', 'B', 'A'][rowIndex] : column.type === 'comment' ? 'Good' : column.type === 'instructor' ? 'Teacher' : 12 + rowIndex * 3}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visibleColumns.length > 9 && <div className={css.reportPreviewOverflow}>+ {visibleColumns.length - 9} more columns in landscape PDF</div>}
            <div style={{ marginTop: 16, padding: 8, borderTop: `2px solid ${SKILLS_TEMPLATE_PREVIEW[settings.template]?.accent || '#3e8f93'}`, background: SKILLS_TEMPLATE_PREVIEW[settings.template]?.soft || '#f0f5f7', color: SKILLS_TEMPLATE_PREVIEW[settings.template]?.primary || '#0C1E28', fontSize: '0.5rem' }}>
              <strong>{SKILLS_TEMPLATE_PREVIEW[settings.template]?.formLabel || "Form Teacher's Comment"}: </strong> Teacher One<br />Good progress this term.
            </div>
            {SKILLS_TEMPLATE_PREVIEW[settings.template]?.showPrincipal && (
              <div style={{ marginTop: 6, padding: 8, borderTop: `2px solid ${SKILLS_TEMPLATE_PREVIEW[settings.template]?.primary || '#0C1E28'}`, color: SKILLS_TEMPLATE_PREVIEW[settings.template]?.primary || '#0C1E28', fontSize: '0.5rem' }}>
                <strong>{SKILLS_TEMPLATE_PREVIEW[settings.template]?.principalLabel || "Principal's Remark"}: </strong> Principal One<br />Continue to grow.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={css.reportDesignFooter}>
        <button type="button" className={css.btnSecondary} onClick={loadSettings} disabled={saving}>Reset unsaved changes</button>
        <button type="button" className={css.btnPrimary} onClick={save} disabled={saving}>
          {saving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <CheckCircleIcon style={{ fontSize: '1rem' }} />}
          {saving ? 'Saving…' : 'Save Report Card Design'}
        </button>
      </div>
    </div>
  );
}

function skillKey(label, fallback) {
  const base = String(label || fallback).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;
  return base;
}

function nextSkillKey(label, existingKeys, fallback) {
  const base = skillKey(label, fallback);
  let key = base;
  let count = 2;
  while (existingKeys.includes(key)) key = `${base}-${count++}`;
  return key;
}

function SkillsReportSettingsEditor({ profile, onProfileUpdated }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await schoolFetch('/school/skills-report-settings');
    if (ok) setSettings(data.settings);
    else setMessage({ error: data.error || 'Failed to load skill report settings.' });
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateScale = (index, field, value) => {
    setMessage(null);
    setSettings(prev => ({
      ...prev,
      scale: prev.scale.map((entry, entryIndex) => entryIndex === index
        ? { ...entry, [field]: field === 'label' ? value : Number(value) }
        : entry),
    }));
  };

  const addScaleEntry = () => {
    const used = new Set(settings.scale.map(entry => Number(entry.value)));
    let value = 0;
    while (used.has(value) && value <= 100) value += 1;
    setSettings(prev => ({ ...prev, scale: [...prev.scale, { value, label: 'New rating' }] }));
  };

  const updateGroup = (groupIndex, updates) => setSettings(prev => ({
    ...prev,
    groups: prev.groups.map((group, index) => index === groupIndex ? { ...group, ...updates } : group),
  }));

  const updateItem = (groupIndex, itemIndex, updates) => setSettings(prev => ({
    ...prev,
    groups: prev.groups.map((group, index) => index !== groupIndex ? group : {
      ...group,
      items: group.items.map((item, itemIndexInGroup) => itemIndexInGroup === itemIndex ? { ...item, ...updates } : item),
    }),
  }));

  const addItem = groupIndex => setSettings(prev => ({
    ...prev,
    groups: prev.groups.map((group, index) => {
      if (index !== groupIndex) return group;
      const label = `Skill ${group.items.length + 1}`;
      return {
        ...group,
        items: [...group.items, {
          key: nextSkillKey(label, group.items.map(item => item.key), `skill-${group.items.length + 1}`),
          label,
        }],
      };
    }),
  }));

  const addGroup = () => setSettings(prev => {
    const title = `Skill Group ${prev.groups.length + 1}`;
    const key = nextSkillKey(title, prev.groups.map(group => group.key), `group-${prev.groups.length + 1}`);
    return {
      ...prev,
      groups: [...prev.groups, {
        key,
        title,
        items: [{ key: 'skill-1', label: 'Skill 1' }],
      }],
    };
  });

  const moveGroup = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= settings.groups.length) return;
    setSettings(prev => {
      const groups = [...prev.groups];
      [groups[index], groups[destination]] = [groups[destination], groups[index]];
      return { ...prev, groups };
    });
  };

  const save = async () => {
    setSaving(true); setMessage(null);
    const { ok, data } = await schoolFetch('/school/skills-report-settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
    if (ok) {
      setSettings(data.settings);
      setMessage({ success: 'Skill report settings saved.' });
      onProfileUpdated?.({ skillsReportSettings: data.settings });
    } else setMessage({ error: data.error || 'Failed to save skill report settings.' });
    setSaving(false);
  };

  if (loading) return <div className={css.card}><SkeletonCards count={1} /></div>;
  if (!settings) return <div className={css.card}><div className={css.inlineError}>Skill report settings could not be loaded.</div></div>;

  const templateKey = profile?.reportCardSettings?.template || 'classic';
  const template = SKILLS_TEMPLATE_PREVIEW[templateKey] || SKILLS_TEMPLATE_PREVIEW.classic;
  const templateOption = REPORT_CARD_TEMPLATE_OPTIONS.find(option => option.key === templateKey) || REPORT_CARD_TEMPLATE_OPTIONS[0];

  return (
    <div className={css.card} id="skills-report-settings">
      <div className={css.cardHeader}>
        <div>
          <h2 className={css.cardTitle}>Skill &amp; Behavioural Report Tables</h2>
          <p className={css.reportDesignIntro}>Build the additional assessment tables that appear after the academic grade scale.</p>
        </div>
        <span className={css.reportDesignStatus}>{settings.groups.length} groups</span>
      </div>
      {message?.error && <div className={css.inlineError}>{message.error}</div>}
      {message?.success && <div className={css.inlineSuccess}>{message.success}</div>}

      <div className={css.reportDesignerLayout}>
        <div className={css.reportDesignerControls}>
          <div className={css.reportControlSection}>
            <div className={css.reportColumnHeading}>
              <label className={css.formLabel}>Skill rating scale</label>
              <button type="button" className={css.btnSecondary} onClick={addScaleEntry} disabled={settings.scale.length >= 10}>
                <AddIcon style={{ fontSize: '0.95rem' }} /> Add Rating
              </button>
            </div>
            <p className={css.reportControlHint}>This scale is used only for skill and behavioural tables. It does not affect the academic Grade Scale.</p>
            <div className={css.tableWrap}>
              <table className={css.table} style={{ width: '100%', minWidth: 360 }}>
                <thead><tr><th>Rating</th><th>Label</th><th aria-label="Remove rating" /></tr></thead>
                <tbody>
                  {settings.scale.map((entry, index) => (
                    <tr key={`${entry.value}-${index}`}>
                      <td><input className={css.formInput} style={{ width: 85 }} type="number" min={0} max={100} value={entry.value} onChange={event => updateScale(index, 'value', event.target.value)} /></td>
                      <td><input className={css.formInput} value={entry.label} maxLength={60} onChange={event => updateScale(index, 'label', event.target.value)} /></td>
                      <td><button type="button" className={css.iconActionBtn} title="Remove rating" onClick={() => setSettings(prev => ({ ...prev, scale: prev.scale.filter((_, entryIndex) => entryIndex !== index) }))} disabled={settings.scale.length <= 1}><DeleteIcon style={{ fontSize: '0.95rem' }} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={css.reportControlSection}>
            <div className={css.reportColumnHeading}>
              <label className={css.formLabel}>Skill groups</label>
              <button type="button" className={css.btnSecondary} onClick={addGroup} disabled={settings.groups.length >= 10}>
                <AddIcon style={{ fontSize: '0.95rem' }} /> Add Group
              </button>
            </div>
            {settings.groups.length === 0 && <p className={css.reportControlHint}>Add a group such as Affective Skills or Psychomotor Skills, then add the items you want teachers to rate.</p>}
            <div style={{ display: 'grid', gap: 12 }}>
              {settings.groups.map((group, groupIndex) => (
                <div key={group.key} style={{ border: '1px solid #dce5e8', borderRadius: 8, padding: 12, background: '#fbfdfd' }}>
                  <div className={css.reportColumnHeading}>
                    <input className={css.formInput} style={{ fontWeight: 700, flex: 1 }} value={group.title} maxLength={80} onChange={event => updateGroup(groupIndex, { title: event.target.value })} aria-label="Skill group title" />
                    <div className={css.reportColumnMoveActions}>
                      <button type="button" onClick={() => moveGroup(groupIndex, -1)} disabled={groupIndex === 0} title="Move group up"><ArrowUpwardIcon /></button>
                      <button type="button" onClick={() => moveGroup(groupIndex, 1)} disabled={groupIndex === settings.groups.length - 1} title="Move group down"><ArrowDownwardIcon /></button>
                      <button type="button" className={css.reportRemoveColumn} onClick={() => setSettings(prev => ({ ...prev, groups: prev.groups.filter((_, index) => index !== groupIndex) }))} title="Remove group"><DeleteIcon /></button>
                    </div>
                  </div>
                  <div className={css.reportColumnList}>
                    {group.items.map((item, itemIndex) => (
                      <div className={css.reportColumnRow} key={item.key} style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
                        <input className={css.reportColumnLabelInput} value={item.label} maxLength={80} onChange={event => updateItem(groupIndex, itemIndex, { label: event.target.value })} aria-label={`${group.title} skill`} />
                        <button type="button" className={css.reportRemoveColumn} onClick={() => updateGroup(groupIndex, { items: group.items.filter((_, index) => index !== itemIndex) })} title="Remove skill" disabled={group.items.length <= 1}><DeleteIcon style={{ fontSize: '0.95rem' }} /></button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className={css.btnSecondary} style={{ marginTop: 9 }} onClick={() => addItem(groupIndex)} disabled={group.items.length >= 20}><AddIcon style={{ fontSize: '0.9rem' }} /> Add Item</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={`${css.reportLivePreview} ${css[`reportPreview_${templateKey}`]}`}>
          <div className={css.reportPreviewLabel}>Live preview</div>
          <div className={css.reportPreviewPaper}>
            <div className={css.reportPreviewSchool} style={{ color: templateKey === 'modern' || templateKey === 'contemporary' ? '#fff' : template.primary }}>YOUR SCHOOL NAME</div>
            <div className={css.reportPreviewTitle} style={{ color: template.accent, borderColor: template.accent }}>{templateOption.title}</div>
            <div style={{ marginBottom: 10, padding: '6px 8px', background: template.soft, color: template.primary, fontSize: '0.52rem', fontWeight: 700 }}>Student: Ada Okafor <span style={{ float: 'right' }}>Third Term · 2025/2026</span></div>
            <div style={{ marginBottom: 6, color: template.primary, fontSize: templateKey === 'compact' ? '0.52rem' : '0.58rem', fontWeight: 800 }}>{template.sectionTitle}</div>
            {settings.groups.length === 0 ? <p className={css.reportControlHint}>Your configured skill tables will appear here.</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: templateKey === 'compact' ? '1fr 1fr' : '1fr 1fr', gap: templateKey === 'compact' ? 5 : 8 }}>
                {settings.groups.slice(0, 4).map(group => (
                  <table key={group.key} style={{ width: '100%', borderCollapse: 'collapse', fontSize: templateKey === 'compact' ? '0.4rem' : '0.48rem', border: `1px solid ${template.accent}`, borderRadius: 4, overflow: 'hidden' }}>
                    <thead><tr><th style={{ padding: 5, background: templateKey === 'heritage' ? template.soft : (templateKey === 'institutional' ? template.accent : template.primary), color: templateKey === 'heritage' ? template.primary : '#fff', textAlign: templateKey === 'institutional' ? 'center' : 'left' }}>{group.title}</th>{templateKey === 'heritage' && <th style={{ padding: 5, background: template.soft, color: template.primary, textAlign: 'center' }}>Rating</th>}</tr></thead>
                    <tbody>{group.items.slice(0, 4).map((item, index) => <tr key={item.key}><td style={{ padding: 4, border: '1px solid #c5c5c5', background: templateKey === 'heritage' ? '#fff' : undefined, color: template.primary }}>{item.label}</td><td style={{ padding: 4, border: '1px solid #c5c5c5', background: templateKey === 'heritage' ? '#fff' : undefined, textAlign: 'center', color: template.accent, fontWeight: 700 }}>{settings.scale[index % settings.scale.length]?.value ?? '—'}</td></tr>)}</tbody>
                  </table>
                ))}
              </div>
            )}
            <div className={css.reportControlHint} style={{ marginTop: 12, color: template.primary }}>{template.scaleTitle}: {settings.scale.map(entry => `${entry.value} = ${entry.label}`).join(' | ')}</div>
            <div style={{ marginTop: 12, padding: 7, borderTop: `2px solid ${template.accent}`, background: template.soft, color: template.primary, fontSize: '0.48rem' }}><strong>{template.formLabel}: </strong> Teacher One<br />Good progress this term.</div>
            {template.showPrincipal && <div style={{ marginTop: 6, padding: 7, borderTop: `2px solid ${template.primary}`, color: template.primary, fontSize: '0.48rem' }}><strong>{template.principalLabel}: </strong> Principal One<br />Continue to grow.</div>}
          </div>
        </div>
      </div>

      <div className={css.reportDesignFooter}>
        <button type="button" className={css.btnSecondary} onClick={loadSettings} disabled={saving}>Reset unsaved changes</button>
        <button type="button" className={css.btnPrimary} onClick={save} disabled={saving}>
          {saving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : <CheckCircleIcon style={{ fontSize: '1rem' }} />}
          {saving ? 'Saving...' : 'Save Skill Report Settings'}
        </button>
      </div>
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────
// ─── Stakeholder Control Toggles ──────────────────────────────────────────────
// Three school-wide switches that shift responsibility/visibility between
// admin, teachers, students and parents. All default OFF so schools that
// never touch this card keep 100% of their current behaviour.
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={css.toggleSwitch}
      data-on={checked ? 'true' : 'false'}
    >
      <span className={css.toggleKnob} />
    </button>
  );
}

function StakeholderControlsEditor({ profile, onProfileUpdated }) {
  const [settings, setSettings] = useState({
    curriculumSetByAdmin: false,
    questionsApprovalRequired: false,
    malpracticeAdminOnly: false,
    teachersCanEditGradedScores: false,
    teachersCanSetRetakes: false,
  });
  const [loaded, setLoaded]   = useState(false);
  const [saving, setSaving]   = useState(null); // key currently saving, or null
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await schoolFetch('/school/control-settings');
      if (!cancelled && ok && data?.controlSettings) {
        setSettings(s => ({ ...s, ...data.controlSettings }));
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (key, value) => {
    setError(''); setSuccess(''); setSaving(key);
    const prev = settings[key];
    setSettings(s => ({ ...s, [key]: value }));
    try {
      const { ok, data } = await schoolFetch('/school/control-settings', {
        method: 'PATCH',
        body: JSON.stringify({ [key]: value }),
      });
      if (ok) {
        setSettings(s => ({ ...s, ...data.controlSettings }));
        setSuccess('Saved.');
        if (onProfileUpdated) onProfileUpdated({ controlSettings: data.controlSettings });
      } else {
        setSettings(s => ({ ...s, [key]: prev }));
        setError(data?.error || 'Failed to save setting.');
      }
    } catch {
      setSettings(s => ({ ...s, [key]: prev }));
      setError('Connection error.');
    } finally {
      setSaving(null);
    }
  };

  const rows = [
    {
      key: 'curriculumSetByAdmin',
      title: 'Curriculum Set by Admin, not Teachers',
      desc: 'When off (default), teachers upload their own curriculum as today. When on, only the school admin can upload/replace curriculum documents and decide per-subject visibility (all, teachers only, or admin only).',
    },
    {
      key: 'questionsApprovalRequired',
      title: 'Questions Set or Approved by Admin',
      desc: 'When on, teachers can still set exam questions but publishing sends the exam to the admin for approval before it is scheduled and visible to students and parents. This does not affect exams the admin creates directly.',
      warning: '⚠ Turning this on increases bureaucracy and slows down activity time — every teacher exam must wait for admin approval before it goes live.',
    },
    {
      key: 'malpracticeAdminOnly',
      title: 'Malpractice Visibility to Only Admin',
      desc: 'When off (default), teachers and parents can see exam violation/integrity reports as today. When on, those sections show "Locked by Admin" until the admin releases visibility per-exam, to teachers and/or parents.',
    },
    {
      key: 'teachersCanEditGradedScores',
      title: 'Teachers Can Correct Graded Scores',
      desc: 'When off (default), only the school admin can correct a student\'s graded score. When on, teachers can also correct a question\'s score after grading — a reason is always required and every edit is logged and visible to the admin.',
    },
    {
      key: 'teachersCanSetRetakes',
      title: 'Teachers Can Allow Exam Retakes',
      desc: 'When on, teachers can assign digital exam retakes for students on exams they created. A reason is required, and the new graded score replaces the previous score. This requires Questions Set or Approved by Admin to be off, and turning that on switches this off.',
    },
  ];

  return (
    <div className={css.card}>
      <h2 className={css.cardTitle} style={{ marginBottom: 6 }}>Stakeholder Controls</h2>
      <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 14 }}>
        Fine-tune who is responsible for curriculum, exam scheduling, and violation visibility across
        your school. These are all opt-in — nothing changes for your school until you turn a switch on.
      </p>
      {error   && <div className={css.inlineError}   style={{ marginBottom: 10 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 10 }}>✓ {success}</div>}
      <div className={css.toggleList}>
        {rows.map(row => (
          <div key={row.key} className={css.toggleRow}>
            <div className={css.toggleInfo}>
              <div className={css.toggleTitle}>{row.title}</div>
              <div className={css.toggleDesc}>{row.desc}</div>
              {row.key === 'teachersCanSetRetakes' && settings.questionsApprovalRequired && <div className={css.toggleDesc} style={{ color: '#9a3412', fontWeight: 700 }}>Turn off Questions Set or Approved by Admin first.</div>}
              {row.warning && settings[row.key] && (
                <div className={css.toggleWarning}>{row.warning}</div>
              )}
            </div>
            <ToggleSwitch
              checked={!!settings[row.key]}
              disabled={!loaded || saving === row.key || (row.key === 'teachersCanSetRetakes' && !settings.teachersCanSetRetakes && !!settings.questionsApprovalRequired)}
              onChange={val => toggle(row.key, val)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SubAdminsTab({ onNavigate }) {
  const defaultApprovalSettings = {
    parentsInvite: true, teachersInvite: true, classesCreate: true,
    feesGenerateInvoices: true, calendarCreate: true, expensesRecord: true, payrollProcess: true,
  };
  const [subAdmins, setSubAdmins] = useState([]);
  const [actions, setActions] = useState([]);
  const [approvalSettings, setApprovalSettings] = useState(defaultApprovalSettings);
  const [candidates, setCandidates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const approvalRows = [
    ['parentsInvite', 'Parent Invites', 'Require approval before a sub-admin invites a parent.'],
    ['teachersInvite', 'Teacher Invites', 'Require approval before a sub-admin invites a teacher.'],
    ['classesCreate', 'Create Classes', 'Require approval before a sub-admin creates a class.'],
    ['feesGenerateInvoices', 'Generate Fee Invoices', 'Require approval before invoices are generated.'],
    ['calendarCreate', 'Create Calendar Events', 'Require approval before a calendar event is created.'],
    ['expensesRecord', 'Record Expenses', 'Require approval before a new expense is recorded.'],
    ['payrollProcess', 'Process Payroll', 'Require approval before a payroll run is processed.'],
  ];
  const load = useCallback(async () => {
    setLoading(true);
    const [adminsRes, settingsRes, actionsRes] = await Promise.all([
      schoolFetch('/school/sub-admins'), schoolFetch('/school/sub-admins/approval-settings'), schoolFetch('/school/sub-admins/actions?limit=50'),
    ]);
    if (adminsRes.ok) setSubAdmins(adminsRes.data.subAdmins || []);
    if (settingsRes.ok) setApprovalSettings(settingsRes.data.approvalSettings || {});
    if (actionsRes.ok) setActions(actionsRes.data.actions || []);
    if (!adminsRes.ok) setError(adminsRes.data?.error || 'Could not load sub-admins.');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const pendingActions = actions.filter(action => action.status === 'pending');
  const toggleApproval = async (key, value) => {
    setApprovalSettings(current => ({ ...current, [key]: value }));
    const result = await schoolFetch('/school/sub-admins/approval-settings', { method: 'PATCH', body: JSON.stringify({ [key]: value }) });
    if (!result.ok) {
      setApprovalSettings(current => ({ ...current, [key]: !value }));
      setError(result.data?.error || 'Could not save approval setting.');
    }
  };
  const openCreate = async () => {
    const result = await schoolFetch('/school/sub-admins/candidates');
    if (result.ok) { setCandidates(result.data.candidates || []); setShowModal(true); }
    else setError(result.data?.error || 'Could not load candidates.');
  };
  const confirmStatusChange = async (password) => {
    const status = statusTarget.status === 'suspended' ? 'active' : 'suspended';
    const result = await schoolFetch(`/school/sub-admins/${statusTarget._id}/status`, { method: 'PATCH', headers: { 'x-admin-password': password }, body: JSON.stringify({ status }) });
    if (result.ok) { setStatusTarget(null); flash(status === 'suspended' ? 'Sub-admin suspended.' : 'Sub-admin reactivated.'); load(); }
    return result;
  };
  const confirmRemove = async (password) => {
    const result = await schoolFetch(`/school/sub-admins/${removeTarget._id}`, { method: 'DELETE', headers: { 'x-admin-password': password } });
    if (result.ok) { setRemoveTarget(null); flash('Sub-admin removed.'); load(); }
    return result;
  };
  const confirmApprove = async (password) => {
    const result = await schoolFetch(`/school/sub-admins/actions/${approveTarget._id}/approve`, { method: 'POST', headers: { 'x-admin-password': password } });
    if (result.ok) { setApproveTarget(null); flash('Action approved.'); load(); }
    return result;
  };
  return (
    <div>
      <div className={css.cardHeader}>
        <div>
          <h2 className={css.cardTitle} style={{ fontSize: '1.15rem' }}>Sub-Admins</h2>
          <p style={{ fontSize: '0.82rem', color: '#90a4ae', margin: '4px 0 0' }}>Give trusted people focused access to the school dashboard.</p>
        </div>
        <button className={css.btnPrimary} onClick={openCreate}><AddIcon style={{ fontSize: '1rem' }} /> Add Sub-Admin</button>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 16 }}>{success}</div>}
      <div className={css.card}>
        <h2 className={css.cardTitle}>Approval Controls</h2>
        <p style={{ fontSize: '0.82rem', color: '#90a4ae', margin: '4px 0 16px' }}>Choose which important actions must wait for your approval. These controls affect sub-admins only.</p>
        <div className={css.toggleList}>{approvalRows.map(([key, title, desc]) => <div key={key} className={css.toggleRow}><div className={css.toggleInfo}><div className={css.toggleTitle}>{title}</div><div className={css.toggleDesc}>{desc}</div></div><ToggleSwitch checked={!!approvalSettings[key]} onChange={value => toggleApproval(key, value)} /></div>)}</div>
      </div>
      {pendingActions.length > 0 && (
        <div className={`${css.card} ${css.pendingApprovalGlow}`}>
          <h2 className={css.cardTitle}>Pending Approvals</h2>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Date</th><th>Sub-Admin</th><th>Action</th><th></th></tr></thead>
              <tbody>{pendingActions.map(action => (
                <tr key={action._id}>
                  <td>{new Date(action.createdAt).toLocaleString()}</td>
                  <td>{action.subAdminLabel}</td>
                  <td><SubAdminActionLabel action={action} onNavigate={onNavigate} /></td>
                  <td><div className={css.actionButtons}><button className={css.btnSecondary} onClick={() => setDetailsTarget(action)}>View details</button><button className={css.btnPrimary} onClick={() => setApproveTarget(action)}>Approve</button><button className={css.btnDanger} onClick={() => setRejectTarget(action)}>Reject</button></div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
      <div className={css.card}>
        <h2 className={css.cardTitle}>Managed Accounts</h2>
        {loading ? <SkeletonTableRows rows={4} cols={5} /> : subAdmins.length === 0 ? <div className={css.emptyState}><p className={css.emptyText}>No sub-admin accounts have been created.</p></div> : <div className={css.tableWrap}><table className={css.table}><thead><tr><th>Name</th><th>Role</th><th>Access</th><th>Status</th><th>Actions</th></tr></thead><tbody>{subAdmins.map(admin => <tr key={admin._id}><td>{admin.firstName} {admin.lastName}<div className={css.cellSub}>{admin.email}</div></td><td>{admin.roleTitle}</td><td>{admin.accessTabs.map(key => NAV.find(item => item.key === key)?.label || key).join(', ')}</td><td><StatusChip status={admin.status} /></td><td><div style={{ display: 'flex', gap: 4 }}><button className={css.iconBtn} title="Edit access" onClick={() => setEditTarget(admin)}><EditIcon style={{ fontSize: '1rem' }} /></button><button className={css.iconBtn} title={admin.status === 'suspended' ? 'Reactivate' : 'Suspend'} onClick={() => setStatusTarget(admin)}><LockIcon style={{ fontSize: '1rem' }} /></button><button className={css.iconBtn} title="Remove" onClick={() => setRemoveTarget(admin)}><DeleteIcon style={{ fontSize: '1rem' }} /></button></div></td></tr>)}</tbody></table></div>}
      </div>
      <div className={css.card}>
        <h2 className={css.cardTitle}>Activity Log</h2>
        <div className={css.tableWrap}><table className={css.table}><thead><tr><th>Date</th><th>Sub-Admin</th><th>Action</th><th>Status</th></tr></thead><tbody>{actions.map(action => <tr key={action._id}><td>{new Date(action.createdAt).toLocaleString()}</td><td>{action.subAdminLabel}</td><td><SubAdminActionLabel action={action} onNavigate={onNavigate} /></td><td><StatusChip status={action.status} /></td></tr>)}</tbody></table></div>
      </div>
      {showModal && <CreateSubAdminModal candidates={candidates} onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); flash('Sub-admin created.'); load(); }} />}
      {editTarget && <EditSubAdminAccessModal admin={editTarget} onClose={() => setEditTarget(null)} onSuccess={() => { setEditTarget(null); flash('Access updated.'); load(); }} />}
      {statusTarget && <DeleteConfirmModal
        title={statusTarget.status === 'suspended' ? 'Reactivate Sub-Admin' : 'Suspend Sub-Admin'}
        message={`${statusTarget.status === 'suspended' ? 'Reactivate' : 'Suspend'} ${statusTarget.firstName} ${statusTarget.lastName}'s access?`}
        confirmLabel={statusTarget.status === 'suspended' ? 'Reactivate' : 'Suspend'}
        danger={statusTarget.status !== 'suspended'}
        onCancel={() => setStatusTarget(null)}
        onConfirm={confirmStatusChange}
      />}
      {removeTarget && <DeleteConfirmModal
        title="Remove Sub-Admin"
        message={`Permanently remove ${removeTarget.firstName} ${removeTarget.lastName}'s sub-admin account? This cannot be undone.`}
        confirmLabel="Remove"
        onCancel={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />}
      {approveTarget && <DeleteConfirmModal
        title="Approve Action"
        message={`Approve "${approveTarget.actionKey}" submitted by ${approveTarget.subAdminLabel}?`}
        confirmLabel="Approve"
        danger={false}
        onCancel={() => setApproveTarget(null)}
        onConfirm={confirmApprove}
      />}
      {rejectTarget && <RejectSubAdminActionModal action={rejectTarget} onNavigate={onNavigate} onClose={() => setRejectTarget(null)} onDone={() => { setRejectTarget(null); flash('Action rejected.'); load(); }} />}
      {detailsTarget && <SubAdminActionDetailsModal action={detailsTarget} isOwner onClose={() => setDetailsTarget(null)} onApprove={() => { setDetailsTarget(null); setApproveTarget(detailsTarget); }} onReject={() => { setDetailsTarget(null); setRejectTarget(detailsTarget); }} />}
    </div>
  );
}

function CreateSubAdminModal({ candidates, onClose, onSuccess }) {
  const [mode, setMode] = useState('invite');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', roleTitle: '', accessTabs: ['overview'] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = key => event => setForm(current => ({ ...current, [key]: event.target.value }));
  const toggleTab = key => { if (key !== 'overview') setForm(current => ({ ...current, accessTabs: current.accessTabs.includes(key) ? current.accessTabs.filter(item => item !== key) : [...current.accessTabs, key] })); };
  const choose = candidate => { setSelected(candidate); const names = candidate.name.split(' '); setForm(current => ({ ...current, firstName: names[0], lastName: names.slice(1).join(' '), email: candidate.email || '' })); };
  const submit = async event => {
    event.preventDefault();
    setError('');
    if (mode !== 'invite' && !selected) { setError('Choose an existing teacher or staff member.'); return; }
    setSaving(true);
    const resolvedMode = mode === 'invite' ? 'invite' : (selected.actorType === 'staff' ? 'existing_staff' : 'existing_teacher');
    const result = await schoolFetch('/school/sub-admins', { method: 'POST', body: JSON.stringify({ ...form, mode: resolvedMode, sourceId: selected?.id }) });
    setSaving(false);
    if (result.ok) onSuccess(); else setError(result.data?.error || 'Could not create sub-admin.');
  };
  return <div className={css.modalOverlay} onClick={onClose}><div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}><h2 className={css.modalTitle}>Add Sub-Admin</h2><p className={css.modalSub}>Create a restricted admin account using the existing school access.</p>{error && <div className={css.inlineError}>{error}</div>}<form onSubmit={submit}><div className={css.formGroup}><label className={css.formLabel}>Account source</label><div className={css.segmentedControl}><button type="button" className={mode === 'invite' ? css.segmentedControlActive : ''} onClick={() => { setMode('invite'); setSelected(null); }}>New person</button><button type="button" className={mode !== 'invite' ? css.segmentedControlActive : ''} onClick={() => { setMode('existing'); setSelected(null); }}>Existing staff or teacher</button></div></div>{mode === 'invite' ? <div className={css.formRow}><div className={css.formGroup}><label className={css.formLabel}>First name</label><input className={css.formInput} value={form.firstName} onChange={set('firstName')} required /></div><div className={css.formGroup}><label className={css.formLabel}>Last name</label><input className={css.formInput} value={form.lastName} onChange={set('lastName')} required /></div></div> : <div className={css.formGroup}><label className={css.formLabel}>Choose existing person</label>{candidates.length === 0 ? <p style={{ fontSize: '0.82rem', color: '#90a4ae' }}>No eligible teachers or staff found.</p> : <div className={css.tableWrap} style={{ maxHeight: 220, overflowY: 'auto' }}><table className={css.table}><tbody>{candidates.map(candidate => <tr key={`${candidate.actorType}:${candidate.id}`} style={{ cursor: 'pointer', background: selected?.id === candidate.id ? '#eefafa' : undefined }} onClick={() => choose(candidate)}><td>{candidate.name}</td><td className={css.cellSub}>{candidate.meta}</td></tr>)}</tbody></table></div>}</div>}<div className={css.formGroup}><label className={css.formLabel}>Email</label><input className={css.formInput} type="email" value={form.email} onChange={set('email')} required /></div><div className={css.formGroup}><label className={css.formLabel}>Role title</label><input className={css.formInput} placeholder="e.g. Accounting Admin" value={form.roleTitle} onChange={set('roleTitle')} required /></div><div className={css.formGroup}><label className={css.formLabel}>Dashboard access</label><div className={css.checkGrid}>{NAV.filter(item => item.key !== 'sub-admins').map(item => <label key={item.key} className={css.checkLabel}><input className="schoolCheckbox" type="checkbox" checked={form.accessTabs.includes(item.key)} onChange={() => toggleTab(item.key)} />{item.label}</label>)}</div></div><div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose}>Cancel</button><button type="submit" className={css.btnPrimary} disabled={saving}>{saving ? 'Sending…' : 'Create & Send Invite'}</button></div></form></div></div>;
}

function EditSubAdminAccessModal({ admin, onClose, onSuccess }) {
  const [roleTitle, setRoleTitle] = useState(admin.roleTitle || '');
  const [accessTabs, setAccessTabs] = useState(() => [...new Set(['overview', ...(admin.accessTabs || [])])]);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toggleTab = key => { if (key !== 'overview') setAccessTabs(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key]); };
  const submit = async event => {
    event.preventDefault();
    if (!accessTabs.length) { setError('Choose at least one access section.'); return; }
    if (!password) { setError('Enter your admin password to confirm.'); return; }
    setError(''); setSaving(true);
    const result = await schoolFetch(`/school/sub-admins/${admin._id}`, { method: 'PATCH', headers: { 'x-admin-password': password }, body: JSON.stringify({ roleTitle, accessTabs }) });
    setSaving(false);
    if (result.ok) onSuccess(); else setError(result.data?.error || 'Could not update access.');
  };
  return <div className={css.modalOverlay} onClick={onClose}><div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}><h2 className={css.modalTitle}>Edit Access</h2><p className={css.modalSub}>{admin.firstName} {admin.lastName} — {admin.email}</p>{error && <div className={css.inlineError}>{error}</div>}<form onSubmit={submit}><div className={css.formGroup}><label className={css.formLabel}>Role title</label><input className={css.formInput} value={roleTitle} onChange={e => setRoleTitle(e.target.value)} required /></div><div className={css.formGroup}><label className={css.formLabel}>Dashboard access</label><div className={css.checkGrid}>{NAV.filter(item => item.key !== 'sub-admins').map(item => <label key={item.key} className={css.checkLabel}><input className="schoolCheckbox" type="checkbox" checked={accessTabs.includes(item.key)} onChange={() => toggleTab(item.key)} />{item.label}</label>)}</div></div><div className={css.formGroup}><label className={css.formLabel}>Your admin password</label><SchoolPasswordInput className={css.formInput} value={password} onChange={e => setPassword(e.target.value)} placeholder="Confirm with your password" /></div><div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button><button type="submit" className={css.btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button></div></form></div></div>;
}

function RejectSubAdminActionModal({ action, onNavigate, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async event => {
    event.preventDefault();
    if (!reason.trim()) { setError('Explain why this action is being rejected.'); return; }
    setError(''); setLoading(true);
    const { ok, data } = await schoolFetch(`/school/sub-admins/actions/${action._id}/reject`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
    setLoading(false);
    if (ok) onDone(); else setError(data.error || 'Failed to reject action.');
  };
  return <div className={css.modalOverlay} onClick={onClose}><div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={event => event.stopPropagation()}><h2 className={css.modalTitle} style={{ color: '#ef5350' }}>Reject Action</h2><p className={css.modalSub}><SubAdminActionLabel action={action} onNavigate={onNavigate} />Submitted by {action.subAdminLabel}</p>{error && <div className={css.inlineError}>{error}</div>}<form onSubmit={handleSubmit}><div className={css.formGroup}><label className={css.formLabel}>Reason *</label><textarea className={css.formTextarea} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this action being rejected?" /></div><div className={css.modalFooter}><button type="button" className={css.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button><button type="submit" className={css.btnDanger} disabled={loading}>{loading ? 'Saving…' : 'Reject'}</button></div></form></div></div>;
}

function ChangePasswordTab() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = key => event => setForm(current => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const result = await schoolFetch('/school/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (result.ok) {
        localStorage.removeItem('school-must-change-pwd');
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSuccess('Password changed successfully.');
      } else {
        setError(result.data?.error || result.data?.message || 'Password change failed.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={css.card}>
      <h2 className={css.cardTitle} style={{ marginBottom: 8 }}>Change Password</h2>
      <p style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: 20 }}>
        Update the password used to access this school administrator account.
      </p>
      {error && <div className={css.inlineError} style={{ marginBottom: 14 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 14 }}>✓ {success}</div>}
      <form onSubmit={handleSubmit}>
        <div className={css.formGroup}>
          <label className={css.formLabel}>Current Password</label>
          <SchoolPasswordInput className={css.formInput} value={form.currentPassword} onChange={set('currentPassword')} autoComplete="current-password" required />
        </div>
        <div className={css.formRow}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>New Password</label>
            <SchoolPasswordInput className={css.formInput} value={form.newPassword} onChange={set('newPassword')} autoComplete="new-password" minLength={8} required />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Confirm New Password</label>
            <SchoolPasswordInput className={css.formInput} value={form.confirmPassword} onChange={set('confirmPassword')} autoComplete="new-password" minLength={8} required />
          </div>
        </div>
        <button type="submit" className={css.btnPrimary} disabled={saving}>
          {saving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Change Password'}
        </button>
      </form>
    </div>
  );
}

function MidTermReportSettingsEditor({ onProfileUpdated }) {
  const [settings, setSettings] = useState({ enabled: false, title: '', columns: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Tracks the last successfully persisted settings so disabling the feature can
  // always succeed even if the in-progress (unsaved) column draft is incomplete.
  const lastGoodSettingsRef = useRef({ enabled: false, title: '', columns: [] });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await schoolFetch('/school/mid-term-report-settings');
    if (ok) {
      const loaded = data.settings || { enabled: false, title: '', columns: [] };
      setSettings(loaded);
      lastGoodSettingsRef.current = loaded;
    }
    else setMessage({ error: data?.error || 'Failed to load mid-term settings.' });
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const normalizeForSave = value => ({
    ...value,
    title: value.title.trim(),
    columns: value.columns.map(column => ({
      ...column,
      key: String(column.key || '').trim(),
      label: String(column.label || '').trim(),
      slot: Number(column.slot),
      percentage: Number(column.percentage),
    })),
  });

  const persist = async value => {
    const { ok, data } = await schoolFetch('/school/mid-term-report-settings', {
      method: 'PATCH',
      body: JSON.stringify(normalizeForSave(value)),
    });
    if (ok) {
      setSettings(data.settings);
      lastGoodSettingsRef.current = data.settings;
      onProfileUpdated?.({ midTermReportSettings: data.settings });
      setMessage({ success: 'Mid-term settings saved.' });
      return true;
    }
    setMessage({ error: data?.error || 'Failed to save mid-term settings.' });
    return false;
  };

  const updateColumn = (index, updates) => {
    setMessage(null);
    setSettings(current => ({
      ...current,
      columns: current.columns.map((column, columnIndex) => columnIndex === index ? { ...column, ...updates } : column),
    }));
  };

  const addColumn = () => {
    if (settings.columns.length >= 10) return;
    const usedSlots = new Set(settings.columns.map(column => Number(column.slot)));
    const slot = Array.from({ length: 10 }, (_, index) => index + 1).find(value => !usedSlots.has(value)) || 1;
    setMessage(null);
    setSettings(current => ({
      ...current,
      columns: [...current.columns, { key: `midterm_${Date.now()}`, label: `CA ${slot}`, slot, percentage: 0 }],
    }));
  };

  const removeColumn = index => {
    setMessage(null);
    setSettings(current => ({ ...current, columns: current.columns.filter((_, columnIndex) => columnIndex !== index) }));
  };

  const toggleEnabled = async enabled => {
    setMessage(null);
    if (enabled) {
      const total = settings.columns.reduce((sum, column) => sum + Number(column.percentage || 0), 0);
      if (!settings.title.trim() || total <= 0) {
        // Flip the switch visually so the configuration fields appear, but don't
        // persist yet — nothing is saved until a valid title/columns exist.
        setSettings(current => ({ ...current, enabled: true }));
        setMessage({ error: 'Enter a title and configure at least one column with a percentage above 0, then click Save to enable mid-term results.' });
        return;
      }
      const previous = settings;
      const next = { ...settings, enabled: true };
      setSaving(true);
      setSettings(next);
      const saved = await persist(next);
      if (!saved) setSettings(previous);
      setSaving(false);
      return;
    }

    // Disabling must always succeed, even if the in-progress column draft is
    // incomplete/invalid — persist the last known-good configuration with
    // enabled turned off instead of the possibly-invalid live draft.
    const previous = settings;
    setSaving(true);
    setSettings(current => ({ ...current, enabled: false }));
    const saved = await persist({ ...lastGoodSettingsRef.current, enabled: false });
    if (!saved) setSettings(previous);
    setSaving(false);
  };

  const save = async () => {
    setMessage(null);
    const value = normalizeForSave(settings);
    if (value.enabled && !value.title) {
      setMessage({ error: 'Enter a title before enabling mid-term results.' });
      return;
    }
    const total = value.columns.reduce((sum, column) => sum + column.percentage, 0);
    if (value.enabled && total <= 0) {
      setMessage({ error: 'Add at least one column with a percentage above 0.' });
      return;
    }
    setSaving(true);
    await persist(value);
    setSaving(false);
  };

  if (loading) return <div className={css.card}><SkeletonCards count={1} /></div>;

  const totalPercentage = settings.columns.reduce((sum, column) => sum + Number(column.percentage || 0), 0);
  const slotOptions = Array.from({ length: 10 }, (_, index) => index + 1);

  return (
    <div className={css.card} id="mid-term-report-settings">
      <h2 className={css.cardTitle} style={{ marginBottom: 6 }}>Mid-Term Result</h2>
      <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 14 }}>
        Release an interim result during the term without changing the full term report, its scales, or its assessment weights.
      </p>
      <div className={css.toggleList}>
        <div className={css.toggleRow}>
          <div className={css.toggleInfo}>
            <div className={css.toggleTitle}>Enable Mid-Term Result</div>
            <div className={css.toggleDesc}>Turn this on to configure custom CA headers and release a mid-term result to students and parents.</div>
          </div>
          <ToggleSwitch checked={settings.enabled} onChange={toggleEnabled} disabled={saving} />
        </div>
      </div>
      {message?.error && <div className={css.inlineError} style={{ marginTop: 14 }}>{message.error}</div>}
      {message?.success && <div className={css.inlineSuccess} style={{ marginTop: 14 }}>✓ {message.success}</div>}
      {settings.enabled && (
        <>
          <div className={css.formGroup} style={{ marginTop: 16 }}>
            <label className={css.formLabel}>Result Title</label>
            <input
              className={css.formInput}
              value={settings.title}
              maxLength={80}
              placeholder="e.g. Mid-Term Assessment Result"
              onChange={event => { setMessage(null); setSettings(current => ({ ...current, title: event.target.value })); }}
            />
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Column Header</th><th>CA Slot</th><th>Percentage (%)</th><th aria-label="Actions"></th></tr>
              </thead>
              <tbody>
                {settings.columns.map((column, index) => (
                  <tr key={column.key || index}>
                    <td><input className={css.formInput} value={column.label} maxLength={40} onChange={event => updateColumn(index, { label: event.target.value })} /></td>
                    <td>
                      <select className={css.formSelect} value={column.slot} onChange={event => updateColumn(index, { slot: Number(event.target.value) })}>
                        {slotOptions.map(slot => {
                          const usedByOther = settings.columns.some((item, itemIndex) => itemIndex !== index && Number(item.slot) === slot);
                          return <option key={slot} value={slot} disabled={usedByOther}>CA {slot}</option>;
                        })}
                      </select>
                    </td>
                    <td><input className={css.formInput} type="number" min="0" max="100" value={column.percentage} onChange={event => updateColumn(index, { percentage: event.target.value === '' ? 0 : Number(event.target.value) })} /></td>
                    <td><button type="button" className={css.iconBtn} title="Remove column" onClick={() => removeColumn(index)}><DeleteIcon style={{ fontSize: '1rem' }} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className={css.btnSecondary} onClick={addColumn} disabled={settings.columns.length >= 10}><AddIcon style={{ fontSize: '1rem' }} /> Add Column</button>
            <span style={{ padding: '6px 12px', borderRadius: 8, background: totalPercentage > 0 ? '#e8f5e9' : '#fff3e0', color: totalPercentage > 0 ? '#2e7d32' : '#e65100', fontSize: '0.82rem', fontWeight: 700 }}>Total: {totalPercentage}%</span>
            <button type="button" className={css.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Mid-Term Settings'}</button>
          </div>
        </>
      )}
    </div>
  );
}

function SettingsTab({ profile, onProfileUpdated }) {
  const [form, setForm] = useState({
    name:             profile?.name             || '',
    phone:            profile?.phone            || '',
    address:          profile?.address          || '',
    country:          profile?.country          || '',
    state:            profile?.state            || '',
    academicSession:  profile?.academicSession  || '',
    currentTerm:      profile?.currentTerm      || 'first',
    logo:             profile?.logo             || '',
    motto:            profile?.motto            || '',
    website:          profile?.website          || '',
  });
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState('');

  // Logo upload state
  const [logoUploadState, setLogoUploadState] = useState('idle'); // 'idle' | 'compressing' | 'uploading' | 'error'
  const [logoUploadError, setLogoUploadError] = useState('');
  const logoFileRef = useRef(null);

  // Grade scale state
  const DEFAULT_GRADE_SCALE = [
    { label: 'A', minScore: 70, maxScore: 100, gradeComment: 'Excellent performance' },
    { label: 'B', minScore: 60, maxScore: 69,  gradeComment: 'Very good work' },
    { label: 'C', minScore: 50, maxScore: 59,  gradeComment: 'Good effort' },
    { label: 'D', minScore: 40, maxScore: 49,  gradeComment: 'Needs improvement' },
    { label: 'F', minScore: 0,  maxScore: 39,   gradeComment: 'Requires support' },
  ];
  const initialGradeScale = profile?.gradeScale?.length > 0
    ? profile.gradeScale.map(row => ({
      ...row,
      gradeComment: row.gradeComment || DEFAULT_GRADE_SCALE.find(defaultRow => defaultRow.label === row.label)?.gradeComment || '',
    }))
    : DEFAULT_GRADE_SCALE;
  const [gradeScale, setGradeScale]       = useState(initialGradeScale);
  const [gsLoading, setGsLoading]         = useState(false);
  const [gsSuccess, setGsSuccess]         = useState(false);
  const [gsError, setGsError]             = useState('');

  // Assessment weights state
  const [caWeight,   setCaWeight]   = useState(profile?.assessmentWeights?.ca   ?? 40);
  const [examWeight, setExamWeight] = useState(profile?.assessmentWeights?.exam ?? 60);
  const [awLoading,  setAwLoading]  = useState(false);
  const [awSuccess,  setAwSuccess]  = useState(false);
  const [awError,    setAwError]    = useState('');

  const set = k => e => { setSuccess(false); setForm(f => ({ ...f, [k]: e.target.value })); };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (logoFileRef.current) logoFileRef.current.value = '';
    if (!file) return;

    setLogoUploadError('');
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setLogoUploadState('error');
      setLogoUploadError(validation.error);
      return;
    }

    let finalFile = file;
    // Convert WebP/GIF to JPEG for PDF compatibility before any size compression
    const needsConvert = file.type === 'image/webp' || file.type === 'image/gif';
    if (needsConvert) {
      setLogoUploadState('compressing');
      try {
        finalFile = await convertToJpeg(file);
      } catch {
        setLogoUploadState('error');
        setLogoUploadError('Image conversion failed. Try JPEG or PNG format.');
        return;
      }
    }
    if (finalFile.size > 2 * 1024 * 1024) {
      setLogoUploadState('compressing');
      try {
        const result = await compressImageIfNeeded(finalFile, 2);
        finalFile = result.file;
      } catch {
        setLogoUploadState('error');
        setLogoUploadError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    setLogoUploadState('uploading');
    try {
      const token = localStorage.getItem('school-token');
      const formData = new FormData();
      formData.append('file', finalFile);

      const res = await fetch('/school/upload/logo', {
        method: 'POST',
        headers: { 'x-access-token': token || '' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setLogoUploadState('error');
        setLogoUploadError(data.error || 'Upload failed. Please try again.');
        return;
      }
      setForm(f => ({ ...f, logo: data.url }));
      if (onProfileUpdated && data.school) onProfileUpdated(data.school);
      setLogoUploadState('idle');
    } catch {
      setLogoUploadState('error');
      setLogoUploadError('Upload failed. Check your connection.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false);
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/profile', {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      if (ok) {
        setSuccess(true);
        if (onProfileUpdated) onProfileUpdated(data.school);
      } else {
        setError(data.error || 'Failed to update profile.');
      }
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  const saveGradeScale = async () => {
    setGsError(''); setGsSuccess(false); setGsLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/grade-scale', {
        method: 'PATCH',
        body: JSON.stringify({ gradeScale }),
      });
      if (ok) { setGsSuccess(true); if (onProfileUpdated) onProfileUpdated(data.school); }
      else setGsError(data?.error || 'Failed to save grade scale.');
    } catch { setGsError('Connection error.'); }
    finally { setGsLoading(false); }
  };

  const saveAssessmentWeights = async () => {
    if (caWeight + examWeight !== 100) { setAwError('CA and Exam weights must add up to 100%.'); return; }
    setAwError(''); setAwSuccess(false); setAwLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/assessment-weights', {
        method: 'PATCH',
        body: JSON.stringify({ ca: caWeight, exam: examWeight }),
      });
      if (ok) {
        setAwSuccess(true);
        if (onProfileUpdated) onProfileUpdated({ assessmentWeights: { ca: caWeight, exam: examWeight } });
      } else setAwError(data?.error || 'Failed to save weights.');
    } catch { setAwError('Connection error.'); }
    finally { setAwLoading(false); }
  };

  const updateGradeRow = (idx, field, val) => {
    setGsSuccess(false);
    setGradeScale(prev => prev.map((row, i) => i === idx ? {
      ...row,
      [field]: field === 'label' || field === 'gradeComment' ? val : Number(val),
    } : row));
  };

  const sub = profile?.subscription || {};
  const copyCode = () => {
    if (profile?.schoolCode) navigator.clipboard.writeText(profile.schoolCode).catch(() => {});
  };

  return (
    <div>
      {/* ── Editable Profile ───────────────────────────── */}
      <div className={css.card}>
        <h2 className={css.cardTitle} style={{ marginBottom: 20 }}>School Profile</h2>
        {error   && <div className={css.inlineError}   style={{ marginBottom: 14 }}>{error}</div>}
        {success && <div className={css.inlineSuccess} style={{ marginBottom: 14 }}>✓ Profile updated successfully.</div>}
        <form onSubmit={handleSubmit}>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>School Name *</label>
              <input className={css.formInput} required value={form.name} onChange={set('name')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Address</label>
            <input className={css.formInput} value={form.address} onChange={set('address')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.country} onChange={set('country')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>State / Province</label>
              <input className={css.formInput} value={form.state} onChange={set('state')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Academic Session</label>
              <input className={css.formInput} value={form.academicSession} onChange={set('academicSession')} placeholder="e.g. 2025/2026" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Current Term</label>
              <select className={css.formSelect} value={form.currentTerm} onChange={set('currentTerm')}>
                <option value="first">First Term</option>
                <option value="second">Second Term</option>
                <option value="third">Third Term</option>
              </select>
            </div>
          </div>
          {/* ── School Logo ── */}
          <div className={css.formGroup}>
            <label className={css.formLabel}>School Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
              {form.logo && (
                <img
                  src={form.logo}
                  alt="logo preview"
                  style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8, background: '#f5f7fa', border: '1px solid #e0e8ea', flexShrink: 0 }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={handleLogoFileChange}
                />
                <button
                  type="button"
                  className={css.btnSecondary}
                  disabled={logoUploadState === 'compressing' || logoUploadState === 'uploading'}
                  onClick={() => { setLogoUploadError(''); setLogoUploadState('idle'); logoFileRef.current?.click(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <UploadIcon style={{ fontSize: '1rem' }} />
                  {logoUploadState === 'compressing' ? 'Compressing image…'
                    : logoUploadState === 'uploading' ? 'Uploading…'
                    : form.logo ? 'Replace Logo'
                    : 'Upload Logo'}
                </button>
                {logoUploadError && (
                  <div style={{ color: '#e53935', fontSize: '0.78rem', marginTop: 4 }}>{logoUploadError}</div>
                )}
                <div style={{ fontSize: '0.75rem', color: '#90a4ae', marginTop: 4 }}>
                  JPEG, PNG, WebP or GIF · max 2 MB. Large images are auto-compressed.
                </div>
              </div>
              {/* Fallback: allow manual URL entry */}
              <input
                className={css.formInput}
                value={form.logo}
                onChange={set('logo')}
                placeholder="or paste URL"
                style={{ width: '100%', marginTop: 6 }}
              />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Motto / Tagline</label>
              <input className={css.formInput} value={form.motto} onChange={set('motto')} placeholder="e.g. Knowledge, Character, Excellence" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Website</label>
              <input className={css.formInput} value={form.website} onChange={set('website')} placeholder="https://yourschool.edu.ng" />
            </div>
          </div>
          <div className={css.modalFooter} style={{ paddingTop: 8, paddingLeft: 0, paddingRight: 0 }}>
            <button type="submit" className={css.btnPrimary} disabled={loading}>
              {loading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Student Enrolment Code ──────────────────────── */}
      {profile?.schoolCode && (
        <div className={css.card}>          <h2 className={css.cardTitle} style={{ marginBottom: 14 }}>Student Self-Enrolment</h2>
          <p style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: 12 }}>Share this code with students to let them self-enrol via the school login page.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 800, fontSize: '1.4rem', color: '#3e8f93', letterSpacing: '0.1em', padding: '8px 16px', background: '#f0fafb', borderRadius: 10, border: '1.5px dashed #5fbec5' }}>
              {profile.schoolCode}
            </div>
            <button className={css.btnSecondary} onClick={copyCode}>Copy Code</button>
          </div>
        </div>
      )}

      {/* ── Grade Scale Editor ──────────────────────────── */}
      <div className={css.card}>
        <h2 className={css.cardTitle} style={{ marginBottom: 6 }}>Grade Scale</h2>
        <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 14 }}>
          Define the grade bands used in student report cards. The grade is assigned based on total score.
        </p>
        {gsError   && <div className={css.inlineError}   style={{ marginBottom: 10 }}>{gsError}</div>}
        {gsSuccess && <div className={css.inlineSuccess} style={{ marginBottom: 10 }}>✓ Grade scale saved.</div>}
        <div className={css.tableWrap}>
        <table className={css.table} style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f0f5f7' }}>
              <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#546e7a' }}>Grade Label</th>
              <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#546e7a' }}>Min Score (%)</th>
              <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#546e7a' }}>Max Score (%)</th>
              <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#546e7a' }}>Grade Comment</th>
              <th style={{ padding: '7px 10px' }}></th>
            </tr>
          </thead>
          <tbody>
            {gradeScale.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eef2f5' }}>
                <td style={{ padding: '6px 10px' }}>
                  <input
                    style={{ width: 70, padding: '4px 8px', border: '1px solid #d0dce0', borderRadius: 6 }}
                    value={row.label}
                    onChange={e => updateGradeRow(i, 'label', e.target.value)}
                    placeholder="e.g. A"
                  />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <input
                    type="number" min={0} max={100}
                    style={{ width: 80, padding: '4px 8px', border: '1px solid #d0dce0', borderRadius: 6 }}
                    value={row.minScore}
                    onChange={e => updateGradeRow(i, 'minScore', e.target.value)}
                  />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <input
                    type="number" min={0} max={100}
                    style={{ width: 80, padding: '4px 8px', border: '1px solid #d0dce0', borderRadius: 6 }}
                    value={row.maxScore}
                    onChange={e => updateGradeRow(i, 'maxScore', e.target.value)}
                  />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <input
                    style={{ width: 230, maxWidth: '100%', padding: '4px 8px', border: '1px solid #d0dce0', borderRadius: 6 }}
                    value={row.gradeComment || ''}
                    onChange={e => updateGradeRow(i, 'gradeComment', e.target.value)}
                    placeholder="e.g. Excellent performance"
                  />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <button
                    onClick={() => setGradeScale(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: '1rem' }}
                    title="Remove"
                  >✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className={css.btnSecondary}
            onClick={() => setGradeScale(prev => [...prev, { label: '', minScore: 0, maxScore: 100, gradeComment: '' }])}
          >+ Add Grade</button>
          <button className={css.btnPrimary} onClick={saveGradeScale} disabled={gsLoading}>
            {gsLoading ? 'Saving…' : 'Save Grade Scale'}
          </button>
        </div>
      </div>

      {/* ── Assessment Weights ─────────────────────────── */}
      <div className={css.card}>
        <h2 className={css.cardTitle} style={{ marginBottom: 6 }}>Assessment Weights</h2>
        <p style={{ fontSize: '0.83rem', color: '#546e7a', marginBottom: 14 }}>
          Set the percentage contribution of Continuous Assessment (CA) and the end-of-term Exam to
          the total score on student report cards. The two values must add up to <strong>100%</strong>.
        </p>
        {awError   && <div className={css.inlineError}   style={{ marginBottom: 10 }}>{awError}</div>}
        {awSuccess && <div className={css.inlineSuccess} style={{ marginBottom: 10 }}>✓ Assessment weights saved.</div>}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#546e7a', marginBottom: 6 }}>CA Weight (%)</div>
            <input
              type="number" min={0} max={100}
              style={{ width: 90, padding: '6px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.95rem', fontWeight: 600 }}
              value={caWeight}
              onChange={e => { setAwSuccess(false); setCaWeight(Math.max(0, Math.min(100, Number(e.target.value)))); }}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#546e7a', marginBottom: 6 }}>Exam Weight (%)</div>
            <input
              type="number" min={0} max={100}
              style={{ width: 90, padding: '6px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.95rem', fontWeight: 600 }}
              value={examWeight}
              onChange={e => { setAwSuccess(false); setExamWeight(Math.max(0, Math.min(100, Number(e.target.value)))); }}
            />
          </div>
          <div style={{
            padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem',
            background: caWeight + examWeight === 100 ? '#e8f5e9' : '#fff3e0',
            color: caWeight + examWeight === 100 ? '#2e7d32' : '#e65100',
            border: `1.5px solid ${caWeight + examWeight === 100 ? '#81c784' : '#ffb74d'}`,
          }}>
            Total: {caWeight + examWeight}%{caWeight + examWeight === 100 ? ' ✓' : ' — must equal 100'}
          </div>
        </div>
        <button
          className={css.btnPrimary}
          onClick={saveAssessmentWeights}
          disabled={awLoading || caWeight + examWeight !== 100}
        >
          {awLoading ? 'Saving…' : 'Save Weights'}
        </button>
      </div>

      <ReportCardDesignEditor onProfileUpdated={onProfileUpdated} />
      <SkillsReportSettingsEditor profile={profile} onProfileUpdated={onProfileUpdated} />
      <MidTermReportSettingsEditor onProfileUpdated={onProfileUpdated} />

      <StakeholderControlsEditor profile={profile} onProfileUpdated={onProfileUpdated} />

      {/* ── Subscription Info (read-only) ───────────────── */}
      <div className={css.card}>
        <h2 className={css.cardTitle} style={{ marginBottom: 14 }}>Subscription & Limits</h2>
        <p style={{ fontSize: '0.8rem', color: '#90a4ae', marginBottom: 14 }}>To change your plan, contact Bubble Ai support.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: '0.875rem' }}>
          {[
            ['Plan',                    sub.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : '—'],
            ['Max Students',            sub.maxStudents ?? '—'],
            ['Max Teachers',            sub.maxTeachers ?? '—'],
            ['Max Exams / Term',        sub.maxExamsPerTerm ?? '—'],
            ['AI Question Generation',  sub.aiQuestionsEnabled ? 'Enabled' : 'Disabled'],
            ['CSV / PDF Exports',       sub.exportEnabled ? 'Enabled' : 'Disabled'],
            ['Advanced Analytics',      sub.advancedAnalyticsEnabled ? 'Enabled' : 'Disabled'],
            ['Plan Start',              sub.startDate ? new Date(sub.startDate).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
            ['Plan Expiry',             sub.endDate   ? new Date(sub.endDate).toLocaleDateString('en-NG',   { day: 'numeric', month: 'short', year: 'numeric' }) : 'No expiry'],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
              <div style={{ color: '#0C1E28', fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>
        {/* Storage usage bar */}
        <div style={{ marginTop: 20 }}>
          <StorageBar subscription={sub} />
        </div>
      </div>
    </div>
  );
}

// ─── Prebuilt exam catalog ────────────────────────────────────────────────────
const PREBUILT_EXAMS = PREBUILT_EXAM_CATALOG;

// ─── Special Symbols Modal ────────────────────────────────────────────────────
function SpecialSymbolsModal({ onInsert, onClose }) {
  const [search, setSearch] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('All');
  const [copiedSymbol, setCopiedSymbol] = React.useState(null);
  const categories = React.useMemo(() => ['All', ...Array.from(new Set(SPECIAL_SYMBOLS.map(s => s.category)))], []);
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return SPECIAL_SYMBOLS.filter(s => {
      const matchesSearch = !q || s.name.toLowerCase().includes(q) || s.symbol === search.trim();
      const matchesCategory = activeCategory === 'All' || s.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);
  const handleSymbol = (s) => {
    navigator.clipboard.writeText(s.symbol).catch(() => {});
    onInsert(s.symbol);
    setCopiedSymbol(s.symbol);
    setTimeout(() => setCopiedSymbol(null), 1200);
  };
  return (
    <div className={css.symOverlay} onClick={onClose}>
      <div className={`${css.symModal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.symHeader}>
          <span className={css.symTitle}>Special Symbols</span>
          <button className={css.symCloseBtn} type="button" onClick={onClose}>✕</button>
        </div>
        <div className={css.symSearchRow}>
          <input className={css.symSearchInput} placeholder="Search by name (e.g. alpha, integral…)" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        </div>
        <div className={css.symCategoryTabs}>
          {categories.map(cat => (
            <button key={cat} type="button" className={`${css.symCatTab} ${activeCategory === cat ? css.symCatTabActive : ''}`} onClick={() => setActiveCategory(cat)}>{cat}</button>
          ))}
        </div>
        <div className={css.symGrid}>
          {filtered.length === 0 ? <div className={css.symEmpty}>No symbols match.</div> : filtered.map((s, i) => (
            <button key={i} type="button" className={`${css.symBtn} ${copiedSymbol === s.symbol ? css.symBtnCopied : ''}`} onClick={() => handleSymbol(s)} title={s.name}>{s.symbol}</button>
          ))}
        </div>
        {copiedSymbol && <div className={css.symCopiedMsg}>"{copiedSymbol}" copied &amp; inserted!</div>}
      </div>
    </div>
  );
}

// ─── Admin Question Row ───────────────────────────────────────────────────────
function AdminQuestionRow({ q, index, onChange, onRemove, equalMarks, autoScore, examId }) {
  const set = k => e => onChange(index, k, e.target.value);
  const [showScoreLock, setShowScoreLock] = React.useState(false);
  const [diagramUploadState, setDiagramUploadState] = React.useState('idle');
  const [diagramUploadError, setDiagramUploadError] = React.useState('');
  const [converting, setConverting] = React.useState(false);
  const [convertError, setConvertError] = React.useState('');
  const conversionCacheRef = React.useRef({ mcq: null, theory: null });
  const diagramFileRef = React.useRef(null);
  const qTextareaRef = React.useRef(null);
  const lastSelRef = React.useRef({ start: 0, end: 0 });
  const [symModalOpen, setSymModalOpen] = React.useState(false);
  const isTheory = q.questionType === 'theory';

  const handleTypeSwitch = async (targetType) => {
    if (targetType === q.questionType || converting) return;
    conversionCacheRef.current[q.questionType] = { ...q };
    const cached = conversionCacheRef.current[targetType];
    if (cached) { onChange(index, '__replace__', cached); return; }
    if (!q.question_text?.trim()) {
      onChange(index, 'questionType', targetType);
      if (targetType === 'theory') { onChange(index, 'options', []); onChange(index, 'correct_answer', ''); }
      else { onChange(index, 'options', ['', '', '', '']); onChange(index, 'marking_guide', ''); onChange(index, 'model_answer', ''); }
      return;
    }
    if (!examId) { onChange(index, 'questionType', targetType); return; }
    setConverting(true); setConvertError('');
    try {
      const { ok, data } = await schoolFetch(`/school/exams/${examId}/convert-question`, {
        method: 'POST', body: JSON.stringify({ question: q, targetType }),
      });
      if (!ok || !data.question) { setConvertError(data.error || 'Conversion failed. Please try again.'); return; }
      conversionCacheRef.current[targetType] = data.question;
      onChange(index, '__replace__', data.question);
    } catch { setConvertError('Connection error during conversion.'); }
    finally { setConverting(false); }
  };

  const handleDiagramFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (diagramFileRef.current) diagramFileRef.current.value = '';
    if (!file) return;
    setDiagramUploadState('idle'); setDiagramUploadError('');
    const validation = validateImageFile(file, 2);
    if (!validation.valid) { setDiagramUploadError(validation.error); return; }
    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      setDiagramUploadState('compressing');
      try { const result = await compressImageIfNeeded(file, 2); finalFile = result.file; }
      catch { setDiagramUploadState('error'); setDiagramUploadError('Image compression failed. Try a smaller image.'); return; }
    }
    setDiagramUploadState('uploading');
    try {
      const token = localStorage.getItem('school-token');
      const formData = new FormData();
      formData.append('file', finalFile);
      if (q.diagram) formData.append('oldUrl', q.diagram);
      const res = await fetch('/school/upload/question-diagram', { method: 'POST', headers: { 'x-access-token': token || '' }, body: formData });
      const data = await res.json();
      if (!res.ok || !data.url) { setDiagramUploadState('error'); setDiagramUploadError(data.error || 'Upload failed.'); return; }
      onChange(index, 'diagram', data.url);
      setDiagramUploadState('idle');
    } catch { setDiagramUploadState('error'); setDiagramUploadError('Upload failed. Check your connection.'); }
  };

  const handleDeleteDiagram = async () => {
    const url = q.diagram;
    onChange(index, 'diagram', '');
    setDiagramUploadError('');
    if (url && url.includes('/school-assets?key=')) {
      const token = localStorage.getItem('school-token');
      fetch('/school/delete/question-diagram', { method: 'POST', headers: { 'x-access-token': token || '', 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) }).catch(() => {});
    }
  };

  const isUploading = diagramUploadState === 'compressing' || diagramUploadState === 'uploading';

  return (
    <div className={css.questionCard}>
      <div className={css.questionHeader}>
        <span className={css.questionNum}>Q{index + 1}</span>
        <div className={css.qTypeToggle}>
          <button type="button" className={`${css.qTypeBtn} ${!isTheory ? css.qTypeBtnActive : ''}`} onClick={() => handleTypeSwitch('mcq')} disabled={converting}>MCQ</button>
          <button type="button" className={`${css.qTypeBtn} ${isTheory ? css.qTypeBtnActive : ''}`} onClick={() => handleTypeSwitch('theory')} disabled={converting}>{converting ? 'converting…' : 'Theory'}</button>
        </div>
        <button className={css.iconBtn} onClick={() => onRemove(index)} title="Remove question"><DeleteIcon fontSize="small" /></button>
      </div>
      {q.instructions ? <div className={css.qInstructions}>{q.instructions}</div> : null}
      {q.diagram ? <img src={q.diagram} alt="diagram" className={css.qDiagram} /> : null}
      <textarea ref={qTextareaRef} className={css.qInput} placeholder="Question text" value={q.question_text || ''} onChange={set('question_text')} rows={2} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6, marginBottom: 6 }}>
        <button type="button" className={css.symTriggerBtn} title="Insert special symbol" onMouseDown={() => { if (qTextareaRef.current) lastSelRef.current = { start: qTextareaRef.current.selectionStart, end: qTextareaRef.current.selectionEnd }; }} onClick={() => setSymModalOpen(true)}>Ω</button>
      </div>
      {symModalOpen && (
        <SpecialSymbolsModal
          onInsert={symbol => {
            const text = q.question_text || '';
            const { start, end } = lastSelRef.current;
            const newText = text.slice(0, start) + symbol + text.slice(end);
            onChange(index, 'question_text', newText);
            lastSelRef.current = { start: start + symbol.length, end: start + symbol.length };
            setTimeout(() => { if (qTextareaRef.current) { qTextareaRef.current.focus(); qTextareaRef.current.setSelectionRange(start + symbol.length, start + symbol.length); } }, 0);
          }}
          onClose={() => setSymModalOpen(false)}
        />
      )}
      {!isTheory ? (
        <>
          <div className={css.optionGrid}>
            {[0, 1, 2, 3].map(i => (
              <input key={i} className={css.optInput} placeholder={`Option ${String.fromCharCode(65 + i)}`} value={(q.options || [])[i] || ''} onChange={e => { const opts = [...(q.options || ['', '', '', ''])]; opts[i] = e.target.value; onChange(index, 'options', opts); }} />
            ))}
          </div>
          <div className={css.qRow}>
            <label className={css.qLabel}>Correct Answer</label>
            <select className={css.qSelect} value={q.correct_answer || ''} onChange={set('correct_answer')}>
              <option value="">— select —</option>
              {(q.options || []).filter(Boolean).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
            </select>
          </div>
        </>
      ) : (
        <>
          <div className={css.qRow}>
            <label className={css.qLabel}>Model Answer</label>
            <textarea className={css.qInput} placeholder="Full sample/model answer…" value={q.model_answer || q.correct_answer || ''} onChange={e => { onChange(index, 'model_answer', e.target.value); onChange(index, 'correct_answer', e.target.value); }} rows={3} />
          </div>
          <div className={css.qRow}>
            <label className={css.qLabel} style={{ color: '#7c3aed' }}>Marking Guide</label>
            <textarea className={css.qInput} placeholder="Numbered marking guide, e.g. 1. Definition (2 marks)…" value={q.marking_guide || ''} onChange={e => { onChange(index, 'marking_guide', e.target.value); onChange(index, 'markingGuideEdited', true); }} rows={4} style={{ borderColor: q.marking_guide ? '#a78bfa' : undefined }} />
          </div>
        </>
      )}
      {converting && <div style={{ fontSize: '0.78rem', color: '#7c3aed', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}><span className={css.aiSpinner} style={{ width: 12, height: 12 }} />Converting question with AI…</div>}
      {convertError && <div style={{ fontSize: '0.78rem', color: '#e53935', padding: '2px 0' }}>{convertError}</div>}
      <div className={css.qRow}>
        <label className={css.qLabel}>Topic</label>
        <input className={css.qInput2} placeholder="optional" value={q.topic || ''} onChange={set('topic')} />
        <label className={css.qLabel} style={{ marginLeft: 12 }}>Difficulty</label>
        <select className={css.qSelect} value={q.difficulty || ''} onChange={set('difficulty')}>
          <option value="">—</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>
      <div className={css.qRow} style={{ alignItems: 'center', gap: 6 }}>
        <label className={css.qLabel}>Diagram</label>
        <input className={css.qInput2} placeholder="https://… or upload →" value={q.diagram || ''} onChange={set('diagram')} style={{ flex: 1, minWidth: 120 }} />
        <input ref={diagramFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleDiagramFileChange} />
        <button type="button" className={css.qTypeBtn} disabled={isUploading} onClick={() => { setDiagramUploadState('idle'); setDiagramUploadError(''); diagramFileRef.current?.click(); }} style={{ whiteSpace: 'nowrap', padding: '6px 10px', fontSize: '0.82rem', flexShrink: 0 }}>
          {diagramUploadState === 'compressing' ? 'Compressing…' : diagramUploadState === 'uploading' ? 'Uploading…' : q.diagram ? '↑ Replace' : '↑ Upload'}
        </button>
        {q.diagram && !isUploading && <button type="button" onClick={handleDeleteDiagram} title="Remove diagram" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#dc143c', display: 'flex', alignItems: 'center', flexShrink: 0 }}><DeleteIcon fontSize="small" /></button>}
      </div>
      {diagramUploadError && <div style={{ color: '#e53935', fontSize: '0.78rem', marginTop: 2, marginLeft: 60 }}>{diagramUploadError}</div>}
      <div className={css.qRow}>
        <label className={css.qLabel}>Marks</label>
        <div style={{ position: 'relative' }}>
          <input className={`${css.qInput2} ${equalMarks ? css.scoreFieldLocked : ''}`} type="number" min="0" step="0.5" value={equalMarks ? (autoScore ?? '') : (q.score ?? '')} readOnly={equalMarks} onChange={equalMarks ? undefined : set('score')} onFocus={() => { if (equalMarks) { setShowScoreLock(true); setTimeout(() => setShowScoreLock(false), 3500); } }} style={{ width: 80, cursor: equalMarks ? 'not-allowed' : 'auto' }} />
          {showScoreLock && <div className={css.scoreLockTooltip}>Disable "Equal marks" to set individual scores</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Prebuilt Exam Picker Modal ───────────────────────────────────────────────
function PrebuiltExamPickerModal({ onSelect, onClose }) {
  return (
    <div className={css.pickerOverlay} onClick={onClose}>
      <div className={`${css.pickerModal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.pickerHeader}>
          <h2 className={css.pickerTitle}>Select Prebuilt Mock Exam</h2>
          <button className={css.closeBtn} onClick={onClose}>✕</button>
        </div>
        <p className={css.pickerSub}>
          Choose a platform mock exam. Students assigned this exam will take that standardised exam.
        </p>
        <div className={css.pickerGrid}>
          {PREBUILT_EXAMS.map(ex => (
            <div key={ex.slug} className={css.pickerCard}>
              <div className={css.pickerCardName}>{ex.examName}</div>
              <div className={css.pickerCardDesc}>{ex.description}</div>
              <button className={css.pickerSelectBtn} type="button" onClick={() => onSelect(ex)}>Select</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Custom Date-Time Picker ──────────────────────────────────────────────────
function DateTimePicker({ value, onChange, disabled, placeholder, min }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);
  const wrapRef = useRef(null);

  const parsed = value ? new Date(value) : null;
  const selYear  = parsed ? parsed.getFullYear() : null;
  const selMonth = parsed ? parsed.getMonth()    : null;
  const selDay   = parsed ? parsed.getDate()     : null;
  const selHour  = parsed ? String(parsed.getHours()).padStart(2, '0')   : '08';
  const selMin   = parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00';

  // Earliest selectable instant (e.g. the exam's start date, when picking the end date)
  const parsedMin = min ? new Date(min) : null;
  const minDate = parsedMin && Number.isFinite(parsedMin.getTime()) ? new Date(parsedMin) : null;
  if (minDate) {
    const hasSubMinute = minDate.getSeconds() > 0 || minDate.getMilliseconds() > 0;
    const roundedMinute = Math.ceil((minDate.getMinutes() + (hasSubMinute ? 1 : 0)) / 5) * 5;
    minDate.setSeconds(0, 0);
    minDate.setMinutes(roundedMinute);
  }
  const isMinDaySelected = !!(minDate && selYear === minDate.getFullYear() && selMonth === minDate.getMonth() && selDay === minDate.getDate());
  const minHour   = isMinDaySelected ? minDate.getHours()   : -1;
  const minMinute = isMinDaySelected ? minDate.getMinutes() : -1;

  useEffect(() => {
    if (open) { const d = parsed || new Date(); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const prevMonth = () => setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
  const nextMonth = () => setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
  const emit = (year, month, day, hh, mm) => { const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${hh}:${mm}`; onChange({ target: { value: iso } }); };
  // Rounds a time up to the next 5-minute mark so it lands on an available minute option
  const clampToMin = (hh, mm, dayMatchesMin) => {
    if (!minDate || !dayMatchesMin) return [hh, mm];
    const h = Number(hh), m = Number(mm);
    if (h > minDate.getHours() || (h === minDate.getHours() && m >= minDate.getMinutes())) return [hh, mm];
    let roundedMin = Math.ceil(minDate.getMinutes() / 5) * 5;
    let nextHour = minDate.getHours();
    if (roundedMin >= 60) { roundedMin = 0; nextHour += 1; }
    return [String(nextHour).padStart(2, '0'), String(roundedMin).padStart(2, '0')];
  };
  const handleDayClick = (day) => {
    if (minDate && new Date(viewYear, viewMonth, day, 23, 59, 59, 999) < minDate) return; // before the earliest allowed instant
    let hh = parsed ? String(parsed.getHours()).padStart(2, '0') : '08';
    let mm = parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00';
    const dayMatchesMin = !!(minDate && viewYear === minDate.getFullYear() && viewMonth === minDate.getMonth() && day === minDate.getDate());
    [hh, mm] = clampToMin(hh, mm, dayMatchesMin);
    emit(viewYear, viewMonth, day, hh, mm);
  };
  const handleHourChange = (e) => {
    if (!parsed) return;
    const [hh, mm] = clampToMin(e.target.value, String(parsed.getMinutes()).padStart(2, '0'), isMinDaySelected);
    emit(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), hh, mm);
  };
  const handleMinChange = (e) => {
    if (!parsed) return;
    const [hh, mm] = clampToMin(String(parsed.getHours()).padStart(2, '0'), e.target.value, isMinDaySelected);
    emit(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), hh, mm);
  };

  const today = new Date();
  const firstDay    = viewYear != null ? new Date(viewYear, viewMonth, 1).getDay() : 0;
  const daysInMonth = viewYear != null ? new Date(viewYear, viewMonth + 1, 0).getDate() : 0;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday    = (day) => viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate();
  const isSelected = (day) => selYear === viewYear && selMonth === viewMonth && selDay === day;
  const isDayDisabled = (day) => !!(minDate && new Date(viewYear, viewMonth, day, 23, 59, 59, 999) < minDate);
  const formatDisplay = () => { if (!parsed) return placeholder || 'Select date & time'; return parsed.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className={css.dtpWrap} ref={wrapRef}>
      <button type="button" className={[css.dtpTrigger, disabled ? css.dtpTriggerDisabled : ''].filter(Boolean).join(' ')} onClick={() => !disabled && setOpen(o => !o)} disabled={disabled}>
        <span style={{ flex: 1 }} className={parsed ? undefined : css.dtpPlaceholder}>{formatDisplay()}</span>
        <span className={css.dtpIcon}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </span>
      </button>
      {open && !disabled && (
        <div className={css.dtpPopup}>
          <div className={css.dtpHeader}>
            <button type="button" className={css.dtpNavBtn} onClick={prevMonth}>‹</button>
            <span className={css.dtpMonthLabel}>{viewMonth != null ? MONTHS[viewMonth] : ''} {viewYear}</span>
            <button type="button" className={css.dtpNavBtn} onClick={nextMonth}>›</button>
          </div>
          <div className={css.dtpDayLabels}>{DAY_NAMES.map(d => <span key={d}>{d}</span>)}</div>
          <div className={css.dtpGrid}>
            {cells.map((day, i) => day === null ? <span key={`e${i}`} /> : (
              <button type="button" key={day} className={[css.dtpDay, isSelected(day) ? css.dtpDaySelected : '', isToday(day) && !isSelected(day) ? css.dtpDayToday : '', isDayDisabled(day) ? css.dtpDayDisabled : ''].filter(Boolean).join(' ')} onClick={() => handleDayClick(day)} disabled={isDayDisabled(day)}>{day}</button>
            ))}
          </div>
          <div className={css.dtpTime}>
            <span className={css.dtpTimeLabel}>Time</span>
            <select className={css.dtpTimeSelect} value={selHour} onChange={handleHourChange} disabled={!parsed}>
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => <option key={h} value={h} disabled={isMinDaySelected && Number(h) < minHour}>{h}</option>)}
            </select>
            <span className={css.dtpTimeSep}>:</span>
            <select className={css.dtpTimeSelect} value={selMin} onChange={handleMinChange} disabled={!parsed}>
              {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => <option key={m} value={m} disabled={isMinDaySelected && Number(selHour) === minHour && Number(m) < minMinute}>{m}</option>)}
            </select>
          </div>
          <div className={css.dtpFooter}>
            <button type="button" className={css.dtpDoneBtn} onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

//  Helper: datetime-local conversion 
// Converts a stored UTC ISO timestamp into a "YYYY-MM-DDTHH:mm" string that
// represents the SAME instant in the current user's local timezone, matching
// the format the DateTimePicker widget reads/writes (built from local Date
// getters). Using toISOString() here would return the UTC wall-clock instead
// of the local one, shifting displayed times by the viewer's UTC offset.
function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Converts a local "YYYY-MM-DDTHH:mm" string (as produced by the
// DateTimePicker, with no timezone offset) into an absolute UTC ISO string
// for the server. `new Date(localStr)` parses the string using the browser's
// local timezone, so this preserves the exact instant the user picked
// regardless of server timezone.
function localDatetimeToISO(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  return isNaN(d) ? null : d.toISOString();
}

//  Admin Exam Performance Modal 
// Score range buckets for admin exam distribution chart
const SCORE_BUCKETS_ADMIN = [
  { label: '0–19%',   color: '#ef5350' },
  { label: '20–39%',  color: '#ff7043' },
  { label: '40–59%',  color: '#ffa726' },
  { label: '60–79%',  color: '#66bb6a' },
  { label: '80–100%', color: '#26a69a' },
];

const ADMIN_VIOLATION_META = {
  tab_switch:      { label: 'Tab switch',        color: '#e53935', bg: '#fce4ec' },
  window_blur:     { label: 'Window blur',        color: '#f57c00', bg: '#fff3e0' },
  copy_attempt:    { label: 'Copy attempt',       color: '#6d4c41', bg: '#efebe9' },
  paste_attempt:   { label: 'Paste attempt',      color: '#6d4c41', bg: '#efebe9' },
  dev_tools:       { label: 'Dev tools opened',   color: '#c62828', bg: '#ffcdd2' },
  right_click:     { label: 'Right click',        color: '#78909c', bg: '#eceff1' },
  fullscreen_exit: { label: 'Fullscreen exit',    color: '#ad1457', bg: '#fce4ec' },
  mouse_leave:     { label: 'Mouse left window',  color: '#00838f', bg: '#e0f7fa' },
  auto_submit:     { label: 'Auto-submitted',     color: '#b71c1c', bg: '#ffebee' },
  // legacy / alternate key names
  copy_paste:      { label: 'Copy/Paste',         color: '#6d4c41', bg: '#efebe9' },
  blur:            { label: 'Window blur',        color: '#f57c00', bg: '#fff3e0' },
  idle:            { label: 'Idle detected',      color: '#607d8b', bg: '#eceff1' },
  visibility:      { label: 'Tab hidden',         color: '#f57c00', bg: '#fff3e0' },
};

// ── Per-student exam detail drawer ──────────────────────────────────────────
function getStudyPlanResourceType(task) {
  const resource = task?.resource;
  const rawType = typeof resource === 'string'
    ? resource
    : resource?.resourceType || resource?.type || task?.resourceType || task?.type || '';
  const resourceType = String(rawType).toLowerCase();
  if (resourceType.includes('video')) return 'video';
  if (resourceType.includes('mock')) return 'mock';
  if (resourceType.includes('note')) return 'note';
  return null;
}

function SchoolAdminStudentExamDetailDrawer({ examId, membershipId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({ topics: true, questions: false, integrity: false, studyPlan: false });
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [studyDetail, setStudyDetail] = useState({ type: null, task: null, loading: false, content: null, error: '' });
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(null);
  const [editingScore, setEditingScore] = useState(null); // { question, newScore, reason, submitting, error }
  const [viewingScoreEdit, setViewingScoreEdit] = useState(null); // last scoreEdit record being viewed
  const [showRetakeModal, setShowRetakeModal] = useState(false);

  const loadData = React.useCallback(() => {
    setLoading(true);
    return schoolFetch(`/school/exams/${examId}/analytics/student/${membershipId}`).then(r => {
      if (r.ok) setData(r.data);
      else setError(r.data?.error || 'Failed to load');
      setLoading(false);
    });
  }, [examId, membershipId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openScoreEdit = (q) => setEditingScore({ question: q, newScore: String(q.scoreAwarded ?? 0), reason: '', submitting: false, error: null });

  const submitScoreEdit = async () => {
    if (!editingScore) return;
    const reason = editingScore.reason.trim();
    if (!reason) { setEditingScore(m => ({ ...m, error: 'A reason is required.' })); return; }
    setEditingScore(m => ({ ...m, submitting: true, error: null }));
    const { ok, data: resp } = await schoolFetch(`/school/exams/${examId}/analytics/student/${membershipId}/question-score`, {
      method: 'PATCH',
      body: JSON.stringify({ questionNumber: editingScore.question.number, newScore: editingScore.newScore, reason }),
    });
    if (ok) {
      setEditingScore(null);
      await loadData();
    } else {
      setEditingScore(m => ({ ...m, submitting: false, error: resp?.error || 'Could not save the score.' }));
    }
  };

  const toggle = key => setExpanded(s => ({ ...s, [key]: !s[key] }));
  const toggleSub = idx => setExpandedSubjects(s => ({ ...s, [idx]: !s[idx] }));

  const toggleParentRelease = async () => {
    if (!data?.exam) return;
    const nextVal = !data.exam.malpracticeRelease?.parent;
    setReleaseBusy(true);
    const { ok, data: resp } = await schoolFetch(`/school/exams/${examId}/malpractice-release`, {
      method: 'PATCH',
      body: JSON.stringify({ parent: nextVal, membershipId }),
    });
    setReleaseBusy(false);
    if (ok) setData(d => ({ ...d, exam: { ...d.exam, malpracticeRelease: resp.malpracticeRelease } }));
  };

  const toggleTeacherRelease = async () => {
    if (!data?.exam) return;
    const nextVal = !data.exam.malpracticeRelease?.teacher;
    setReleaseBusy(true);
    const { ok, data: resp } = await schoolFetch(`/school/exams/${examId}/malpractice-release`, {
      method: 'PATCH',
      body: JSON.stringify({ teacher: nextVal }),
    });
    setReleaseBusy(false);
    if (ok) setData(d => ({ ...d, exam: { ...d.exam, malpracticeRelease: resp.malpracticeRelease } }));
  };

  const openStudyTask = async task => {
    const type = getStudyPlanResourceType(task);
    if (!type) return;
    const title = task.resource?.title || task.title || '';
    setStudyDetail({ type, task, loading: true, content: null, error: '' });
    let url = `/school/exams/${examId}/study-plan/${type}`;
    const params = new URLSearchParams();
    if (type === 'video') params.set('q', title);
    if (type === 'note') {
      params.set('membershipId', membershipId);
      params.set('examObjectId', data?.examObjectId || '');
      params.set('title', title);
      params.set('resourceTitle', task.resource?.title || '');
    }
    if (type === 'mock') {
      params.set('membershipId', membershipId);
      params.set('topic', title);
    }
    const response = await schoolFetch(`${url}?${params}`);
    setStudyDetail(current => ({
      ...current,
      loading: false,
      content: response.ok ? response.data?.[type === 'mock' ? 'result' : type] : null,
      error: response.ok ? '' : (response.data?.error || 'Failed to load task details'),
    }));
  };

  const exportExcel = () => {
    if (!data) return;
    const { student, exam: examInfo, attempt, topicBreakdown, subjectBreakdown, questions } = data;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Field', 'Value'],
      ['Student', `${student.firstName} ${student.lastName}`],
      ['Class', `${student.className}${student.classArm ? ' ' + student.classArm : ''}`],
      ['Exam', examInfo.title],
      ['Score', `${attempt.score ?? ''}/${attempt.totalScore ?? ''}`],
      ['Percentage', `${attempt.percentage ?? ''}%`],
      ['Result', attempt.passed === true ? 'PASS' : attempt.passed === false ? 'FAIL' : ''],
      ['Time (min)', attempt.timeTaken != null ? Math.round(attempt.timeTaken / 60) : ''],
    ]), 'Summary');
    if (subjectBreakdown?.length > 1) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Subject', 'Score', 'Total', '%', 'Result'],
        ...subjectBreakdown.map(sb => [sb.subjectName, sb.score, sb.totalScore, `${sb.percentage}%`, sb.passed ? 'PASS' : 'FAIL']),
      ]), 'Subjects');
    }
    if (topicBreakdown?.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Topic', 'Correct Rate'],
        ...topicBreakdown.map(t => [t.topic, `${t.correctRate ?? t.avgCorrectRate ?? 0}%`]),
      ]), 'Topics');
    }
    if (questions?.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['#', 'Question', 'Correct', 'Your Answer', 'Correct Answer', 'Topic', 'Time(s)'],
        ...questions.map(q => [q.number, (q.questionText || '').slice(0, 80), q.isCorrect ? 'Yes' : 'No', q.chosenAnswer || '', q.correctAnswer || '', q.topic || '', q.timeTaken || '']),
      ]), 'Questions');
    }
    XLSX.writeFile(wb, `${(student.firstName || 'student').toLowerCase()}_exam.xlsx`);
  };

  const exportPDF = () => {
    if (!data) return;
    const { student, exam: examInfo, attempt, topicBreakdown, subjectBreakdown } = data;
    const eTitle = examInfo?.title || 'Exam';
    const studentName = `${student.firstName} ${student.lastName}`;
    const classLabel = `${student.className}${student.classArm ? ' · ' + student.classArm : ''}`;

    const studentRows = `<tr>
      <td>${studentName}</td>
      <td>${attempt.score ?? '—'}${attempt.totalScore != null ? '/' + attempt.totalScore : ''}</td>
      <td style="color:${(attempt.percentage ?? 0) >= 50 ? '#2e7d32' : '#c62828'};font-weight:700">${attempt.percentage != null ? attempt.percentage + '%' : '—'}</td>
      <td style="color:${attempt.passed ? '#2e7d32' : '#c62828'}">${attempt.passed === true ? 'Pass' : attempt.passed === false ? 'Fail' : '—'}</td>
      <td>${attempt.timeTaken != null ? Math.round(attempt.timeTaken / 60) + ' min' : '—'}</td>
    </tr>`;

    const subjectRows = (subjectBreakdown?.length > 1 ? subjectBreakdown : []).map(sb =>
      `<tr><td>${sb.subjectName}</td><td>${sb.score}/${sb.totalScore}</td><td style="color:${sb.passed ? '#2e7d32' : '#c62828'};font-weight:700">${sb.percentage}%</td><td style="color:${sb.passed ? '#2e7d32' : '#c62828'}">${sb.passed ? 'Pass' : 'Fail'}</td></tr>`
    ).join('');

    const topicRows = (topicBreakdown || []).map(t => {
      const pct = t.correctRate ?? t.avgCorrectRate ?? 0;
      const color = pct >= 70 ? '#2e7d32' : pct >= 40 ? '#f57c00' : '#c62828';
      return `<tr><td>${t.topic}</td><td style="color:${color};font-weight:700">${pct}%</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${eTitle} — ${studentName}</title>
<style>body{font-family:sans-serif;font-size:13px;color:#1a1a2e;padding:24px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#1a6e73;margin:20px 0 8px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px}th{background:#e0f4f5;font-weight:600}.stat{display:inline-block;margin:4px 16px 4px 0;font-weight:600}@media print{body{padding:12px}}</style>
</head><body>
<h1>${eTitle}</h1>
<p style="margin:0 0 12px;color:#607d8b">${studentName} · ${classLabel}</p>
<h2>Result</h2>
<table><thead><tr><th>Student</th><th>Score</th><th>%</th><th>Result</th><th>Time</th></tr></thead><tbody>${studentRows}</tbody></table>
${subjectRows ? `<h2>Per-Subject Breakdown</h2><table><thead><tr><th>Subject</th><th>Score</th><th>%</th><th>Result</th></tr></thead><tbody>${subjectRows}</tbody></table>` : ''}
${topicRows ? `<h2>Topic Breakdown</h2><table><thead><tr><th>Topic</th><th>Correct Rate</th></tr></thead><tbody>${topicRows}</tbody></table>` : ''}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const renderTopicPills = (topics) => {
    if (!topics?.length) return <p style={{ color: '#90a4ae', fontSize: '0.82rem' }}>No topic data.</p>;
    return (
      <div className={css.topicPillRow}>
        {topics.map(t => {
          const rate = t.correctRate ?? t.avgCorrectRate ?? 0;
          const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#e53935';
          return (
            <div key={t.topic} className={css.topicPill}>
              <span className={css.topicPillName}>{t.topic}</span>
              <span className={css.topicPillStat} style={{ color }}>{rate}%</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderQTable = (questions) => {
    if (!questions?.length) return <p style={{ color: '#90a4ae', fontSize: '0.82rem' }}>No question data.</p>;
    return (
      <div className={css.tableWrap}>
        <table className={css.table}>
          <thead><tr><th>#</th><th>Question</th><th>Result</th><th>Your Answer</th><th>Correct</th><th>Score</th><th>Topic</th><th>Time</th></tr></thead>
          <tbody>
            {questions.map((q, i) => (
              <tr key={i} className={q.isCorrect ? css.qRowCorrect : css.qRowIncorrect}>
                <td>{q.number}</td>
                <td className={css.qText}>{(q.questionText || '').slice(0, 60)}{(q.questionText || '').length > 60 ? '…' : ''}</td>
                <td>{q.isCorrect ? '✓' : '✗'}</td>
                <td><span className={css.answerPill} style={{ background: q.isCorrect ? '#e8f5e9' : '#fce4ec', color: q.isCorrect ? '#2e7d32' : '#c62828' }}>{q.chosenAnswer || '—'}</span></td>
                <td><span className={css.answerPill} style={{ background: '#e8f5e9', color: '#2e7d32' }}>{q.correctAnswer || '—'}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {q.scoreAwarded != null ? `${q.scoreAwarded}/${q.maxScore ?? '—'}` : '—'}
                  {q.lastScoreEdit && (
                    <button
                      type="button"
                      onClick={() => setViewingScoreEdit(q.lastScoreEdit)}
                      title="Corrected score — tap to view details"
                      style={{ marginLeft: 4, border: 'none', background: 'none', color: '#f57c00', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 4px' }}
                    >✎</button>
                  )}
                  <button
                    type="button"
                    onClick={() => openScoreEdit(q)}
                    disabled={['pending', 'in_progress', 'grading'].includes(data.attempt.retake?.status)}
                    title={data.attempt.retake?.status ? 'Score corrections are unavailable while a retake is active' : 'Edit score'}
                    style={{ marginLeft: 6, border: 'none', background: 'none', color: '#1a6e73', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
                  >Edit</button>
                </td>
                <td style={{ fontSize: '0.75rem', color: '#607d8b' }}>{q.topic || '—'}</td>
                <td style={{ fontSize: '0.75rem' }}>{q.timeTaken != null ? `${q.timeTaken}s` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSubjectSection = (sb) => {
    const sScore   = sb.score      != null ? Math.round(sb.score)      : null;
    const sTotal   = sb.totalScore != null ? Math.round(sb.totalScore) : null;
    const sPct     = sb.percentage != null ? Math.round(sb.percentage) : null;
    return (
      <div key={sb.subjectIdx} style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }} onClick={() => toggleSub(sb.subjectIdx)}>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0c1e28' }}>{sb.subjectName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={css.chip} style={{ background: sb.passed ? '#d1fae5' : '#fce4ec', color: sb.passed ? '#065f46' : '#b91c1c' }}>{sb.passed ? 'Pass' : 'Fail'}</span>
            <span style={{ color: '#b0bec5', fontSize: '0.8rem' }}>{expandedSubjects[sb.subjectIdx] ? '▲' : '▼'}</span>
          </div>
        </div>
        <div className={css.perfCardRow}>
          {[
            { label: 'Score', value: sScore != null ? `${sScore}${sTotal != null ? '/' + sTotal : ''}` : '—' },
            { label: 'Percentage', value: sPct != null ? `${sPct}%` : '—' },
            { label: 'Result', value: sb.passed ? 'PASS' : 'FAIL', color: sb.passed ? '#10b981' : '#e53935' },
          ].map(c => (
            <div key={c.label} className={css.perfCard}>
              <div className={css.perfCardLabel}>{c.label}</div>
              <div className={css.perfCardValue} style={{ fontSize: '1rem', ...(c.color ? { color: c.color } : {}) }}>{c.value}</div>
            </div>
          ))}
        </div>
        {expandedSubjects[sb.subjectIdx] && (
          <div style={{ marginTop: 8 }}>
            {sb.topicBreakdown?.length > 0 && <><div className={css.drawerSectionTitle} style={{ marginTop: 8 }}>Topics</div>{renderTopicPills(sb.topicBreakdown)}</>}
            {sb.questions?.length > 0 && <><div className={css.drawerSectionTitle} style={{ marginTop: 12 }}>Questions</div>{renderQTable(sb.questions)}</>}
            {/* Study Plan — shown per subject when available */}
            {data?.studyPlan && (() => {
              const plan = data.studyPlan;
              const schedule = Array.isArray(plan?.schedule) ? plan.schedule : null;
              if (!schedule) return null;
              const subjectSchedule = schedule
                .map(day => ({ ...day, tasks: (day.tasks || []).filter(task => task.subjectIdx === sb.subjectIdx) }))
                .filter(day => day.tasks.length > 0);
              const totalTasks = subjectSchedule.reduce((s, d) => s + d.tasks.length, 0);
              const doneTasks  = subjectSchedule.reduce((s, d) => s + d.tasks.filter(t => t.done || t.completed).length, 0);
              const pct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
              if (totalTasks === 0) return null;
              return (
                <div className={css.studyPlanSection} style={{ marginTop: 12 }}>
                  <div className={css.studyPlanToggle} onClick={e => { e.stopPropagation(); toggle('studyPlan'); }}>
                    <span>📚 Study Plan</span>
                    <span>{expanded.studyPlan ? '▲' : '▼'}</span>
                  </div>
                  {expanded.studyPlan && (
                    <div className={css.studyPlanBody}>
                      <div className={css.spTeacherProgress}>
                        <div className={css.spTeacherProgressBar}><div className={css.spTeacherProgressFill} style={{ width: `${pct}%` }} /></div>
                        <span className={css.spTeacherProgressLabel}>{doneTasks}/{totalTasks} tasks completed ({pct}%)</span>
                      </div>
                      {subjectSchedule.map((day, di) => (
                        <div key={di} className={css.spTeacherDay}>
                          <p className={css.spTeacherDayLabel}>{day.date || day.day || `Day ${di + 1}`}</p>
                          {(day.tasks || []).map((task, ti) => {
                            const isDone = task.done || task.completed || false;
                            const type = getStudyPlanResourceType(task);
                            const typeTag = type === 'video' ? 'Video' : type === 'note' ? 'Notes' : type === 'mock' ? 'Mock' : null;
                            return (
                              <div
                                key={ti}
                                className={`${teacherModalCss.spTeacherTask} ${isDone ? teacherModalCss.spTeacherTaskDone : ''} ${type ? teacherModalCss.spTeacherTaskClickable : ''}`}
                                onClick={type ? () => openStudyTask(task) : undefined}
                                style={type ? { cursor: 'pointer' } : undefined}
                              >
                                <span className={`${teacherModalCss.spTeacherDot} ${isDone ? teacherModalCss.spTeacherDotDone : ''}`} />
                                <div className={teacherModalCss.spTeacherTaskInfo}>
                                  <span className={teacherModalCss.spTeacherTaskTitle}>{task.title || task.task}</span>
                                  <span className={teacherModalCss.spTeacherTaskMeta}>
                                    {(task.timeAllocation || task.duration) && <span>{task.timeAllocation || task.duration} min</span>}
                                    {typeTag && <span className={teacherModalCss.spTeacherTypeTag}>{typeTag}</span>}
                                    {task.importance >= 4 && <span className={teacherModalCss.spTeacherStarTag}>★ Priority</span>}
                                    {type && <span className={teacherModalCss.spTeacherViewHint}>View ›</span>}
                                  </span>
                                </div>
                                <span className={`${teacherModalCss.spTeacherStatus} ${isDone ? teacherModalCss.spTeacherStatusDone : teacherModalCss.spTeacherStatusPending}`}>{isDone ? 'Done' : 'Pending'}</span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={css.drawerOverlay} onClick={onClose}>
      <div className={css.drawer} onClick={e => e.stopPropagation()}>
        <div className={css.drawerHeader}>
          <div>
            {data?.student
              ? <><div className={css.drawerTitle}>{data.student.firstName} {data.student.lastName}</div><div className={css.drawerSubtitle}>{data.student.className}{data.student.classArm ? ` · ${data.student.classArm}` : ''}{data.exam?.title ? ` · ${data.exam.title}` : ''}</div></>
              : <div className={css.drawerTitle}>{loading ? 'Loading…' : 'Student Detail'}</div>
            }
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#90a4ae', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div className={css.drawerBody}>
          {loading ? (
            <div className={css.drawerLoading}>Loading student detail…</div>
          ) : error ? (
            <div style={{ color: '#c62828', textAlign: 'center', padding: '24px 0', fontSize: '0.9rem' }}>{error}</div>
          ) : !data?.attempt ? (
            <div style={{ color: '#90a4ae', textAlign: 'center', padding: '32px 0', fontSize: '0.9rem' }}>This student has not attempted the exam.</div>
          ) : (
            <>
              {/* Exam badge */}
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 700, color: '#1a6e73', fontSize: '0.88rem' }}>{data.exam?.title}</span>
                {data.exam?.isMultiSubject && <span className={css.chip} style={{ marginLeft: 8, background: '#e0f4f5', color: '#0c7c84' }}>Multi-Subject</span>}
                <RetakeBadge count={data.attempt.retakeCount} retake={data.attempt.retake} />
                {data.exam?.canAssignRetake && data.attempt.retakeEligibility?.eligible && <button type="button" className={css.btnSecondary} style={{ marginLeft: 8, padding: '4px 9px', fontSize: '0.75rem' }} onClick={() => setShowRetakeModal(true)}>Allow retake</button>}
              </div>
              <div className={css.drawerSection}>
                <div className={css.drawerSectionTitle}>Retake history</div>
                <RetakeHistoryList history={data.attempt.retakeHistory || []} />
              </div>

              {/* Overall score cards */}
              {(() => {
                const isMultiSub = data.exam?.isMultiSubject && (data.subjectBreakdown?.length ?? 0) > 1;
                const computedEarned = isMultiSub
                  ? Math.round(data.subjectBreakdown.reduce((s, sb) => s + (sb.score || 0), 0))
                  : (data.attempt.score != null ? Math.round(data.attempt.score) : null);
                const computedTotal = isMultiSub
                  ? Math.round(data.subjectBreakdown.reduce((s, sb) => s + (sb.totalScore || 0), 0))
                  : (data.attempt.totalScore != null ? Math.round(data.attempt.totalScore) : null);
                const computedPct = computedEarned != null && computedTotal > 0
                  ? Math.round((computedEarned / computedTotal) * 100)
                  : (data.attempt.percentage != null ? Math.round(data.attempt.percentage) : null);
                return (
                  <div className={css.perfCardRow}>
                    {[
                      { label: 'Score', value: computedEarned != null ? `${computedEarned}${computedTotal != null ? '/' + computedTotal : ''}` : '—' },
                      { label: 'Percentage', value: computedPct != null ? `${computedPct}%` : '—' },
                      { label: 'Time Taken', value: data.attempt.timeTaken != null ? `${Math.round(data.attempt.timeTaken / 60)}m` : '—' },
                      { label: 'Result', value: data.attempt.passed === true ? 'PASS' : data.attempt.passed === false ? 'FAIL' : '—', color: data.attempt.passed === true ? '#10b981' : data.attempt.passed === false ? '#e53935' : '#607d8b' },
                    ].map(c => (
                      <div key={c.label} className={css.perfCard}>
                        <div className={css.perfCardLabel}>{c.label}</div>
                        <div className={css.perfCardValue} style={c.color ? { color: c.color } : {}}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Per-subject OR single-subject content */}
              {data.exam?.isMultiSubject && data.subjectBreakdown?.length > 1 ? (
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle}>Per-Subject Breakdown</div>
                  {data.subjectBreakdown.map(sb => renderSubjectSection(sb))}
                </div>
              ) : (
                <>
                  <div className={css.drawerSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }} onClick={() => toggle('topics')}>
                      <div className={css.drawerSectionTitle} style={{ borderBottom: 'none', margin: 0, padding: 0 }}>Topic Breakdown</div>
                      <span style={{ color: '#b0bec5', fontSize: '0.8rem' }}>{expanded.topics ? '▲' : '▼'}</span>
                    </div>
                    {expanded.topics && renderTopicPills(data.topicBreakdown)}
                  </div>
                  <div className={css.drawerSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }} onClick={() => toggle('questions')}>
                      <div className={css.drawerSectionTitle} style={{ borderBottom: 'none', margin: 0, padding: 0 }}>Question Analysis</div>
                      <span style={{ color: '#b0bec5', fontSize: '0.8rem' }}>{expanded.questions ? '▲' : '▼'}</span>
                    </div>
                    {expanded.questions && renderQTable(data.questions)}
                  </div>
                </>
              )}

              {/* Integrity Report */}
              {['manual', 'manual_scores'].includes(data.exam?.examMode) ? (
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle}>🔍 Integrity Report</div>
                  <p style={{ color: '#607d8b', fontSize: '0.82rem', margin: 0 }}>Manual exam taken; no integrity report is available.</p>
                </div>
              ) : data.attempt.proctoring && (() => {
                const p = data.attempt.proctoring;
                const totalViolations =
                  (p.tabSwitchCount     || 0) + (p.windowBlurCount    || 0) +
                  (p.copyAttemptCount   || 0) + (p.pasteAttemptCount  || 0) +
                  (p.devToolsCount      || 0) + (p.rightClickCount    || 0) +
                  (p.fullscreenExitCount|| 0) + (p.mouseLeaveCount    || 0);
                const riskBand =
                  p.riskScore >= 40 ? { label: 'High Risk',   color: '#c62828', bg: '#fce4ec' } :
                  p.riskScore >= 20 ? { label: 'Medium Risk', color: '#f57c00', bg: '#fff3e0' } :
                                      { label: 'Low Risk',    color: '#2e7d32', bg: '#e8f5e9' };
                const countRows = [
                  { key: 'tab_switch',      value: p.tabSwitchCount      },
                  { key: 'window_blur',     value: p.windowBlurCount     },
                  { key: 'copy_attempt',    value: p.copyAttemptCount    },
                  { key: 'paste_attempt',   value: p.pasteAttemptCount   },
                  { key: 'dev_tools',       value: p.devToolsCount       },
                  { key: 'right_click',     value: p.rightClickCount     },
                  { key: 'fullscreen_exit', value: p.fullscreenExitCount },
                  { key: 'mouse_leave',     value: p.mouseLeaveCount     },
                ].filter(r => r.value > 0);
                const violations = Array.isArray(p.violations) ? p.violations : [];
                return (
                  <div className={css.drawerSection}>
                    <div
                      className={css.drawerSectionTitle}
                      style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => toggle('integrity')}
                    >
                      <span>
                        🔍 Integrity Report
                        {p.flaggedForReview && (
                          <span style={{ marginLeft: 8, fontSize: '0.7rem', fontWeight: 700, background: '#fce4ec', color: '#c62828', borderRadius: 6, padding: '2px 7px', verticalAlign: 'middle' }}>FLAGGED</span>
                        )}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>{expanded.integrity ? '▲' : '▼'}</span>
                    </div>

                    {expanded.integrity && (
                      <>
                        {data.exam?.malpracticeAdminOnly && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: '#f0fafb', border: '1px solid #b2e0e3', borderRadius: 9, padding: '10px 14px', marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                              <span style={{ fontSize: '0.82rem', color: '#1a6e73' }}>
                                <strong>Malpractice visibility is admin-only.</strong> Release this report to the parent so they can view it.
                              </span>
                              <ToggleSwitch
                                checked={!!data.exam?.malpracticeRelease?.parent}
                                disabled={releaseBusy}
                                onChange={toggleParentRelease}
                              />
                            </div>
                            {data.exam?.hasTeacher && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: '1px dashed #b2e0e3', paddingTop: 8 }}>
                                <span style={{ fontSize: '0.82rem', color: '#1a6e73' }}>
                                  Also release this report to the <strong>teacher</strong> who set the exam.
                                </span>
                                <ToggleSwitch
                                  checked={!!data.exam?.malpracticeRelease?.teacher}
                                  disabled={releaseBusy}
                                  onChange={toggleTeacherRelease}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {/* Summary stat cards */}
                        <div className={css.perfCardRow} style={{ marginBottom: 16 }}>
                          <div className={css.perfCard} style={{ borderTopColor: riskBand.color, background: riskBand.bg }}>
                            <div className={css.perfCardLabel}>Risk Score</div>
                            <div className={css.perfCardValue} style={{ color: riskBand.color, fontSize: '1.3rem' }}>
                              {p.riskScore ?? 0}<span style={{ fontSize: '0.75rem', fontWeight: 500, color: riskBand.color }}>/100</span>
                            </div>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: riskBand.color, marginTop: 2 }}>{riskBand.label}</div>
                          </div>
                          <div className={css.perfCard}>
                            <div className={css.perfCardLabel}>Total Events</div>
                            <div className={css.perfCardValue} style={{ fontSize: '1.3rem' }}>{totalViolations}</div>
                          </div>
                          <div className={css.perfCard}>
                            <div className={css.perfCardLabel}>Timeline Entries</div>
                            <div className={css.perfCardValue} style={{ fontSize: '1.3rem' }}>{violations.length}</div>
                          </div>
                        </div>

                        {/* Per-type breakdown pills */}
                        {countRows.length > 0 && (
                          <div className={css.topicPillRow} style={{ marginBottom: 16 }}>
                            {countRows.map(({ key, value }) => {
                              const meta = ADMIN_VIOLATION_META[key] || { label: key, color: '#555', bg: '#f5f5f5' };
                              return (
                                <div key={key} className={css.topicPill} style={{ borderColor: meta.color, background: meta.bg }}>
                                  <span className={css.topicPillName} style={{ color: meta.color }}>{meta.label}</span>
                                  <span className={css.topicPillStat} style={{ color: meta.color }}>×{value}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Violation timeline */}
                        {violations.length > 0 ? (
                          <div className={css.tableWrap}>
                            <table className={css.table}>
                              <thead>
                                <tr><th>#</th><th>Event</th><th>Time</th><th>Detail</th><th>Screenshot</th></tr>
                              </thead>
                              <tbody>
                                {violations.map((v, i) => {
                                  const meta = ADMIN_VIOLATION_META[v.type] || { label: v.type, color: '#555', bg: '#f5f5f5' };
                                  return (
                                    <tr key={i}>
                                      <td style={{ color: '#90a4ae', fontSize: '0.78rem' }}>{i + 1}</td>
                                      <td><span className={css.answerPill} style={{ background: meta.bg, color: meta.color }}>{meta.label}</span></td>
                                      <td style={{ fontSize: '0.78rem', color: '#546e7a', whiteSpace: 'nowrap' }}>
                                        {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : '—'}
                                      </td>
                                      <td style={{ fontSize: '0.78rem', color: '#78909c' }}>{v.detail || '—'}</td>
                                      <td>
                                        {v.screenshotUrl
                                          ? <button type="button" onClick={() => setScreenshotModal(v.screenshotUrl)} style={{ color: '#5fbec5', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>📷 View</button>
                                          : <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>—</span>
                                        }
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.82rem', color: '#90a4ae', fontStyle: 'italic', margin: 0 }}>
                            No detailed violation timeline recorded.
                          </p>
                        )}

                        {totalViolations === 0 && (
                          <p style={{ fontSize: '0.82rem', color: '#43a047', fontStyle: 'italic', margin: '8px 0 0' }}>
                            ✓ No integrity events recorded for this attempt.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Study Plan — shown for single-subject exams at the bottom of the drawer */}
              {!data.exam?.isMultiSubject && data.studyPlan && (() => {
                const plan = data.studyPlan;
                const schedule = Array.isArray(plan?.schedule) ? plan.schedule : null;
                if (!schedule) return null;
                const totalTasks = schedule.reduce((s, d) => s + (d.tasks?.length || 0), 0);
                const doneTasks  = schedule.reduce((s, d) => s + (d.tasks?.filter(t => t.done || t.completed).length || 0), 0);
                const pct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
                return (
                  <div className={css.studyPlanSection}>
                    <div className={css.studyPlanToggle} onClick={() => toggle('studyPlan')}>
                      <span>📚 Assigned Study Plan</span>
                      <span>{expanded.studyPlan ? '▲' : '▼'}</span>
                    </div>
                    {expanded.studyPlan && (
                      <div className={css.studyPlanBody}>
                        <div className={css.spTeacherProgress}>
                          <div className={css.spTeacherProgressBar}><div className={css.spTeacherProgressFill} style={{ width: `${pct}%` }} /></div>
                          <span className={css.spTeacherProgressLabel}>{doneTasks}/{totalTasks} tasks completed ({pct}%)</span>
                        </div>
                        {schedule.map((day, di) => (
                          <div key={di} className={css.spTeacherDay}>
                            <p className={css.spTeacherDayLabel}>{day.date || day.day || `Day ${di + 1}`}</p>
                            {(day.tasks || []).map((task, ti) => {
                              const isDone = task.done || task.completed || false;
                              const type = getStudyPlanResourceType(task);
                              const typeTag = type === 'video' ? 'Video' : type === 'note' ? 'Notes' : type === 'mock' ? 'Mock' : null;
                              return (
                                <div
                                  key={ti}
                                  className={`${teacherModalCss.spTeacherTask} ${isDone ? teacherModalCss.spTeacherTaskDone : ''} ${type ? teacherModalCss.spTeacherTaskClickable : ''}`}
                                  onClick={type ? () => openStudyTask(task) : undefined}
                                  style={type ? { cursor: 'pointer' } : undefined}
                                >
                                  <span className={`${teacherModalCss.spTeacherDot} ${isDone ? teacherModalCss.spTeacherDotDone : ''}`} />
                                  <div className={teacherModalCss.spTeacherTaskInfo}>
                                    <span className={teacherModalCss.spTeacherTaskTitle}>{task.title || task.task}</span>
                                    <span className={teacherModalCss.spTeacherTaskMeta}>
                                      {(task.timeAllocation || task.duration) && <span>{task.timeAllocation || task.duration} min</span>}
                                      {typeTag && <span className={teacherModalCss.spTeacherTypeTag}>{typeTag}</span>}
                                      {task.importance >= 4 && <span className={teacherModalCss.spTeacherStarTag}>★ Priority</span>}
                                      {type && <span className={teacherModalCss.spTeacherViewHint}>View ›</span>}
                                    </span>
                                  </div>
                                  <span className={`${teacherModalCss.spTeacherStatus} ${isDone ? teacherModalCss.spTeacherStatusDone : teacherModalCss.spTeacherStatusPending}`}>{isDone ? 'Done' : 'Pending'}</span>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Meta */}
              <div className={css.drawerMeta}>
                {data.attempt.timeTaken != null && <span>{Math.round(data.attempt.timeTaken / 60)} min</span>}
                {data.attempt.submittedAt && <span>Submitted {new Date(data.attempt.submittedAt).toLocaleString()}</span>}
              </div>

              {/* Export buttons */}
              <div className={css.drawerExportRow}>
                <button className={css.drawerExportBtn} onClick={exportPDF}>🖨 Export PDF</button>
                <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={exportExcel}>📊 Export Excel</button>
              </div>
            </>
          )}
        </div>
      </div>

      {studyDetail.type && (
        <div className={teacherModalCss.tModalOverlay} onClick={() => setStudyDetail({ type: null, task: null, loading: false, content: null, error: '' })}>
          <div className={`${teacherModalCss.tModalBox} BubblePopIn`} onClick={event => event.stopPropagation()}>
            <div className={teacherModalCss.tModalHeader}>
              <div>
                <span className={teacherModalCss.tModalBadge}>
                  {studyDetail.type === 'video' ? '▶ Video' : studyDetail.type === 'note' ? '📄 Study Notes' : '🎯 Practice Mock'}
                </span>
                <h3 className={teacherModalCss.tModalTitle}>{studyDetail.task?.title || studyDetail.task?.task}</h3>
              </div>
              <button className={teacherModalCss.tModalClose} onClick={() => setStudyDetail({ type: null, task: null, loading: false, content: null, error: '' })}>✕</button>
            </div>
            <div className={teacherModalCss.tModalBody}>
              {studyDetail.loading && (
                <div className={teacherModalCss.tModalLoading}><span className={teacherModalCss.tModalSpinner} /><p>Loading…</p></div>
              )}
              {!studyDetail.loading && studyDetail.error && <p className={teacherModalCss.tModalError}>{studyDetail.error}</p>}
              {!studyDetail.loading && !studyDetail.error && studyDetail.type === 'video' && (
                studyDetail.content ? (
                  <div>
                    <div className={teacherModalCss.tModalVideoWrap}>
                      <iframe
                        src={`https://www.youtube.com/embed/${studyDetail.content.videoId}?rel=0`}
                        title={studyDetail.content.title || studyDetail.task?.title || 'Study video'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className={teacherModalCss.tModalVideoFrame}
                      />
                    </div>
                    <p className={teacherModalCss.tModalVideoTitle}>{studyDetail.content.title}</p>
                    <p className={teacherModalCss.tModalVideoChannel}>{studyDetail.content.channelTitle}</p>
                    <p className={teacherModalCss.tModalHint}>Read-only view — this is the video available for this study topic.</p>
                  </div>
                ) : <p className={teacherModalCss.tModalEmpty}>No video found for this topic.</p>
              )}
              {!studyDetail.loading && !studyDetail.error && studyDetail.type === 'note' && (
                studyDetail.content ? (
                  <div>
                    <div className={teacherModalCss.tModalNoteBody}>{renderMarkdownNote(studyDetail.content.content)}</div>
                    <p className={teacherModalCss.tModalHint}>Generated: {studyDetail.content.createdAt ? new Date(studyDetail.content.createdAt).toLocaleDateString() : '—'}</p>
                  </div>
                ) : <p className={teacherModalCss.tModalEmpty}>The student has not generated notes for this task yet.</p>
              )}
              {!studyDetail.loading && !studyDetail.error && studyDetail.type === 'mock' && (
                studyDetail.content ? (
                  <div>
                    <div className={teacherModalCss.tModalScoreCard} style={{ borderColor: studyDetail.content.passed ? '#a5d6a7' : '#ef9a9a', background: studyDetail.content.passed ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : 'linear-gradient(135deg,#fce4ec,#fef9fa)' }}>
                      <span className={teacherModalCss.tModalScorePct} style={{ color: studyDetail.content.passed ? '#2e7d32' : '#c62828' }}>{studyDetail.content.pct}%</span>
                      <span className={teacherModalCss.tModalScoreSub}>{studyDetail.content.score}/{studyDetail.content.total} correct · {studyDetail.content.passed ? 'Pass' : 'Needs Work'}</span>
                      {studyDetail.content.timeTaken > 0 && <span className={teacherModalCss.tModalScoreSub}>Time: {Math.floor(studyDetail.content.timeTaken / 60)}m {studyDetail.content.timeTaken % 60}s</span>}
                    </div>
                    {(studyDetail.content.questions || []).length > 0 && (
                      <div className={teacherModalCss.tModalQList}>
                        {studyDetail.content.questions.map((question, index) => (
                          <div key={index} className={`${teacherModalCss.tModalQItem} ${question.isCorrect ? teacherModalCss.tModalQCorrect : teacherModalCss.tModalQWrong}`}>
                            <div className={teacherModalCss.tModalQHeader}>
                              <span className={teacherModalCss.tModalQBadge}>{question.isCorrect ? '✓' : '✗'}</span>
                              <p className={teacherModalCss.tModalQText}><strong>Q{index + 1}.</strong> {question.questionText || question.question_text}</p>
                            </div>
                            <div className={teacherModalCss.tModalQAnswers}>
                              {!question.isCorrect && question.chosen && <span className={teacherModalCss.tModalQYours}>Student: {question.chosen}</span>}
                              <span className={teacherModalCss.tModalQCorrect2}>Correct: {question.correct || question.correct_answer}</span>
                            </div>
                            {question.explanation && <p className={teacherModalCss.tModalQExplanation}>{question.explanation}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={teacherModalCss.tModalEmpty}>
                    {studyDetail.task?.done || studyDetail.task?.completed
                      ? 'Practice was completed but no result data is available (may have been done before tracking was enabled).'
                      : 'This student has not completed the practice mock for this task yet.'}
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {screenshotModal && (
        <div className={teacherModalCss.tModalOverlay} onClick={() => setScreenshotModal(null)}>
          <div className={`${teacherModalCss.tModalBox} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={teacherModalCss.tModalHeader}>
              <div>
                <span className={teacherModalCss.tModalBadge}>📷 Screenshot</span>
                <h3 className={teacherModalCss.tModalTitle}>Violation Screenshot</h3>
              </div>
              <button className={teacherModalCss.tModalClose} onClick={() => setScreenshotModal(null)}>✕</button>
            </div>
            <div className={teacherModalCss.tModalBody}>
              <div style={{ textAlign: 'center' }}>
                <div
                  id="violation-screenshot-wrap-admin"
                  style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e0eaec', marginBottom: 12 }}
                >
                  <img
                    src={screenshotModal}
                    alt="Violation screenshot"
                    style={{ maxWidth: '100%', display: 'block' }}
                  />
                </div>
                <button
                  className={teacherModalCss.primaryBtn}
                  style={{ fontSize: '0.82rem' }}
                  onClick={() => {
                    const wrap = document.getElementById('violation-screenshot-wrap-admin');
                    if (wrap?.requestFullscreen) wrap.requestFullscreen();
                  }}
                >
                  ⛶ Fullscreen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Score correction modal (mandatory reason) ── */}
      {editingScore && (
        <div className={css.modalOverlay} onClick={() => !editingScore.submitting && setEditingScore(null)}>
          <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Correct Score — Q{editingScore.question.number}</h2>
              <button className={css.closeBtn} onClick={() => !editingScore.submitting && setEditingScore(null)}>&times;</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ fontSize: '0.82rem', color: '#78909c', margin: '0 0 16px' }}>
                This changes the marks awarded for this question only — the student's total score,
                percentage and pass/fail result are recalculated automatically. The change and your
                reason are permanently logged; the student and parent only see the corrected score.
              </p>
              {editingScore.error && <div className={css.inlineError}>{editingScore.error}</div>}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Score (max {editingScore.question.maxScore ?? '—'})</label>
                <input
                  className={css.formInput}
                  type="number"
                  min="0"
                  max={editingScore.question.maxScore ?? undefined}
                  step="0.5"
                  value={editingScore.newScore}
                  onChange={e => setEditingScore(m => ({ ...m, newScore: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Reason (required)</label>
                <textarea
                  className={css.formInput}
                  rows={3}
                  value={editingScore.reason}
                  onChange={e => setEditingScore(m => ({ ...m, reason: e.target.value }))}
                  placeholder="e.g. Re-marked against the marking guide, the AI missed a valid method."
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditingScore(null)} disabled={editingScore.submitting}>Cancel</button>
              <button
                className={css.btnPrimary}
                disabled={editingScore.submitting || !editingScore.reason.trim()}
                onClick={submitScoreEdit}
              >
                {editingScore.submitting ? 'Saving…' : 'Save Correction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Score edit detail (tap/click-friendly — works on mobile too) ── */}
      {viewingScoreEdit && (
        <div className={css.modalOverlay} onClick={() => setViewingScoreEdit(null)}>
          <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Score Correction Details</h2>
              <button className={css.closeBtn} onClick={() => setViewingScoreEdit(null)}>&times;</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ fontSize: '0.85rem', color: '#0C1E28', margin: '0 0 8px' }}>
                <strong>{viewingScoreEdit.editedBy?.name || 'Staff'}</strong>
                {' '}({viewingScoreEdit.editedBy?.actorType === 'school_admin' ? 'Admin' : 'Teacher'}) changed this
                question's score from {viewingScoreEdit.previousScoreAwarded} to {viewingScoreEdit.newScoreAwarded}
                {viewingScoreEdit.editedAt ? ` on ${new Date(viewingScoreEdit.editedAt).toLocaleString()}` : ''}.
              </p>
              <p style={{ fontSize: '0.85rem', color: '#546e7a', margin: 0 }}>
                <strong>Reason:</strong> {viewingScoreEdit.reason}
              </p>
            </div>
          </div>
        </div>
      )}
      {showRetakeModal && data?.student && (
        <RetakeAssignmentModal
          exam={{ _id: examId, title: data.exam?.title }}
          selectedStudents={[data.student]}
          actor="admin"
          fetchFn={schoolFetch}
          dateTimePicker={DateTimePicker}
          classes={css}
          reportCardReleased={data.exam?.reportCardReleased}
          renderQuestionEditor={editor => <AdminRetakeQuestionEditor {...editor} examId={examId} />}
          onClose={() => setShowRetakeModal(false)}
          onComplete={() => loadData()}
        />
      )}
    </div>
  );
}

function StudentCareerPathDrawer({ membershipId, studentName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    schoolFetch(`/school/students/${membershipId}/career-path`).then(({ ok, data }) => {
      if (ok) setData(data);
      setLoading(false);
    });
  }, [membershipId]);

  return (
    <div className={css.drawerOverlay} onClick={onClose}>
      <div className={css.drawer} onClick={e => e.stopPropagation()}>
        <div className={css.drawerHeader}>
          <div>
            <div className={css.drawerTitle}>{studentName || 'Career Path'}</div>
            <div className={css.drawerSubtitle}>AI Career Path Prediction</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#90a4ae', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div className={css.drawerBody}>
          {loading ? (
            <div className={css.drawerLoading}>Loading career path…</div>
          ) : (
            <CareerPathSummaryCard
              enabled={data?.enabled}
              status={data?.status}
              eligibility={data?.eligibility}
              snapshot={data?.snapshot}
              lastComputedAt={data?.lastComputedAt}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AdminRetakeQuestionEditor({ examId, questions, onChange, onRemove, onAdd, equalMarks, autoScore, uploadButton }) {
  return (
    <>
      <div className={css.qListHeader}>
        <span>{questions.length} question(s)</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {uploadButton}
          <button type="button" className={css.addQBtn} onClick={onAdd}><AddIcon fontSize="small" /> Add Question</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {questions.map((question, index) => (
          <AdminQuestionRow
            key={index}
            q={question}
            index={index}
            onChange={onChange}
            onRemove={onRemove}
            equalMarks={equalMarks}
            autoScore={autoScore}
            examId={examId}
          />
        ))}
      </div>
    </>
  );
}

// ── Exam performance modal (admin) ───────────────────────────────────────────
function AdminExamPerformanceModal({ exam, onClose, onChanged }) {
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubjectIdx, setActiveSubjectIdx] = useState('all');
  const [filters, setFilters] = useState({ result: 'all', risk: 'all', flagged: 'all' });
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);
  const filterMenuRef = useRef(null);
  const [filterMenuPos, setFilterMenuPos] = useState({ top: 0, left: 0 });
  const [expandedSections, setExpandedSections] = useState({ topics: true, questions: false });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [manualStudent, setManualStudent] = useState(null);
  const [selectedRetakeIds, setSelectedRetakeIds] = useState([]);
  const [showRetakeModal, setShowRetakeModal] = useState(false);

  const reloadAnalytics = () => {
    setLoading(true);
    schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
      if (result.ok) setPerfData(result.data);
      else setError(result.data?.error || 'Failed to load analytics');
      setLoading(false);
    });
  };

  useEffect(() => {
    schoolFetch(`/school/exams/${exam._id}/analytics`).then(r => {
      if (r.ok) setPerfData(r.data);
      else setError(r.data?.error || 'Failed to load analytics');
      setLoading(false);
    });
  }, [exam._id]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    const handler = e => { if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterMenuOpen(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterMenuOpen]);

  const isMulti = perfData?.exam?.isMultiSubject && (perfData?.subjectAnalytics?.length ?? 0) > 1;
  const currentSubjectData = isMulti && activeSubjectIdx !== 'all'
    ? (perfData.subjectAnalytics || []).find(s => s.subjectIdx === activeSubjectIdx) ?? null
    : null;

  const allStudents = perfData?.students || [];
  const filteredStudents = allStudents.filter(s => {
    if (filters.result === 'pass' && !s.passed) return false;
    if (filters.result === 'fail' && s.passed !== false) return false;
    if (filters.risk !== 'all' && s.riskLevel !== filters.risk) return false;
    if (filters.flagged === 'yes' && !s.proctoring?.flaggedForReview) return false;
    if (filters.flagged === 'no' && s.proctoring?.flaggedForReview) return false;
    return true;
  });
  const hasFilters = filters.result !== 'all' || filters.risk !== 'all' || filters.flagged !== 'all';
  const canManageRetakes = !!perfData?.canAssignRetake;
  const selectableStudents = canManageRetakes ? filteredStudents.filter(student => student.retakeEligibility?.eligible) : [];
  const selectedRetakeStudents = allStudents.filter(student => selectedRetakeIds.includes(String(student.membershipId)));
  const allVisibleSelected = selectableStudents.length > 0 && selectableStudents.every(student => selectedRetakeIds.includes(String(student.membershipId)));
  const toggleAllVisible = checked => setSelectedRetakeIds(current => checked
    ? [...new Set([...current, ...selectableStudents.map(student => String(student.membershipId))])]
    : current.filter(id => !selectableStudents.some(student => String(student.membershipId) === id)));
  const cancelRetake = async membershipId => {
    const result = await schoolFetch(`/school/exams/${exam._id}/retakes/${membershipId}`, { method: 'DELETE' });
    if (result.ok) reloadAnalytics();
    else setError(result.data?.error || 'Unable to cancel retake.');
  };

  const topicData = currentSubjectData?.topicBreakdown ?? perfData?.topicBreakdown ?? [];
  const questionData = currentSubjectData?.questionAnalysis ?? perfData?.questionAnalysis ?? [];
  const scoreDistribution = currentSubjectData?.scoreDistribution ?? perfData?.overallScoreDistribution ?? [];
  const maxDistCount = Math.max(...scoreDistribution.map(b => b.count ?? 0), 1);

  const openFilter = (key, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterMenuPos({ top: rect.bottom + 4, left: rect.left });
    setFilterMenuOpen(prev => prev === key ? null : key);
  };
  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setFilterMenuOpen(null); };
  const toggleSection = key => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  const exportExcel = () => {
    if (!perfData) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Field', 'Value'],
      ['Exam', perfData.exam?.title],
      ['Assigned', perfData.summary.totalAssigned],
      ['Submitted', perfData.summary.totalSubmitted],
      ['Avg %', perfData.summary.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : ''],
      ['Pass Rate', perfData.summary.passRate != null ? `${perfData.summary.passRate}%` : ''],
    ]), 'Summary');
    if (allStudents.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Name', 'Score %', 'Retakes', 'Pass', 'Risk', 'Flagged', 'Time (min)'],
        ...allStudents.map(s => [`${s.firstName} ${s.lastName}`.trim(), s.percentage != null ? `${s.percentage}%` : '', s.retakeCount || 0, s.passed === true ? 'Pass' : s.passed === false ? 'Fail' : '', s.riskLevel || '', s.proctoring?.flaggedForReview ? 'Yes' : 'No', s.timeTaken != null ? Math.round(s.timeTaken / 60) : '']),
      ]), 'Students');
    }
    if (topicData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Topic', 'Avg Correct Rate'],
        ...topicData.map(t => [t.topic, `${t.avgCorrectRate}%`]),
      ]), 'Topics');
    }
    XLSX.writeFile(wb, `${(perfData.exam?.title || 'exam').toLowerCase().replace(/\s+/g, '_')}_analytics.xlsx`);
  };

  const exportPDF = () => {
    if (!perfData) return;
    const eTitle = perfData.exam?.title || 'Exam';
    const subjectLine = [perfData.exam?.subjectName].filter(Boolean).join(' · ');

    const studentRows = allStudents.map(s =>
      `<tr>
        <td>${s.firstName} ${s.lastName}</td>
        <td>${s.retakeCount > 0 ? `Retaken ×${s.retakeCount}` : '—'}</td>
        <td style="color:${(s.percentage ?? 0) >= 50 ? '#2e7d32' : '#c62828'};font-weight:700">${s.percentage != null ? `${s.percentage}%` : '—'}</td>
        <td style="color:${s.passed ? '#2e7d32' : '#c62828'}">${s.passed === true ? 'Pass' : s.passed === false ? 'Fail' : '—'}</td>
        <td>${s.riskLevel || '—'}</td>
        <td>${s.proctoring?.flaggedForReview ? '<span style="color:#c62828">⚑ Flagged</span>' : '—'}</td>
      </tr>`
    ).join('');

    const topicRows = topicData.map(t => {
      const pct = t.avgCorrectRate ?? 0;
      const color = pct >= 70 ? '#2e7d32' : pct >= 40 ? '#f57c00' : '#c62828';
      return `<tr><td>${t.topic}</td><td style="color:${color};font-weight:700">${pct}%</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${eTitle} – Analytics</title>
<style>body{font-family:sans-serif;font-size:13px;color:#1a1a2e;padding:24px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#1a6e73;margin:20px 0 8px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px}th{background:#e0f4f5;font-weight:600}.stat{display:inline-block;margin:4px 16px 4px 0;font-weight:600}@media print{body{padding:12px}}</style>
</head><body>
<h1>${eTitle}</h1>
<p style="margin:0 0 12px;color:#607d8b">${subjectLine}</p>
<h2>Summary</h2>
<span class="stat">Assigned: ${perfData.summary.totalAssigned}</span>
<span class="stat">Submitted: ${perfData.summary.totalSubmitted}</span>
<span class="stat">Avg Score: ${perfData.summary.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : '—'}</span>
<span class="stat">Pass Rate: ${perfData.summary.passRate != null ? `${perfData.summary.passRate}%` : '—'}</span>
${allStudents.length > 0 ? `<h2>Student Performance</h2><table><thead><tr><th>Student</th><th>Retakes</th><th>%</th><th>Result</th><th>Risk</th><th>Flagged</th></tr></thead><tbody>${studentRows}</tbody></table>` : ''}
${topicData.length > 0 ? `<h2>Topic Breakdown</h2><table><thead><tr><th>Topic</th><th>Correct Rate</th></tr></thead><tbody>${topicRows}</tbody></table>` : ''}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  return (
    <>
      <div className={css.modalOverlay} onClick={onClose}>
        <div className={`${css.modalXL} BubblePopIn`} onClick={e => e.stopPropagation()}>
          {/* Dark gradient header */}
          <div className={css.perfHeader}>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>
                {exam.title}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#90a4ae', marginTop: 4 }}>Results Dashboard</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#90a4ae', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
          </div>

          {/* Body */}
          <div className={css.modalBody}>
            {loading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#78909c' }}>Loading analytics…</div>
            ) : error ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#c62828' }}>{error}</div>
            ) : perfData && (
              <>
                {/* Summary cards */}
                <div className={css.perfCardRow}>
                  {[
                    { label: 'Assigned', value: perfData.summary.totalAssigned },
                    { label: 'Submitted', value: perfData.summary.totalSubmitted },
                    { label: 'In Progress', value: perfData.summary.inProgress },
                    { label: 'Not Attempted', value: (perfData.notAttempted || []).length },
                    { label: 'Retakes Pending', value: perfData.summary.retakesPending || 0 },
                    { label: 'Retakes Active', value: perfData.summary.retakesInProgress || 0 },
                    { label: 'Avg Score', value: perfData.summary.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : '—' },
                    { label: 'Pass Rate', value: perfData.summary.passRate != null ? `${perfData.summary.passRate}%` : '—' },
                  ].map(c => (
                    <div key={c.label} className={css.perfCard}>
                      <div className={css.perfCardLabel}>{c.label}</div>
                      <div className={css.perfCardValue}>{c.value ?? '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Subject tabs for multi-subject exams */}
                {isMulti && (
                  <div className={css.subjectTabRow}>
                    <button className={`${css.subjectTabPill} ${activeSubjectIdx === 'all' ? css.subjectTabPillActive : ''}`} onClick={() => setActiveSubjectIdx('all')}>All Subjects</button>
                    {perfData.subjectAnalytics.map(s => (
                      <button key={s.subjectIdx} className={`${css.subjectTabPill} ${activeSubjectIdx === s.subjectIdx ? css.subjectTabPillActive : ''}`} onClick={() => setActiveSubjectIdx(s.subjectIdx)}>{s.subjectName}</button>
                    ))}
                  </div>
                )}

                {/* Score Distribution */}
                <div className={css.distSection}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#455a64', marginBottom: 14 }}>
                    Score Distribution{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
                  </div>
                  {scoreDistribution.length > 0 ? (
                    <div className={css.scoreDistRow}>
                      {SCORE_BUCKETS_ADMIN.map(b => {
                        const entry = scoreDistribution.find(d => d.label === b.label);
                        const count = entry?.count ?? 0;
                        const barH = Math.max(4, Math.round((count / maxDistCount) * 70));
                        return (
                          <div key={b.label} className={css.scoreDistBucket}>
                            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: b.color }}>{count}</span>
                            <div className={css.scoreDistBar} style={{ background: b.color, height: barH }} />
                            <span style={{ fontSize: '0.7rem', color: '#78909c' }}>{b.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#90a4ae', fontSize: '0.82rem' }}>No distribution data yet.</p>
                  )}
                </div>

                {/* Student Performance table */}
                {allStudents.length > 0 && (
                  <div className={css.drawerSection}>
                    <div className={css.perfTableHead}>
                      <div className={css.drawerSectionTitle}>
                        Student Performance 
                        <span className={css.perfFilterCount}>{filteredStudents.length}/{allStudents.length}</span>
                      </div>
                      {hasFilters && <button className={css.filterResetLink} onClick={() => setFilters({ result: 'all', risk: 'all', flagged: 'all' })}>Reset filters</button>}
                    </div>
                    {canManageRetakes && selectedRetakeIds.length > 0 && (
                      <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 8, border: '1px solid #b7d8d5', borderRadius: 6, background: '#f0faf8' }}>
                        <strong style={{ fontSize: '0.8rem', color: '#185c57' }}>{selectedRetakeIds.length} selected</strong>
                        <button type="button" className={css.btnPrimary} onClick={() => setShowRetakeModal(true)}>Allow retake</button>
                        <button type="button" className={css.filterResetLink} onClick={() => setSelectedRetakeIds([])}>Clear</button>
                      </div>
                    )}
                    <div className={css.tableWrap}>
                      <table className={css.table}>
                        <thead>
                          <tr>
                            {canManageRetakes && <th><input type="checkbox" aria-label="Select all eligible students currently shown" checked={allVisibleSelected} disabled={!selectableStudents.length} onChange={event => toggleAllVisible(event.target.checked)} /></th>}
                            <th>Name</th>
                            <th>Score</th>
                            <th className={css.filterTh}>
                              <button className={`${css.filterHeadBtn} ${filters.result !== 'all' ? css.filterHeadBtnActive : ''}`} onClick={e => openFilter('result', e)}>Result <span className={css.filterCaret}>▼</span></button>
                            </th>
                            <th className={css.filterTh}>
                              <button className={`${css.filterHeadBtn} ${filters.risk !== 'all' ? css.filterHeadBtnActive : ''}`} onClick={e => openFilter('risk', e)}>Risk <span className={css.filterCaret}>▼</span></button>
                            </th>
                            <th className={css.filterTh}>
                              <button className={`${css.filterHeadBtn} ${filters.flagged !== 'all' ? css.filterHeadBtnActive : ''}`} onClick={e => openFilter('flagged', e)}>Flagged <span className={css.filterCaret}>▼</span></button>
                            </th>
                            <th>Time</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.map((s, i) => {
                            const rowExamMode = perfData.exam?.examMode || exam.examMode;
                            const manualNeedsUpload = s.attemptStatus !== 'missed' && rowExamMode === 'manual' && s.manualSubmission?.status !== 'confirmed';
                            const manualScoreNeedsEntry = s.attemptStatus !== 'missed' && rowExamMode === 'manual_scores' && s.manualScoreEntry?.status !== 'confirmed';
                            return (
                            <tr
                              key={i}
                              className={css.clickableRow}
                              onClick={() => {
                                if (manualNeedsUpload || manualScoreNeedsEntry) {
                                  setSelectedStudent(null);
                                  setManualStudent({ ...s, scoreEntry: manualScoreNeedsEntry });
                                } else {
                                  setManualStudent(null);
                                  setSelectedStudent(s);
                                }
                              }}
                            >
                              {canManageRetakes && <td onClick={event => event.stopPropagation()}><input type="checkbox" aria-label={`Select ${s.firstName} ${s.lastName} for retake`} checked={selectedRetakeIds.includes(String(s.membershipId))} disabled={!s.retakeEligibility?.eligible} title={s.retakeEligibility?.reason || ''} onChange={event => setSelectedRetakeIds(current => event.target.checked ? [...new Set([...current, String(s.membershipId)])] : current.filter(id => id !== String(s.membershipId)))} /></td>}
                              <td className={css.bold}>{s.firstName} {s.lastName} <RetakeBadge count={s.retakeCount} retake={s.retake} /></td>
                              <td>{s.percentage != null ? `${s.percentage}%` : '—'}</td>
                              <td><span className={css.chip} style={{ background: s.passed ? '#d1fae5' : '#fce4ec', color: s.passed ? '#065f46' : '#b91c1c', fontSize: '0.72rem' }}>{s.passed === true ? 'Pass' : s.passed === false ? 'Fail' : '—'}</span></td>
                              <td><span style={{ color: s.riskLevel === 'high' ? '#e53935' : s.riskLevel === 'medium' ? '#f59e0b' : '#10b981', fontWeight: 700, fontSize: '0.78rem', textTransform: 'capitalize' }}>{s.riskLevel || '—'}</span></td>
                              <td>{s.proctoring?.flaggedForReview ? (
                                  <span style={{ background: '#fce4ec', color: '#c62828', fontSize: '0.7rem', fontWeight: 700, borderRadius: 6, padding: '2px 8px', border: '1px solid #ef9a9a', whiteSpace: 'nowrap' }}>⚑ Flagged</span>
                                ) : (
                                  <span style={{ color: '#b0bec5', fontSize: '0.75rem' }}>—</span>
                                )}</td>
                              <td>{s.timeTaken != null ? `${Math.round(s.timeTaken / 60)}m` : '—'}</td>
                              <td onClick={event => event.stopPropagation()}>
                                {manualNeedsUpload || manualScoreNeedsEntry ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }} onClick={event => event.stopPropagation()}>
                                    <button type="button" className={css.btnSecondary} style={{ padding: '3px 9px', fontSize: '0.72rem', lineHeight: 1.4 }} onClick={() => {
                                      setSelectedStudent(null);
                                      setManualStudent({ ...s, scoreEntry: manualScoreNeedsEntry });
                                    }}>{manualScoreNeedsEntry ? 'Add Score' : 'Add Script'}</button>
                                    <ManualExamMissedAction
                                      css={css}
                                      actionCss={css}
                                      actor="school"
                                      examMode={rowExamMode}
                                      examId={exam._id}
                                      examTitle={exam.title}
                                      membershipId={s.membershipId}
                                      student={s}
                                      compact
                                      onMarked={() => {
                                        schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
                                          if (result.ok) setPerfData(result.data);
                                        });
                                      }}
                                    />
                                  </div>
                                ) : rowExamMode !== 'cbt' && s.attemptStatus === 'missed' ? (
                                  <span style={{ background: '#fce4ec', color: '#c62828', fontSize: '0.7rem', fontWeight: 700, borderRadius: 6, padding: '2px 8px', border: '1px solid #ef9a9a', whiteSpace: 'nowrap' }}>Missed</span>
                                ) : <span className={css.rowCue}>View →</span>}
                                {canManageRetakes && s.retakeEligibility?.eligible && <button type="button" className={css.btnSecondary} style={{ marginLeft: 6, padding: '3px 8px', fontSize: '0.72rem' }} onClick={() => { setSelectedRetakeIds([String(s.membershipId)]); setShowRetakeModal(true); }}>Retake</button>}
                                {canManageRetakes && s.retake?.status === 'pending' && <button type="button" className={css.btnDanger} style={{ marginLeft: 6, padding: '3px 8px', fontSize: '0.72rem' }} onClick={() => cancelRetake(s.membershipId)}>Cancel retake</button>}
                              </td>
                            </tr>
                            );
                          })}
                          {filteredStudents.length === 0 && (
                            <tr><td colSpan={canManageRetakes ? 8 : 7} style={{ textAlign: 'center', color: '#90a4ae', padding: '20px 0' }}>No students match the current filters.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Not attempted */}
                {(perfData.notAttempted || []).length > 0 && (
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Not Attempted ({perfData.notAttempted.length})</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {perfData.notAttempted.map((s, i) => (
                        <span key={i} className={css.chip} style={{ background: '#f5f7fa', color: '#607d8b', fontSize: '0.75rem' }}>{s.firstName} {s.lastName}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Topic Performance — hidden for the "All Subjects" pill on multi-subject exams;
                    only meaningful when a specific subject is selected (or the exam is single-subject) */}
                {topicData.length > 0 && !(isMulti && activeSubjectIdx === 'all') && (
                  <div className={css.drawerSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }} onClick={() => toggleSection('topics')}>
                      <div className={css.drawerSectionTitle} style={{ borderBottom: 'none', margin: 0, padding: 0 }}>Topic Performance{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</div>
                      <span style={{ color: '#b0bec5', fontSize: '0.8rem' }}>{expandedSections.topics ? '▲' : '▼'}</span>
                    </div>
                    {expandedSections.topics && (
                      <div className={css.topicPillRow}>
                        {topicData.map(t => {
                          const rate = t.avgCorrectRate ?? 0;
                          const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#e53935';
                          return (
                            <div key={t.topic} className={css.topicPill}>
                              <span className={css.topicPillName}>{t.topic}</span>
                              <span className={css.topicPillStat} style={{ color }}>{rate}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Question Analysis */}
                {questionData.length > 0 && (
                  <div className={css.drawerSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }} onClick={() => toggleSection('questions')}>
                      <div className={css.drawerSectionTitle} style={{ borderBottom: 'none', margin: 0, padding: 0 }}>Question Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</div>
                      <span style={{ color: '#b0bec5', fontSize: '0.8rem' }}>{expandedSections.questions ? '▲' : '▼'}</span>
                    </div>
                    {expandedSections.questions && (
                      <div className={css.tableWrap}>
                        <table className={css.table}>
                          <thead><tr><th>#</th><th>Correct Rate</th><th>Avg Time</th></tr></thead>
                          <tbody>
                            {questionData.map(q => (
                              <tr key={q.questionNumber}>
                                <td>Q{q.questionNumber}</td>
                                <td><span style={{ fontWeight: 700, color: (q.correctRate ?? 0) >= 70 ? '#10b981' : (q.correctRate ?? 0) >= 40 ? '#f59e0b' : '#e53935' }}>{q.correctRate ?? 0}%</span></td>
                                <td>{q.avgTime != null ? `${q.avgTime}s` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Export buttons */}
                <div className={css.drawerExportRow}>
                  <button className={css.drawerExportBtn} onClick={exportPDF}>🖨 Export PDF</button>
                  <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={exportExcel}>📊 Export Excel</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Column-filter dropdown */}
      {filterMenuOpen && (
        <div ref={filterMenuRef} className={css.filterMenu} style={{ top: filterMenuPos.top, left: filterMenuPos.left }}>
          {filterMenuOpen === 'result' && ['all', 'pass', 'fail'].map(v => (
            <button key={v} className={`${css.filterMenuItem} ${filters.result === v ? css.filterMenuItemActive : ''}`} onClick={() => setFilter('result', v)}>
              <span className={css.filterMenuCheck}>{filters.result === v ? '✓' : ''}</span>
              {v === 'all' ? 'All Results' : v === 'pass' ? 'Pass' : 'Fail'}
            </button>
          ))}
          {filterMenuOpen === 'risk' && ['all', 'low', 'medium', 'high'].map(v => (
            <button key={v} className={`${css.filterMenuItem} ${filters.risk === v ? css.filterMenuItemActive : ''}`} onClick={() => setFilter('risk', v)}>
              <span className={css.filterMenuCheck}>{filters.risk === v ? '✓' : ''}</span>
              {v === 'all' ? 'All Risk' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
          {filterMenuOpen === 'flagged' && ['all', 'yes', 'no'].map(v => (
            <button key={v} className={`${css.filterMenuItem} ${filters.flagged === v ? css.filterMenuItemActive : ''}`} onClick={() => setFilter('flagged', v)}>
              <span className={css.filterMenuCheck}>{filters.flagged === v ? '✓' : ''}</span>
              {v === 'all' ? 'All' : v === 'yes' ? 'Flagged' : 'Not Flagged'}
            </button>
          ))}
        </div>
      )}

      {/* Student detail drawer */}
      {selectedStudent && (
        <SchoolAdminStudentExamDetailDrawer
          examId={exam._id}
          membershipId={selectedStudent.membershipId}
          onClose={() => setSelectedStudent(null)}
        />
      )}
      {manualStudent && (
        manualStudent.scoreEntry ? <ManualScoreEntryDrawer
          css={css}
          actor="school"
          examId={exam._id}
          examTitle={exam.title}
          membershipId={manualStudent.membershipId}
          student={manualStudent}
          onClose={() => setManualStudent(null)}
          onConfirmed={() => {
            setManualStudent(null);
            onChanged?.();
            schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
              if (result.ok) setPerfData(result.data);
            });
          }}
          onMissed={() => {
            setManualStudent(null);
            onChanged?.();
            schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
              if (result.ok) setPerfData(result.data);
            });
          }}
        /> : <ManualScriptUploadDrawer
          css={css}
          actor="school"
          examId={exam._id}
          examTitle={exam.title}
          membershipId={manualStudent.membershipId}
          student={manualStudent}
          onClose={() => setManualStudent(null)}
          onConfirmed={() => {
            setManualStudent(null);
            schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
              if (result.ok) setPerfData(result.data);
            });
          }}
          onMissed={() => {
            setManualStudent(null);
            schoolFetch(`/school/exams/${exam._id}/analytics`).then(result => {
              if (result.ok) setPerfData(result.data);
            });
          }}
        />
      )}
      {showRetakeModal && selectedRetakeStudents.length > 0 && (
        <RetakeAssignmentModal
          exam={exam}
          selectedStudents={selectedRetakeStudents}
          actor="admin"
          fetchFn={schoolFetch}
          dateTimePicker={DateTimePicker}
          classes={css}
          reportCardReleased={perfData?.exam?.reportCardReleased}
          renderQuestionEditor={editor => <AdminRetakeQuestionEditor {...editor} examId={exam._id} />}
          onClose={() => { setShowRetakeModal(false); setSelectedRetakeIds([]); }}
          onComplete={() => { setSelectedRetakeIds([]); reloadAnalytics(); onChanged?.(); }}
        />
      )}
    </>
  );
}

//  Admin Exam Modal 
function AdminExamModal({ exam, subjects, classes, school, onClose, onSaved, onDraftSaved, reviewRequest, onApprove, onReject, initialExamMode = 'cbt' }) {
  const isEdit = !!exam;
  const initialMode = exam?.examMode || initialExamMode;
  const isManualMode = ['manual', 'manual_scores'].includes(initialMode);
  const manualSchedule = !isEdit && isManualMode ? (() => {
    const start = new Date();
    const end = initialMode === 'manual_scores' ? start : new Date(start.getTime() + 5 * 60 * 1000);
    return { startDate: toDatetimeLocal(start), endDate: toDatetimeLocal(end) };
  })() : null;
  const isScheduled = isEdit && ['scheduled', 'closed'].includes(exam?.status);
  const hasStarted = isEdit && isScheduled && exam?.status !== 'closed' && exam?.settings?.startDate && new Date(exam.settings.startDate) <= new Date();
  const canEditScheduledSettings = exam?.status === 'scheduled' && !hasStarted;
  // Autosave only writes drafts; scheduled and pending exams are saved explicitly.
  const autosaveEnabled = !reviewRequest && (!isEdit || exam?.status === 'draft');
  const aiEnabled = school?.subscription?.aiQuestionsEnabled;

  // Multi-subject state
  const [isMultiSubject, setIsMultiSubject] = useState(exam?.isMultiSubject || false);
  const [selectedSubjects, setSelectedSubjects] = useState(() => {
    if (exam?.isMultiSubject && exam.subjects?.length) return exam.subjects.map(s => ({ subjectId: String(s.subjectId), subjectName: s.subjectName, duration: s.duration || 30 }));
    if (exam?.subjectId) return [{ subjectId: String(exam.subjectId?._id || exam.subjectId), subjectName: exam.subjectName || '', duration: exam.settings?.duration || 60 }];
    return [];
  });
  const [activeSubjectPane, setActiveSubjectPane] = useState(0);

  const [form, setForm] = useState({
    title: exam?.title || '',
    classId: exam?.classId?._id || exam?.classId || '',
    classArm: exam?.classArm || '',
    instructions: exam?.instructions || '',
    source: exam?.source || 'internal',
    prebuiltConfig: exam?.prebuiltConfig || null,
    settings: {
      duration: exam?.settings?.duration || 60,
      passmark: exam?.settings?.passmark || 50,
      totalScore: exam?.settings?.totalScore || '',
      shuffleQuestions: exam?.settings?.shuffleQuestions || false,
      shuffleOptions: exam?.settings?.shuffleOptions || false,
      maxAttempts: exam?.settings?.maxAttempts || 1,
      resultVisibility: exam?.settings?.resultVisibility || 'immediate',
      startDate: manualSchedule?.startDate || toDatetimeLocal(exam?.settings?.startDate),
      endDate: manualSchedule?.endDate || toDatetimeLocal(exam?.settings?.endDate),
      term: exam?.settings?.term || school?.currentTerm || '',
      session: exam?.settings?.session || school?.academicSession || '',
      proctoring: {
        enabled: exam?.settings?.proctoring?.enabled !== false,
        requireFullscreen: exam?.settings?.proctoring?.requireFullscreen ?? true,
        maxViolations: exam?.settings?.proctoring?.maxViolations ?? 20,
        blockOnMaxViolations: exam?.settings?.proctoring?.blockOnMaxViolations ?? true,
      },
    },
    questions: exam?.questions || [],
    examCategory: exam?.examCategory || 'test',
    examMode: exam?.examMode || initialExamMode,
  });
  const [equalMarks, setEqualMarks] = useState(exam?.settings?.equalMarks !== false);
  const [saving, setSaving] = useState(false);
  const [autosaveState, setAutosaveState] = useState('idle');
  const [error, setError] = useState('');
  const [activePane, setActivePane] = useState('details');

  // Prebuilt state
  const [showPrebuiltPicker, setShowPrebuiltPicker] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [genStatus, setGenStatus] = useState('');
  const [questionsReady, setQuestionsReady] = useState(isEdit && !!(exam?.questions?.length));
  const [curriculumCoverage, setCurriculumCoverage] = useState(null);
  const [curriculumCoverageLoading, setCurriculumCoverageLoading] = useState(false);

  // AI state
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [aiDiff, setAiDiff] = useState('medium');
  const [aiQType, setAiQType] = useState('mcq');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiSuccess, setAiSuccess] = useState('');
  const [savedExamId, setSavedExamIdState] = useState(isEdit ? exam._id : null);
  const savedExamIdRef = useRef(isEdit ? exam._id : null);
  const setSavedExamId = id => { savedExamIdRef.current = id; setSavedExamIdState(id); };
  const autosaveTimerRef = useRef(null);
  const persistedRef = useRef(false);
  const autosaveQueueRef = useRef(Promise.resolve());
  const lastAutosaveSnapshotRef = useRef(null);
  const latestAutosaveRef = useRef(null);
  const [unlockedFields, setUnlockedFields] = useState({});
  const questionsEndRef = useRef(null);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [uploadQModal, setUploadQModal] = useState(false);
  const [uploadQLoading, setUploadQLoading] = useState(false);
  const [uploadQError, setUploadQError] = useState('');
  const [uploadQType, setUploadQType] = useState('mcq');
  const [uploadQFileName, setUploadQFileName] = useState('');
  const uploadQFileRef = useRef(null);
  const uploadQFileDataRef = useRef(null);

  // Computed
  const isPrebuilt = form.source === 'external' && !!form.prebuiltConfig;
  const prebuiltEntry = isPrebuilt ? PREBUILT_EXAMS.find(e => e.slug === form.prebuiltConfig?.slug) : null;

  // Curriculum state
  const [currSelectedTopics, setCurrSelectedTopics] = useState([]);
  const [currCountPerTopic, setCurrCountPerTopic] = useState(2);
  const [currQType, setCurrQType] = useState('mcq');
  const [currDifficulty, setCurrDifficulty] = useState('medium');
  const [currLoading, setCurrLoading] = useState(false);
  const [currError, setCurrError] = useState('');

  // Active subject's curriculum (from school subjects list, which includes curriculum field)
  const activeSubjectId = isMultiSubject ? selectedSubjects[activeSubjectPane]?.subjectId : selectedSubjects[0]?.subjectId;
  const activeSubjectDoc = activeSubjectId ? subjects.find(s => String(s._id) === String(activeSubjectId)) : null;
  const activeClassCurriculum = (Array.isArray(activeSubjectDoc?.curriculum) ? activeSubjectDoc.curriculum : []).find(c => String(c.classId) === String(form.classId));
  const hasCurriculum = !!(activeClassCurriculum?.key);
  const currTopics = activeClassCurriculum?.topics || [];
  const coveredSelectedTopics = currSelectedTopics.filter(topic => curriculumCoverage?.topics?.some(item => item.name === topic && item.covered));

  useEffect(() => {
    let active = true;
    if (!activeSubjectId || !form.classId) {
      setCurriculumCoverage(null);
      return undefined;
    }
    setCurriculumCoverageLoading(true);
    schoolFetch(`/school/curriculum/coverage/${activeSubjectId}/${form.classId}`)
      .then(({ ok, data }) => {
        if (active) setCurriculumCoverage(ok ? data.coverage : null);
      })
      .catch(() => {
        if (active) setCurriculumCoverage(null);
      })
      .finally(() => {
        if (active) setCurriculumCoverageLoading(false);
      });
    return () => { active = false; };
  }, [activeSubjectId, form.classId]);

  const handleCurriculumGenerate = async () => {
    if (!currSelectedTopics.length) { setCurrError('Select at least one topic'); return; }
    if (!form.title.trim()) { setCurrError('Enter a title first'); return; }
    if (!(await flushDraft())) { setCurrError('Could not save the draft before generating questions.'); return; }
    const examId = savedExamIdRef.current;
    if (!examId) return;
    setCurrLoading(true); setCurrError('');
    try {
      const { ok, data } = await schoolFetch(`/school/exams/${examId}/curriculum-generate`, {
        method: 'POST', body: JSON.stringify({ topics: currSelectedTopics, countPerTopic: currCountPerTopic, questionType: currQType, difficulty: currDifficulty }),
      });
      if (ok) {
        const sectionIdx = isMultiSubject ? activeSubjectPane : null;
        const tagged = (data.questions || []).map(q => ({ ...q, subjectSectionIdx: sectionIdx }));
        setForm(f => ({ ...f, questions: [...f.questions, ...tagged] }));
        setScrollToBottom(true);
        setCurrSelectedTopics([]);
      } else { setCurrError(data?.error || 'Curriculum generation failed'); }
    } catch { setCurrError('Connection error'); }
    finally { setCurrLoading(false); }
  };

  useEffect(() => {
    if (scrollToBottom && questionsEndRef.current) {
      questionsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setScrollToBottom(false);
    }
  }, [scrollToBottom]);

  // Subjects available for the selected class (null = all); further narrowed by prebuilt when applicable
  const subjectsForClass = form.classId
    ? subjects.filter(s => (s.classIds || []).some(cid => String(cid._id || cid) === form.classId))
    : subjects;
  const availableSubjects = isPrebuilt && prebuiltEntry?.subjects?.length
    ? subjectsForClass.filter(s => prebuiltEntry.subjects.some(ps => ps.toLowerCase() === s.name.toLowerCase()))
    : subjectsForClass;

  const setSetting = k => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, settings: { ...f.settings, [k]: val } }));
  };
  // Keeps the end date valid whenever the start date moves past it, instead of
  // silently leaving an impossible date range for the student to be caught by later
  const handleStartDateChange = e => {
    const newStart = e.target.value;
    if (canEditScheduledSettings && !newStart) {
      setError('A start time is required.');
      return;
    }
    if (canEditScheduledSettings && new Date(newStart) <= new Date()) {
      setError('The new start time must be in the future.');
      return;
    }
    if (canEditScheduledSettings && !unlockedFields.endDate && form.settings.endDate && new Date(form.settings.endDate) <= new Date(newStart)) {
      setError('Unlock the end time too before moving the start time past it.');
      return;
    }
    setForm(f => {
      const currentEnd = f.settings.endDate;
      const needsBump = newStart && currentEnd && new Date(currentEnd) <= new Date(newStart);
      const nextEnd = needsBump ? toDatetimeLocal(new Date(new Date(newStart).getTime() + 60 * 60 * 1000)) : currentEnd;
      return { ...f, settings: { ...f.settings, startDate: newStart, endDate: nextEnd } };
    });
  };
  const handleEndDateChange = e => {
    const newEnd = e.target.value;
    if (canEditScheduledSettings && (!newEnd || new Date(newEnd) <= new Date(form.settings.startDate))) {
      setError('The end time must be after the start time.');
      return;
    }
    setForm(f => ({ ...f, settings: { ...f.settings, endDate: newEnd } }));
  };
  const setProctoringField = k => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, settings: { ...f.settings, proctoring: { ...f.settings.proctoring, [k]: val } } }));
  };

  const addSubject = subjectId => {
    const subj = subjects.find(s => String(s._id) === subjectId);
    if (!subj) return;
    if (selectedSubjects.find(s => s.subjectId === subjectId)) return;
    const defaultDuration = isPrebuilt && prebuiltEntry?.duration ? prebuiltEntry.duration : 30;
    setSelectedSubjects(prev => [...prev, { subjectId, subjectName: subj.name, duration: defaultDuration }]);
  };
  const removeSubject = idx => setSelectedSubjects(prev => prev.filter((_, i) => i !== idx));
  const updateSubjectDuration = (idx, val) => setSelectedSubjects(prev => prev.map((s, i) => i === idx ? { ...s, duration: val === '' ? '' : (Number(val) || 0) } : s));

  // For multi-subject, total duration = sum of subject durations
  const computedDuration = isMultiSubject
    ? selectedSubjects.reduce((sum, s) => sum + (s.duration || 30), 0)
    : Number(form.settings.duration) || 60;

  const totalScoreNum = Number(form.settings.totalScore) || 0;
  const autoScore = totalScoreNum > 0 && form.questions.length > 0
    ? Math.round((totalScoreNum / form.questions.length) * 100) / 100 : 0;
  const scoreSumManual = form.questions.reduce((s, q) => s + (Number(q.score) || 0), 0);

  const sameTime = (a, b) => (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
  const originalSettings = exam?.settings || {};
  const startChanged = !!unlockedFields.startDate && !sameTime(localDatetimeToISO(form.settings.startDate), originalSettings.startDate);
  const endChanged = !!unlockedFields.endDate && !sameTime(localDatetimeToISO(form.settings.endDate), originalSettings.endDate);
  // These edits notify students and parents, so they are saved explicitly and never by autosave.
  const scheduleChangePending = canEditScheduledSettings && (
    startChanged || endChanged
    || (!!unlockedFields.examCategory && form.examCategory !== (exam.examCategory || 'test'))
    || (!!unlockedFields.duration && Number(computedDuration) !== Number(originalSettings.duration))
    || (!!unlockedFields.passmark && Number(form.settings.passmark) !== Number(originalSettings.passmark))
    || (!!unlockedFields.totalScore && Number(form.settings.totalScore) !== Number(originalSettings.totalScore))
    || (!!unlockedFields.resultVisibility && form.settings.resultVisibility !== originalSettings.resultVisibility)
  );
  const validateScheduledEdits = () => {
    const start = new Date(form.settings.startDate).getTime();
    const end = new Date(form.settings.endDate).getTime();
    const passmark = Number(form.settings.passmark);
    if (startChanged && !(start > Date.now())) return 'The start time must be in the future.';
    if ((startChanged || endChanged) && !(end > start)) return 'The end time must be after the start time.';
    if (unlockedFields.duration && !(computedDuration > 0)) return 'Duration must be greater than zero.';
    if (unlockedFields.passmark && (String(form.settings.passmark).trim() === '' || !(passmark >= 0 && passmark <= 100))) return 'Pass mark must be between 0 and 100.';
    if (unlockedFields.totalScore && !(totalScoreNum > 0)) return 'Total score must be greater than zero.';
    return '';
  };

  // Keep each theory question's displayed marking_guide numbers summing to
  // its actual score (equal-marks auto-score, or its manual score) so AI-
  // generated guides look correct immediately in the builder, not only after
  // a save+reload round-trip (backend reconciles again on save as a safety net).
  const questionsScoreSig = form.questions.map(q => q.score).join(',');
  useEffect(() => {
    setForm(f => {
      const reconciled = reconcileQuestionsForDisplay(f.questions, (i) => (
        equalMarks
          ? (i === 0 ? Math.round((autoScore + Math.round((totalScoreNum - autoScore * f.questions.length) * 100) / 100) * 100) / 100 : autoScore)
          : (Number(f.questions[i]?.score) || 0)
      ));
      return reconciled === f.questions ? f : { ...f, questions: reconciled };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equalMarks, totalScoreNum, form.questions.length, questionsScoreSig]);

  const buildPayload = () => {
    // A scheduled exam never receives blank rows; equal marks are rebalanced over the rows sent.
    const sendable = isScheduled ? form.questions.filter(q => q.question_text?.trim()) : form.questions;
    const sendableScore = totalScoreNum > 0 && sendable.length > 0 ? Math.round((totalScoreNum / sendable.length) * 100) / 100 : 0;
    const questionsWithScores = sendable.map((q, i) => ({
      ...q,
      difficulty: q.difficulty ? q.difficulty.toLowerCase() : '',
      score: equalMarks
        ? (i === 0 ? Math.round((sendableScore + Math.round((totalScoreNum - sendableScore * sendable.length) * 100) / 100) * 100) / 100 : sendableScore)
        : (Number(q.score) || 0),
    }));
    const payload = {
      title: form.title,
      classId: form.classId || null,
      classArm: form.classArm || null,
      instructions: form.instructions,
      questions: questionsWithScores,
      settings: {
        ...form.settings,
        proctoring: {
          ...form.settings.proctoring,
          enabled: form.examMode === 'cbt' ? form.settings.proctoring.enabled : false,
        },
        equalMarks,
        duration: isScheduled && !unlockedFields.duration ? exam?.settings?.duration ?? computedDuration : computedDuration,
        startDate: isScheduled && !unlockedFields.startDate ? exam?.settings?.startDate || null : localDatetimeToISO(form.settings.startDate),
        endDate: isScheduled && !unlockedFields.endDate ? exam?.settings?.endDate || null : localDatetimeToISO(form.settings.endDate),
      },
      examCategory: form.examCategory,
      examMode: ['manual', 'manual_scores'].includes(form.examMode) ? form.examMode : 'cbt',
      isMultiSubject,
      source: form.source || 'internal',
      prebuiltConfig: form.prebuiltConfig || null,
    };
    if (isMultiSubject) {
      payload.subjects = selectedSubjects;
      payload.subjectId = selectedSubjects[0]?.subjectId || null;
      payload.subjectName = selectedSubjects[0]?.subjectName || '';
    } else {
      payload.subjectId = selectedSubjects[0]?.subjectId || null;
      payload.subjectName = selectedSubjects[0]?.subjectName || '';
    }
    return payload;
  };

  const queueDraftSave = (payload, snapshot) => {
    const save = async () => {
      if (!payload.title?.trim() && !savedExamIdRef.current) {
        setAutosaveState('error');
        setError('Add an exam title to save this draft.');
        return { ok: false };
      }
      setAutosaveState('saving');
      const examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      const savePayload = payload;
      try {
        const { ok, data } = await schoolFetch(examId ? `/school/exams/${examId}` : '/school/exams', {
          method: examId ? 'PATCH' : 'POST',
          headers: { 'x-draft-autosave': '1' },
          body: JSON.stringify(savePayload),
        });
        if (!ok) {
          setAutosaveState('error');
          setError(data?.error || 'Draft could not be saved. Your changes are still here.');
          return { ok: false };
        }
        if (!examId) setSavedExamId(data.exam._id);
        persistedRef.current = true;
        lastAutosaveSnapshotRef.current = snapshot;
        setAutosaveState('saved');
        setError('');
        if (!examId) onDraftSaved?.();
        return { ok: true, exam: data.exam };
      } catch {
        setAutosaveState('error');
        setError('Draft could not be saved. Check your connection and retry.');
        return { ok: false };
      }
    };
    const queuedSave = autosaveQueueRef.current.catch(() => {}).then(save);
    autosaveQueueRef.current = queuedSave;
    return queuedSave;
  };

  useEffect(() => {
    const payload = buildPayload();
    const snapshot = JSON.stringify(payload);
    latestAutosaveRef.current = { payload, snapshot };
    if (lastAutosaveSnapshotRef.current === null) {
      lastAutosaveSnapshotRef.current = snapshot;
      return undefined;
    }
    if (!autosaveEnabled || snapshot === lastAutosaveSnapshotRef.current) return undefined;
    clearTimeout(autosaveTimerRef.current);
    setAutosaveState('pending');
    autosaveTimerRef.current = setTimeout(() => queueDraftSave(payload, snapshot), 900);
    return () => clearTimeout(autosaveTimerRef.current);
    // buildPayload intentionally snapshots form and selected-subject state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, equalMarks, isMultiSubject, selectedSubjects, autosaveEnabled]);

  useEffect(() => () => clearTimeout(autosaveTimerRef.current), []);

  const flushDraft = async () => {
    clearTimeout(autosaveTimerRef.current);
    if (!autosaveEnabled) return true;
    const payload = buildPayload();
    const snapshot = JSON.stringify(payload);
    latestAutosaveRef.current = { payload, snapshot };
    if (snapshot !== lastAutosaveSnapshotRef.current) {
      const result = await queueDraftSave(payload, snapshot);
      return result.ok;
    }
    await autosaveQueueRef.current.catch(() => {});
    return lastAutosaveSnapshotRef.current === snapshot;
  };

  const handleClose = async () => {
    if (!autosaveEnabled) {
      const readOnly = reviewRequest || hasStarted || exam?.status === 'closed';
      const hasUnsavedChanges = !readOnly && JSON.stringify(buildPayload()) !== lastAutosaveSnapshotRef.current;
      if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard them and close?')) return;
      onClose();
      return;
    }
    if (!savedExamIdRef.current && !form.title.trim()) {
      const hasUnsavedChanges = JSON.stringify(buildPayload()) !== lastAutosaveSnapshotRef.current;
      if (!hasUnsavedChanges || window.confirm('A title is required to save this draft. Discard these unsaved changes?')) onClose();
      return;
    }
    if (!(await flushDraft()) && !window.confirm('Your latest changes could not be saved. Close and discard them?')) return;
    if (persistedRef.current) onDraftSaved?.();
    onClose();
  };

  const unlockScheduledField = (field, label) => {
    if (!canEditScheduledSettings) return;
    if (window.confirm(`Edit ${label}? Assigned students and their linked parents may be notified when this change is saved.`)) {
      setUnlockedFields(current => ({ ...current, [field]: true }));
    }
  };

  const scheduledEditLink = (field, label) => canEditScheduledSettings && !unlockedFields[field] ? (
    <button type="button" onClick={() => unlockScheduledField(field, label)} style={{ marginLeft: 8, padding: 0, border: 0, background: 'none', color: '#0c7c84', fontSize: '0.76rem', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Edit</button>
  ) : null;

  const changeQuestion = (i, key, val) => {
    setForm(f => {
      const qs = [...f.questions];
      if (key === '__replace__') {
        qs[i] = { ...val, subjectSectionIdx: qs[i].subjectSectionIdx };
      } else {
        qs[i] = { ...qs[i], [key]: val };
      }
      return { ...f, questions: qs };
    });
  };
  const removeQuestion = i => setForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));
  const addBlankQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...f.questions, {
        question_text: '', options: ['', '', '', ''], correct_answer: '', topic: '', difficulty: '',
        instructions: '', questionType: 'mcq', score: 1, marking_guide: '', model_answer: '',
        subjectSectionIdx: isMultiSubject ? activeSubjectPane : null,
      }],
    }));
    setScrollToBottom(true);
  };

  const handleUploadQuestions = async () => {
    const file = uploadQFileDataRef.current;
    if (!file) { setUploadQError('Please select a file first.'); return; }
    if (!form.title.trim()) { setUploadQError('Enter an exam title first, then upload questions.'); return; }
    if (!(await flushDraft())) { setUploadQError('Could not save the draft before uploading questions.'); return; }
    setUploadQLoading(true);
    setUploadQError('');
    try {
      let examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      if (!examId) {
        if (!form.title.trim()) { setUploadQError('Enter an exam title first, then upload questions.'); return; }
        const initPayload = { ...buildPayload(), questions: buildPayload().questions.filter(q => q.question_text?.trim()) };
        const { ok: initOk, data: initData } = await schoolFetch('/school/exams', { method: 'POST', body: JSON.stringify(initPayload) });
        if (!initOk) { setUploadQError(initData.error || 'Could not save exam before uploading.'); return; }
        examId = initData.exam._id;
        setSavedExamId(examId);
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionType', uploadQType);
      const { ok, data } = await schoolFetch(`/school/exams/${examId}/upload-questions`, { method: 'POST', body: formData });
      if (!ok || !data.questions) { setUploadQError(data.error || 'Upload failed. Please try again.'); return; }
      const tagged = data.questions.map(q => ({ ...q, subjectSectionIdx: isMultiSubject ? activeSubjectPane : null }));
      setForm(f => ({ ...f, questions: [...f.questions, ...tagged] }));
      setActivePane('questions');
      setUploadQModal(false);
      setUploadQFileName('');
      uploadQFileDataRef.current = null;
      setAiSuccess(`${data.questions.length} question${data.questions.length !== 1 ? 's' : ''} extracted and added.`);
      setScrollToBottom(true);
      setTimeout(() => setAiSuccess(''), 5000);
    } catch { setUploadQError('Connection error. Please try again.'); }
    finally { setUploadQLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (isPrebuilt) {
      if (selectedSubjects.length === 0) { setError('Select at least one subject from the prebuilt exam'); return; }
    } else {
      if (isMultiSubject && selectedSubjects.length < 2) { setError('Select at least 2 subjects for a multi-subject exam'); return; }
      if (!isMultiSubject && selectedSubjects.length === 0) { setError('Please select a subject'); return; }
    }
    if (!form.settings.totalScore || Number(form.settings.totalScore) <= 0) { setError('Total score is required'); return; }
    if (scheduleChangePending) {
      const scheduleError = validateScheduledEdits();
      if (scheduleError) { setError(scheduleError); return; }
      if (!window.confirm('Save these changes? Assigned students and their linked parents will be notified.')) return;
    } else if (!(await flushDraft())) return;
    setSaving(true); setError('');
    try {
      const existingId = savedExamIdRef.current || (isEdit ? exam._id : null);
      const method = existingId ? 'PATCH' : 'POST';
      const url = reviewRequest ? `/school/exams/requests/${exam._id}` : (existingId ? `/school/exams/${existingId}` : '/school/exams');
      const payload = buildPayload();
      const { ok, data } = await schoolFetch(url, { method, body: JSON.stringify(payload) });
      if (ok) { onSaved(data.exam); }
      else setError(data.error || 'Failed to save exam');
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  const handleApproveClick = async () => {
    if (!onApprove) return;
    setSaving(true); setError('');
    try {
      const payload = buildPayload();
      const { ok, data } = await schoolFetch(`/school/exams/requests/${exam._id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      if (!ok) { setError(data.error || 'Failed to save changes'); return; }
      await onApprove(exam._id);
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  const handleAiGenerate = async () => {
    if (!aiTopic) { setAiError('Enter a topic'); return; }
    if (!form.title.trim()) { setAiError('Enter a title first'); return; }
    if (!(await flushDraft())) { setAiError('Could not save the draft before generating questions.'); return; }
    if (!savedExamIdRef.current) {
      const { ok: initOk, data: initData } = await schoolFetch('/school/exams', { method: 'POST', body: JSON.stringify({ ...buildPayload(), questions: [] }) });
      if (!initOk) { setAiError(initData?.error || 'Could not initialize exam'); return; }
      setSavedExamId(initData.exam._id);
      const subjectName = isMultiSubject ? selectedSubjects[activeSubjectPane]?.subjectName : selectedSubjects[0]?.subjectName;
      setAiLoading(true); setAiError(''); setAiSuccess('');
      const { ok, data } = await schoolFetch(`/school/exams/${initData.exam._id}/ai-suggest`, {
        method: 'POST',
        body: JSON.stringify({ topic: aiTopic, count: aiCount, difficulty: aiDiff, questionType: aiQType, subjectName }),
      });
      setAiLoading(false);
      if (ok) {
        const tagged = (data.questions || []).map(q => ({
          ...q,
          ...(q.questionType === 'theory' ? { marking_guide: ensureMarkingGuideMarks(q.marking_guide || '') } : {}),
          subjectSectionIdx: isMultiSubject ? activeSubjectPane : null,
        }));
        setForm(f => ({ ...f, questions: [...f.questions, ...tagged] }));
        setAiSuccess(`${data.questions.length} questions added.`);
        setScrollToBottom(true);
        setTimeout(() => setAiSuccess(''), 4000);
      } else setAiError(data?.error || 'AI generation failed');
      return;
    }
    const subjectName = isMultiSubject ? selectedSubjects[activeSubjectPane]?.subjectName : selectedSubjects[0]?.subjectName;
    setAiLoading(true); setAiError(''); setAiSuccess('');
    const { ok, data } = await schoolFetch(`/school/exams/${savedExamIdRef.current}/ai-suggest`, {
      method: 'POST',
      body: JSON.stringify({ topic: aiTopic, count: aiCount, difficulty: aiDiff, questionType: aiQType, subjectName }),
    });
    setAiLoading(false);
    if (ok) {
      const tagged = (data.questions || []).map(q => ({
        ...q,
        ...(q.questionType === 'theory' ? { marking_guide: ensureMarkingGuideMarks(q.marking_guide || '') } : {}),
        subjectSectionIdx: isMultiSubject ? activeSubjectPane : null,
      }));
      setForm(f => ({ ...f, questions: [...f.questions, ...tagged] }));
      setAiSuccess(`${data.questions.length} questions added.`);
      setScrollToBottom(true);
      setTimeout(() => setAiSuccess(''), 4000);
    } else setAiError(data?.error || 'AI generation failed');
  };

  const handleGenerate = async (regenerate = false) => {
    if (!form.title.trim()) { setGenError('Title is required'); return; }
    if (selectedSubjects.length === 0) {
      setGenError(`Please select at least one subject for ${form.prebuiltConfig?.examName} before generating.`);
      return;
    }
    if (regenerate && !window.confirm(`Replace the ${form.questions.length} existing question(s) with a freshly generated set?`)) return;
    if (!(await flushDraft())) { setGenError('Could not save the draft before generating questions.'); return; }
    setGenerating(true); setGenError(''); setError('');
    const msgs = ['Loading exam bank…','Selecting questions…','Preparing question set…','Formatting questions…','Almost done…'];
    let msgIdx = 0; setGenStatus(msgs[0]);
    const timer = setInterval(() => { msgIdx = Math.min(msgIdx + 1, msgs.length - 1); setGenStatus(msgs[msgIdx]); }, 1800);
    try {
      let examId = savedExamIdRef.current;
      const payload = buildPayload();
      if (!examId) {
        const { ok, data } = await schoolFetch('/school/exams', { method: 'POST', body: JSON.stringify(payload) });
        if (!ok) { setGenError(data.error || 'Failed to save exam'); return; }
        examId = data.exam._id;
        setSavedExamId(examId);
      } else {
        await schoolFetch(`/school/exams/${examId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      }
      // Generate questions for each selected subject from the prebuilt bank
      const allQuestions = [];
      for (let idx = 0; idx < selectedSubjects.length; idx++) {
        const subj = selectedSubjects[idx];
        const { ok: aiOk, data: aiData } = await schoolFetch(`/school/exams/${examId}/prebuilt-generate`, {
          method: 'POST',
          body: JSON.stringify({ examName: form.prebuiltConfig.examName, subject: subj.subjectName, questionCount: 40 }),
        });
        if (!aiOk) { setGenError(aiData.error || 'Generation failed — check your school plan or try again'); return; }
        const tagged = (aiData.questions || []).map(q => ({ ...q, subjectSectionIdx: idx }));
        allQuestions.push(...tagged);
      }
      setForm(f => ({ ...f, questions: allQuestions }));
      setQuestionsReady(true);
      setActivePane('questions');
    } catch { setGenError('Connection error'); }
    finally { clearInterval(timer); setGenStatus(''); setGenerating(false); }
  };

  const handleSaveDraft = async () => {
    if (!savedExamId) return;
    setSaving(true); setError('');
    try {
      const { ok, data } = await schoolFetch(`/school/exams/${savedExamId}`, { method: 'PATCH', body: JSON.stringify(buildPayload()) });
      if (ok) onSaved(data.exam);
      else setError(data.error || 'Save failed');
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!form.title.trim())           { setError('Title is required');           return; }
    if (selectedSubjects.length === 0){ setError('Please select a subject');     return; }
    if (!form.classId)                { setError('Class is required');           return; }
    if (!isManualMode && !form.settings.startDate) { setError('Start date is required'); return; }
    if (!isManualMode && !form.settings.endDate)   { setError('End date is required'); return; }
    if (!isManualMode && form.settings.startDate && form.settings.endDate &&
        new Date(form.settings.endDate) <= new Date(form.settings.startDate)) {
      setError('End date must be after the start date'); return;
    }
    if (!form.settings.totalScore || Number(form.settings.totalScore) <= 0) {
      setError('Total score is required'); return;
    }
    if (!(await flushDraft())) return;
    setSaving(true); setError('');
    try {
      let examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      const payload = buildPayload();
      // 1. Save / update exam
      if (!examId) {
        const { ok: saveOk, data: saveData } = await schoolFetch('/school/exams', { method: 'POST', body: JSON.stringify(payload) });
        if (!saveOk) { setError(saveData.error || 'Failed to save exam'); return; }
        examId = saveData.exam._id;
        setSavedExamId(examId);
      } else {
        const { ok: saveOk, data: saveData } = await schoolFetch(`/school/exams/${examId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        if (!saveOk) { setError(saveData.error || 'Failed to save exam'); return; }
      }
      // 2. Publish / schedule
      const scheduledAt = localDatetimeToISO(form.settings.startDate);
      const endDate = localDatetimeToISO(form.settings.endDate);
      const { ok, data } = await schoolFetch(`/school/exams/${examId}/publish`, { method: 'POST', body: JSON.stringify({ scheduledAt, endDate }) });
      if (!ok) { setError(data?.error || 'Publish failed'); return; }
      // 3. Auto-assign to selected class and arm — fire-and-forget, failure is non-fatal
      schoolFetch(`/school/exams/${examId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ classId: form.classId, ...(form.classArm ? { classArm: form.classArm } : {}) }),
      }).catch(e => console.warn('Auto-assign failed:', e));
      onSaved(data.exam);
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  return (
    <>
    <div className={css.modalOverlay} onClick={event => { if (event.target === event.currentTarget) handleClose(); }}>
      <div className={`${css.modalLarge} BubblePopIn`} style={{ maxWidth: 900, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <div className={css.modalTitleRow}>
            <h2 className={css.modalTitle}>{reviewRequest ? 'Review Exam Request' : isEdit ? 'Edit Exam' : 'Create Exam'}</h2>
            <span className={css.examModeBadge}>
              {form.examMode === 'manual_scores' ? 'Manual Score Entry' : form.examMode === 'manual' ? 'Manual + AI Script Grading' : 'Computer-Based (CBT)'}
            </span>
          </div>
          <button className={css.closeBtn} onClick={handleClose} aria-label="Close exam editor">&times;</button>
        </div>

        {reviewRequest && exam?.teacherId && (
          <div style={{ margin: '12px 24px 0', background: '#f0fafb', border: '1px solid #b2e0e3', borderRadius: 9, padding: '8px 14px', fontSize: '0.83rem', color: '#1a6e73' }}>
            Submitted by <strong>{exam.teacherId.firstName} {exam.teacherId.lastName}</strong>{exam.teacherId.email ? ` (${exam.teacherId.email})` : ''} — review and edit as needed before approving.
          </div>
        )}

        {/* Pane tabs */}
        <div className={css.modalTabs}>
          <button
            className={`${css.modalTab} ${activePane === 'details' ? css.modalTabActive : ''}`}
            onClick={() => setActivePane('details')}
          >
            Details &amp; Settings
            {isScheduled && <span className={css.tabLockBadge}>{canEditScheduledSettings ? 'settings locked' : 'read-only'}</span>}
          </button>
          <button
            className={`${css.modalTab} ${activePane === 'questions' ? css.modalTabActive : ''}`}
            onClick={() => { if (isPrebuilt && !questionsReady) return; setActivePane('questions'); }}
            style={isPrebuilt && !questionsReady ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            title={isPrebuilt && !questionsReady ? 'Generate the exam first to view questions' : ''}
          >
            Questions ({form.questions.length})
            {isPrebuilt && !questionsReady && <span style={{ fontSize: '0.7rem', marginLeft: 5 }}>🔒</span>}
          </button>
        </div>

        {activePane === 'details' && (
          <div className={css.modalBody}>
            {error && <div className={css.inlineError}>{error}</div>}
            {isScheduled && (
              <div className={css.scheduledBanner}>
                {hasStarted
                  ? 'This exam is live. Editing is locked.'
                  : 'This exam is scheduled. Questions remain editable; use Edit beside supported settings to change them before the start time.'}
              </div>
            )}
            {scheduleChangePending && (
              <div role="status" style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 7, border: '1px solid #f3d18b', background: '#fff8e7', color: '#79530b', fontSize: '0.82rem' }}>
                Settings changes are not applied until you click Save Changes, which notifies assigned students and their linked parents.
              </div>
            )}
            {questionsReady && !isScheduled && (
              <div className={css.genBanner}>
                ✓ {form.questions.length} questions generated — edits here are preserved. Click <strong>View Questions</strong> when ready.
              </div>
            )}

            {/* Exam Title */}
            <div className={css.examFR}>
              <label>Exam Title *</label>
              <input className={css.formInput} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. UTME Mock - June 2026" disabled={isScheduled} />
            </div>

            {/* Prebuilt Exam Panel */}
            <div className={css.prebuiltPanel}>
              <div className={css.prebuiltHeader}>
                <SchoolIcon fontSize="small" style={{ color: '#5fbec5' }} />
                <span className={css.prebuiltTitle}>Assign Prebuilt Mock Exam</span>
              </div>
              <p className={css.prebuiltSub}>
                Link this assignment to a platform prebuilt mock exam (UTME, WAEC, etc.). The generated exam will exactly mimic the selected exam.
              </p>
              {form.source === 'external' && form.prebuiltConfig ? (
                <div className={css.selectedPrebuilt}>
                  <div className={css.selectedPrebuiltInfo}>
                    <CheckCircleIcon fontSize="small" style={{ color: '#5fbec5', flexShrink: 0 }} />
                    <div>
                      <div className={css.selectedPrebuiltName}>{form.prebuiltConfig.examName}</div>
                      <div className={css.selectedPrebuiltDesc}>{form.prebuiltConfig.description}</div>
                    </div>
                  </div>
                  {!isScheduled && (
                    <div className={css.prebuiltActions}>
                      <button className={css.prebuiltChangeBtn} type="button" onClick={() => setShowPrebuiltPicker(true)}>Change</button>
                      <button className={css.prebuiltRemoveBtn} type="button" onClick={() => { setForm(f => ({ ...f, source: 'internal', prebuiltConfig: null })); setIsMultiSubject(false); setSelectedSubjects([]); setQuestionsReady(false); }}>Remove</button>
                    </div>
                  )}
                </div>
              ) : !isScheduled ? (
                <button className={css.prebuiltBtn} type="button" onClick={() => setShowPrebuiltPicker(true)}>Browse &amp; Select Exam</button>
              ) : null}
              {isPrebuilt && (
                <p className={css.hintText} style={{ marginTop: 8 }}>
                  Select up to 4 subjects below. Questions for each subject will be drawn from the <strong>{form.prebuiltConfig?.examName}</strong> question bank and each subject&apos;s duration is set automatically.
                </p>
              )}
              {genError && <div className={css.inlineError} style={{ marginTop: 10 }}>{genError}</div>}
              {genStatus && <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#5fbec5', fontStyle: 'italic' }}>{genStatus}</div>}
              {isPrebuilt && !aiEnabled && (
                <div className={css.premiumLockBanner}>
                  🔒 Prebuilt exam generation is a <strong>Premium</strong> feature. Contact your administrator to upgrade your school plan.
                </div>
              )}
            </div>

            {/* Class & Arm — selected first so subject list can be filtered by class */}
            <div className={css.formGrid2}>
              <div className={css.examFR}>
                <label>Class</label>
                <select className={css.formSelect} value={form.classId} onChange={e => {
                  setForm(f => ({ ...f, classId: e.target.value, classArm: '' }));
                  // Clear subject selection when class changes
                  if (!isMultiSubject) setSelectedSubjects([]);
                }} disabled={isScheduled}>
                  <option value="">— All classes —</option>
                  {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className={css.examFR}>
                <label>Arm (optional)</label>
                <select className={css.formSelect} value={form.classArm} onChange={e => setForm(f => ({ ...f, classArm: e.target.value }))} disabled={isScheduled || !form.classId}>
                  <option value="">— Whole class —</option>
                  {(classes.find(c => String(c._id) === form.classId)?.arms || []).map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <p className={css.hintText}>
              {isManualMode ? 'Students will be added to this exam without schedule or missed-exam notifications.' : 'Students in the selected class will be assigned and notified by email when you schedule the exam.'}
            </p>

            {/* Multi-subject toggle — hidden when a prebuilt exam is selected (always multi-subject) */}
            {!isPrebuilt && (
              <div style={{ background: isMultiSubject ? 'rgba(95,190,197,0.06)' : '#fafcfc', border: `1.5px solid ${isMultiSubject ? '#5fbec5' : '#e0eaec'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isScheduled ? 'not-allowed' : 'pointer' }}>
                  <input className="schoolCheckbox" type="checkbox" checked={isMultiSubject} onChange={e => { setIsMultiSubject(e.target.checked); if (!e.target.checked && selectedSubjects.length > 1) setSelectedSubjects(prev => [prev[0]]); }} disabled={isScheduled} style={{ accentColor: '#5fbec5', width: 16, height: 16 }} />
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0c1e28' }}>Multi-subject exam (e.g. UTME — 4 subjects)</span>
                </label>
                <p style={{ margin: '4px 0 0 26px', fontSize: '0.77rem', color: '#90a4ae', lineHeight: 1.5 }}>
                  Students see subject tabs and can switch freely. Each subject has its own timer that sums to the total duration.
                </p>
              </div>
            )}

            {/* Subject selection — filtered by class and prebuilt */}
            {!isMultiSubject ? (
              <div className={css.examFR}>
                <label>Subject{isPrebuilt && prebuiltEntry?.subjects?.length > 0 ? ' (filtered by selected exam)' : ''}</label>
                <select className={css.formSelect} value={selectedSubjects[0]?.subjectId || ''} onChange={e => {
                  const subj = subjects.find(s => String(s._id) === e.target.value);
                  setSelectedSubjects(subj ? [{ subjectId: String(subj._id), subjectName: subj.name, duration: form.settings.duration || 60 }] : []);
                }} disabled={isScheduled}>
                  <option value="">— Select subject —</option>
                  {availableSubjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                {isPrebuilt && prebuiltEntry?.subjects?.length > 0 && availableSubjects.length === 0 && (
                  <p style={{ fontSize: '0.78rem', color: '#e53935', margin: '4px 0 0' }}>No matching subjects found. Add the relevant subject in your subjects settings first.</p>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#455a64', marginBottom: 6, display: 'block' }}>{isPrebuilt ? `Subjects — up to 4 from ${form.prebuiltConfig?.examName} bank` : 'Subjects (max 4)'}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      {selectedSubjects.map((sel, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, background: activeSubjectPane === idx ? '#e0f7fa' : '#f0f4f6', borderRadius: 8, padding: '5px 10px', border: `1.5px solid ${activeSubjectPane === idx ? '#5fbec5' : '#cfd8dc'}` }}>
                      <button onClick={() => setActiveSubjectPane(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', color: activeSubjectPane === idx ? '#0c7c84' : '#546e7a' }}>{sel.subjectName}</button>
                        <input type="number" min="5" max="180" value={sel.duration} onChange={e => updateSubjectDuration(idx, e.target.value)} disabled={isScheduled && !unlockedFields.duration} style={{ width: 52, padding: '2px 6px', borderRadius: 6, border: '1px solid #cfd8dc', fontSize: '0.78rem' }} title="Duration (minutes)" />
                      <span style={{ fontSize: '0.7rem', color: '#90a4ae' }}>min</span>
                      {!isScheduled && <button onClick={() => removeSubject(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: '0.8rem', padding: 0 }}>✕</button>}
                    </div>
                  ))}
                </div>
                {selectedSubjects.length < 4 && !isScheduled && (
                  <select className={css.formSelect} value="" onChange={e => { addSubject(e.target.value); setActiveSubjectPane(selectedSubjects.length); }} style={{ maxWidth: 260 }}>
                    <option value="">+ Add subject...</option>
                    {availableSubjects.filter(s => !selectedSubjects.find(sel => sel.subjectId === String(s._id))).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                )}
                {isMultiSubject && selectedSubjects.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#607d8b' }}>
                    Total duration: <strong>{selectedSubjects.reduce((s, sub) => s + (sub.duration || 30), 0)} minutes</strong>
                  </div>
                )}
              </div>
            )}

            {/* Exam Category — radio buttons with descriptions */}
            <div className={css.examFR}>
              <label>Exam Category{scheduledEditLink('examCategory', 'exam category')}</label>
              <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                {[
                  { value: 'test', label: 'Test', desc: 'Informal test, not counted in report cards' },
                  { value: 'ca',   label: 'CA (Continuous Assessment)', desc: 'Counted in term report card' },
                  { value: 'exam', label: 'End-of-Term Exam', desc: 'Main exam for term report card' },
                ].map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: isScheduled && !unlockedFields.examCategory ? 'not-allowed' : 'pointer', fontSize: '0.82rem', opacity: isScheduled && !unlockedFields.examCategory ? 0.55 : 1 }}>
                    <input className="schoolRadio" type="radio" value={opt.value} checked={form.examCategory === opt.value} onChange={() => (!isScheduled || unlockedFields.examCategory) && setForm(f => ({ ...f, examCategory: opt.value }))} disabled={isScheduled && !unlockedFields.examCategory} style={{ marginTop: 2 }} />
                    <span>
                      <strong>{opt.label}</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className={css.examFR}>
              <label>Instructions</label>
              <textarea className={css.formTextarea} rows={2} value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} placeholder="Optional exam instructions..." disabled={isScheduled} />
            </div>

            <div className={css.sectionLabel}>Settings</div>
            <div className={css.formGrid3}>
              <div className={css.examFR}>
                <label>{isMultiSubject ? 'Duration (auto = sum of subjects)' : 'Duration (minutes)'}{scheduledEditLink('duration', 'duration')}</label>
                {isMultiSubject ? (
                  <input className={css.formInput} type="number" value={computedDuration} disabled readOnly style={{ background: '#f0f4f6', color: '#607d8b' }} />
                ) : (
                  <input className={css.formInput} type="number" min="1" value={form.settings.duration} onChange={setSetting('duration')} disabled={isScheduled && !unlockedFields.duration} />
                )}
              </div>
              <div className={css.examFR}>
                <label>Pass Mark (%) {scheduledEditLink('passmark', 'pass mark')}</label>
                <input className={css.formInput} type="number" min="0" max="100" value={form.settings.passmark} onChange={setSetting('passmark')} disabled={isScheduled && !unlockedFields.passmark} />
              </div>
              <div className={css.examFR}>
                <label>Total Score * {scheduledEditLink('totalScore', 'total score')}</label>
                <input className={css.formInput} type="number" min="1" value={form.settings.totalScore} onChange={setSetting('totalScore')} disabled={isScheduled && !unlockedFields.totalScore} placeholder="e.g. 100" />
              </div>
            </div>

            <div className={css.formGrid2}>
              <div className={css.examFR}>
                <label>Start Date &amp; Time *{scheduledEditLink('startDate', 'start time')}</label>
                <DateTimePicker value={form.settings.startDate} onChange={handleStartDateChange} disabled={isManualMode || (isScheduled && !unlockedFields.startDate)} placeholder="Pick start date & time" min={new Date()} />
              </div>
              <div className={css.examFR}>
                <label>End Date &amp; Time *{scheduledEditLink('endDate', 'end time')}</label>
                <DateTimePicker value={form.settings.endDate} onChange={handleEndDateChange} disabled={isManualMode || (isScheduled && !unlockedFields.endDate)} placeholder="Pick end date & time" min={form.settings.startDate ? new Date(new Date(form.settings.startDate).getTime() + 1) : new Date()} />
              </div>
            </div>

            <div className={css.formGrid2}>
              <div className={css.examFR}>
                <label>Result Visibility{scheduledEditLink('resultVisibility', 'result visibility')}</label>
                <select className={css.formSelect} value={form.settings.resultVisibility} onChange={setSetting('resultVisibility')} disabled={isScheduled && !unlockedFields.resultVisibility}>
                  <option value="immediate">Immediately after submit</option>
                  <option value="after_deadline">After exam closes</option>
                  <option value="manual">Manual (teacher releases)</option>
                </select>
              </div>
              <div className={css.examFR}>
                <label>Max Attempts</label>
                <input className={css.formInput} type="number" min="1" value={form.settings.maxAttempts} onChange={setSetting('maxAttempts')} disabled={isScheduled} />
              </div>
            </div>
            <div className={css.checkRow}>
              <label><input className="schoolCheckbox" type="checkbox" checked={form.settings.shuffleQuestions} onChange={setSetting('shuffleQuestions')} disabled={isScheduled} /> Shuffle questions</label>
              <label><input className="schoolCheckbox" type="checkbox" checked={form.settings.shuffleOptions} onChange={setSetting('shuffleOptions')} disabled={isScheduled} /> Shuffle options</label>
            </div>

            {/* Proctoring — with full descriptions matching TeacherDashboard */}
            {form.examMode === 'cbt' && <>
            <div className={css.sectionLabel} style={{ marginTop: 18 }}>Proctoring</div>
            <div style={{ border: '1.5px solid', borderColor: form.settings.proctoring.enabled ? '#5fbec5' : '#e0eaec', borderRadius: 10, padding: '14px 16px', background: form.settings.proctoring.enabled ? 'rgba(95,190,197,0.04)' : '#fafcfc', transition: 'all .2s' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isScheduled ? 'not-allowed' : 'pointer', marginBottom: 2 }}>
                <input className="schoolCheckbox" type="checkbox" checked={form.settings.proctoring.enabled} onChange={setProctoringField('enabled')} disabled={isScheduled} style={{ width: 16, height: 16, accentColor: '#5fbec5' }} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0c1e28' }}>Enable proctoring for this exam</span>
              </label>
              <p style={{ margin: '2px 0 10px 26px', fontSize: '0.78rem', color: '#90a4ae', lineHeight: 1.5 }}>
                When enabled, students are monitored for suspicious activity (tab switches, copy attempts, dev tools, etc.). Violations are logged and visible in exam analytics.
              </p>
              {form.settings.proctoring.enabled && (
                <div style={{ marginLeft: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: isScheduled ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: isScheduled ? 0.55 : 1 }}>
                    <input className="schoolCheckbox" type="checkbox" checked={form.settings.proctoring.requireFullscreen} onChange={setProctoringField('requireFullscreen')} disabled={isScheduled} style={{ marginTop: 2, accentColor: '#5fbec5' }} />
                    <span>
                      <strong style={{ color: '#0c1e28' }}>Require fullscreen mode</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>Students must enter fullscreen before questions appear. Exiting fullscreen is logged as a violation.</span>
                    </span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0c1e28', whiteSpace: 'nowrap' }}>Max violations before flagging:</label>
                    <input type="number" min="1" max="20" style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1.5px solid #e0eaec', fontSize: '0.85rem' }} value={form.settings.proctoring.maxViolations} onChange={setProctoringField('maxViolations')} disabled={isScheduled} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: isScheduled ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: isScheduled ? 0.55 : 1 }}>
                    <input className="schoolCheckbox schoolCheckboxDanger" type="checkbox" checked={form.settings.proctoring.blockOnMaxViolations} onChange={setProctoringField('blockOnMaxViolations')} disabled={isScheduled} style={{ marginTop: 2, accentColor: '#dc2626' }} />
                    <span>
                      <strong style={{ color: '#dc2626' }}>Auto-submit when max violations are reached</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>The exam will be automatically submitted if a student exceeds the limit above. Use with caution.</span>
                    </span>
                  </label>
                </div>
              )}
            </div>
            </>}

            {school?.currentTerm && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fafb', border: '1px solid #b2e0e3', borderRadius: 9, padding: '8px 14px', marginTop: 12, fontSize: '0.83rem', color: '#1a6e73' }}>
                <span style={{ fontWeight: 700 }}>Active Term:</span>
                <span>{school.currentTerm === 'first' ? 'First' : school.currentTerm === 'second' ? 'Second' : 'Third'} Term{school.academicSession ? ` · ${school.academicSession}` : ''}</span>
              </div>
            )}
          </div>
        )}

        {activePane === 'questions' && (
          <div className={css.modalBody}>
            {hasStarted && (
              <div className={css.startedLockBanner}>
                🔒 <strong>This exam is live.</strong> Questions cannot be edited once the start time has passed. Students are currently taking this exam.
              </div>
            )}
            {/* Multi-subject question tabs */}
            {isMultiSubject && selectedSubjects.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {selectedSubjects.map((sel, idx) => {
                  const cnt = form.questions.filter(q => (q.subjectSectionIdx ?? 0) === idx).length;
                  return (
                    <button key={idx} onClick={() => setActiveSubjectPane(idx)} style={{
                      padding: '5px 14px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer',
                      borderColor: activeSubjectPane === idx ? '#0c7c84' : '#cfd8dc',
                      background: activeSubjectPane === idx ? '#0c7c84' : '#fff',
                      color: activeSubjectPane === idx ? '#fff' : '#546e7a',
                      fontWeight: 600, fontSize: '0.82rem',
                    }}>
                      {sel.subjectName} <span style={{ opacity: 0.75, fontSize: '0.75rem' }}>({cnt})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* AI panel */}
            <div className={css.aiPanel}>
              <div className={css.aiPanelHeader}>
                {aiEnabled ? <AutoFixHighIcon fontSize="small" style={{ color: '#5fbec5' }} /> : <LockIcon fontSize="small" style={{ color: '#90a4ae' }} />}
                <span className={css.aiPanelTitle}>AI Question Generator {!aiEnabled && <span style={{ color: '#90a4ae', fontWeight: 400 }}>(not enabled on your plan)</span>}</span>
                {isMultiSubject && <span style={{ fontSize: '0.75rem', background: '#e0f7fa', color: '#0c7c84', borderRadius: 99, padding: '2px 8px', fontWeight: 700 }}>For: {selectedSubjects[activeSubjectPane]?.subjectName}</span>}
                {hasCurriculum && <span className={css.aiCurriculumTag}>✓ Curriculum-Grounded</span>}
              </div>
              {/* Curriculum-grounded generation panel */}
              {hasCurriculum && aiEnabled && currTopics.length > 0 && (
                <div className={css.currGenPanel}>
                  <div className={css.currGenHeader}>
                    <span className={css.currGenTitle}>📚 From Curriculum</span>
                    <span className={css.currGenSub}>Generate questions directly from the uploaded curriculum for {activeSubjectDoc?.name}.</span>
                  </div>
                  <div>
                    <div className={css.currGenTopicsRow}>
                      {currTopics.map(t => (
                        <button key={t} type="button" className={`${css.currGenTopicBtn} ${currSelectedTopics.includes(t) ? css.currGenTopicBtnSel : ''}`} onClick={() => setCurrSelectedTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}>
                          <span className={`${css.coverageTopicChip} ${curriculumCoverage?.topics?.find(item => item.name === t)?.covered ? css.coverageTopicChipCovered : css.coverageTopicChipUncovered}`}>
                            {curriculumCoverage?.topics?.find(item => item.name === t) ? (curriculumCoverage.topics.find(item => item.name === t).covered ? '✓' : '○') : (curriculumCoverageLoading ? '…' : '○')} {t}
                            {curriculumCoverage?.topics?.find(item => item.name === t)?.covered && curriculumCoverage.topics.find(item => item.name === t).questionCount > 0 && (
                              <span className={css.coverageTopicCount}>{curriculumCoverage.topics.find(item => item.name === t).questionCount}</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button type="button" className={css.currGenLinkBtn} onClick={() => setCurrSelectedTopics(currTopics)}>Select all</button>
                      {currSelectedTopics.length > 0 && <button type="button" className={css.currGenLinkBtn} onClick={() => setCurrSelectedTopics([])}>Clear</button>}
                      <span className={css.currGenSelectedCount}>{currSelectedTopics.length} topic{currSelectedTopics.length !== 1 ? 's' : ''} selected</span>
                    </div>
                    {coveredSelectedTopics.length > 0 && (
                      <div className={css.coverageNotice} role="status">
                        {coveredSelectedTopics.length} selected topic{coveredSelectedTopics.length !== 1 ? 's already have' : ' already has'} exam(s) generated previously. This is just a heads up even though you can still generate.
                      </div>
                    )}
                  </div>
                  {currSelectedTopics.length > 0 && (
                    <div className={css.currGenSelectedTopics} aria-label="Selected topics">
                      <span className={css.currGenSelectedTopicsLabel}>Selected topics:</span>
                      {currSelectedTopics.map(topic => (
                        <span key={topic} className={css.currGenSelectedTopic}>
                          {topic}
                          <button
                            type="button"
                            className={css.currGenSelectedTopicRemove}
                            onClick={() => setCurrSelectedTopics(previous => previous.filter(item => item !== topic))}
                            aria-label={`Remove ${topic}`}
                            title={`Remove ${topic}`}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={css.currGenControlsRow}>
                    <span className={css.currGenLabel}>Per topic:</span>
                    <select className={css.aiSelect} value={currCountPerTopic} onChange={e => setCurrCountPerTopic(Number(e.target.value))} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className={css.currGenLabel}>Difficulty:</span>
                    <select className={css.aiSelect} value={currDifficulty} onChange={e => setCurrDifficulty(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
                      <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                    </select>
                    <select className={css.aiSelect} value={currQType} onChange={e => setCurrQType(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
                      <option value="mcq">MCQ</option><option value="theory">Theory</option><option value="mixed">Mixed</option>
                    </select>
                    <span className={css.currGenTotal}>{currSelectedTopics.length * currCountPerTopic} Qs total</span>
                    <button className={css.currGenBtn} onClick={handleCurriculumGenerate} disabled={currLoading || !currSelectedTopics.length}>{currLoading ? 'Generating…' : 'Generate from Curriculum'}</button>
                  </div>
                  {currError && <div className={css.inlineError} style={{ marginTop: 4, marginBottom: 0 }}>{currError}</div>}
                </div>
              )}
              <div className={css.aiInputRow}>
                <input className={css.aiInput} value={aiTopic} onChange={e => setAiTopic(e.target.value)} placeholder="Topic (e.g. Quadratic equations)" disabled={!aiEnabled} />
                <input className={css.formInput} style={{ width: 60 }} type="number" min="1" max="20" value={aiCount} onChange={e => setAiCount(Number(e.target.value))} disabled={!aiEnabled} />
                <select className={css.aiSelect} value={aiDiff} onChange={e => setAiDiff(e.target.value)} disabled={!aiEnabled}>
                  <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                </select>
                <select className={css.aiSelect} value={aiQType} onChange={e => setAiQType(e.target.value)} disabled={!aiEnabled}>
                  <option value="mcq">MCQ</option><option value="theory">Theory</option>
                </select>
                <button className={css.aiBtn} onClick={handleAiGenerate} disabled={aiLoading || !aiEnabled}>{aiLoading ? 'Generating...' : 'Generate'}</button>
              </div>
              {aiError && <div className={css.inlineError} style={{ marginTop: 8 }}>{aiError}</div>}
              {aiSuccess && <div className={css.inlineSuccess} style={{ marginTop: 8 }}>{aiSuccess}</div>}
            </div>

            {/* Question list header + equal marks toggle */}
            <div className={css.qListHeader}>
              <span>{form.questions.filter(q => !isMultiSubject || (q.subjectSectionIdx ?? 0) === activeSubjectPane).length} question(s){isMultiSubject ? ` for ${selectedSubjects[activeSubjectPane]?.subjectName || ''}` : ''}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className={css.addQBtn}
                  disabled={hasStarted}
                  onClick={() => { setUploadQModal(true); setUploadQError(''); setUploadQFileName(''); uploadQFileDataRef.current = null; }}
                >
                  <UploadIcon fontSize="small" /> Upload questions
                </button>
                <button className={css.addQBtn} onClick={addBlankQuestion}><AddIcon fontSize="small" /> Add Question</button>
              </div>
            </div>

            {/* Equal marks toggle */}
            <div className={css.equalMarksBar}>
              <label className={css.toggleSwitchSm}>
                <input className="schoolCheckbox" type="checkbox" checked={equalMarks} onChange={e => setEqualMarks(e.target.checked)} />
                <span className={css.toggleSliderSm} />
              </label>
              <div>
                <div className={css.equalMarksLabel}>Equal marks per question</div>
                <div className={css.equalMarksSub}>
                  {equalMarks
                    ? `Each question: ${form.questions.length > 0 && totalScoreNum > 0 ? (totalScoreNum / form.questions.length).toFixed(2) : '—'} marks`
                    : 'Set individual marks per question below'}
                </div>
              </div>
              {!equalMarks && (
                <span className={css.scoreSumBadge} style={{ color: form.questions.length > 0 && Math.abs(scoreSumManual - totalScoreNum) > 0.01 ? '#e53935' : '#10b981' }}>
                  {Math.round(scoreSumManual * 100) / 100} / {totalScoreNum} marks
                </span>
              )}
            </div>

            {/* Questions list — filtered by active subject tab for multi-subject */}
            {(isMultiSubject
              ? form.questions.filter(q => (q.subjectSectionIdx ?? 0) === activeSubjectPane)
              : form.questions
            ).length === 0 ? (
              <div className={css.emptyState} style={{ padding: '32px 0' }}>No questions yet{isMultiSubject ? ` for ${selectedSubjects[activeSubjectPane]?.subjectName}` : ''}. Add one above or use AI.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {form.questions.map((q, globalIdx) => {
                  if (isMultiSubject && (q.subjectSectionIdx ?? 0) !== activeSubjectPane) return null;
                  const subjectQs = isMultiSubject ? form.questions.filter(x => (x.subjectSectionIdx ?? 0) === activeSubjectPane) : form.questions;
                  const localIdx = subjectQs.indexOf(q);
                  return (
                    <AdminQuestionRow
                      key={globalIdx}
                      q={q}
                      index={localIdx}
                      onChange={(localI, key, val) => changeQuestion(globalIdx, key, val)}
                      onRemove={() => removeQuestion(globalIdx)}
                      equalMarks={equalMarks}
                      autoScore={autoScore}
                      examId={savedExamId}
                    />
                  );
                })}
                <div ref={questionsEndRef} />
              </div>
            )}
          </div>
        )}

        {uploadQModal && (
          <div className={css.modalOverlay} onClick={() => setUploadQModal(false)} style={{ zIndex: 1400 }}>
            <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div className={css.modalHeader}>
                <h2 className={css.modalTitle}>Upload Questions from File</h2>
                <button className={css.closeBtn} onClick={() => setUploadQModal(false)}>✕</button>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#607d8b', margin: '0 0 14px' }}>
                Upload a PDF, Word document, spreadsheet, image, or plain text file. Text PDFs and structured spreadsheets are read directly; scanned or non-copyable PDFs and images use AI vision before formatting.
              </p>
              <div
                style={{ border: '2px dashed #b2d8da', borderRadius: 10, padding: '22px 16px', textAlign: 'center', cursor: 'pointer', background: '#f0fafb', marginBottom: 14 }}
                onClick={() => uploadQFileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) { uploadQFileDataRef.current = file; setUploadQFileName(file.name); }
                }}
              >
                <input
                  ref={uploadQFileRef}
                  type="file"
                  accept=".pdf,.txt,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) { uploadQFileDataRef.current = file; setUploadQFileName(file.name); }
                  }}
                />
                <UploadIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 6 }} />
                {uploadQFileName
                  ? <div style={{ fontWeight: 700, color: '#0c7c84', fontSize: '0.88rem' }}>{uploadQFileName}</div>
                  : <><div style={{ fontWeight: 600, color: '#607d8b', fontSize: '0.88rem' }}>Click or drag &amp; drop</div><div style={{ fontSize: '0.75rem', color: '#90a4ae' }}>PDF, Word, Excel, CSV, image, or TXT - max 10 MB</div></>}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#546e7a', display: 'block', marginBottom: 5 }}>Extract as</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['mcq', 'theory'].map(type => (
                    <button key={type} type="button" onClick={() => setUploadQType(type)} style={{ padding: '6px 18px', borderRadius: 8, border: '1.5px solid', borderColor: uploadQType === type ? '#5fbec5' : '#dde4e8', background: uploadQType === type ? 'rgba(95,190,197,0.12)' : '#fff', color: uploadQType === type ? '#0c7c84' : '#607d8b', fontWeight: uploadQType === type ? 700 : 500, fontSize: '0.84rem', cursor: 'pointer' }}>
                      {type === 'mcq' ? 'MCQ' : 'Theory'}
                    </button>
                  ))}
                </div>
              </div>
              {uploadQError && <div className={css.inlineError} style={{ marginBottom: 10 }}>{uploadQError}</div>}
              <div className={css.modalFooter}>
                <button className={css.cancelBtn} onClick={() => setUploadQModal(false)}>Cancel</button>
                <button className={css.saveBtn} onClick={handleUploadQuestions} disabled={uploadQLoading || !uploadQFileName}>
                  {uploadQLoading ? 'Extracting...' : 'Extract Questions'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={css.modalFooter}>
          <span role="status" aria-live="polite" style={{ marginRight: 'auto', color: autosaveState === 'error' ? '#b42318' : '#607d8b', fontSize: '0.78rem' }}>
            {autosaveState === 'saving' || autosaveState === 'pending' ? 'Saving draft…' : autosaveState === 'saved' ? 'Draft saved' : autosaveState === 'error' ? 'Draft not saved' : ''}
          </span>
          {autosaveState === 'error' && <button type="button" className={css.draftBtn} onClick={() => {
            const latest = latestAutosaveRef.current || { payload: buildPayload(), snapshot: JSON.stringify(buildPayload()) };
            queueDraftSave(latest.payload, latest.snapshot);
          }} disabled={saving}>Retry</button>}
          <button className={css.cancelBtn} onClick={handleClose}>{isScheduled ? 'Close' : 'Cancel'}</button>
          {reviewRequest ? (
            <>
              <button className={css.draftBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              <button
                className={css.btnSecondary}
                style={{ color: '#e53935', borderColor: '#fca5a5' }}
                onClick={() => onReject && onReject(exam)}
                disabled={saving}
              >
                Reject
              </button>
              <button className={css.publishBtn} onClick={handleApproveClick} disabled={saving}>{saving ? 'Approving...' : 'Approve & Schedule'}</button>
            </>
          ) : isScheduled ? (
            !hasStarted && (activePane === 'questions' || scheduleChangePending) && (
              <button className={css.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : scheduleChangePending ? 'Save Changes' : 'Save Questions'}</button>
            )
          ) : isPrebuilt ? (
            <>
              {savedExamId && <button className={css.draftBtn} onClick={handleSaveDraft} disabled={saving}>{saving ? 'Saving...' : 'Save Draft'}</button>}
              {questionsReady ? (
                <button className={css.publishBtn} onClick={handlePublish} disabled={saving}>{saving ? (isManualMode ? 'Adding...' : 'Scheduling...') : (isManualMode ? 'Add Exam' : 'Schedule Exam')}</button>
              ) : (
                <button className={css.generateBtn} onClick={() => handleGenerate(questionsReady)} disabled={generating || !aiEnabled}>
                  {generating ? genStatus || 'Generating…' : questionsReady ? 'Re-generate Questions' : 'Generate Questions'}
                </button>
              )}
            </>
          ) : (
            <>
              <button className={css.draftBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save as Draft'}</button>
              {(savedExamId || isEdit) && (
                <button className={css.publishBtn} onClick={handlePublish} disabled={saving}>{saving ? (isManualMode ? 'Adding...' : 'Scheduling...') : (isManualMode ? 'Add Exam' : 'Schedule Exam')}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {showPrebuiltPicker && (
      <PrebuiltExamPickerModal
        onSelect={ex => {
          setForm(f => ({ ...f, source: 'external', prebuiltConfig: { slug: ex.slug, examName: ex.examName, description: ex.description } }));
          setIsMultiSubject(true);
          setSelectedSubjects([]);
          setQuestionsReady(false);
          setShowPrebuiltPicker(false);
        }}
        onClose={() => setShowPrebuiltPicker(false)}
      />
    )}
    </>
  );
}

//  Admin Exams Tab 
// ─── Exam Approval Requests (controlSettings.questionsApprovalRequired) ──────
// Teacher-submitted exams awaiting admin sign-off before they schedule/go live.
function AdminExamRequestsTable({ onChanged, subjects, classes, school }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');
  const [viewingExam, setViewingExam] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const requestRef = useRef(0);
  const viewRequestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    ++viewRequestRef.current;
    setViewLoading(false);
    setLoading(true);
    try {
      const { ok, data } = await schoolFetch('/school/exams/requests');
      if (requestId !== requestRef.current) return;
      if (ok && Array.isArray(data.requests)) {
        const next = data.requests;
        setRequests(next);
        setViewingExam(current => current && !next.some(exam => exam._id === current._id) ? null : current);
        setRejectingId(current => current && !next.some(exam => exam._id === current) ? null : current);
      } else {
        setRequests([]);
        setViewingExam(null);
        setRejectingId(null);
        setError(data?.error || 'Could not refresh exam requests.');
      }
    } catch {
      if (requestId === requestRef.current) {
        setRequests([]);
        setViewingExam(null);
        setRejectingId(null);
        setError('Connection error. Could not refresh exam requests.');
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useSchoolExamSync(load, id => {
    ++requestRef.current;
    ++viewRequestRef.current;
    setViewLoading(false);
    setRequests(current => current.filter(exam => String(exam._id) !== id));
    setViewingExam(current => String(current?._id) === id ? null : current);
    setRejectingId(current => String(current) === id ? null : current);
  });

  const handleApprove = async (id) => {
    setBusyId(id); setError('');
    const { ok, status, data } = await schoolFetch(`/school/exams/${id}/approve`, { method: 'POST' });
    setBusyId('');
    if (ok) { setViewingExam(null); load(); if (onChanged) onChanged(); }
    else {
      setError(data?.error || 'Failed to approve exam');
      if (status === 404 || status === 409) { setViewingExam(null); load(); }
    }
  };

  const openReject = (idOrExam) => {
    const id = typeof idOrExam === 'string' ? idOrExam : idOrExam._id;
    setViewingExam(null);
    setRejectingId(id);
    setRejectionReason('');
  };

  const submitReject = async () => {
    if (!rejectingId) return;
    setBusyId(rejectingId); setError('');
    const { ok, status, data } = await schoolFetch(`/school/exams/${rejectingId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejectionReason }),
    });
    setBusyId('');
    if (ok) { setRejectingId(null); setRejectionReason(''); load(); if (onChanged) onChanged(); }
    else {
      setError(data?.error || 'Failed to reject exam');
      if (status === 404 || status === 409) { setRejectingId(null); load(); }
    }
  };

  const openView = async (id) => {
    const requestId = ++viewRequestRef.current;
    setViewLoading(true); setError('');
    const { ok, data } = await schoolFetch(`/school/exams/requests/${id}`);
    if (requestId !== viewRequestRef.current) return;
    setViewLoading(false);
    if (ok) setViewingExam(data.exam);
    else setError(data?.error || 'Failed to load exam');
  };

  if (!loading && requests.length === 0 && !error) return null;

  return (
    <div className={css.card} style={{ marginBottom: 18 }}>
      <div className={css.cardHeader}>
        <div>
          <h2 className={css.cardTitle}>Exam Requests</h2>
          <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>Teacher exams awaiting your approval before they can be scheduled</span>
        </div>
      </div>
      {error && <div className={css.inlineError}>{error} <button className={css.btnSecondary} onClick={() => { setError(''); load(); }}>Retry</button></div>}
      {loading ? (
        <SkeletonTableRows rows={2} cols={6} />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Subject</th>
                <th>Class</th>
                <th>Teacher</th>
                <th>Questions</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r._id}>
                  <td className={css.bold}>{r.title}</td>
                  <td>{r.subjectName}</td>
                  <td>{r.className}{r.classArm ? ` ${r.classArm}` : ''}</td>
                  <td>{r.teacher ? `${r.teacher.firstName} ${r.teacher.lastName}` : '—'}</td>
                  <td>{r.questionsCount}</td>
                  <td style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
                    {r.requestedAt ? new Date(r.requestedAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className={css.btnSecondary}
                        style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                        disabled={viewLoading}
                        onClick={() => openView(r._id)}
                        title="View & Edit before deciding"
                      >
                        <VisibilityIcon style={{ fontSize: '0.9rem' }} /> View
                      </button>
                      <button
                        className={css.btnPrimary}
                        style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                        disabled={busyId === r._id}
                        onClick={() => handleApprove(r._id)}
                      >
                        {busyId === r._id ? '…' : 'Approve'}
                      </button>
                      <button
                        className={css.btnSecondary}
                        style={{ padding: '5px 10px', fontSize: '0.78rem', color: '#e53935' }}
                        disabled={busyId === r._id}
                        onClick={() => openReject(r._id)}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejectingId && (
        <div className={css.modalOverlay} onClick={() => setRejectingId(null)}>
          <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Reject Exam Request</h2>
              <button className={css.closeBtn} onClick={() => setRejectingId(null)}>&times;</button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Reason (optional, shown to teacher)</label>
                <textarea
                  className={css.formInput}
                  rows={3}
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="e.g. Please revise question 3 for clarity"
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnPrimary} onClick={submitReject} disabled={busyId === rejectingId}>
                {busyId === rejectingId ? 'Rejecting…' : 'Confirm Reject'}
              </button>
              <button className={css.btnSecondary} onClick={() => setRejectingId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {viewingExam && (
        <AdminExamModal
          exam={viewingExam}
          subjects={subjects}
          classes={classes}
          school={school}
          reviewRequest
          onClose={() => setViewingExam(null)}
          onSaved={updated => setViewingExam(current => current?._id === updated._id ? updated : current)}
          onApprove={handleApprove}
          onReject={openReject}
        />
      )}
    </div>
  );
}

function AdminExamsTab({ school, subjects, classes }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterClassId, setFilterClassId] = useState('');
  const [filterClassArm, setFilterClassArm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [showModeChoice, setShowModeChoice] = useState(false);
  const [createExamMode, setCreateExamMode] = useState('cbt');
  const [editExam, setEditExam] = useState(null);
  const [viewResultsExam, setViewResultsExam] = useState(null);
  const [deleteConfirmExam, setDeleteConfirmExam] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);

  const [loadError, setLoadError] = useState('');
  const examListRequestRef = useRef(0);
  const filterArms = classes.find(c => String(c._id) === filterClassId)?.arms || [];

  const examHasStarted = exam => exam.settings?.startDate && new Date(exam.settings.startDate) <= new Date();

  const loadExams = useCallback(async () => {
    const requestId = ++examListRequestRef.current;
    setLoading(true);
    setLoadError('');
    let requestedPage = page;
    try {
      while (true) {
        const params = new URLSearchParams({ page: requestedPage, limit: 15 });
        if (filterStatus) params.set('status', filterStatus);
        if (filterSubject) params.set('subjectId', filterSubject);
        if (filterTerm) params.set('term', filterTerm);
        if (filterSession) params.set('session', filterSession);
        if (filterClassId) params.set('classId', filterClassId);
        if (filterClassArm) params.set('classArm', filterClassArm);
        const { ok, data } = await schoolFetch(`/school/exams?${params}`);
        if (requestId !== examListRequestRef.current) return;
        if (!ok) {
          setExams([]);
          setLoadError(data?.error || 'Could not refresh exams. Please retry.');
          return;
        }
        if (!Array.isArray(data.exams) || !data.pagination) {
          setExams([]);
          setLoadError('Could not load the exam list. Please retry.');
          return;
        }
        const lastPage = Math.max(1, data.pagination?.pages || 0);
        if (requestedPage > lastPage) { requestedPage = lastPage; continue; }
        setExams(data.exams || []);
        setPagination(data.pagination || {});
        if (requestedPage !== page) setPage(requestedPage);
        return;
      }
    } catch {
      if (requestId === examListRequestRef.current) {
        setExams([]);
        setLoadError('Connection error. Could not refresh exams. Please retry.');
      }
    } finally {
      if (requestId === examListRequestRef.current) setLoading(false);
    }
  }, [page, filterStatus, filterSubject, filterTerm, filterSession, filterClassId, filterClassArm]);

  useEffect(() => { loadExams(); }, [loadExams]);

  useSchoolExamSync(loadExams, id => {
    ++examListRequestRef.current;
    setExams(current => current.filter(exam => String(exam._id) !== id));
    setEditExam(current => String(current?._id) === id ? null : current);
    setViewResultsExam(current => String(current?._id) === id ? null : current);
    setDeleteConfirmExam(current => String(current?._id) === id ? null : current);
  });

  const handleDuplicate = async exam => {
    setDuplicatingId(exam._id);
    const { ok, data } = await schoolFetch(`/school/exams/${exam._id}/duplicate`, { method: 'POST' });
    setDuplicatingId(null);
    if (ok && data.exam) setExams(prev => [data.exam, ...prev]);
    else alert(data?.error || 'Duplicate failed');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmExam) return;
    const isDraft = deleteConfirmExam.status === 'draft';
    if (!isDraft && !deletePassword.trim()) { setDeleteError('Enter your password to confirm'); return; }
    setIsDeleting(true); setDeleteError('');
    const body = isDraft ? {} : { password: deletePassword };
    const { ok, data } = await schoolFetch(`/school/exams/${deleteConfirmExam._id}`, { method: 'DELETE', body: JSON.stringify(body) });
    setIsDeleting(false);
    if (ok) { setDeleteConfirmExam(null); setDeletePassword(''); loadExams(); }
    else setDeleteError(data?.error || 'Delete failed');
  };

  const handleSetCategory = async (id, category) => {
    const { ok, data } = await schoolFetch(`/school/exams/${id}`, { method: 'PATCH', body: JSON.stringify({ examCategory: category }) });
    if (ok) loadExams(); else alert(data?.error || 'Update failed');
  };



  return (
    <div>
      {loadError && <div className={css.inlineError} role="alert">{loadError} <button className={css.btnSecondary} onClick={loadExams}>Retry</button></div>}
      {school?.controlSettings?.questionsApprovalRequired && (
        <AdminExamRequestsTable onChanged={loadExams} subjects={subjects} classes={classes} school={school} />
      )}
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Tests & Exams</h2>
        <div className={css.tabBarRight}>
          <select className={css.filterSelect} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="closed">Closed</option>
          </select>
          <select className={css.filterSelect} value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(1); }}>
            <option value="">All Subjects</option>
            {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <select className={css.filterSelect} value={filterTerm} onChange={e => { setFilterTerm(e.target.value); setPage(1); }}>
            <option value="">All Terms</option>
            <option value="first">First Term</option>
            <option value="second">Second Term</option>
            <option value="third">Third Term</option>
          </select>
          <select className={css.filterSelect} value={filterClassId} onChange={e => { setFilterClassId(e.target.value); setFilterClassArm(''); setPage(1); }}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className={css.filterSelect} value={filterClassArm} onChange={e => { setFilterClassArm(e.target.value); setPage(1); }} disabled={!filterClassId}>
            <option value="">All Arms</option>
            {filterArms.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            className={css.filterSelect}
            style={{ width: 140 }}
            type="text"
            name="examSessionFilter"
            autoComplete="off"
            placeholder="Session e.g. 2025/2026"
            value={filterSession}
            onChange={e => { setFilterSession(e.target.value); setPage(1); }}
          />
          <button className={css.primaryBtn} onClick={() => setShowModeChoice(true)}><AddIcon fontSize="small" /> New Exam</button>
        </div>
      </div>

      {school?.currentTerm && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fafb', border: '1px solid #b2e0e3', borderRadius: 9, padding: '8px 14px', marginBottom: 12, fontSize: '0.83rem', color: '#1a6e73' }}>
          <span style={{ fontWeight: 700 }}>Active Term:</span>
          <span>{school.currentTerm === 'first' ? 'First' : school.currentTerm === 'second' ? 'Second' : 'Third'} Term{school.academicSession ? ` · ${school.academicSession}` : ''}</span>
        </div>
      )}

      {loading ? <SkeletonTableRows rows={5} cols={10} /> : loadError ? null : exams.length === 0 ? (
        <div className={css.emptyState}>No exams yet. Create your first one!</div>
      ) : (
        <div className={css.tableWrap}>
          <table className={`${css.table} ${css.examsTable}`}>
            <thead>
              <tr>
                <th>Title</th><th>Subject(s)</th><th>Class</th><th>Term</th><th>Session</th>
                <th>Questions</th><th>Start</th><th>End</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exams.map(exam => {
                const teacherOwned = !!exam.teacherId;
                return (
                <tr key={exam._id}>
                  <td className={css.bold}>
                    {exam.title}
                    {exam.isMultiSubject && <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#e0f7fa', color: '#0c7c84', borderRadius: 99, padding: '1px 7px', fontWeight: 700 }}>MULTI</span>}
                    {teacherOwned && (
                      <div style={{ marginTop: 3, fontSize: '0.72rem', color: '#7c3aed', fontWeight: 600 }}>
                        By {exam.teacherId.firstName} {exam.teacherId.lastName}
                      </div>
                    )}
                  </td>
                  <td>{exam.isMultiSubject ? (exam.subjects || []).map(s => s.subjectName).join(', ') : (exam.subjectName || '—')}</td>
                  <td>{exam.classId?.name || '—'}{exam.classArm ? ` (${exam.classArm})` : ''}</td>
                  <td>{exam.settings?.term ? (exam.settings.term === 'first' ? 'First' : exam.settings.term === 'second' ? 'Second' : 'Third') : '—'}</td>
                  <td>{exam.settings?.session || '—'}</td>
                  <td>{(exam.questions || []).length}</td>
                  <td className={css.dateCell}>{renderExamDate(exam.settings?.startDate)}</td>
                  <td className={css.dateCell}>{renderExamDate(exam.settings?.endDate)}</td>
                  <td><StatusChip status={exam.status === 'pending_approval' ? 'pending_approval' : exam.examMode === 'manual_scores' ? (exam.manualScoreStatus === 'recorded' ? 'manual_score_recorded' : 'manual_score_ready') : exam.examMode === 'manual' && exam.status === 'closed' && exam.manualGradingStatus === 'ready' ? 'manual_grading_ready' : exam.status} /></td>
                  <td>
                    <ExamActionsMenu
                      css={css}
                      actions={[
                        exam.status !== 'closed' && (exam.status === 'draft' || !examHasStarted(exam)) && {
                          key: 'edit', label: 'Edit exam', icon: <EditIcon fontSize="small" />, onClick: () => setEditExam(exam),
                        },
                        (exam.status === 'draft' || (exam.status === 'scheduled' && !examHasStarted(exam))) && {
                          key: 'duplicate', label: 'Duplicate exam', icon: <ContentCopyIcon fontSize="small" />, accent: true,
                          onClick: () => handleDuplicate(exam), disabled: duplicatingId === exam._id,
                        },
                        (exam.status === 'draft' || (exam.settings?.term === school?.currentTerm && exam.settings?.session === school?.academicSession)) && {
                          key: 'delete', label: 'Delete exam', icon: <DeleteIcon fontSize="small" />, danger: true,
                          onClick: () => { setDeletePassword(''); setDeleteError(''); setDeleteConfirmExam(exam); },
                        },
                        exam.status !== 'draft' && {
                          key: 'results', label: exam.examMode === 'manual_scores' ? 'Record/View Scores' : exam.examMode === 'manual' && exam.status === 'closed' && exam.manualGradingStatus === 'ready' ? 'Upload Scripts to Grade' : 'View results', icon: <BarChartIcon fontSize="small" />, accent: true,
                          onClick: () => setViewResultsExam(exam),
                        },
                        exam.status !== 'draft' && exam.settings?.resultVisibility === 'manual' && !exam.resultsReleasedAt && {
                          key: 'release', label: 'Release results', icon: <LockIcon fontSize="small" />,
                          onClick: async () => {
                            if (!window.confirm('Release results to all submitted students?')) return;
                            const { ok, data } = await schoolFetch(`/school/exams/${exam._id}/release-results`, { method: 'POST' });
                            if (ok) { alert(data.message || 'Results released!'); loadExams(); }
                            else alert(data?.error || 'Failed');
                          },
                        },
                      ].filter(Boolean)}
                      releasedAt={exam.resultsReleasedAt}
                      categories={['ca', 'exam', 'test'].map(key => {
                        const labels = { ca: 'CA', exam: 'Exam', test: 'Test' };
                        const active = (exam.examCategory || 'test') === key;
                        const started = examHasStarted(exam);
                        return {
                          key, label: labels[key], active, disabled: active || started,
                          title: active ? `Currently set as ${labels[key]}` : started ? 'Cannot change — exam started' : `Switch to ${labels[key]}`,
                          onClick: () => setPendingCategory({ examId: exam._id, category: key, label: labels[key] }),
                        };
                      })}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className={css.pagination} style={{ justifyContent: 'center' }}>
          <button className={css.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          <span className={css.pageInfo}>{page} / {pagination.pages}</span>
          <button className={css.pageBtn} disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {showModeChoice && (
        <ExamModeChoiceModal
          css={css}
          onClose={() => setShowModeChoice(false)}
          onSelect={mode => { setCreateExamMode(mode); setShowModeChoice(false); setShowCreate(true); }}
        />
      )}
      {showCreate && <AdminExamModal exam={null} subjects={subjects} classes={classes} school={school} initialExamMode={createExamMode} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); loadExams(); }} onDraftSaved={loadExams} />}
      {editExam && <AdminExamModal exam={editExam} subjects={subjects} classes={classes} school={school} onClose={() => setEditExam(null)} onSaved={() => { setEditExam(null); loadExams(); }} onDraftSaved={loadExams} />}
      {viewResultsExam && <AdminExamPerformanceModal exam={viewResultsExam} onClose={() => setViewResultsExam(null)} onChanged={loadExams} />}



      {/* Category confirm */}
      {pendingCategory && (
        <div className={css.modalOverlay} onClick={() => setPendingCategory(null)}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}><h2 className={css.modalTitle}>Change Category</h2><button className={css.closeBtn} onClick={() => setPendingCategory(null)}>X</button></div>
            <div className={css.modalBody}><p style={{ color: '#455a64', fontSize: '0.92rem' }}>Switch to <strong>{pendingCategory.label}</strong>? This affects term reports.</p></div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setPendingCategory(null)}>Cancel</button>
              <button className={css.saveBtn} onClick={() => { handleSetCategory(pendingCategory.examId, pendingCategory.category); setPendingCategory(null); }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirmExam && (
        <div className={css.modalOverlay} onClick={() => { if (!isDeleting) setDeleteConfirmExam(null); }}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}><h2 className={css.modalTitle}>Delete Exam</h2><button className={css.closeBtn} onClick={() => setDeleteConfirmExam(null)} disabled={isDeleting}>X</button></div>
            <div className={css.modalBody}>
              <p style={{ marginBottom: 12, color: '#455a64', fontSize: '0.92rem' }}>Permanently delete <strong>"{deleteConfirmExam.title}"</strong>? This removes its assigned attempts, current and archived results, uploaded scripts, linked calendar event, and related inbox notifications. This cannot be undone.</p>
              {deleteConfirmExam.status !== 'draft' && (
                <>
                  <p style={{ color: '#b91c1c', fontSize: '0.88rem', fontWeight: 600, marginBottom: 8 }}>This exam has been published. Enter your password to confirm.</p>
                  <SchoolPasswordInput className={css.formInput} name="deleteExamConfirmPassword" autoComplete="new-password" placeholder="Your password" value={deletePassword} onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }} autoFocus />
                </>
              )}
              {deleteError && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{deleteError}</p>}
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setDeleteConfirmExam(null)} disabled={isDeleting}>Cancel</button>
              <button className={css.saveBtn} style={{ background: '#dc2626' }} onClick={handleDeleteConfirm} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

//  Force Change Password Modal 
function ForceChangePasswordModal({ onDone }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await schoolFetch('/school/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword, confirmPassword: form.confirmPassword })
      });
      if (res.ok) {
        localStorage.removeItem('school-must-change-pwd');
        setSuccess(true);
        setTimeout(onDone, 1500);
      } else {
        setError(res.data.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(12,30,40,0.92)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '40px 36px',
        width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <img src="/favicon.ico" alt="Bubble Ai" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#5fbec5' }}>Bubble Ai Schools</span>
        </div>
        <h2 style={{ margin: '0 0 6px', color: '#0C1E28', fontSize: '1.25rem' }}>Set your password</h2>
        <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '0.88rem' }}>
          For security, you must change your temporary password before continuing.
        </p>

        {success ? (
          <p style={{ color: '#3e8f93', fontWeight: 600, textAlign: 'center' }}>Password changed! Loading your dashboard…</p>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && <div style={{ color: '#e53935', fontSize: '0.85rem', background: '#fff0f0', borderRadius: 8, padding: '8px 12px' }}>{error}</div>}
            <SchoolPasswordInput
              placeholder="Temporary password (from your email)" required
              value={form.currentPassword} onChange={set('currentPassword')}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: '0.95rem' }}
            />
            <SchoolPasswordInput
              placeholder="New password (min 8 characters)" required
              value={form.newPassword} onChange={set('newPassword')}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: '0.95rem' }}
            />
            <SchoolPasswordInput
              placeholder="Confirm new password" required
              value={form.confirmPassword} onChange={set('confirmPassword')}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: '0.95rem' }}
            />
            <button
              type="submit" disabled={loading}
              style={{
                background: '#3e8f93', color: '#fff', border: 'none',
                borderRadius: 8, padding: '12px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer'
              }}
            >
              {loading ? 'Saving…' : 'Set Password & Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function SchoolAdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return NAV.some(item => item.key === requested) ? requested : 'overview';
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profile, setProfile]     = useState(null);
  const [adminId, setAdminId]     = useState(null);
  const [adminScope, setAdminScope] = useState({ role: 'owner', accessTabs: [], roleTitle: '', email: '', firstName: '', lastName: '' });
  const [classes, setClasses]     = useState([]);
  const [subjects, setSubjects]   = useState([]);
  const [teachers, setTeachers]   = useState([]);
  const [stats, setStats]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [authError, setAuthError] = useState('');
  const [approvalNotice, setApprovalNotice] = useState('');
  const [mustChangePwd, setMustChangePwd] = useState(() => localStorage.getItem('school-must-change-pwd') === '1');

  useEffect(() => {
    const handleQueued = event => {
      setApprovalNotice(event.detail?.message || 'Your request was submitted for owner admin approval.');
      window.setTimeout(() => setApprovalNotice(''), 5000);
    };
    window.addEventListener('school-sub-admin-action-queued', handleQueued);
    return () => window.removeEventListener('school-sub-admin-action-queued', handleQueued);
  }, []);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('school-token');
    const actor = localStorage.getItem('school-actor');
    if (!token || actor !== 'admin') {
      navigate('/school', { replace: true });
    }
  }, [navigate]);

  // Bootstrap: fetch profile + classes + stats
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [profileRes, classesRes] = await Promise.all([
          schoolFetch('/school/profile'),
          schoolFetch('/school/classes'),
        ]);

        if (!profileRes.ok) {
          if (profileRes.status === 401 || profileRes.status === 403) {
            localStorage.removeItem('school-token');
            localStorage.removeItem('school-actor');
            navigate('/school', { replace: true });
            return;
          }
          setAuthError('Failed to load school data.');
          return;
        }

        if (!cancelled) {
          setProfile(profileRes.data.school);
          setAdminId(profileRes.data.adminId || null);
          setAdminScope({
            role: profileRes.data.role || 'owner',
            accessTabs: profileRes.data.accessTabs || [],
            roleTitle: profileRes.data.roleTitle || '',
            email: profileRes.data.adminEmail || '',
            firstName: profileRes.data.adminFirstName || '',
            lastName: profileRes.data.adminLastName || '',
          });
          setClasses(classesRes.data.classes || []);
        }

        // Light stats: student + teacher counts
        const [studRes, allStudRes, teachRes, subjectRes, demoRes] = await Promise.all([
          schoolFetch('/school/students?limit=1&status=active'),
          schoolFetch('/school/students?limit=1&status=all'),
          schoolFetch('/school/teachers'),
          schoolFetch('/school/subjects'),
          schoolFetch('/school/overview/demographics'),
        ]);
        if (!cancelled) {
          setSubjects(subjectRes.data.subjects || []);
          setTeachers(teachRes.data.teachers || []);
          setStats({
            students: studRes.data.pagination?.total ?? demoRes.data.students ?? 0,
            totalStudents: allStudRes.data?.pagination?.total ?? null,
            teachers: (teachRes.data.teachers || []).length,
            classes:  (classesRes.data.classes || []).length,
            subjects: (subjectRes.data.subjects || []).length,
            male:         demoRes.data.male ?? 0,
            female:       demoRes.data.female ?? 0,
            unclassified: demoRes.data.unclassified ?? 0,
            averageAge:   demoRes.data.averageAge ?? null,
            parents:      demoRes.data.parents ?? 0,
          });
        }
      } catch {
        if (!cancelled) setAuthError('Could not connect. Please refresh.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('school-token');
    localStorage.removeItem('school-actor');
    navigate('/school', { replace: true });
  };

  if (loading) {
    return <Fetching />;
  }

  if (authError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
        <p style={{ color: '#c62828', fontWeight: 600 }}>{authError}</p>
        <button className={css.btnPrimary} onClick={() => navigate('/school')}>Back to Login</button>
      </div>
    );
  }

  const isSubAdmin = adminScope.role === 'sub_admin';
  const visibleNav = isSubAdmin
    ? NAV.filter(item => item.key !== 'sub-admins' && (item.key === 'overview' || item.key === 'change-password' || adminScope.accessTabs.includes(item.key)))
    : NAV;
  const pageTitle = NAV.find(n => n.key === activeTab)?.label || 'Dashboard';

  return (
    <div className={css.shell}>
      {/* Force password change overlay — shown on first login */}
      {mustChangePwd && <ForceChangePasswordModal onDone={() => setMustChangePwd(false)} />}
      {mobileNavOpen && <div className={css.navOverlay} onClick={() => setMobileNavOpen(false)} />}
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className={`${css.sidebar} ${mobileNavOpen ? css.sidebarOpen : ''}`}>
        <div className={css.sidebarLogo}>
          <img src={profile?.logo || '/favicon.ico'} alt={profile?.name || 'Bubble Ai Schools'} className={css.sidebarLogoImg} />
          <span className={css.sidebarLogoText}>{profile?.name || 'Bubble Ai Schools'}</span>
        </div>

        {visibleNav.map(({ key, label, Icon }) => (
          <div
            key={key}
            className={`${css.navItem} ${activeTab === key ? css.navItemActive : ''}`}
            onClick={() => { setActiveTab(key); setMobileNavOpen(false); }}
          >
            <Icon className={css.navIcon} />
            {label}
          </div>
        ))}

        <div className={css.sidebarSpacer} />
        <div className={css.sidebarFooter}>
          {adminScope.email && <div className={css.adminEmail}>{adminScope.email}</div>}
          <button className={css.logoutBtn} onClick={handleLogout}>
            <LogoutIcon style={{ fontSize: '1.1rem' }} /> Logout
          </button>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <div className={css.main}>
        {/* Top bar */}
        <header className={css.topbar}>
          <div className={css.topbarLeft}>
            <button className={css.hamburgerBtn} onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
              <MenuIcon fontSize="small" />
            </button>
            <h1 className={css.topbarTitle}>{pageTitle}</h1>
          </div>
          <div className={css.topbarRight}>
            <SchoolNotificationCenter inline />
          </div>
        </header>

        {/* Page body */}
        <main className={css.pageBody}>
          {approvalNotice && <div className={css.inlineSuccess} style={{ marginBottom: 16 }} role="status">✓ {approvalNotice}</div>}
          {activeTab === 'overview' && <SchoolPWAInstallBanner />}
          {activeTab === 'overview'   && <OverviewTab profile={profile} stats={stats} onGoToSettings={() => setActiveTab('settings')} isOwner={!isSubAdmin} />}
          {activeTab === 'students'   && <StudentsTab school={profile} classes={classes} />}
          {activeTab === 'parents'    && <ParentsTab school={profile} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'teachers'   && <TeachersTab school={profile} subjects={subjects} classes={classes} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} onRefreshStats={() => setStats(s => ({ ...s }))} />}
          {activeTab === 'classes'    && <ClassesSubjectsTab isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'curriculum' && <CurriculumOverviewTab classes={classes} subjects={subjects} school={profile} />}
          {activeTab === 'exams'      && <AdminExamsTab school={profile} subjects={subjects} classes={classes} />}
          {activeTab === 'fees'       && <FeesBillingTab school={profile} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'calendar'   && <CalendarTab school={profile} classes={classes} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'timetable'  && <TimetableGrid mode="multi" classes={classes} subjects={subjects} teachers={teachers} editable fetchFn={schoolFetch} basePath="/school" term={profile?.currentTerm} session={profile?.academicSession} managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false} />}
          {activeTab === 'attendance' && <AttendanceTab school={profile} classes={classes} subjects={subjects} />}
          {activeTab === 'lesson-plans' && <LessonPlansAdminTab school={profile} classes={classes} subjects={subjects} />}
          {activeTab === 'expenses'   && <ExpensesTab school={profile} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'payroll'    && <PayrollTab school={profile} isOwner={!isSubAdmin} onNavigate={tab => setActiveTab(tab)} />}
          {activeTab === 'inventory'  && <InventoryTab school={profile} />}
          {activeTab === 'messages'   && (
            <MessagingSegment
              fetchFn={schoolFetchRaw}
              basePath="/school"
              actorType="school_admin"
              currentActorId={adminId}
              managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false}
              canCreateClassGroup
              canCreateAnnouncement
              classOptions={classes}
            />
          )}
          {activeTab === 'analytics'  && <AnalyticsTab school={profile} />}
          {activeTab === 'reports'    && <ReportsTab school={profile} classes={classes} onConfigureReportCards={() => { setActiveTab('settings'); setTimeout(() => document.getElementById('report-card-design')?.scrollIntoView({ behavior: 'smooth' }), 0); }} onConfigureMidTermReports={() => { setActiveTab('settings'); setTimeout(() => document.getElementById('mid-term-report-settings')?.scrollIntoView({ behavior: 'smooth' }), 0); }} />}
          {activeTab === 'settings'   && <SettingsTab profile={profile} onProfileUpdated={updated => setProfile(prev => ({ ...prev, ...updated }))} />}
          {activeTab === 'change-password' && <ChangePasswordTab />}
          {activeTab === 'sub-admins' && !isSubAdmin && <SubAdminsTab onNavigate={tab => { setActiveTab(tab); setMobileNavOpen(false); }} />}
          <SchoolPoweredFooter role="admin" />
        </main>
      </div>
    </div>
  );
}
